// Node.js test-mode stubs -- must run before module init so that document,
// performance, etc. are available when state and refs are initialised.
if (typeof window === 'undefined') {
  const _noop = () => {};
  const _fakeCtx: any = {
    imageSmoothingEnabled: false,
    canvas: { width: 960, height: 544 },
    fillRect: _noop, strokeRect: _noop, clearRect: _noop,
    fillText: _noop, strokeText: _noop, drawImage: _noop,
    save: _noop, restore: _noop, scale: _noop, translate: _noop,
    beginPath: _noop, moveTo: _noop, lineTo: _noop, arc: _noop,
    stroke: _noop, fill: _noop, clip: _noop, closePath: _noop,
    bezierCurveTo: _noop, quadraticCurveTo: _noop, rotate: _noop,
    setTransform: _noop, transform: _noop,
    createPattern: () => null,
    createLinearGradient: () => ({ addColorStop: _noop }),
    putImageData: _noop,
    getImageData: () => ({ data: new Uint8ClampedArray(4) }),
    createImageData: () => ({ data: new Uint8ClampedArray(4) }),
    measureText: () => ({ width: 0 }),
  };
  const _fakeClassList = { add: _noop, remove: _noop, toggle: _noop, contains: () => false };
  const _fakeEl: any = {
    getContext: () => _fakeCtx,
    width: 1140,
    height: 600,
    style: {},
    classList: _fakeClassList,
    addEventListener: _noop,
    removeEventListener: _noop,
    setAttribute: _noop,
    removeAttribute: _noop,
    querySelector: () => null,
    querySelectorAll: () => [],
    appendChild: _noop,
    textContent: '',
    innerHTML: '',
  };
  (global as any).document = {
    getElementById: () => ({ ..._fakeEl }),
    createElement: () => ({ ..._fakeEl, width: 0, height: 0 }),
    querySelector: () => null,
    querySelectorAll: () => [],
    addEventListener: _noop,
    removeEventListener: _noop,
    body: { appendChild: _noop, classList: _fakeClassList },
  };
  (global as any).performance = { now: () => Date.now() };
  (global as any).requestAnimationFrame = _noop;
  (global as any).Image = function Image() { (this as any).onload = null; (this as any).src = null; };
  (global as any).HTMLCanvasElement = function () {};
  (global as any).window = {
    devicePixelRatio: 1,
    location: { search: '', pathname: '/' },
    addEventListener: _noop,
    removeEventListener: _noop,
    setTimeout,
    clearTimeout,
    requestAnimationFrame: _noop,
  };
}

// -- Imports from extracted modules ------------------------------------------------

import { canonicalGameId, canonicalTeamId, deriveReturnAbility, FRANCHISE_FIELD_BACKGROUNDS, FRANCHISE_POSSESSION_SONGS, getFranchiseById, getPlayerRating, getScheduledGame, hydrateSharedData, isSharedDataHydrated, versionFranchiseImageSrc } from '#shared';
import { CLIENT_ERROR_REPORT_PATH, createErrorReference, isErrorReference, serializeError, userFacingErrorMessage } from '#shared';
import { PLAY_TIMING, INTERMISSION_TIMING, REPLAY_HOLD_MS, LIVE_FRAME_MS, LIVE_SEQUENCE_VERSION } from './constants.js';

import { lerp, easeInOut, clamp01, snapToPixel, phaseAmount, clampRange, averageNumbers, escapeHtml } from './util/math.js';
import { liveFrameWindow, interpolateBallPoint, interpolateLivePlayers } from './util/live-frame.js';

import { orthogonalStep, naturalStep, stepPosition, catmullRomPointAt, catmullRomPoint, movementDistanceForStyle, pathDistance, pointDistance, spotReached, movePointTowards } from './util/interpolation.js';
import type { Point } from './util/interpolation.js';

import { rgbToHsl, hslToRgb } from '#shared';
import {
  normalizeHex,
  hexToRgb,
  rgbaFromHex,
  shiftHexColor,
  shiftRgbColor,
  isNeutralColorShift,
} from './util/color.js';

import {
  fieldMetrics as fieldMetricsParam,
  yardToX, worldToScreenX, screenXForYard, goalLineXForDirection,
  orientX, clampAdvanceX, goalPostWorldX, kickAttemptCameraTargetX, kickAttemptZoomScale,
  kickAttemptZoomFocusX, ballFrontX, ballBreaksPlane,
} from './field/metrics.js';
import { advanceComfortCamera, puntBallCameraTargetX, puntCameraMotionOptions } from './field/camera-motion.js';
import {
  exitNativeFullscreen,
  isElementFullscreen,
  nativeFullscreenElement,
  requestNativeFullscreen,
  setFallbackFullscreen,
} from './mobile-fullscreen.js';

import {
  createPlayer, clonePlayers, findPlayer, carriedBallPoint, centerSnapBallPoint,
  puntSnapBallPoint, fieldGoalSnapBallPoint,
  scrimmageBallPoint, blendBallPoint, ballAnchorPointsForPlayer, setPlayerState,
  setPlayersStill, nearestPlayerToPoint, regularPlayerSpriteOriginX, regularPlayerVisualCenterX,
} from './player/model.js';
import type { FieldPlayer, BallPoint } from './player/model.js';
import { resolveCelebrationBallOffset, resolvePlayerPresentationTransform } from './player/presentation-motion.js';
import {
  applyDefensiveReactionStagger,
  applyPursuitLaneSpacing,
  resolveAirborneBallShadow,
  resolveFumbleImpactCue,
  cpuVisualPolishTuning,
  usesAirborneBallPresentation,
} from './player/cpu-visual-polish.js';

import {
  stopReasonForPlay, stopPointForPlay, ribbonHeadlineForPlay, visualYardsForPlay,
  isInterceptionReturn, turnoverReturnYards, interceptionReturnEndYardLine,
  touchdownRunoutDepth, visualEndXForPlay, isScrimmageSnap, isSpecialTeamsPlay,
  isReturnTouchdown, isCompletedPassPlay, isInterceptionPlay, isSackPlay,
  playHash, playChance, pickDeterministic, pickDeterministicGroup,
} from './play/helpers.js';

import {
  buildTeamSpeedProfile, teamSpeedProfile, speedRatingForRole, liveTempoForSpeed,
  addLiveTempo, runPixelsPerSecondForSpeed, passPixelsPerSecondForSpeed,
  playerPixelsPerFrame,
} from './play/speed.js';

import {
  compactPathPoints, createPathSegment, createHoldSegment, evaluateTrack,
  polylinePointAt, sampleTrackPoint, createMotionState, velocityMagnitude,
  dampMotionState, guidePointAt, projectedPoint, steerMotionState,
  playersFromMotionStates, freezeLiveFrame, cloneLiveFrame,
  spreadTargetAwayFromPeers, roleSpacingDistance,
} from './play/track.js';

import {
  buildScrimmageFormation, buildKickoffFormation, buildPuntFormation,
  buildFieldGoalFormation, buildFormation,
} from './play/formation.js';

import {
  directionForOffense as directionForOffenseParam,
  liveFrameCount, kickoffFlightWindow, livePlayDurationMs, playDurationMs,
  puntFlightWindow, scrimmageChangeoverMs,
  buildPlayContext as buildPlayContextParam,
} from './play/context.js';
import { assignPersonnelToFormation, transitionPersonnelPlayers } from './play/personnel.js';

import {
  buildRouteShapePoints, buildPostCatchPoints, interceptionReturnEndPoint,
  buildInterceptionReturnPoints, turnoverDefenderRole, kickoffReturnLaneY,
  kickoffFinishY, buildRunWaypoints, buildLossRunWaypoints, liveSequenceCacheKey,
  routePlan, lossRunLaneY,
} from './play/waypoints.js';

import { buildKickoffLiveSequenceV4 } from './play/live-kickoff.js';
import { buildRunLiveSequence } from './play/live-run.js';
import { buildPassLiveSequence } from './play/live-pass.js';
import { buildPuntLiveSequence } from './play/live-punt.js';
import { buildFieldGoalLiveSequence } from './play/live-field-goal.js';
import {
  divingHeroBaseElapsedMs,
  divingHeroPresentedDurationMs,
} from './play/diving-hero-moment.js';
import { teamPlayersAtPosition, teamReturnerPlayer } from '../team-roster.js';

function isScrimmageRunPass(play: any): boolean {
  return play?.family === "run" || play?.family === "pass";
}

function hasRawArcadeReplay(play: any): boolean {
  return play?.arcadeReplay?.source === "arcade-sim-v1"
    && play?.arcadePlaybackMode === "raw";
}

export function shouldHaltInsteadOfLegacyFallback(play: any): boolean {
  return isScrimmageRunPass(play);
}

  type VisualPlayerCacheEntry = {
    x: number;
    y: number;
    spriteFacing?: number;
    moveStyle?: string;
  };

  type StageLoadingPhase = {
    key: string;
    title: string;
    status: string;
    estimateMs: number;
  };

  type LiveBroadcastSync = {
    status?: string;
    currentPlayIndex?: number;
    currentPlayElapsedMs?: number;
    kickoffAt?: string;
    halftime?: {
      active?: boolean;
      phase?: "adjustments" | "preparing" | "finalizing";
      authorityReady?: boolean;
      durationMs?: number;
      remainingMs?: number;
      startsAt?: string;
      adjustmentsEndAt?: string;
      endsAt?: string;
    } | null;
  };

  export function halftimeCountdownPresentation(
    halftime: LiveBroadcastSync["halftime"],
    nowMs = Date.now(),
    options: { secondHalfStarted?: boolean } = {},
  ) {
    const endsAtMs = Date.parse(String(halftime?.endsAt || ""));
    const adjustmentsEndAtMs = Date.parse(String(halftime?.adjustmentsEndAt || ""));
    const durationMs = Math.max(1, Number(halftime?.durationMs) || 120_000);
    const authorityReady = Boolean(halftime?.authorityReady);
    const valid = Boolean(halftime?.active) && Number.isFinite(endsAtMs);
    const remainingMs = valid ? Math.max(0, endsAtMs - nowMs) : 0;
    // A stale response from a viewer that has not adopted the authoritative
    // halftime package must never put the badge back over third-quarter play.
    const visible = !options.secondHalfStarted && valid && (remainingMs > 0 || !authorityReady);
    const totalSeconds = Math.ceil(remainingMs / 1000);
    const phase = remainingMs <= 0 && !authorityReady
      ? "finalizing"
      : Number.isFinite(adjustmentsEndAtMs) && nowMs < adjustmentsEndAtMs
        ? "adjustments"
        : "preparing";
    const phaseLabel = phase === "adjustments"
      ? "ADJUSTMENTS OPEN"
      : phase === "preparing"
        ? "PREPARING SECOND HALF"
        : "FINALIZING SECOND HALF";

    return {
      visible,
      authorityReady,
      phase,
      phaseLabel,
      timeLabel: `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`,
      progressPercent: `${Math.max(0, Math.min(100, (remainingMs / durationMs) * 100)).toFixed(2)}%`,
    };
  }

  export function shouldHoldForHalftimeAuthority(
    presentation: ReturnType<typeof halftimeCountdownPresentation>,
  ) {
    return presentation.visible && !presentation.authorityReady;
  }

  const LIVE_SYNC_POLL_MS = 5_000;
  const LIVE_SYNC_INITIAL_OVERLAY_MS = 1_400;
  const LIVE_SYNC_KICKOFF_MIN_GRACE_MS = 12_000;

  export function liveBroadcastPollUrl(gameId: string, broadcastStatus?: string | null) {
    const baseUrl = `/api/games/${encodeURIComponent(gameId)}/live`;
    return broadcastStatus === "scheduled" ? baseUrl : `${baseUrl}?gameWindow=1`;
  }

  export function kickoffSyncGraceMs(
    openingPlayDurationMs: number,
    pollMs = LIVE_SYNC_POLL_MS,
  ) {
    const durationMs = Number.isFinite(openingPlayDurationMs)
      ? Math.max(0, openingPlayDurationMs)
      : 0;
    const pollingBufferMs = Number.isFinite(pollMs) ? Math.max(0, pollMs) : 0;
    return Math.max(LIVE_SYNC_KICKOFF_MIN_GRACE_MS, durationMs + pollingBufferMs);
  }

  export function halftimeKickoffIndex(plays: any[] = []): number {
    return plays.findIndex((play: any) => (
      Number(play?.quarter) >= 3
      && play?.playId === "kickoff"
      && String(play?.driveStartReason || "").toLowerCase().includes("halftime")
    ));
  }

  export function shouldPreservePregameKickoff(options: {
    viewerGameId?: string | null;
    currentGameId?: string | null;
    kickoffPresented?: boolean;
    broadcastStatus?: string | null;
  }) {
    return Boolean(
      options.viewerGameId
      && options.viewerGameId === options.currentGameId
      && !options.kickoffPresented
      && options.broadcastStatus === "live",
    );
  }

  export function shouldReloadPregamePackage(options: {
    hasLoadedGame: boolean;
    loadedGeneratedAt?: string | null;
    authoritativeGeneratedAt?: string | null;
  }) {
    return !options.hasLoadedGame
      || !options.loadedGeneratedAt
      || !options.authoritativeGeneratedAt
      || options.loadedGeneratedAt !== options.authoritativeGeneratedAt;
  }

  const AUDIO_ASSET_VERSION = "ambient-crowd-v1";
  const AMBIENT_CROWD_ASSET = `assets/sounds/ambientCrowd.mp3?v=${AUDIO_ASSET_VERSION}`;
  const HOSTED_SOUND_ASSET_BASE = "https://assets.dflplaymaker.com/assets/sounds";
  const DEFAULT_MATCH_MUSIC_ID = "fight-song-1";
  const BUMP_SOUND_ASSET = "synth:bump";
  const TACKLE_SOUND_ASSET = "synth:tackle";
  const CATCH_SOUND_ASSET = "synth:catch";
  const SNAP_SOUND_ASSET = "synth:snap";
  const LINE_SURGE_SOUND_ASSET = "synth:line-surge";
  const HANDOFF_SOUND_ASSET = "synth:handoff";
  const KICK_SOUND_ASSET = "synth:kick";
  const RETURN_FIELD_SOUND_ASSET = "synth:return-fielded";
  const FIRST_DOWN_SOUND_ASSET = "synth:first-down";
  const SACK_SOUND_ASSET = "synth:sack";
  const LOSS_SOUND_ASSET = "synth:loss";
  const INCOMPLETE_SOUND_ASSET = "synth:incomplete";
  const KICK_GOOD_SOUND_ASSET = "synth:kick-good";
  const KICK_NO_GOOD_SOUND_ASSET = "synth:kick-no-good";
  const PASS_THROW_SOUND_ASSET = "synth:throw";
  const TURNOVER_SOUND_ASSET = "synth:turnover";
  const DIVE_SPOTLIGHT_SOUND_ASSET = "synth:dive-spotlight";
  const TOUCHDOWN_SOUND_ASSET = "synth:touchdown";
  const CROWD_APPLAUSE_ASSET = `${HOSTED_SOUND_ASSET_BASE}/crowdApplause.mp3?v=big-play-v1`;
  const CROWD_APPLAUSE_TOUCHDOWN_VOLUME = 0.088;
  const SOUND_EFFECT_VOLUME_MULTIPLIER = 1.8;
  const AUDIO_MUTED_STORAGE_KEY = "dfl:sim-audio-muted";
  const AMBIENT_CROWD_VOLUME = 0.944;
  const AMBIENT_CROWD_CROSSFADE_SECONDS = 2.4;
  const MATCH_MUSIC_VOLUME = 0.504;
  const DEFAULT_MATCH_MUSIC_LOOP_START_SECONDS = 8.5;
  const DEFAULT_MATCH_MUSIC_LOOP_END_SECONDS: number | null = null;
  const MATCH_MUSIC_LOOP_POINTS: Record<string, { start: number; end: number | null }> = {
    "fight-song-1": { start: 7.5, end: 76.9 },
    "fight-song-2": { start: 7.25, end: null },
  };

  type AmbientCrowdLoop = {
    context: AudioContext;
    masterGain: GainNode;
    buffer: AudioBuffer | null;
    bufferAsset: string | null;
    loadingPromise: Promise<AudioBuffer> | null;
    sources: AudioBufferSourceNode[];
    timer: ReturnType<typeof setTimeout> | null;
    nextStartTime: number;
    nextSourceOffset: number;
    active: boolean;
  };

  function readStoredAudioMuted() {
    return true;
  }

  function writeStoredAudioMuted(muted: boolean) {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.setItem(AUDIO_MUTED_STORAGE_KEY, muted ? "true" : "false");
    } catch {
      // Storage can be unavailable in private or embedded browser contexts.
    }
  }

  const state: {
    bootstrap: any;
    game: any;
    playIndex: number;
    autoplay: boolean;
    playMode: string;
    lastAdvance: number;
    pausedElapsed: number | null;
    cameraX: number | null;
    cameraVelocityX: number;
    cameraUpdatedAt: number | null;
    intermissionCameraStartX: number | null;
    intermissionKey: string | null;
    renderedStatsCount: number;
    renderedPlayIndex: number;
    renderedCaption: string | null;
    renderedProgressPercent: number | null;
    prevResult: any;
    visualPlayerCache: Map<string, VisualPlayerCacheEntry>;
    fieldZoom: number;
    zoomFocusX: number | null;
    zoomFocusY: number | null;
    loadingActive: boolean;
    loadingError: boolean;
    loadingTitle: string;
    loadingStatus: string;
    loadingStartedAt: number | null;
    loadingEstimateMs: number;
    loadingProgress: number;
    loadingPhaseIndex: number;
    loadingPhases: StageLoadingPhase[];
    loadingRequestId: number;
    loadingFetchController: AbortController | null;
    liveSyncTimer: ReturnType<typeof setInterval> | null;
    liveSyncInFlight: boolean;
    liveSyncLastBroadcast: LiveBroadcastSync | null;
    liveBroadcastSlotMs: number | null;
    liveSyncOverlayUntil: number | null;
    liveSyncOverlayTitle: string;
    liveSyncOverlayStatus: string;
    liveSyncKickoffGraceUntil: number | null;
    pregameAuthorityPending: boolean;
    pregameAuthorityVersion: string | null;
    halftimeAuthorityVersion: string | null;
    scheduledPackageGeneratedAt: string | null;
    pregameViewerGameId: string | null;
    pregameKickoffPresented: boolean;
    halftimeKickoffPresented: boolean;
    pregamePreview: { away: any; home: any } | null;
    selectedLiveStatsTeam: "away" | "home";
    audioMuted: boolean;
    ambientCrowdAudio: AmbientCrowdLoop | null;
    matchMusicAudio: AmbientCrowdLoop | null;
    oneShotAudioBuffers: Map<string, Promise<AudioBuffer>>;
    recentSoundEffects: Map<string, number>;
    playedSoundEffects: Set<string>;
    playbackHaltKey: string | null;
    playbackHaltMessage: string | null;
  } = {
    bootstrap: null,
    game: null,
    playIndex: 0,
    autoplay: true,
    playMode: "continue", // "hold" | "replay" | "continue"
    lastAdvance: performance.now(),
    pausedElapsed: null,   // ms elapsed at time of pause; null when playing
    cameraX: null,
    cameraVelocityX: 0,
    cameraUpdatedAt: null,
    intermissionCameraStartX: null,
    intermissionKey: null,
    renderedStatsCount: -1,
    renderedPlayIndex: -1,
    renderedCaption: "",
    renderedProgressPercent: null,
    prevResult: null,
    visualPlayerCache: new Map(),
    fieldZoom: 1,
    zoomFocusX: null,
    zoomFocusY: null,
    loadingActive: true,
    loadingError: false,
    loadingTitle: "Loading game...",
    loadingStatus: "Preparing the broadcast.",
    loadingStartedAt: performance.now(),
    loadingEstimateMs: 2600,
    loadingProgress: 0,
    loadingPhaseIndex: 0,
    loadingPhases: [],
    loadingRequestId: 0,
    loadingFetchController: null,
    liveSyncTimer: null,
    liveSyncInFlight: false,
    liveSyncLastBroadcast: null,
    liveBroadcastSlotMs: null,
    liveSyncOverlayUntil: null,
    liveSyncOverlayTitle: "Syncing with live stream...",
    liveSyncOverlayStatus: "Aligning the broadcast clock and play state.",
    liveSyncKickoffGraceUntil: null,
    pregameAuthorityPending: false,
    pregameAuthorityVersion: null,
    halftimeAuthorityVersion: null,
    scheduledPackageGeneratedAt: null,
    pregameViewerGameId: null,
    pregameKickoffPresented: false,
    halftimeKickoffPresented: false,
    pregamePreview: null,
    selectedLiveStatsTeam: "away",
    audioMuted: readStoredAudioMuted(),
    ambientCrowdAudio: null,
    matchMusicAudio: null,
    oneShotAudioBuffers: new Map(),
    recentSoundEffects: new Map(),
    playedSoundEffects: new Set(),
    playbackHaltKey: null,
    playbackHaltMessage: null,
  };

  const currentPathname = typeof window !== "undefined" ? (window.location?.pathname || "/") : "/";
  const visualTestPage = currentPathname === "/visual-test.html" || currentPathname.endsWith("/visual-test.html");
  const assetBaseUrl = typeof window !== "undefined"
    ? String((window as any).__DFL_ASSET_BASE_URL__ || "").replace(/\/+$/, "")
    : "";
  const FIELD_ASSET_VERSION = "field-canvas-v3";
  const PLAYER_LAYER_ASSET_VERSION = "player-layer-baselines-v6";
  const MASK_SPRITE_ASSET_VERSION = "mask-sheets-v1";
  const GOBLIN_HELMET_VARIANT = 5;
  const GRASS_TEXTURE_ASSET = `assets/textures/grass-texture.png?v=${FIELD_ASSET_VERSION}`;
  const GOAL_POST_ASSET = `assets/sprites/fg-post.png?v=${FIELD_ASSET_VERSION}`;
  const GOAL_BASE_ASSET = `assets/sprites/fg-base.png?v=${FIELD_ASSET_VERSION}`;

  function fieldBackgroundOption(texture: any) {
    return FRANCHISE_FIELD_BACKGROUNDS.find((option) => option.id === texture)
      || FRANCHISE_FIELD_BACKGROUNDS[0];
  }

  function fieldBackgroundAsset(texture: any) {
    const option = fieldBackgroundOption(texture);
    return `${option.assetPath}?v=${FIELD_ASSET_VERSION}`;
  }

  let mobileStadiumBackdropCache: {
    canvas: HTMLCanvasElement;
    width: number;
    height: number;
    fieldBottom: number;
    visualKey: string;
    textureWidth: number;
    textureHeight: number;
  } | null = null;
  let mobileFieldSurfaceCache: {
    canvas: HTMLCanvasElement;
    width: number;
    height: number;
    key: string;
  } | null = null;

  function isMobilePerformanceMode() {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia("(pointer: coarse), (max-width: 820px)").matches;
  }

  function currentUrlParams() {
    return new URLSearchParams(window.location.search || "");
  }

  function versionedAssetPath(path: string, version: string) {
    return `${path}${path.includes("?") ? "&" : "?"}v=${version}`;
  }

  function playerLayerVariantAssetPath(layerId: string, variantIndex: number) {
    return versionedAssetPath(
      `assets/sprites/players/v2/${layerId}/${layerId}${variantIndex}.png`,
      PLAYER_LAYER_ASSET_VERSION,
    );
  }

  const NFT_PLAYER_SPRITE_ASSET_VERSION = "nft-player-layers-v2";
  const NFT_PLAYER_SPRITE_BASE_PATH = "assets/sprites/nftPlayers";
  // Hosted layer art is keyed by the catalog's cosmetic trait values under the
  // CDN naming convention: folder segments (species, trait folder) are
  // Capitalized, file names are lowercase —
  // <Species>/<TraitFolder>/<value>.png, including the body at <Species>/Body/<body>.png.
  // Order here is composite draw order (body first, then gear from feet up).
  // The 5-star aura is NOT a composite layer: it is animated art
  // (<Species>/Aura/<value>.gif, same 96×48 two-pose grid) drawn live as an
  // underlay beneath the cached sprite each frame — see drawNftAuraUnderlay.
  const NFT_APPEARANCE_LAYERS: Array<{ key: string; folder: string; tintKey?: string }> = [
    { key: "pants", folder: "Pants", tintKey: "pants" },
    { key: "shoes", folder: "Shoes", tintKey: "shoes" },
    { key: "jersey", folder: "Jersey", tintKey: "jersey" },
    { key: "gloves", folder: "Gloves", tintKey: "gloves" },
    { key: "facewear", folder: "Facewear" },
    { key: "helm", folder: "Helm" },
  ];
  const NFT_FOOTBALL_HELM_VALUES = new Set(["helmet", "footballhelmet", "vintage"]);

  function nftPlayerLayerAssetPath(species: string, folder: string, value: string) {
    return versionedAssetPath(
      `${NFT_PLAYER_SPRITE_BASE_PATH}/${encodeURIComponent(capitalizedSpriteFolder(species))}/${folder}/${encodeURIComponent(lowercaseSpriteFileName(value))}.png`,
      NFT_PLAYER_SPRITE_ASSET_VERSION,
    );
  }

export function normalizedNftAppearance(player: any) {
  const appearance = player?.appearance || {};
  const species = String(appearance.species || "").trim();
  if (!species) {
    return null;
  }
  const normalized: any = { species };
  const body = String(appearance.body || "").trim();
  if (body && body.toLowerCase() !== "none") {
    normalized.body = body;
  }
  NFT_APPEARANCE_LAYERS.forEach(({ key }) => {
    const value = String(appearance[key] || "").trim();
    if (value && value.toLowerCase() !== "none") {
      normalized[key] = value;
    }
  });
  // The 5-star aura rides along for the animated underlay (not a sheet layer).
  const aura = String(appearance.aura || "").trim();
  if (aura && aura.toLowerCase() !== "none") {
    normalized.aura = aura;
  }
  return normalized;
}

/** Mobile and desktop render the same authored NFT appearance, including auras. */
export function renderableNftAppearance(player: any, _mobileLite = isMobilePerformanceMode()) {
  return normalizedNftAppearance(player);
}

// CDN naming convention: folder segments (the species and trait folders) are
// Capitalized, file names (trait values) are lowercase. Trait values arrive in
// catalog casing, so both are normalized here rather than trusted.
function capitalizedSpriteFolder(segment: string) {
  const trimmed = segment.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function lowercaseSpriteFileName(value: string) {
  return value.trim().toLowerCase();
}

export function nftAuraAssetPath(species: string, aura: string) {
  return versionedAssetPath(
    `${NFT_PLAYER_SPRITE_BASE_PATH}/${encodeURIComponent(capitalizedSpriteFolder(species))}/Aura/${encodeURIComponent(lowercaseSpriteFileName(aura))}.gif`,
    NFT_PLAYER_SPRITE_ASSET_VERSION,
  );
}

export function imageAssetReady(image: any): boolean {
  return Boolean(
    image
    && (
      (image.complete && image.naturalWidth > 0)
      || (image.width > 0 && image.height > 0)
    )
  );
}

export function fieldLogoCacheSignature(src: string, image: any) {
  return {
    src,
    ready: imageAssetReady(image),
    width: image?.naturalWidth || image?.width || 0,
    height: image?.naturalHeight || image?.height || 0,
  };
}

export function nftPlayerLayerConfigs(appearance: any) {
  const layers: any[] = [{
    // Body is finished art keyed by the NFT Body trait. Older pool records may
    // not carry that trait, so the species-named body remains the hosted-catalog
    // compatibility fallback. id "skin" keeps the layer untinted.
    id: "skin",
    src: nftPlayerLayerAssetPath(appearance.species, "Body", appearance.body || appearance.species),
  }];
  NFT_APPEARANCE_LAYERS.forEach(({ key, folder, tintKey }) => {
    const value = appearance[key];
    if (!value) {
      return;
    }
    // Football-shell colors are authored differently for every species, so
    // the trait value is the reliable signal for applying the team helmet
    // color. Hats and other novelty headwear remain authored art.
    const brandingTintKey = tintKey
      || (key === "helm" && NFT_FOOTBALL_HELM_VALUES.has(String(value).toLowerCase().replace(/[^a-z0-9]/g, "")) ? "helmet" : undefined);
    layers.push({
      id: brandingTintKey || `nft-${key}`,
      src: nftPlayerLayerAssetPath(appearance.species, folder, value),
      // Not every trait value exists as art for every species — a missing
      // combination drops the layer, not the whole composed sheet.
      optional: true,
      ...(brandingTintKey ? {} : { noTint: true }),
    });
  });
  return layers;
}

export function nftSheetConfigForPalette(baseSheetConfig: any, palette: any) {
  const appearance = palette?.nftAppearance;
  if (!appearance?.species || !Array.isArray(baseSheetConfig?.layers)) {
    return null;
  }
  return {
    ...baseSheetConfig,
    src: ["nft", appearance.species, appearance.body || appearance.species, ...NFT_APPEARANCE_LAYERS.map(({ key }) => appearance[key] || "")].join("|"),
    layers: nftPlayerLayerConfigs(appearance),
  };
}

// -- 5-star aura underlay ----------------------------------------------------
// Aura art is an animated GIF on the same 96×48 two-pose grid as the layer
// sheets. It cannot live inside the static composed sheet (it would freeze),
// so it is decoded once per (species, aura) and drawn live beneath the cached
// sprite every frame. Without ImageDecoder support (or while decoding), the
// GIF's first frame renders via the normal image loader.
type NftAuraAnimation = { frames: any[]; durations: number[]; totalMs: number };
const nftAuraAnimations = new Map<string, NftAuraAnimation | "static" | "loading">();

function ensureNftAuraAnimation(src: string) {
  if (nftAuraAnimations.has(src)) {
    return;
  }
  if (
    typeof window === "undefined"
    || typeof (window as any).ImageDecoder !== "function"
    || typeof fetch !== "function"
    || typeof createImageBitmap !== "function"
  ) {
    nftAuraAnimations.set(src, "static");
    return;
  }
  nftAuraAnimations.set(src, "loading");
  (async () => {
    try {
      const response = await fetch(normalizeAssetSrc(src));
      if (!response.ok) {
        throw new Error(`aura fetch failed (${response.status})`);
      }
      const decoder = new (window as any).ImageDecoder({ data: await response.arrayBuffer(), type: "image/gif" });
      await decoder.tracks.ready;
      const frameCount = decoder.tracks.selectedTrack?.frameCount || 1;
      const frames: any[] = [];
      const durations: number[] = [];
      for (let index = 0; index < frameCount; index += 1) {
        const { image } = await decoder.decode({ frameIndex: index });
        frames.push(await createImageBitmap(image));
        // image.duration is microseconds; default to a retro 110ms tick.
        durations.push(Math.max(20, (image.duration || 110000) / 1000));
        image.close();
      }
      decoder.close();
      nftAuraAnimations.set(src, { frames, durations, totalMs: durations.reduce((total, ms) => total + ms, 0) });
    } catch {
      // Missing or undecodable art: keep the static first-frame fallback.
      nftAuraAnimations.set(src, "static");
    }
  })();
}

function currentNftAuraFrame(src: string, now: number) {
  const animation = nftAuraAnimations.get(src);
  if (!animation || animation === "loading" || animation === "static") {
    const image = loadImageAsset(src);
    return imageReady(image) ? image : null;
  }
  let elapsed = now % animation.totalMs;
  for (let index = 0; index < animation.frames.length; index += 1) {
    elapsed -= animation.durations[index];
    if (elapsed < 0) {
      return animation.frames[index];
    }
  }
  return animation.frames[0];
}

function drawNftAuraUnderlay(palette: any, variantName: string, dx: number, dy: number, dw: number, dh: number, flipX: boolean) {
  const appearance = palette?.nftAppearance;
  if (!appearance?.aura || !appearance?.species) {
    return;
  }
  const sheetConfig = visualConfig.spriteSheets.players;
  const variant = (sheetConfig?.variants as any)?.[variantName];
  if (!variant) {
    return;
  }
  const src = nftAuraAssetPath(appearance.species, appearance.aura);
  ensureNftAuraAnimation(src);
  const frame = currentNftAuraFrame(src, performance.now());
  if (!frame) {
    return;
  }
  ctx.save();
  if (flipX) {
    ctx.translate(dx + dw, dy);
    ctx.scale(-1, 1);
    dx = 0;
    dy = 0;
  }
  ctx.drawImage(
    frame as any,
    variant.x * sheetConfig.frameWidth,
    variant.y * sheetConfig.frameHeight,
    sheetConfig.frameWidth,
    sheetConfig.frameHeight,
    dx,
    dy,
    dw,
    dh,
  );
  ctx.restore();
}

  function skinLayerOverrideForVariants(layerVariants: any, helmetColor: any) {
    return Number(layerVariants?.helmet) === GOBLIN_HELMET_VARIANT ? helmetColor || null : null;
  }

  function currentScheduledGameId() {
    if (visualTestPage || typeof window === "undefined" || !window.location) {
      return null;
    }

    const queryGameId = currentUrlParams().get("game");
    if (queryGameId) {
      return queryGameId;
    }

    const routeMatch = window.location.pathname.match(/^\/game\/([^/]+)$/);
    return routeMatch ? decodeURIComponent(routeMatch[1]) : null;
  }

  function showScheduledStatsPage(gameId: string | null) {
    if (!gameId || isDebugMode() || typeof window === "undefined" || !window.location) {
      return false;
    }

    const statsPath = `/game/${encodeURIComponent(canonicalGameId(gameId))}/stats`;
    if (window.location.pathname === statsPath) {
      return true;
    }

    window.location.replace(statsPath);
    return true;
  }

  function isDebugMode() {
    return currentUrlParams().get("debug") === "true";
  }

  function currentVisualTestConfig() {
    const params = currentUrlParams();
    const fallbackPlayId = state.bootstrap?.playbook?.[0]?.id || "inside-zone";
    const parsedYards = Number(params.get("yards"));
    return {
      playId: params.get("playId") || fallbackPlayId,
      yards: Number.isFinite(parsedYards) ? Math.round(parsedYards) : 0,
      engine: params.get("engine") === "arcade" ? "arcade" : "legacy",
      arcadeSeed: params.get("arcadeSeed") || "",
      arcadeView: params.get("arcadeView") === "raw" ? "raw" : "presented",
      passOutcome: (() => {
        const outcome = params.get("passOutcome") || "auto";
        return outcome === "completion"
          ? "completion"
          : outcome === "incomplete"
            ? "incomplete"
            : outcome === "interception"
              ? "interception"
              : outcome === "sack"
                ? "sack"
                : "auto";
      })(),
      turnoverOutcome: (() => {
        const outcome = params.get("turnoverOutcome") || "auto";
        return outcome === "interception" ? "interception" : outcome === "fumble" ? "fumble" : "auto";
      })(),
      onsideOutcome: (() => {
        const outcome = params.get("onsideOutcome") || "auto";
        return outcome === "kicking-team" ? "kicking-team" : outcome === "receiving-team" ? "receiving-team" : "auto";
      })(),
      celebrationStyle: params.get("celebrationStyle") || "",
      embed: params.get("embed") === "1",
    };
  }

  function replayHoldMs() {
    return visualTestPage ? 2000 : REPLAY_HOLD_MS;
  }

  function resetFieldZoomState(loopState: any = state) {
    loopState.fieldZoom = 1;
    loopState.zoomFocusX = null;
    loopState.zoomFocusY = null;
  }

  export function resetReplayLoopState(loopState: any, play: any, now: number, options: { visualTest: boolean } = { visualTest: false }) {
    loopState.lastAdvance = now;
    loopState.intermissionCameraStartX = null;
    loopState.intermissionKey = null;
    resetFieldZoomState(loopState);
    if (!options.visualTest) {
      loopState.prevResult = play?.result || null;
      return;
    }

    loopState.prevResult = null;
    loopState.pausedElapsed = null;
    loopState.cameraX = null;
    loopState.cameraVelocityX = 0;
    loopState.cameraUpdatedAt = null;
    loopState.renderedStatsCount = -1;
    loopState.renderedPlayIndex = -1;
    loopState.renderedCaption = "";
    loopState.visualPlayerCache?.clear?.();

    if (play && typeof play === "object" && "_liveSequence" in play) {
      delete play._liveSequence;
    }
  }

  export function advanceContinuePlayback(loopState: any, play: any, now: number, options: { skipDeadHold?: boolean } = {}) {
    loopState.prevResult = play?.result || null;
    loopState.playIndex += 1;
    loopState.lastAdvance = options.skipDeadHold ? now - PLAY_TIMING.deadHoldMs : now;
    loopState.intermissionCameraStartX = null;
    loopState.intermissionKey = null;
  }

  function clearVisualPlayerCache() {
    state.visualPlayerCache.clear();
  }

  function stopLiveBroadcastSync() {
    if (state.liveSyncTimer) {
      clearInterval(state.liveSyncTimer);
      state.liveSyncTimer = null;
    }
    state.liveSyncInFlight = false;
    state.liveSyncLastBroadcast = null;
    state.liveBroadcastSlotMs = null;
    clearLiveSyncPresentationState();
    renderHalftimeCountdown();
  }

  function visualPlayerCacheKey(cachePrefix = "", role: string, index: number) {
    return `${cachePrefix}:${role || `player-${index}`}`;
  }

  function snapshotVisualPlayerEntries(
    players: any[] = [],
    cache: Map<string, VisualPlayerCacheEntry> = new Map(),
    cachePrefix = "",
  ) {
    const snapshot = new Map<string, VisualPlayerCacheEntry>();
    players.forEach((player: any, index: number) => {
      const role = player?.role || `player-${index}`;
      const cached = cache.get(visualPlayerCacheKey(cachePrefix, role, index));
      if (cached) {
        snapshot.set(role, { ...cached });
      }
    });
    return snapshot;
  }

  function defaultSpriteFacingForTeam(teamType: string, direction: number) {
    return teamType === "offense" ? (direction >= 0 ? 1 : -1) : (direction >= 0 ? -1 : 1);
  }

  function resolveFacingSign(value: number, fallback: number) {
    if (value > 0) {
      return 1;
    }
    if (value < 0) {
      return -1;
    }
    return fallback;
  }

  const BALL_PURSUIT_TRACKING_STATES = new Set([
    "pass-flight",
    "recovering",
    "loose",
    "kick-blocked",
  ]);
  const OWNED_BALL_PURSUIT_STATES = new Set([
    "carried",
    "hold",
    "secured",
    "recovering",
  ]);
  const POST_PLAY_TRANSITION_PHASES = new Set([
    "special-exit",
    "scrimmage-changeover",
    "final-exit",
    "halftime-exit",
  ]);

  function resolvePursuitFacingTowardBall(
    player: any,
    previousEntry: VisualPlayerCacheEntry | undefined,
    fallbackFacing: number,
    teamType: "offense" | "defense",
    ballOwner: any,
    ballOwnerTeamType: string | null,
    frame: any,
  ) {
    const previousFacing = Number.isFinite(previousEntry?.spriteFacing) ? previousEntry!.spriteFacing! : fallbackFacing;
    if (!player?.moving) {
      return null;
    }

    const ballState = String(frame?.ball?.arcadeState || "");
    const trackingOpposingOwner = !!ballOwner && !!ballOwnerTeamType && ballOwnerTeamType !== teamType;
    const trackingFreeBall = !ballOwnerTeamType && BALL_PURSUIT_TRACKING_STATES.has(ballState);
    if (!trackingOpposingOwner && !trackingFreeBall) {
      return null;
    }

    const target = trackingOpposingOwner ? ballOwner : frame?.ball;
    const targetX = Number.isFinite(target?.x) ? target.x : target?.ballX;
    const targetY = Number.isFinite(target?.y) ? target.y : target?.ballY;
    if (!Number.isFinite(targetX) || !Number.isFinite(targetY)) {
      return null;
    }

    const deltaX = Number.isFinite(previousEntry?.x) ? player.x - previousEntry!.x : 0;
    const activeHorizontalMove = Math.abs(deltaX) >= 4;
    const ballDx = targetX - player.x;
    const ballDy = targetY - player.y;
    const ballDistance = Math.hypot(ballDx, ballDy);
    const closingTowardBall = activeHorizontalMove
      && Math.abs(ballDx) >= 8
      && Math.sign(deltaX) === Math.sign(ballDx);
    const pursuitWindow = ballDistance <= 240 || Math.abs(ballDx) <= 220;
    const activeCarrierPursuit = trackingOpposingOwner
      && OWNED_BALL_PURSUIT_STATES.has(ballState)
      && pursuitWindow
      && Math.abs(ballDx) >= 8;

    if (closingTowardBall && pursuitWindow) {
      return resolveFacingSign(ballDx, previousFacing);
    }

    if (activeCarrierPursuit) {
      return resolveFacingSign(ballDx, previousFacing);
    }

    const sustainedPursuitFacing = pursuitWindow
      && ballDistance <= 240
      && Math.abs(ballDx) >= 16
      && Math.sign(previousFacing) === Math.sign(ballDx);

    return sustainedPursuitFacing ? previousFacing : null;
  }

  function clampVisualPlayerPosition(player: any, metrics = fieldMetrics()) {
    const x = snapToPixel(clampRange(Number(player?.x) || 0, metrics.endZoneLeft + 6, metrics.endZoneRight - 20));
    const y = snapToPixel(clampRange(Number(player?.y) || 0, metrics.fieldTop - 10, metrics.fieldBottom - 18));
    return { ...player, x, y };
  }

  export function stabilizeVisualPlayers(
    players: any[] = [],
    fallbackPlayers: any[] = [],
    cache: Map<string, VisualPlayerCacheEntry> = new Map(),
    cachePrefix = "",
    metrics = fieldMetrics()
  ) {
    const fallbackByRole = new Map((fallbackPlayers || []).map((player: any) => [player?.role, player]));
    return players.map((player: any, index: number) => {
      const role = player?.role || `player-${index}`;
      const cacheKey = visualPlayerCacheKey(cachePrefix, role, index);
      const fallback = fallbackByRole.get(player?.role);
      const cached = cache.get(cacheKey);
      const cachedX = cached?.x;
      const cachedY = cached?.y;
      const resolvedX = Number.isFinite(player?.x)
        ? player.x
        : Number.isFinite(cachedX)
          ? cachedX
          : Number.isFinite(fallback?.x)
            ? fallback.x
            : metrics.goalLeft;
      const resolvedY = Number.isFinite(player?.y)
        ? player.y
        : Number.isFinite(cachedY)
          ? cachedY
          : Number.isFinite(fallback?.y)
            ? fallback.y
            : metrics.centerY;
      const stabilized = clampVisualPlayerPosition({ ...player, x: resolvedX, y: resolvedY }, metrics);
      cache.set(cacheKey, { x: stabilized.x, y: stabilized.y });
      return stabilized;
    });
  }

  function rememberVisualPlayers(players: any[] = [], cache: Map<string, VisualPlayerCacheEntry> = new Map(), cachePrefix = "") {
    players.forEach((player: any, index: number) => {
      if (!Number.isFinite(player?.x) || !Number.isFinite(player?.y)) {
        return;
      }
      const role = player?.role || `player-${index}`;
      cache.set(visualPlayerCacheKey(cachePrefix, role, index), {
        x: player.x,
        y: player.y,
        spriteFacing: Number.isFinite(player?.spriteFacing) ? player.spriteFacing : undefined,
        moveStyle: typeof player?.moveStyle === "string" ? player.moveStyle : undefined,
      });
    });
  }

  function spreadPlayersApart(
    left: any,
    right: any,
    sameTeam: boolean,
    anchorPlayer: any,
    metrics: any,
    desiredDistance = sameTeam ? 14 : 8
  ) {
    if (!left || !right) {
      return;
    }

    let deltaX = (left.x + 7) - (right.x + 7);
    let deltaY = (left.y + 9) - (right.y + 9);
    let distance = Math.hypot(deltaX, deltaY);

    if (distance >= desiredDistance) {
      return;
    }

    if (distance < 0.001) {
      const angle = collisionBias(left.role, right.role);
      deltaX = Math.cos(angle) * (sameTeam ? 1.8 : 1);
      deltaY = Math.sin(angle) * (sameTeam ? 1.25 : 0.75);
      distance = Math.hypot(deltaX, deltaY);
    }

    const overlap = desiredDistance - distance;
    const normalX = deltaX / distance;
    const normalY = deltaY / distance;
    const leftAnchored = anchorPlayer && left.role === anchorPlayer.role;
    const rightAnchored = anchorPlayer && right.role === anchorPlayer.role;
    const leftShare = leftAnchored ? 0.18 : rightAnchored ? 0.82 : 0.5;
    const rightShare = 1 - leftShare;
    const softness = sameTeam ? 0.74 : 0.38;
    const pushX = normalX * overlap * softness;
    const pushY = normalY * overlap * softness;

    Object.assign(left, clampVisualPlayerPosition({
      ...left,
      x: left.x + pushX * leftShare,
      y: left.y + pushY * leftShare,
    }, metrics));
    Object.assign(right, clampVisualPlayerPosition({
      ...right,
      x: right.x - pushX * rightShare,
      y: right.y - pushY * rightShare,
    }, metrics));
  }

  function resolveTightPlayerClusters(players: any[], anchorPlayer: any, metrics: any, minDistance = 12) {
    for (let pass = 0; pass < 6; pass += 1) {
      for (let leftIndex = 0; leftIndex < players.length; leftIndex += 1) {
        for (let rightIndex = leftIndex + 1; rightIndex < players.length; rightIndex += 1) {
          spreadPlayersApart(players[leftIndex], players[rightIndex], true, anchorPlayer, metrics, minDistance);
        }
      }
    }
  }

  function resolveDefenseSpriteFacing(
    player: any,
    previousEntry: VisualPlayerCacheEntry | undefined,
    direction: number,
    ballOwner: any,
    ballOwnerTeamType: string | null,
    offensePlayers: any[],
    frame: any,
  ) {
    const baseFacing = defaultSpriteFacingForTeam("defense", direction);
    const previousFacing = Number.isFinite(previousEntry?.spriteFacing) ? previousEntry!.spriteFacing! : baseFacing;
    const deltaX = Number.isFinite(previousEntry?.x) ? player.x - previousEntry!.x : 0;
    const strongHorizontalMove = Math.abs(deltaX) >= 8;
    const phase = String(frame?.scenePhase || "");

    if (!player?.moving) {
      return previousFacing;
    }

    if (POST_PLAY_TRANSITION_PHASES.has(phase)) {
      return previousFacing;
    }

    const pursuitFacing = resolvePursuitFacingTowardBall(
      player,
      previousEntry,
      baseFacing,
      "defense",
      ballOwner,
      ballOwnerTeamType,
      frame,
    );
    if (pursuitFacing !== null) {
      return pursuitFacing;
    }

    const nearestOffender = nearestPlayerToPoint(offensePlayers, { x: player.x, y: player.y });
    const nearestOffenderDistance = nearestOffender ? pointDistance(player, nearestOffender) : Infinity;
    if (nearestOffender && nearestOffenderDistance <= 34 && Math.abs(nearestOffender.x - player.x) >= 4) {
      return resolveFacingSign(nearestOffender.x - player.x, previousFacing);
    }

    if (strongHorizontalMove) {
      return previousFacing;
    }

    return baseFacing;
  }

  function resolveLiveCarrierFacing(
    player: any,
    previousEntry: VisualPlayerCacheEntry | undefined,
    fallbackFacing: number,
    advanceFacing: number,
  ) {
    const previousFacing = Number.isFinite(previousEntry?.spriteFacing) ? previousEntry!.spriteFacing! : fallbackFacing;
    const deltaX = Number.isFinite(previousEntry?.x) ? player.x - previousEntry!.x : 0;
    if (Math.abs(deltaX) >= 4) {
      return resolveFacingSign(deltaX, previousFacing);
    }
    // Arcade run frames often advance in sub-pixel steps. Once a player owns
    // the ball, prefer the possession direction over a stale pre-handoff or
    // pitch-facing cache; a clear 4px movement above still wins for genuine
    // reverses, cutbacks, and defensive returns.
    return resolveFacingSign(advanceFacing, previousFacing || fallbackFacing);
  }

  function resolveQuarterbackSpriteFacing(
    player: any,
    previousEntry: VisualPlayerCacheEntry | undefined,
    direction: number,
    frame: any,
    ownsBall: boolean,
  ) {
    const baseFacing = defaultSpriteFacingForTeam("offense", direction);
    const previousFacing = Number.isFinite(previousEntry?.spriteFacing) ? previousEntry!.spriteFacing! : baseFacing;
    const deltaX = Number.isFinite(previousEntry?.x) ? player.x - previousEntry!.x : 0;
    const absX = Math.abs(deltaX);
    const playFamily = frame?.play?.family || "";
    const ballState = String(frame?.ball?.arcadeState || "");

    if (!player?.moving) {
      return previousFacing;
    }

    if (playFamily === "pass" && ownsBall) {
      if (absX >= 8 && Math.sign(deltaX) === Math.sign(direction)) {
        return resolveFacingSign(deltaX, previousFacing);
      }
      return baseFacing;
    }

    if (playFamily === "pass" && ballState === "pass-flight") {
      if (absX >= 8 && Math.sign(deltaX) === Math.sign(direction)) {
        return resolveFacingSign(deltaX, previousFacing);
      }
      return baseFacing;
    }

    if (playFamily === "run" && ownsBall) {
      return resolveLiveCarrierFacing(player, previousEntry, baseFacing, direction);
    }

    return previousFacing;
  }

  function resolveDefenseMoveStyle(
    player: any,
    previousEntry: VisualPlayerCacheEntry | undefined,
    ballOwner: any,
    ballOwnerTeamType: string | null,
    offensePlayers: any[],
  ) {
    if (player?.down || player?.tackled) {
      return "fall";
    }

    const previousMoveStyle = typeof previousEntry?.moveStyle === "string" ? previousEntry.moveStyle : "idle";
    if (!player?.moving) {
      return previousMoveStyle === "engaged" ? "engaged" : "idle";
    }

    const deltaX = Number.isFinite(previousEntry?.x) ? player.x - previousEntry!.x : 0;
    const deltaY = Number.isFinite(previousEntry?.y) ? player.y - previousEntry!.y : 0;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const facing = Number.isFinite(player?.spriteFacing) ? player.spriteFacing : -1;
    const nearestOffender = nearestPlayerToPoint(offensePlayers, { x: player.x, y: player.y });
    const nearestOffenderDistance = nearestOffender ? pointDistance(player, nearestOffender) : Infinity;

    if (nearestOffender && nearestOffenderDistance <= 28) {
      return "engaged";
    }

    if (ballOwner && ballOwnerTeamType === "offense") {
      const carrierDx = ballOwner.x - player.x;
      const carrierDy = ballOwner.y - player.y;
      const carrierDistance = Math.hypot(carrierDx, carrierDy);
      const horizontallyCommittedToCarrier = absX >= 4
        && Math.abs(carrierDx) >= 8
        && Math.sign(deltaX) === Math.sign(carrierDx);
      const pursuitWindow = carrierDistance <= 240 || Math.abs(carrierDx) <= 220;

      if (horizontallyCommittedToCarrier && pursuitWindow) {
        return "run";
      }
    }

    if (absY >= 6 && absY > absX * 1.2) {
      return "shuffle";
    }

    if (absX >= 4 && Math.sign(deltaX) !== Math.sign(facing)) {
      return "backpedal";
    }

    return "run";
  }

  function resolveQuarterbackMoveStyle(
    player: any,
    previousEntry: VisualPlayerCacheEntry | undefined,
    frame: any,
    direction: number,
    ownsBall: boolean,
  ) {
    if (player?.down || player?.tackled) {
      return "fall";
    }

    const previousMoveStyle = typeof previousEntry?.moveStyle === "string" ? previousEntry.moveStyle : "idle";
    const playFamily = frame?.play?.family || "";
    const phase = frame?.scenePhase || "live";
    const ballState = String(frame?.ball?.arcadeState || "");
    const deltaX = Number.isFinite(previousEntry?.x) ? player.x - previousEntry!.x : 0;
    const deltaY = Number.isFinite(previousEntry?.y) ? player.y - previousEntry!.y : 0;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (phase === "huddle" || phase === "huddleBreak" || phase === "presnap") {
      return "idle";
    }

    if (!player?.moving) {
      if (playFamily === "pass") {
        return "set";
      }
      return previousMoveStyle === "handoff" ? "handoff" : "idle";
    }

    if (playFamily === "run") {
      if (ownsBall || ballState === "handoff" || ballState === "snap") {
        return "handoff";
      }
      return "run";
    }

    if (playFamily === "pass") {
      if (ownsBall) {
        if (absX >= 10 && Math.sign(deltaX) === Math.sign(direction)) {
          return "rollout";
        }
        if (absX >= 6 && Math.sign(deltaX) !== Math.sign(direction)) {
          return "dropback";
        }
        if (absY >= 8 && absY > absX * 1.2) {
          return "dropback";
        }
        return "set";
      }

      if (ballState === "pass-flight") {
        if (absX >= 10 && Math.sign(deltaX) === Math.sign(direction)) {
          return "rollout";
        }
        return "set";
      }
    }

    return "run";
  }

  function applyVisualMoveStyles(
    offensePlayers: any[],
    defensePlayers: any[],
    offensePreviousEntries: Map<string, VisualPlayerCacheEntry>,
    defensePreviousEntries: Map<string, VisualPlayerCacheEntry>,
    ballOwner: any,
    ballOwnerTeamType: string | null,
    frame: any,
    direction: number,
  ) {
    offensePlayers.forEach((player: any, index: number) => {
      const role = player?.role || `player-${index}`;
      const previousEntry = offensePreviousEntries.get(role);
      const ownsBall = ballOwnerTeamType === "offense" && ballOwner?.role === player?.role;
      if (player?.down || player?.tackled) {
        player.moveStyle = "fall";
        return;
      }
      if (player?.role === "QB") {
        player.moveStyle = resolveQuarterbackMoveStyle(
          player,
          previousEntry,
          frame,
          direction,
          ownsBall,
        );
        return;
      }
      if (ownsBall) {
        player.moveStyle = "run";
        return;
      }
      if (!player?.moving) {
        player.moveStyle = "idle";
        return;
      }
      player.moveStyle = typeof previousEntry?.moveStyle === "string" && previousEntry.moveStyle === "engaged"
        ? "engaged"
        : "run";
    });

    defensePlayers.forEach((player: any, index: number) => {
      const role = player?.role || `player-${index}`;
      const previousEntry = defensePreviousEntries.get(role);
      player.moveStyle = resolveDefenseMoveStyle(
        player,
        previousEntry,
        ballOwner,
        ballOwnerTeamType,
        offensePlayers,
      );
    });
  }

  function applyVisualSpriteFacing(
    offensePlayers: any[],
    defensePlayers: any[],
    direction: number,
    offensePreviousEntries: Map<string, VisualPlayerCacheEntry>,
    defensePreviousEntries: Map<string, VisualPlayerCacheEntry>,
    ballOwner: any,
    ballOwnerTeamType: string | null,
    frame: any,
  ) {
    const offenseDefaultFacing = defaultSpriteFacingForTeam("offense", direction);
    offensePlayers.forEach((player: any, index: number) => {
      const role = player?.role || `player-${index}`;
      const previousEntry = offensePreviousEntries.get(role);
      const ownsBall = ballOwnerTeamType === "offense" && ballOwner?.role === player?.role;
      if (player?.down || player?.tackled) {
        player.spriteFacing = Number.isFinite(previousEntry?.spriteFacing)
          ? previousEntry!.spriteFacing!
          : offenseDefaultFacing;
        return;
      }
      if (player?.role === "QB") {
        player.spriteFacing = resolveQuarterbackSpriteFacing(
          player,
          previousEntry,
          direction,
          frame,
          ownsBall,
        );
        return;
      }
      if (ownsBall) {
        player.spriteFacing = resolveLiveCarrierFacing(
          player,
          previousEntry,
          offenseDefaultFacing,
          direction,
        );
        return;
      }
      const pursuitFacing = resolvePursuitFacingTowardBall(
        player,
        previousEntry,
        offenseDefaultFacing,
        "offense",
        ballOwner,
        ballOwnerTeamType,
        frame,
      );
      if (pursuitFacing !== null) {
        player.spriteFacing = pursuitFacing;
        return;
      }
      player.spriteFacing = Number.isFinite(previousEntry?.spriteFacing)
        ? previousEntry!.spriteFacing!
        : offenseDefaultFacing;
    });

    defensePlayers.forEach((player: any, index: number) => {
      const role = player?.role || `player-${index}`;
      const previousEntry = defensePreviousEntries.get(role);
      const ownsBall = ballOwnerTeamType === "defense" && ballOwner?.role === player?.role;
      if (player?.down || player?.tackled) {
        player.spriteFacing = Number.isFinite(previousEntry?.spriteFacing)
          ? previousEntry!.spriteFacing!
          : defaultSpriteFacingForTeam("defense", direction);
        return;
      }
      if (ownsBall) {
        player.spriteFacing = resolveLiveCarrierFacing(
          player,
          previousEntry,
          defaultSpriteFacingForTeam("defense", direction),
          -direction,
        );
        return;
      }
      player.spriteFacing = resolveDefenseSpriteFacing(
        player,
        previousEntry,
        direction,
        ballOwner,
        ballOwnerTeamType,
        offensePlayers,
        frame,
      );
    });
  }

  const POSSESSION_ANCHORED_BALL_STATES = new Set(["carried", "hold", "punt-prep", "recovering", "secured"]);

  function resolveOwnedBallPoint(frame: any, offensePlayers: any[], defensePlayers: any[]) {
    const ownerRole = frame?.ball?.arcadeOwnerRole;
    const ballState = String(frame?.ball?.arcadeState || "");
    if (!ownerRole || !POSSESSION_ANCHORED_BALL_STATES.has(ballState)) {
      return null;
    }

    const offensiveOwner = offensePlayers.find((player: any) => player.role === ownerRole);
    if (offensiveOwner) {
      return {
        ...frame.ball,
        ...carriedBallPoint(
          offensiveOwner,
          frame.ball.ballX,
          frame.ball.ballY,
          frame.direction || 1,
          "offense",
        ),
      };
    }

    const defensiveOwner = defensePlayers.find((player: any) => player.role === ownerRole);
    if (defensiveOwner) {
      return {
        ...frame.ball,
        ...carriedBallPoint(
          defensiveOwner,
          frame.ball.ballX,
          frame.ball.ballY,
          -(frame.direction || 1),
          "defense",
        ),
      };
    }

    return null;
  }

  // Embed mode: strip all chrome when loaded inside catalog.html's scaled iframe.
  if (currentUrlParams().get("embed") === "1") {
    document.body.classList?.add("catalog-embed");
  }
  if (isDebugMode()) {
    document.body.classList?.add("debug-mode");
  }
  if (visualTestPage) {
    document.body.classList?.add("visual-test-page");
  }



  const refs = {
    homeSelect: document.getElementById("home-select") as HTMLSelectElement | null,
    awaySelect: document.getElementById("away-select") as HTMLSelectElement | null,
    seedInput: document.getElementById("seed-input") as HTMLInputElement | null,
    simulateButton: document.getElementById("simulate-button") as HTMLButtonElement | null,
    randomButton: document.getElementById("random-button") as HTMLButtonElement | null,
    ownerCoachingButton: document.getElementById("owner-coaching-button") as HTMLButtonElement | null,
    ownerCoachingPanel: document.getElementById("owner-coaching-panel") as HTMLElement | null,
    ownerCoachingClose: document.getElementById("owner-coaching-close") as HTMLButtonElement | null,
    ownerWalletConnect: document.getElementById("owner-wallet-connect") as HTMLButtonElement | null,
    ownerCoachingConnectView: document.getElementById("owner-coaching-connect-view") as HTMLElement | null,
    ownerCoachingForm: document.getElementById("owner-coaching-form") as HTMLElement | null,
    ownerCoachingTeam: document.getElementById("owner-coaching-team") as HTMLSelectElement | null,
    ownerCoachingPreset: document.getElementById("owner-coaching-preset") as HTMLSelectElement | null,
    ownerCoachingOffense: document.getElementById("owner-coaching-offense") as HTMLSelectElement | null,
    ownerCoachingDefense: document.getElementById("owner-coaching-defense") as HTMLSelectElement | null,
    ownerCoachingSave: document.getElementById("owner-coaching-save") as HTMLButtonElement | null,
    ownerCoachingStatus: document.getElementById("owner-coaching-status") as HTMLElement | null,
    prevPlayButton: document.getElementById("prev-play-button") as HTMLButtonElement | null,
    playPauseButton: document.getElementById("playpause-button") as HTMLButtonElement | null,
    nextPlayButton: document.getElementById("next-play-button") as HTMLButtonElement | null,
    playProgressBar: document.getElementById("play-progress-bar") as HTMLElement | null,
    playProgressFill: document.getElementById("play-progress-fill") as HTMLElement | null,
    modeButtons: document.querySelectorAll(".play-mode-btn"),
    playCaption: document.getElementById("play-caption") as HTMLElement | null,
    stageLoading: document.getElementById("stage-loading") as HTMLElement | null,
    stageLoadingTitle: document.getElementById("stage-loading-title") as HTMLElement | null,
    stageLoadingStatus: document.getElementById("stage-loading-status") as HTMLElement | null,
    stageLoadingFill: document.getElementById("stage-loading-fill") as HTMLElement | null,
    stageLoadingProgress: document.getElementById("stage-loading-progress") as HTMLElement | null,
    stageLoadingEta: document.getElementById("stage-loading-eta") as HTMLElement | null,
    stageScreen: document.querySelector(".stage-screen") as HTMLElement | null,
    fieldViewport: document.querySelector(".field-viewport") as HTMLElement | null,
    audioToggleButton: document.getElementById("audio-toggle-button") as HTMLButtonElement | null,
    audioToggleIcon: document.getElementById("audio-toggle-icon") as HTMLElement | null,
    fullscreenButton: document.getElementById("fullscreen-button") as HTMLButtonElement | null,
    fullscreenIcon: document.getElementById("fullscreen-icon") as HTMLElement | null,
    pregameOverlay: document.getElementById("pregame-overlay") as HTMLElement | null,
    halftimeCountdown: document.getElementById("halftime-countdown") as HTMLElement | null,
    halftimeCountdownRing: document.getElementById("halftime-countdown-ring") as HTMLElement | null,
    halftimeCountdownTime: document.getElementById("halftime-countdown-time") as HTMLElement | null,
    halftimeCountdownPhase: document.getElementById("halftime-countdown-phase") as HTMLElement | null,
    broadcastHud: document.getElementById("broadcast-hud") as HTMLElement | null,
    leagueGrid: document.getElementById("league-grid") as HTMLElement | null,
    rosters: document.getElementById("rosters") as HTMLElement | null,
    boxScore: document.getElementById("box-score") as HTMLElement | null,
    liveStats: document.getElementById("live-stats") as HTMLElement | null,
    playLog: document.getElementById("play-log") as HTMLElement | null,
    recentPlaysStrip: document.getElementById("recent-plays-strip") as HTMLElement | null,
    canvas: document.getElementById("field-canvas") as HTMLCanvasElement | null,
  };

  let ownerCoachingSessionToken = "";
  let ownerCoachingIdentity: any = null;

  function ownerCoachingApiBase(): string {
    return String((window as any).__DFL_FRANCHISE_API_BASE_URL__ || "http://localhost:3001").replace(/\/+$/, "");
  }

  function setOwnerCoachingStatus(message: string, tone: "" | "error" | "success" = "") {
    if (!refs.ownerCoachingStatus) return;
    refs.ownerCoachingStatus.textContent = message;
    refs.ownerCoachingStatus.className = `owner-coaching-status${tone ? ` is-${tone}` : ""}`;
  }

  async function ownerCoachingJson(pathname: string, init: RequestInit = {}): Promise<any> {
    const response = await fetch(`${ownerCoachingApiBase()}${pathname}`, init);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.error || `Request failed (${response.status}).`);
    return body;
  }

  function gameTeamLabel(franchiseId: string): string {
    const team = state.game ? teamInGameById(state.game, franchiseId) : null;
    return String(team?.name || team?.abbr || franchiseId);
  }

  async function loadOwnerCoachingIdentity() {
    const franchiseId = refs.ownerCoachingTeam?.value;
    if (!franchiseId) return;
    const entry = await ownerCoachingJson(`/api/franchises/${encodeURIComponent(franchiseId)}/identity`);
    ownerCoachingIdentity = entry.identity;
    if (refs.ownerCoachingPreset) {
      const presets = Array.isArray(entry.identity.coachingPresets) ? entry.identity.coachingPresets : [];
      refs.ownerCoachingPreset.innerHTML = [
        '<option value="">Custom / current setup</option>',
        ...presets.map((preset: any) => `<option value="${escapeHtml(String(preset.id))}">${escapeHtml(String(preset.name))} · v${Number(preset.revision) || 1}</option>`),
      ].join("");
      refs.ownerCoachingPreset.value = "";
    }
    if (refs.ownerCoachingOffense) refs.ownerCoachingOffense.value = entry.identity.offense;
    if (refs.ownerCoachingDefense) refs.ownerCoachingDefense.value = entry.identity.defense;
    setOwnerCoachingStatus("Ready. Saved changes will be read at the live halftime lock.");
  }

  function selectedOwnerCoachingPreset(): any | null {
    const presetId = refs.ownerCoachingPreset?.value;
    return ownerCoachingIdentity?.coachingPresets?.find((preset: any) => preset.id === presetId) || null;
  }

  function previewOwnerCoachingPreset() {
    const preset = selectedOwnerCoachingPreset();
    if (!preset) return;
    if (refs.ownerCoachingOffense) refs.ownerCoachingOffense.value = preset.offense;
    if (refs.ownerCoachingDefense) refs.ownerCoachingDefense.value = preset.defense;
    setOwnerCoachingStatus(`${preset.name} loaded. Queue it when ready.`);
  }

  async function connectOwnerCoachingWallet() {
    const ethereum = (window as any).ethereum;
    if (!ethereum?.request) throw new Error("Open this game in a wallet browser or install MetaMask.");
    setOwnerCoachingStatus("Requesting wallet verification…");
    const accounts = await ethereum.request({ method: "eth_requestAccounts" });
    const address = String(accounts?.[0] || "");
    if (!address) throw new Error("No wallet account was returned.");
    const nonce = await ownerCoachingJson("/api/wallet/nonce", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ address }),
    });
    const signature = await ethereum.request({ method: "personal_sign", params: [nonce.message, address] });
    const session = await ownerCoachingJson("/api/wallet/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ address, signature }),
    });
    const activeTeamIds = new Set([
      state.game?.home?.id || state.game?.home,
      state.game?.away?.id || state.game?.away,
    ].filter(Boolean).map(String));
    const assignments = (session.ownedAssignments || []).filter((assignment: any) => activeTeamIds.has(String(assignment.franchiseId)));
    if (!assignments.length) throw new Error("This wallet does not own either franchise in the current game.");
    ownerCoachingSessionToken = session.token;
    if (refs.ownerCoachingTeam) {
      refs.ownerCoachingTeam.innerHTML = assignments
        .map((assignment: any) => `<option value="${escapeHtml(String(assignment.franchiseId))}">${escapeHtml(gameTeamLabel(String(assignment.franchiseId)))}</option>`)
        .join("");
    }
    if (refs.ownerCoachingConnectView) refs.ownerCoachingConnectView.hidden = true;
    if (refs.ownerCoachingForm) refs.ownerCoachingForm.hidden = false;
    await loadOwnerCoachingIdentity();
  }

  async function saveOwnerCoachingSchemas() {
    const franchiseId = refs.ownerCoachingTeam?.value;
    if (!franchiseId || !ownerCoachingIdentity || !ownerCoachingSessionToken) throw new Error("Connect and verify the owner wallet first.");
    refs.ownerCoachingSave!.disabled = true;
    setOwnerCoachingStatus("Saving authoritative halftime choices…");
    try {
      const preset = selectedOwnerCoachingPreset();
      const saved = await ownerCoachingJson(`/api/franchises/${encodeURIComponent(franchiseId)}/identity`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ownerCoachingSessionToken}`,
        },
        body: JSON.stringify({
          ...ownerCoachingIdentity,
          ...(preset || {}),
          offense: refs.ownerCoachingOffense?.value,
          defense: refs.ownerCoachingDefense?.value,
          coachingPresets: ownerCoachingIdentity.coachingPresets,
        }),
      });
      ownerCoachingIdentity = saved.identity;
      setOwnerCoachingStatus("Schemas saved. The shared broadcast will apply them at the halftime lock.", "success");
    } finally {
      refs.ownerCoachingSave!.disabled = false;
    }
  }

  // Local wrappers that supply closure values to parameterized module functions
  function fieldMetrics() { return fieldMetricsParam(refs.canvas!.height); }
  function directionForOffense(offense: any) { return directionForOffenseParam(offense, state.game); }
  function buildPlayContext(play: any, centerY: number) {
    const context = buildPlayContextParam(play, centerY, state.game, resolveLaneY, teamUniformPalette);
    return {
      ...context,
      formation: assignPersonnelToFormation(play, context.offense, context.defense, context.formation),
    };
  }


  const ctx = refs.canvas!.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  const baseCanvasWidth = refs.canvas!.width;
  const baseCanvasHeight = refs.canvas!.height;
  let fieldControlsTimer: ReturnType<typeof setTimeout> | null = null;

  type MobileOrientationController = ScreenOrientation & {
    lock?: (orientation: "landscape" | "landscape-primary" | "landscape-secondary") => Promise<void>;
    unlock?: () => void;
  };

  function isMobileViewport() {
    return window.matchMedia("(pointer: coarse), (max-width: 820px)").matches;
  }

  async function lockMobileLandscape() {
    if (!isMobileViewport()) {
      return;
    }
    const orientation = screen.orientation as MobileOrientationController | undefined;
    if (!orientation?.lock) {
      return;
    }
    try {
      await orientation.lock("landscape");
    } catch {
      // Orientation locking is best-effort and varies by mobile browser.
    }
  }

  function unlockMobileOrientation() {
    if (!isMobileViewport()) {
      return;
    }
    const orientation = screen.orientation as MobileOrientationController | undefined;
    try {
      orientation?.unlock?.();
    } catch {
      // Some browsers expose unlock but reject it outside their own state machine.
    }
  }

  function fullscreenViewportSize() {
    const visualViewport = window.visualViewport;
    return {
      width: Math.max(1, Math.round(visualViewport?.width || window.innerWidth)),
      height: Math.max(1, Math.round(visualViewport?.height || window.innerHeight)),
    };
  }

  function syncFullscreenCanvasSize() {
    if (!refs.canvas || !refs.fieldViewport) {
      return;
    }
    const isFullscreen = isElementFullscreen(refs.stageScreen);
    const viewport = fullscreenViewportSize();
    if (isFullscreen) {
      refs.stageScreen?.style.setProperty("--fullscreen-viewport-width", `${viewport.width}px`);
      refs.stageScreen?.style.setProperty("--fullscreen-viewport-height", `${viewport.height}px`);
    } else {
      refs.stageScreen?.style.removeProperty("--fullscreen-viewport-width");
      refs.stageScreen?.style.removeProperty("--fullscreen-viewport-height");
    }
    const isLandscapeFullscreen = isFullscreen && viewport.width > viewport.height;
    const isShortLandscape = isLandscapeFullscreen && viewport.height <= 680;
    const reservedHudHeight = isShortLandscape
      ? 12
      : Math.min(118, Math.max(84, viewport.height * 0.15));
    const availableFieldHeight = Math.max(1, viewport.height - reservedHudHeight);
    const targetWidth = isLandscapeFullscreen
      ? Math.max(baseCanvasWidth, Math.min(1440, Math.round(baseCanvasHeight * (viewport.width / availableFieldHeight))))
      : baseCanvasWidth;
    if (refs.canvas.width !== targetWidth || refs.canvas.height !== baseCanvasHeight) {
      refs.canvas.width = targetWidth;
      refs.canvas.height = baseCanvasHeight;
      ctx.imageSmoothingEnabled = false;
      state.cameraX = null;
      state.cameraVelocityX = 0;
      state.cameraUpdatedAt = null;
      mobileStadiumBackdropCache = null;
      mobileFieldSurfaceCache = null;
    }
    refs.fieldViewport.style.setProperty("--field-aspect", (targetWidth / baseCanvasHeight).toFixed(4));
    refs.fieldViewport.style.setProperty("--field-aspect-ratio", `${targetWidth} / ${baseCanvasHeight}`);
  }

  function updateFullscreenButton() {
    if (!refs.fullscreenButton) {
      return;
    }
    const isFullscreen = isElementFullscreen(refs.stageScreen);
    refs.fullscreenButton.title = isFullscreen ? "Exit fullscreen" : "Fullscreen";
    refs.fullscreenButton.setAttribute("aria-label", isFullscreen ? "Exit fullscreen" : "Enter fullscreen");
    refs.fullscreenIcon?.classList.toggle("fa-expand", !isFullscreen);
    refs.fullscreenIcon?.classList.toggle("fa-compress", isFullscreen);
  }

  function resolveAudioAssetSrc(src: string) {
    const normalized = String(src || "").replace(/^\/+/, "");
    if (!normalized) {
      return "";
    }
    if (/^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(normalized) || normalized.startsWith("data:") || normalized.startsWith("blob:")) {
      return normalized;
    }
    return `/${normalized}`;
  }

  function ambientCrowdContextCtor() {
    if (typeof window === "undefined") {
      return null;
    }
    return window.AudioContext || (window as any).webkitAudioContext || null;
  }

  function createAudioLoop(initialVolume: number): AmbientCrowdLoop | null {
    const AudioContextCtor = ambientCrowdContextCtor();
    if (!AudioContextCtor) {
      return null;
    }
    const context = new AudioContextCtor();
    const masterGain = context.createGain();
    masterGain.gain.value = state.audioMuted ? 0 : initialVolume;
    masterGain.connect(context.destination);
    return {
      context,
      masterGain,
      buffer: null,
      bufferAsset: null,
      loadingPromise: null,
      sources: [],
      timer: null,
      nextStartTime: 0,
      nextSourceOffset: 0,
      active: false,
    };
  }

  function ensureAmbientCrowdAudio() {
    if (!state.ambientCrowdAudio) {
      state.ambientCrowdAudio = createAudioLoop(AMBIENT_CROWD_VOLUME);
    }
    return state.ambientCrowdAudio;
  }

  function ensureMatchMusicAudio() {
    if (!state.matchMusicAudio) {
      state.matchMusicAudio = createAudioLoop(MATCH_MUSIC_VOLUME);
    }
    return state.matchMusicAudio;
  }

  function matchMusicConfigForHomeTeam() {
    const homeTeam = state.game?.home as { visuals?: { music?: { possessionSong?: unknown } } } | undefined;
    const configuredSongId = typeof homeTeam?.visuals?.music?.possessionSong === "string"
      ? homeTeam.visuals.music.possessionSong
      : DEFAULT_MATCH_MUSIC_ID;
    const song = FRANCHISE_POSSESSION_SONGS.find((candidate) => candidate.id === configuredSongId)
      || FRANCHISE_POSSESSION_SONGS.find((candidate) => candidate.id === DEFAULT_MATCH_MUSIC_ID);
    const loopPoints = MATCH_MUSIC_LOOP_POINTS[song?.id || DEFAULT_MATCH_MUSIC_ID] || {
      start: DEFAULT_MATCH_MUSIC_LOOP_START_SECONDS,
      end: DEFAULT_MATCH_MUSIC_LOOP_END_SECONDS,
    };
    return {
      src: song?.src || `${HOSTED_SOUND_ASSET_BASE}/fightSong.mp3?v=match-loop-v4`,
      loopStart: loopPoints.start,
      loopEnd: loopPoints.end,
    };
  }

  async function loadAudioLoopBuffer(loop: AmbientCrowdLoop, asset: string, label: string) {
    if (loop.bufferAsset !== asset) {
      loop.buffer = null;
      loop.loadingPromise = null;
      loop.bufferAsset = asset;
      stopAmbientCrowdSources(loop);
      loop.active = false;
      loop.nextStartTime = 0;
      loop.nextSourceOffset = 0;
    }
    if (loop.buffer) {
      return loop.buffer;
    }
    if (!loop.loadingPromise) {
      loop.loadingPromise = fetch(resolveAudioAssetSrc(asset))
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Unable to load ${label} audio (${response.status})`);
          }
          return response.arrayBuffer();
        })
        .then((arrayBuffer) => loop.context.decodeAudioData(arrayBuffer));
    }
    loop.buffer = await loop.loadingPromise;
    return loop.buffer;
  }

  async function loadAmbientCrowdBuffer(loop: AmbientCrowdLoop) {
    return loadAudioLoopBuffer(loop, AMBIENT_CROWD_ASSET, "ambient crowd");
  }

  async function loadMatchMusicBuffer(loop: AmbientCrowdLoop) {
    return loadAudioLoopBuffer(loop, matchMusicConfigForHomeTeam().src, "match music");
  }

  function stopAmbientCrowdSources(loop: AmbientCrowdLoop) {
    loop.sources.forEach((source) => {
      try {
        source.stop();
      } catch {
        // Already stopped sources throw in some browsers.
      }
    });
    loop.sources = [];
  }

  function scheduleAmbientCrowdSource(loop: AmbientCrowdLoop, startTime: number, fadeSeconds: number, offsetSeconds = 0) {
    if (!loop.buffer) {
      return 0;
    }
    const source = loop.context.createBufferSource();
    const gain = loop.context.createGain();
    const duration = loop.buffer.duration;
    const offset = clampRange(offsetSeconds, 0, Math.max(0, duration - 0.5));
    const playbackDuration = Math.max(0.5, duration - offset);
    const resolvedFadeSeconds = Math.min(fadeSeconds, Math.max(0.05, playbackDuration / 4));
    source.buffer = loop.buffer;
    source.connect(gain);
    gain.connect(loop.masterGain);

    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(1, startTime + resolvedFadeSeconds);
    gain.gain.setValueAtTime(1, Math.max(startTime + resolvedFadeSeconds, startTime + playbackDuration - resolvedFadeSeconds));
    gain.gain.linearRampToValueAtTime(0, startTime + playbackDuration);

    source.onended = () => {
      loop.sources = loop.sources.filter((candidate) => candidate !== source);
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        // Disconnect cleanup is best-effort.
      }
    };
    source.start(startTime, offset, playbackDuration);
    source.stop(startTime + playbackDuration + 0.05);
    loop.sources.push(source);
    return playbackDuration;
  }

  function scheduleAmbientCrowdLoop(loop: AmbientCrowdLoop, crossfadeSeconds = AMBIENT_CROWD_CROSSFADE_SECONDS, loopStartSeconds = 0) {
    if (!loop.active || !loop.buffer) {
      return;
    }
    const duration = loop.buffer.duration;
    const loopOffset = clampRange(loopStartSeconds, 0, Math.max(0, duration - 0.5));
    const loopPlaybackDuration = Math.max(0.5, duration - loopOffset);
    const fadeSeconds = Math.min(crossfadeSeconds, Math.max(0.05, loopPlaybackDuration / 4));
    const loopStep = Math.max(0.5, loopPlaybackDuration - fadeSeconds);
    const now = loop.context.currentTime;
    if (!Number.isFinite(loop.nextStartTime) || loop.nextStartTime < now + 0.05) {
      loop.nextStartTime = now + 0.05;
    }

    while (loop.nextStartTime < now + loopStep * 2.5) {
      const offset = Math.max(0, Math.min(loop.nextSourceOffset || 0, duration - 0.5));
      const playbackDuration = scheduleAmbientCrowdSource(loop, loop.nextStartTime, fadeSeconds, offset);
      loop.nextSourceOffset = loopOffset;
      loop.nextStartTime += Math.max(0.5, playbackDuration - fadeSeconds);
    }

    if (loop.timer) {
      clearTimeout(loop.timer);
    }
    loop.timer = setTimeout(() => scheduleAmbientCrowdLoop(loop, crossfadeSeconds, loopStartSeconds), Math.max(250, (loopStep * 0.45) * 1000));
  }

  function scheduleMatchMusicLoop(loop: AmbientCrowdLoop, loopStartSeconds: number, loopEndSeconds: number | null) {
    if (!loop.active || !loop.buffer) {
      return;
    }
    stopAmbientCrowdSources(loop);

    const source = loop.context.createBufferSource();
    const duration = loop.buffer.duration;
    const resolvedLoopStart = clampRange(loopStartSeconds, 0, Math.max(0, duration - 0.5));
    const resolvedLoopEnd = Number.isFinite(loopEndSeconds)
      ? clampRange(Number(loopEndSeconds), resolvedLoopStart + 0.5, duration)
      : duration;
    source.buffer = loop.buffer;
    source.loop = true;
    source.loopStart = resolvedLoopStart;
    source.loopEnd = resolvedLoopEnd;
    source.connect(loop.masterGain);
    source.onended = () => {
      loop.sources = loop.sources.filter((candidate) => candidate !== source);
      try {
        source.disconnect();
      } catch {
        // Disconnect cleanup is best-effort.
      }
    };
    source.start(loop.context.currentTime + 0.05);
    loop.sources.push(source);
  }

  function updateAudioToggleButton() {
    if (!refs.audioToggleButton) {
      return;
    }
    refs.audioToggleButton.classList.toggle("audio-muted", state.audioMuted);
    refs.audioToggleButton.title = state.audioMuted ? "Unmute sound effects" : "Mute sound effects";
    refs.audioToggleButton.setAttribute("aria-label", state.audioMuted ? "Unmute sound effects" : "Mute sound effects");
    refs.audioToggleButton.setAttribute("aria-pressed", state.audioMuted ? "true" : "false");
    refs.audioToggleIcon?.classList.toggle("fa-volume-xmark", state.audioMuted);
    refs.audioToggleIcon?.classList.toggle("fa-volume-high", !state.audioMuted);
  }

  function stopAmbientCrowdAudio() {
    const loop = state.ambientCrowdAudio;
    if (!loop) {
      return;
    }
    loop.active = false;
    loop.nextStartTime = 0;
    loop.nextSourceOffset = 0;
    if (loop.timer) {
      clearTimeout(loop.timer);
      loop.timer = null;
    }
    stopAmbientCrowdSources(loop);
  }

  function stopMatchMusicAudio() {
    const loop = state.matchMusicAudio;
    if (!loop) {
      return;
    }
    loop.active = false;
    loop.nextStartTime = 0;
    loop.nextSourceOffset = 0;
    if (loop.timer) {
      clearTimeout(loop.timer);
      loop.timer = null;
    }
    stopAmbientCrowdSources(loop);
  }

  async function playAmbientCrowdAudio() {
    if (visualTestPage || !state.game || state.audioMuted) {
      return;
    }
    const loop = ensureAmbientCrowdAudio();
    if (!loop) {
      return;
    }
    try {
      loop.masterGain.gain.setTargetAtTime(AMBIENT_CROWD_VOLUME, loop.context.currentTime, 0.08);
      if (loop.context.state === "suspended") {
        await loop.context.resume();
      }
      await loadAmbientCrowdBuffer(loop);
      if (state.audioMuted || !state.game) {
        return;
      }
      if (!loop.active) {
        loop.active = true;
        loop.nextStartTime = loop.context.currentTime + 0.05;
        loop.nextSourceOffset = 0;
        scheduleAmbientCrowdLoop(loop);
      }
    } catch {
      // Mobile and desktop browsers often require a user gesture before audio can start.
    }
  }

  async function playMatchMusicAudio() {
    if (visualTestPage || !state.game || state.audioMuted) {
      return;
    }
    const loop = ensureMatchMusicAudio();
    if (!loop) {
      return;
    }
    try {
      loop.masterGain.gain.setTargetAtTime(MATCH_MUSIC_VOLUME, loop.context.currentTime, 0.12);
      if (loop.context.state === "suspended") {
        await loop.context.resume();
      }
      await loadMatchMusicBuffer(loop);
      if (state.audioMuted || !state.game) {
        return;
      }
      if (!loop.active) {
        loop.active = true;
        loop.nextStartTime = 0;
        loop.nextSourceOffset = 0;
        const musicConfig = matchMusicConfigForHomeTeam();
        scheduleMatchMusicLoop(loop, musicConfig.loopStart, musicConfig.loopEnd);
      }
    } catch {
      // Match music is best-effort under browser autoplay rules.
    }
  }

  function effectContext() {
    const loop = ensureAmbientCrowdAudio();
    return loop?.context || null;
  }

  function envelopeParam(param: AudioParam, startTime: number, peak: number, duration: number) {
    param.setValueAtTime(0.0001, startTime);
    param.linearRampToValueAtTime(Math.max(0.0001, peak), startTime + Math.min(0.018, duration * 0.35));
    param.exponentialRampToValueAtTime(0.0001, startTime + duration);
  }

  function playTone(
    context: AudioContext,
    options: {
      type?: OscillatorType;
      frequency: number;
      endFrequency?: number;
      startTime?: number;
      duration: number;
      gain: number;
      destination?: AudioNode;
    },
  ) {
    const startTime = options.startTime ?? context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = options.type || "square";
    oscillator.frequency.setValueAtTime(Math.max(1, options.frequency), startTime);
    if (Number.isFinite(options.endFrequency)) {
      oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, options.endFrequency!), startTime + options.duration);
    }
    envelopeParam(gain.gain, startTime, options.gain, options.duration);
    oscillator.connect(gain);
    gain.connect(options.destination || context.destination);
    oscillator.start(startTime);
    oscillator.stop(startTime + options.duration + 0.02);
  }

  function playNoiseBurst(
    context: AudioContext,
    options: {
      startTime?: number;
      duration: number;
      gain: number;
      filterType?: BiquadFilterType;
      filterFrequency?: number;
      destination?: AudioNode;
    },
  ) {
    const startTime = options.startTime ?? context.currentTime;
    const sampleRate = context.sampleRate;
    const length = Math.max(1, Math.floor(sampleRate * options.duration));
    const buffer = context.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < length; index += 1) {
      const decay = 1 - index / length;
      data[index] = (Math.random() * 2 - 1) * decay;
    }
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = buffer;
    filter.type = options.filterType || "lowpass";
    filter.frequency.setValueAtTime(options.filterFrequency || 900, startTime);
    envelopeParam(gain.gain, startTime, options.gain, options.duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(options.destination || context.destination);
    source.start(startTime);
    source.stop(startTime + options.duration + 0.02);
  }

  function playRetroSoundEffect(kind: string, volume = 0.62) {
    if (visualTestPage || state.audioMuted) {
      return;
    }
    const context = effectContext();
    if (!context) {
      return;
    }
    context.resume?.().catch(() => {
      // Browser audio unlock is best-effort.
    });
    const now = context.currentTime + 0.012;
    const master = context.createGain();
    master.gain.value = volume * SOUND_EFFECT_VOLUME_MULTIPLIER;
    master.connect(context.destination);

    switch (kind) {
      case "bump":
        playTone(context, { type: "square", frequency: 86, endFrequency: 62, startTime: now, duration: 0.055, gain: 0.12, destination: master });
        playNoiseBurst(context, { startTime: now, duration: 0.045, gain: 0.08, filterType: "lowpass", filterFrequency: 520, destination: master });
        break;
      case "catch":
        playTone(context, { type: "square", frequency: 330, endFrequency: 190, startTime: now, duration: 0.075, gain: 0.2, destination: master });
        playTone(context, { type: "triangle", frequency: 148, endFrequency: 92, startTime: now + 0.018, duration: 0.09, gain: 0.12, destination: master });
        break;
      case "tackle":
        playTone(context, { type: "square", frequency: 118, endFrequency: 54, startTime: now, duration: 0.13, gain: 0.24, destination: master });
        playNoiseBurst(context, { startTime: now, duration: 0.16, gain: 0.32, filterType: "lowpass", filterFrequency: 650, destination: master });
        break;
      case "snap":
        playNoiseBurst(context, { startTime: now, duration: 0.035, gain: 0.14, filterType: "highpass", filterFrequency: 1400, destination: master });
        playTone(context, { type: "square", frequency: 330, endFrequency: 250, startTime: now + 0.012, duration: 0.035, gain: 0.08, destination: master });
        break;
      case "line-surge":
        playTone(context, { type: "square", frequency: 76, endFrequency: 58, startTime: now, duration: 0.08, gain: 0.12, destination: master });
        playNoiseBurst(context, { startTime: now, duration: 0.09, gain: 0.1, filterType: "lowpass", filterFrequency: 420, destination: master });
        break;
      case "handoff":
        playTone(context, { type: "square", frequency: 210, endFrequency: 145, startTime: now, duration: 0.045, gain: 0.1, destination: master });
        playNoiseBurst(context, { startTime: now + 0.008, duration: 0.035, gain: 0.055, filterType: "bandpass", filterFrequency: 740, destination: master });
        break;
      case "kick":
        playTone(context, { type: "square", frequency: 92, endFrequency: 44, startTime: now, duration: 0.18, gain: 0.28, destination: master });
        playNoiseBurst(context, { startTime: now, duration: 0.07, gain: 0.16, filterType: "lowpass", filterFrequency: 760, destination: master });
        break;
      case "return-fielded":
        playTone(context, { type: "square", frequency: 240, endFrequency: 180, startTime: now, duration: 0.055, gain: 0.11, destination: master });
        playTone(context, { type: "triangle", frequency: 360, endFrequency: 260, startTime: now + 0.018, duration: 0.05, gain: 0.06, destination: master });
        break;
      case "kick-good":
        [523, 659, 784, 1046].forEach((frequency, index) => {
          playTone(context, { type: "square", frequency, startTime: now + index * 0.07, duration: 0.07, gain: index === 3 ? 0.14 : 0.1, destination: master });
        });
        break;
      case "kick-no-good":
        [392, 330, 247, 185].forEach((frequency, index) => {
          playTone(context, { type: "square", frequency, endFrequency: frequency * 0.94, startTime: now + index * 0.075, duration: 0.075, gain: index === 3 ? 0.13 : 0.1, destination: master });
        });
        playNoiseBurst(context, { startTime: now + 0.06, duration: 0.12, gain: 0.05, filterType: "bandpass", filterFrequency: 480, destination: master });
        break;
      case "throw":
        playNoiseBurst(context, { startTime: now, duration: 0.13, gain: 0.1, filterType: "bandpass", filterFrequency: 1350, destination: master });
        playTone(context, { type: "triangle", frequency: 660, endFrequency: 360, startTime: now + 0.01, duration: 0.11, gain: 0.08, destination: master });
        break;
      case "turnover":
        [740, 520, 740, 440].forEach((frequency, index) => {
          playTone(context, { type: "square", frequency, endFrequency: frequency * 0.92, startTime: now + index * 0.07, duration: 0.055, gain: 0.14, destination: master });
        });
        break;
      case "dive-spotlight":
        playNoiseBurst(context, { startTime: now, duration: 0.24, gain: 0.09, filterType: "bandpass", filterFrequency: 1450, destination: master });
        playTone(context, { type: "triangle", frequency: 310, endFrequency: 620, startTime: now, duration: 0.19, gain: 0.12, destination: master });
        playTone(context, { type: "sine", frequency: 880, endFrequency: 660, startTime: now + 0.13, duration: 0.14, gain: 0.075, destination: master });
        break;
      case "first-down":
        [392, 523, 659].forEach((frequency, index) => {
          playTone(context, { type: "square", frequency, startTime: now + index * 0.055, duration: 0.07, gain: index === 2 ? 0.13 : 0.09, destination: master });
        });
        break;
      case "sack":
        playTone(context, { type: "square", frequency: 104, endFrequency: 42, startTime: now, duration: 0.18, gain: 0.28, destination: master });
        playNoiseBurst(context, { startTime: now, duration: 0.19, gain: 0.26, filterType: "lowpass", filterFrequency: 520, destination: master });
        playTone(context, { type: "triangle", frequency: 70, endFrequency: 36, startTime: now + 0.05, duration: 0.14, gain: 0.1, destination: master });
        break;
      case "loss":
        playTone(context, { type: "square", frequency: 150, endFrequency: 72, startTime: now, duration: 0.11, gain: 0.16, destination: master });
        playNoiseBurst(context, { startTime: now + 0.025, duration: 0.08, gain: 0.1, filterType: "lowpass", filterFrequency: 460, destination: master });
        break;
      case "incomplete":
        playTone(context, { type: "triangle", frequency: 260, endFrequency: 130, startTime: now, duration: 0.12, gain: 0.1, destination: master });
        playNoiseBurst(context, { startTime: now + 0.02, duration: 0.05, gain: 0.06, filterType: "bandpass", filterFrequency: 900, destination: master });
        break;
      case "touchdown":
        [523, 659, 784, 1046, 1318].forEach((frequency, index) => {
          playTone(context, { type: "square", frequency, startTime: now + index * 0.07, duration: 0.085, gain: index === 4 ? 0.16 : 0.12, destination: master });
        });
        break;
      default:
        break;
    }

    window.setTimeout(() => {
      try {
        master.disconnect();
      } catch {
        // Master cleanup is best-effort after all short voices have stopped.
      }
    }, 700);
  }

  function loadOneShotAudioBuffer(context: AudioContext, src: string) {
    const resolvedSrc = resolveAudioAssetSrc(src);
    let bufferPromise = state.oneShotAudioBuffers.get(resolvedSrc);
    if (!bufferPromise) {
      bufferPromise = fetch(resolvedSrc)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Unable to load sound effect (${response.status})`);
          }
          return response.arrayBuffer();
        })
        .then((arrayBuffer) => context.decodeAudioData(arrayBuffer.slice(0)));
      state.oneShotAudioBuffers.set(resolvedSrc, bufferPromise);
    }
    return bufferPromise;
  }

  async function preloadOneShotSound(src: string) {
    const context = effectContext();
    if (!context) {
      return;
    }
    try {
      if (context.state === "suspended") {
        await context.resume();
      }
      await loadOneShotAudioBuffer(context, src);
    } catch {
      // One-shot preloading is best-effort.
    }
  }

  async function playBufferedOneShotSound(src: string, volume = 0.62) {
    const context = effectContext();
    if (!context) {
      return false;
    }
    try {
      if (context.state === "suspended") {
        await context.resume();
      }
      const buffer = await loadOneShotAudioBuffer(context, src);
      if (state.audioMuted) {
        return false;
      }
      const source = context.createBufferSource();
      const gain = context.createGain();
      source.buffer = buffer;
      gain.gain.value = volume * SOUND_EFFECT_VOLUME_MULTIPLIER;
      source.connect(gain);
      gain.connect(context.destination);
      source.onended = () => {
        try {
          source.disconnect();
          gain.disconnect();
        } catch {
          // One-shot cleanup is best-effort.
        }
      };
      source.start(context.currentTime + 0.01);
      return true;
    } catch {
      return false;
    }
  }

  function playOneShotSound(src: string, volume = 0.62) {
    if (visualTestPage || state.audioMuted || typeof window === "undefined") {
      return false;
    }
    if (src.startsWith("synth:")) {
      playRetroSoundEffect(src.slice("synth:".length), volume);
      return true;
    }
    playBufferedOneShotSound(src, volume).then((played) => {
      if (played || typeof Audio === "undefined" || state.audioMuted) {
        return;
      }
      const audio = new Audio(resolveAudioAssetSrc(src));
      audio.preload = "auto";
      audio.volume = clampRange(volume * SOUND_EFFECT_VOLUME_MULTIPLIER, 0, 1);
      audio.play().catch(() => {
        // One-shot effects are best-effort under browser autoplay rules.
      });
    });
    return true;
  }

  function pruneRecentSoundEffects(now: number) {
    state.recentSoundEffects.forEach((playedAt, key) => {
      if (now - playedAt > 1200) {
        state.recentSoundEffects.delete(key);
      }
    });
  }

  function recentlyPlayedSoundEffect(kind: string, src: string) {
    const now = performance.now();
    pruneRecentSoundEffects(now);
    const key = `${kind}:${src}`;
    const playedAt = state.recentSoundEffects.get(key);
    if (typeof playedAt === "number" && now - playedAt < 450) {
      return true;
    }
    state.recentSoundEffects.set(key, now);
    return false;
  }

  function shouldPlayKickResultSound(play: any) {
    return !!play && (play.playId === "field-goal" || play.playId === "pat");
  }

  function kickAttemptIsGood(play: any) {
    const resultText = String(play?.result || "").toLowerCase();
    return !!play && (
      !!play.fieldGoal?.good
      || stopReasonForPlay(play) === "good"
      || resultText.includes("adds the extra point")
      || resultText.includes("drills a")
    );
  }

  function touchdownHeadlineCueReached(frame: any, play: any, fallbackProgressPoint: number) {
    if (!shouldPlayTouchdownSound(play)) {
      return false;
    }
    const ballX = Number(frame?.ball?.ballX);
    const direction = Number(frame?.direction) || 1;
    const defensiveScore = !!play?.defensiveScore || (isInterceptionPlay(play) && !!play?.turnover);
    const scoringDirection = defensiveScore ? -direction : direction;
    const goalLineX = screenXForYard(defensiveScore ? 0 : 100, direction);
    if (Number.isFinite(ballX) && Number.isFinite(goalLineX)) {
      // The score is authoritative as soon as the ball breaks the plane. A
      // positive visual buffer hid the banner on goal-line finishes whose
      // runout ended immediately after the crossing frame.
      const planeBufferPx = 0;
      return scoringDirection > 0
        ? ballX >= goalLineX + planeBufferPx
        : ballX <= goalLineX - planeBufferPx;
    }
    return clamp01(frame?.liveProgress ?? 0) >= fallbackProgressPoint;
  }

  function ribbonHeadlineCueReached(frame: any) {
    const play = frame?.play;
    if (!play || frame.scenePhase !== "live") {
      return false;
    }
    const stopPoint = stopPointForPlay(play);
    if (shouldPlayTouchdownSound(play)) {
      return touchdownHeadlineCueReached(frame, play, stopPoint);
    }
    return clamp01(frame.liveProgress ?? 0) >= stopPoint;
  }

  function soundEffectKey(kind: string, playIndex: number) {
    const play = state.game?.plays?.[playIndex];
    const gameKey = state.game?.gameId || state.game?.id || `${state.game?.away?.id || "away"}-${state.game?.home?.id || "home"}`;
    return `${gameKey}:${play?.sequence ?? playIndex}:${kind}`;
  }

  function triggerSoundEffectOnce(kind: string, playIndex: number, src: string, volume?: number) {
    if (visualTestPage || state.audioMuted || typeof window === "undefined") {
      return;
    }
    const key = soundEffectKey(kind, playIndex);
    if (state.playedSoundEffects.has(key)) {
      return;
    }
    if (recentlyPlayedSoundEffect(kind, src)) {
      return;
    }
    if (playOneShotSound(src, volume)) {
      state.playedSoundEffects.add(key);
    }
  }

  function shouldPlayCatchSound(play: any) {
    return isCompletedPassPlay(play) && !isInterceptionPlay(play) && !isSackPlay(play);
  }

  function shouldPlayTackleSound(play: any) {
    return stopReasonForPlay(play) === "tackle";
  }

  function shouldPlayIncompleteSound(play: any) {
    return !!play
      && play.family === "pass"
      && !isSackPlay(play)
      && !isInterceptionPlay(play)
      && stopReasonForPlay(play) === "incomplete";
  }

  function shouldPlayReturnFieldedSound(play: any) {
    return !!play
      && (play.playId === "kickoff" || play.playId === "punt")
      && stopReasonForPlay(play) !== "touchback";
  }

  function shouldPlayLossSound(play: any) {
    return !!play
      && !isSackPlay(play)
      && !shouldPlayTouchdownSound(play)
      && visualYardsForPlay(play) < 0;
  }

  function shouldPlayFirstDownSound(play: any) {
    return !!play
      && !!play.firstDown
      && !shouldPlayTouchdownSound(play)
      && !isInterceptionPlay(play)
      && !isSackPlay(play);
  }

  function shouldSuppressImmediatePassTackleSound(play: any) {
    if (!shouldPlayCatchSound(play)) {
      return false;
    }
    const keyEvents = play?.arcadeReplay?.keyEvents || {};
    const catchFrame = Number(keyEvents.catchResult?.simFrame ?? keyEvents.catch?.simFrame);
    const contactFrame = Number(keyEvents.firstContact?.simFrame ?? keyEvents.tackle?.simFrame);
    const yacYards = Number(play?.arcadeReplay?.metadata?.yacYards ?? play?.yacYards ?? 0);
    if (Number.isFinite(catchFrame) && Number.isFinite(contactFrame) && contactFrame - catchFrame <= 8) {
      return true;
    }
    return Number.isFinite(yacYards) && yacYards <= 1.5;
  }

  function interceptionCatchFrameForPlay(play: any) {
    const replay = play?.arcadeReplay;
    const keyEvents = replay?.keyEvents || {};
    const interceptionEvent = replay?.events?.find?.((event: any) => event?.type === "pass_intercepted");
    const catchResult = keyEvents.catchResult;
    const catchResultIsInterception = String(catchResult?.payload?.result || replay?.metadata?.catchResult || "").toLowerCase() === "interception";
    const frame = Number(
      interceptionEvent?.simFrame
      ?? keyEvents.interception?.simFrame
      ?? (catchResultIsInterception ? catchResult?.simFrame : undefined)
      ?? (catchResultIsInterception ? keyEvents.catch?.simFrame : undefined),
    );
    return Number.isFinite(frame) ? frame : null;
  }

  function interceptionSoundCueReached(frame: any, play: any, fallbackProgressPoint: number) {
    if (isInterceptionPlay(play) && frame?.takeawayFlash?.teamType === "defense") {
      return true;
    }
    const interceptionFrame = interceptionCatchFrameForPlay(play);
    const frameSimFrame = Number(frame?.simFrame);
    if (interceptionFrame !== null && Number.isFinite(frameSimFrame)) {
      return frameSimFrame >= interceptionFrame;
    }
    if (isInterceptionPlay(play)) {
      return catchSoundCueReached(frame, play);
    }
    return clamp01(frame?.liveProgress ?? 0) >= fallbackProgressPoint;
  }

  function shouldSuppressImmediateInterceptionTackleSound(play: any) {
    if (!isInterceptionPlay(play)) {
      return false;
    }
    const interceptionFrame = interceptionCatchFrameForPlay(play);
    const keyEvents = play?.arcadeReplay?.keyEvents || {};
    const contactFrame = Number(keyEvents.firstContact?.simFrame ?? keyEvents.tackle?.simFrame);
    const returnYards = Number(play?.arcadeReplay?.metadata?.returnYards ?? play?.returnYards ?? 0);
    if (interceptionFrame !== null && Number.isFinite(contactFrame) && contactFrame - interceptionFrame <= 8) {
      return true;
    }
    return Number.isFinite(returnYards) && returnYards <= 1.5;
  }

  function catchSoundCueReached(frame: any, play: any) {
    const keyEvents = play?.arcadeReplay?.keyEvents || {};
    const catchFrame = Number(keyEvents.catchResult?.simFrame ?? keyEvents.catch?.simFrame);
    const frameSimFrame = Number(frame?.simFrame);
    if (Number.isFinite(catchFrame) && Number.isFinite(frameSimFrame)) {
      return frameSimFrame >= catchFrame;
    }
    const progress = clamp01(frame?.liveProgress ?? 0);
    return progress >= Math.max(0.35, Math.min(0.9, stopPointForPlay(play) - 0.12));
  }

  function passThrowCueReached(frame: any, play: any) {
    if (play?.family !== "pass" || isSackPlay(play)) {
      return false;
    }
    const keyEvents = play?.arcadeReplay?.keyEvents || {};
    const throwFrame = Number(keyEvents.throw?.simFrame);
    const frameSimFrame = Number(frame?.simFrame);
    if (Number.isFinite(throwFrame) && Number.isFinite(frameSimFrame)) {
      return frameSimFrame >= throwFrame;
    }
    const ballState = String(frame?.ball?.arcadeState || "");
    if (ballState === "pass-flight") {
      return true;
    }
    return clamp01(frame?.liveProgress ?? 0) >= 0.28;
  }

  function puntKickCueReached(frame: any, play: any) {
    if (play?.playId !== "punt") {
      return false;
    }
    const keyEvents = play?.arcadeReplay?.keyEvents || {};
    const puntFrame = Number(keyEvents.punt?.simFrame ?? keyEvents.kick?.simFrame);
    const frameSimFrame = Number(frame?.simFrame);
    if (Number.isFinite(puntFrame) && Number.isFinite(frameSimFrame)) {
      return frameSimFrame >= puntFrame;
    }
    const flightWindow = puntFlightWindow(play);
    return clamp01(frame?.liveProgress ?? 0) >= Math.max(0.04, flightWindow.start - 0.015);
  }

  function firstContactCueReached(frame: any, play: any) {
    const keyEvents = play?.arcadeReplay?.keyEvents || {};
    const contactFrame = Number(keyEvents.firstContact?.simFrame);
    const frameSimFrame = Number(frame?.simFrame);
    if (Number.isFinite(contactFrame) && Number.isFinite(frameSimFrame)) {
      return frameSimFrame >= contactFrame;
    }
    return false;
  }

  function returnFieldedCueReached(frame: any, play: any) {
    if (!shouldPlayReturnFieldedSound(play)) {
      return false;
    }
    const keyEvents = play?.arcadeReplay?.keyEvents || {};
    const fieldedFrame = Number(keyEvents.catch?.simFrame ?? keyEvents.fielded?.simFrame ?? keyEvents.returnStart?.simFrame);
    const frameSimFrame = Number(frame?.simFrame);
    if (Number.isFinite(fieldedFrame) && Number.isFinite(frameSimFrame)) {
      return frameSimFrame >= fieldedFrame;
    }
    const fallbackPoint = play.playId === "punt" ? 0.62 : 0.58;
    return clamp01(frame?.liveProgress ?? 0) >= fallbackPoint;
  }

  function maybePlayCatchSoundForReceiverCue(frame: any, indicator: any) {
    if (!frame || !indicator?.player || indicator.teamType !== "offense") {
      return;
    }
    if (frame.play?.arcadeReplay?.keyEvents?.catch || frame.play?.arcadeReplay?.keyEvents?.catchResult) {
      return;
    }
    if (!shouldPlayCatchSound(frame.play) || indicator.player.role === "QB") {
      return;
    }
    const ballState = String(frame.ball?.arcadeState || "");
    if (ballState && ballState !== "carried" && ballState !== "catch-transfer" && ballState !== "secured") {
      return;
    }
    triggerSoundEffectOnce("catch", state.playIndex, CATCH_SOUND_ASSET, 0.7);
  }

  function shouldPlayTurnoverSound(play: any) {
    if (!play) {
      return false;
    }
    if (isInterceptionPlay(play)) {
      return true;
    }
    return !!play.turnover && !play.turnoverOnDowns;
  }

  function shouldPlayTouchdownSound(play: any) {
    return !!play && (stopReasonForPlay(play) === "touchdown" || play.touchdown || play.defensiveScore || play.returnTouchdown);
  }

  function shouldPlayBigPlayApplause(play: any) {
    return shouldPlayTouchdownSound(play);
  }

  function maybePlayVisualFrameSoundEffects(frame: any) {
    if (!frame?.play || frame.play !== currentPlay() || frame.scenePhase !== "live") {
      return;
    }
    const play = frame.play;
    const progress = clamp01(frame.liveProgress ?? 0);
    const stopPoint = stopPointForPlay(play);
    const touchdown = shouldPlayTouchdownSound(play);
    const turnover = shouldPlayTurnoverSound(play);
    const turnoverPoint = Math.max(0.52, Math.min(0.78, stopPoint - 0.18));
    const tacklePoint = Math.max(0.68, stopPoint - 0.02);

    if ((isScrimmageSnap(play) || play.playId === "punt" || play.playId === "field-goal" || play.playId === "pat") && progress >= 0.01) {
      triggerSoundEffectOnce("snap", state.playIndex, SNAP_SOUND_ASSET, 0.48);
    }
    if (isScrimmageSnap(play) && progress >= 0.08) {
      triggerSoundEffectOnce("line-surge", state.playIndex, LINE_SURGE_SOUND_ASSET, 0.26);
    }
    if (play.family === "run" && play.playId !== "kneel" && progress >= 0.18) {
      triggerSoundEffectOnce("handoff", state.playIndex, HANDOFF_SOUND_ASSET, 0.28);
    }
    if (play.playId === "kickoff" && progress >= 0.04) {
      triggerSoundEffectOnce("kick", state.playIndex, KICK_SOUND_ASSET, 0.66);
    } else if (puntKickCueReached(frame, play)) {
      triggerSoundEffectOnce("kick", state.playIndex, KICK_SOUND_ASSET, 0.64);
    } else if ((play.playId === "field-goal" || play.playId === "pat") && progress >= 0.3) {
      triggerSoundEffectOnce("kick", state.playIndex, KICK_SOUND_ASSET, 0.64);
    }
    if (returnFieldedCueReached(frame, play)) {
      triggerSoundEffectOnce("return-fielded", state.playIndex, RETURN_FIELD_SOUND_ASSET, 0.34);
    }
    if (shouldPlayKickResultSound(play) && ribbonHeadlineCueReached(frame)) {
      const kickGood = kickAttemptIsGood(play);
      triggerSoundEffectOnce(
        kickGood ? "kick-good" : "kick-no-good",
        state.playIndex,
        kickGood ? KICK_GOOD_SOUND_ASSET : KICK_NO_GOOD_SOUND_ASSET,
        0.5,
      );
    }
    if (passThrowCueReached(frame, play)) {
      triggerSoundEffectOnce("throw", state.playIndex, PASS_THROW_SOUND_ASSET, 0.42);
    }
    if (frame.heroMoment) {
      triggerSoundEffectOnce(
        "dive-spotlight",
        state.playIndex,
        DIVE_SPOTLIGHT_SOUND_ASSET,
        Number(frame.heroMoment.soundVolume || 0.34),
      );
    }
    if (shouldPlayCatchSound(play) && catchSoundCueReached(frame, play)) {
      triggerSoundEffectOnce("catch", state.playIndex, CATCH_SOUND_ASSET, 0.7);
    }
    if (shouldPlayIncompleteSound(play) && catchSoundCueReached(frame, play)) {
      triggerSoundEffectOnce("incomplete", state.playIndex, INCOMPLETE_SOUND_ASSET, 0.34);
    }
    if (firstContactCueReached(frame, play)) {
      triggerSoundEffectOnce("bump", state.playIndex, BUMP_SOUND_ASSET, 0.24);
    }
    if (turnover && interceptionSoundCueReached(frame, play, turnoverPoint)) {
      triggerSoundEffectOnce("turnover", state.playIndex, TURNOVER_SOUND_ASSET, 0.58);
    }
    if (
      shouldPlayTackleSound(play)
      && !shouldSuppressImmediatePassTackleSound(play)
      && !shouldSuppressImmediateInterceptionTackleSound(play)
      && progress >= tacklePoint
    ) {
      triggerSoundEffectOnce("tackle", state.playIndex, TACKLE_SOUND_ASSET, 0.85);
    }
    if (isSackPlay(play) && progress >= tacklePoint) {
      triggerSoundEffectOnce("sack", state.playIndex, SACK_SOUND_ASSET, 0.74);
    } else if (shouldPlayLossSound(play) && progress >= tacklePoint) {
      triggerSoundEffectOnce("loss", state.playIndex, LOSS_SOUND_ASSET, 0.42);
    }
    if (touchdown && touchdownHeadlineCueReached(frame, play, stopPoint)) {
      triggerSoundEffectOnce("touchdown", state.playIndex, TOUCHDOWN_SOUND_ASSET, 0.64);
    }
    if (shouldPlayFirstDownSound(play) && progress >= stopPoint) {
      triggerSoundEffectOnce("first-down", state.playIndex, FIRST_DOWN_SOUND_ASSET, 0.42);
    }
    const applauseCueReached = touchdown
      ? touchdownHeadlineCueReached(frame, play, stopPoint)
      : progress >= Math.min(0.94, stopPoint);
    if (shouldPlayBigPlayApplause(play) && applauseCueReached) {
      triggerSoundEffectOnce("crowd-applause", state.playIndex, CROWD_APPLAUSE_ASSET, CROWD_APPLAUSE_TOUCHDOWN_VOLUME);
    }
  }

  function setAudioMuted(muted: boolean) {
    state.audioMuted = muted;
    writeStoredAudioMuted(muted);
    const loop = ensureAmbientCrowdAudio();
    if (loop) {
      loop.masterGain.gain.setTargetAtTime(muted ? 0 : AMBIENT_CROWD_VOLUME, loop.context.currentTime, 0.08);
      if (muted) {
        stopAmbientCrowdAudio();
      } else {
        playAmbientCrowdAudio();
        preloadOneShotSound(CROWD_APPLAUSE_ASSET);
      }
    }
    const musicLoop = ensureMatchMusicAudio();
    if (musicLoop) {
      musicLoop.masterGain.gain.setTargetAtTime(muted ? 0 : MATCH_MUSIC_VOLUME, musicLoop.context.currentTime, 0.12);
      if (muted) {
        stopMatchMusicAudio();
      } else {
        playMatchMusicAudio();
      }
    }
    updateAudioToggleButton();
  }

  function fieldChromeButtons() {
    return [refs.fullscreenButton, refs.audioToggleButton].filter(Boolean) as HTMLButtonElement[];
  }

  function hideFieldControls() {
    refs.stageScreen?.classList.remove("field-controls-visible");
    if (fieldControlsTimer) {
      clearTimeout(fieldControlsTimer);
      fieldControlsTimer = null;
    }
  }

  function revealFieldControls() {
    if (!refs.stageScreen) {
      return;
    }
    refs.stageScreen.classList.add("field-controls-visible");
    if (fieldControlsTimer) {
      clearTimeout(fieldControlsTimer);
    }
    fieldControlsTimer = setTimeout(() => {
      refs.stageScreen?.classList.remove("field-controls-visible");
      fieldControlsTimer = null;
    }, 2000);
  }

  async function toggleStageFullscreen() {
    if (!refs.stageScreen || !refs.fullscreenButton) {
      return;
    }

    try {
      if (nativeFullscreenElement() === refs.stageScreen) {
        await exitNativeFullscreen();
      } else if (isElementFullscreen(refs.stageScreen)) {
        setFallbackFullscreen(refs.stageScreen, false);
        unlockMobileOrientation();
      } else {
        const enteredNativeFullscreen = await requestNativeFullscreen(refs.stageScreen);
        if (!enteredNativeFullscreen) {
          setFallbackFullscreen(refs.stageScreen, true);
        }
        await lockMobileLandscape();
        revealFieldControls();
      }
    } finally {
      syncFullscreenCanvasSize();
      updateFullscreenButton();
    }
  }

  refs.fullscreenButton?.addEventListener("click", () => {
    toggleStageFullscreen().catch(() => {});
  });
  refs.stageScreen?.addEventListener("mousemove", revealFieldControls);
  refs.stageScreen?.addEventListener("touchstart", revealFieldControls, { passive: true });
  fieldChromeButtons().forEach((button) => {
    button.addEventListener("focus", revealFieldControls);
    button.addEventListener("pointerdown", revealFieldControls, { passive: true });
  });
  const handleFullscreenChange = () => {
    syncFullscreenCanvasSize();
    updateFullscreenButton();
    if (nativeFullscreenElement() === refs.stageScreen) {
      revealFieldControls();
    } else {
      hideFieldControls();
      revealFieldControls();
      unlockMobileOrientation();
    }
  };
  document.addEventListener("fullscreenchange", handleFullscreenChange);
  document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
  window.addEventListener("resize", syncFullscreenCanvasSize);
  window.addEventListener("orientationchange", syncFullscreenCanvasSize);
  window.visualViewport?.addEventListener("resize", syncFullscreenCanvasSize);
  syncFullscreenCanvasSize();
  updateFullscreenButton();
  updateAudioToggleButton();
  window.setTimeout(revealFieldControls, 300);

  const assetCache = new Map();
  const recolorCache = new Map();
  const spriteFrameCache = new Map();

  const PLAYER_SPRITE_DRAW_SIZE = 48;
  const PLAYER_SPRITE_PREWARM_ROLES = [
    "generic",
    "QB",
    "RB",
    "FB",
    "WR1",
    "WR2",
    "SLOT",
    "TE",
    "LT",
    "LG",
    "C",
    "RG",
    "RT",
    "DE1",
    "DT1",
    "DT2",
    "DE2",
    "LB1",
    "LB2",
    "LB3",
    "CB1",
    "CB2",
    "S1",
    "S2",
    "KICKER",
    "RETURNER",
  ];
  const PLAYER_LAYER_VARIANT_IDS = new Set(["helmet", "jersey", "pants", "accent", "gloves", "shoes"]);

  function spriteLayerVariantsForRole(role: any) {
    void role;
    return null;
  }

  const DEFAULT_FIELD_LOGO_SRC = "assets/logos/cyber-midfield.svg";

  function isDefaultFieldLogoSrc(src: any) {
    const value = String(src || "").trim();
    if (!value) {
      return false;
    }
    try {
      const pathname = new URL(value, "https://dfl.local").pathname.replace(/^\/+/, "");
      return pathname === DEFAULT_FIELD_LOGO_SRC;
    } catch {
      return value.replace(/^\/+/, "") === DEFAULT_FIELD_LOGO_SRC;
    }
  }

  const visualConfig = {
    midfieldLogo: {
      enabled: true,
      src: DEFAULT_FIELD_LOGO_SRC,
      width: 168,
      height: 168,
      alpha: 0.34,
      glow: "rgba(111, 190, 255, 0.24)",
    },
    spriteSheets: {
      players: {
        mode: "layered",
        src: "assets/sprites/players/v2",
        fallbackSrc: versionedAssetPath("assets/sprites/player-mask-sheet.png", MASK_SPRITE_ASSET_VERSION),
        frameWidth: 48,
        frameHeight: 48,
        variants: {
          idleRight: { x: 0, y: 0 },
          idle1Right: { x: 0, y: 0 },
          idle2Right: { x: 0, y: 0 },
          runRight: { x: 0, y: 0 },
          run1Right: { x: 0, y: 0 },
          run2Right: { x: 0, y: 0 },
          run3Right: { x: 0, y: 0 },
          tackleRight: { x: 0, y: 0 },
          fallRight: { x: 1, y: 0 },
        },
        colorRegions: {
          outline: "#000000",
          shadow: "#3f3f3f",
          skin: "#ffd2a6",
          helmet: "#00ff00",
          jersey: "#ff0000",
          pants: "#0000ff",
          accent: "#ffff00",
          gloves: "#ff7700",
          shoes: "#ff00ff",
        },
        layers: [
          { id: "skin", src: versionedAssetPath("assets/sprites/players/v2/skin/base.png", PLAYER_LAYER_ASSET_VERSION) },
          { id: "jersey", src: versionedAssetPath("assets/sprites/players/v2/jersey/base.png", PLAYER_LAYER_ASSET_VERSION) },
          { id: "accent", src: versionedAssetPath("assets/sprites/players/v2/accent/base.png", PLAYER_LAYER_ASSET_VERSION) },
          { id: "gloves", src: versionedAssetPath("assets/sprites/players/v2/gloves/base.png", PLAYER_LAYER_ASSET_VERSION) },
          { id: "pants", src: versionedAssetPath("assets/sprites/players/v2/pants/base.png", PLAYER_LAYER_ASSET_VERSION) },
          { id: "shoes", src: versionedAssetPath("assets/sprites/players/v2/shoes/base.png", PLAYER_LAYER_ASSET_VERSION) },
          { id: "hair", src: versionedAssetPath("assets/sprites/players/v2/hair/base.png", PLAYER_LAYER_ASSET_VERSION) },
          { id: "helmet", src: versionedAssetPath("assets/sprites/players/v2/helmet/base.png", PLAYER_LAYER_ASSET_VERSION) },
        ],
      },
      football: {
        mode: "mask",
        src: versionedAssetPath("assets/sprites/football-mask-sheet.png", MASK_SPRITE_ASSET_VERSION),
        frameWidth: 8,
        frameHeight: 8,
        variants: {
          default: { x: 0, y: 0 },
          spiral: { x: 1, y: 0 },
        },
        colorRegions: {
          outline: "#000000",
          leather: "#8b4513",
          laces: "#ffffff",
          glow: "#00eaff",
        },
      },
    },
    defaults: {
      playerUniform: {
        skin: "#c68642",
        helmet: "#2a4158",
        jersey: "#2f6bff",
        pants: "#d7e5ff",
        accent: "#ffffff",
        gloves: "#ffffff",
        shoes: "#111111",
        hair: "#2c1a12",
        trim: "#ffffff",
        accessories: "#111111",
        outline: "#04111f",
        shadow: "#09131f",
      },
      footballPalette: {
        outline: "#1a0f06",
        leather: "#8a4f1d",
        laces: "#f5fbff",
        glow: "#4ad5ff",
      },
    },
  };

  const SAME_ORIGIN_ASSET_PATHS = new Set([
    visualConfig.spriteSheets.players.src,
    visualConfig.spriteSheets.football.src,
  ]);

  function defaultPlayerSpritePalette(colors: any = {}) {
    const defaults = visualConfig.defaults.playerUniform;
    return {
      helmet: colors.primary || defaults.helmet,
      jersey: colors.primary || defaults.jersey,
      pants: colors.secondary || defaults.pants,
      accent: colors.accent || defaults.accent || defaults.trim,
      gloves: colors.accent || defaults.gloves,
      shoes: defaults.shoes,
    };
  }

  const DEFAULT_FIELD_VISUALS = {
    homeFieldColor: { hueShift: 0, chromaShift: 0, lightnessShift: 0 },
    fieldMarkingColor: { hueShift: 0, chromaShift: 0, lightnessShift: 0 },
    fieldBoardColor: { hueShift: 0, chromaShift: 0, lightnessShift: 0 },
    fieldBackground: { texture: 'cyber', hueShift: 0, chromaShift: 0, lightnessShift: 0 },
    fieldGoalPostColor: { hueShift: 0, chromaShift: 0, lightnessShift: 0 },
    fieldGoalBaseColor: { hueShift: 0, chromaShift: 0, lightnessShift: 0 },
    logo: {
      src: DEFAULT_FIELD_LOGO_SRC,
      hueShift: 0,
      chromaShift: 0,
      lightnessShift: 0,
    },
  };

  export function teamLogoAssetSrc(team: any) {
    const explicit = team?.logo || team?.logoSrc || team?.logoUrl;
    if (explicit) {
      return versionFranchiseImageSrc(explicit, team?.nft);
    }
    // NFT-bound franchises have no branded logo file, but every team NFT's
    // collection image (its metadata `image` field) is the team logo.
    const nftTokenId = String(team?.nft?.tokenId ?? "").trim();
    if (nftTokenId) {
      return versionFranchiseImageSrc(
        `collection/teams/images/${encodeURIComponent(nftTokenId)}.png`,
        team.nft,
      );
    }
    const city = String(team?.city || "").trim();
    const name = String(team?.name || team?.displayName || "").trim();
    const displayName = city && name
      ? `${city} ${name}`
      : String(team?.displayName || team?.name || "").trim();
    const slug = displayName
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return slug ? `assets/logos/${slug}.png` : null;
  }

  function homeFieldVisuals(team = state.game?.home) {
    const field = team?.visuals?.field || {};
    const logo = typeof field.logo === "string"
      ? { ...DEFAULT_FIELD_VISUALS.logo, src: field.logo }
      : { ...DEFAULT_FIELD_VISUALS.logo, ...(field.logo || {}) };
    const logoSrc = !logo.src || isDefaultFieldLogoSrc(logo.src)
      ? teamLogoAssetSrc(team) || logo.src
      : versionFranchiseImageSrc(logo.src, team?.nft);
    return {
      homeFieldColor: { ...DEFAULT_FIELD_VISUALS.homeFieldColor, ...(field.homeFieldColor || {}) },
      fieldMarkingColor: { ...DEFAULT_FIELD_VISUALS.fieldMarkingColor, ...(field.fieldMarkingColor || {}) },
      fieldBoardColor: { ...DEFAULT_FIELD_VISUALS.fieldBoardColor, ...(field.fieldBoardColor || {}) },
      fieldBackground: { ...DEFAULT_FIELD_VISUALS.fieldBackground, ...(field.fieldBackground || {}) },
      fieldGoalPostColor: { ...DEFAULT_FIELD_VISUALS.fieldGoalPostColor, ...(field.fieldGoalPostColor || {}) },
      fieldGoalBaseColor: { ...DEFAULT_FIELD_VISUALS.fieldGoalBaseColor, ...(field.fieldGoalBaseColor || {}) },
      logo: { ...logo, src: logoSrc },
    };
  }

function loadImageAsset(src: any) {
  if (!src || isDefaultFieldLogoSrc(src)) {
    return null;
  }
  const normalizedSrc = normalizeAssetSrc(src);
  if (!normalizedSrc) {
    return null;
  }
  if (!assetCache.has(normalizedSrc)) {
      const image = new Image();
      image.decoding = "async";
      if (
        typeof window !== "undefined"
        && /^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(normalizedSrc)
        && new URL(normalizedSrc, window.location.href).origin !== window.location.origin
      ) {
        image.crossOrigin = "anonymous";
      }
      image.src = normalizedSrc;
      assetCache.set(normalizedSrc, image);
  }
  return assetCache.get(normalizedSrc);
}

function normalizeAssetSrc(src: any) {
  if (isDefaultFieldLogoSrc(src)) {
    return "";
  }
  const normalized = String(src || "").replace(/^\/+/, "");
  if (normalized.startsWith("assets/") || normalized.startsWith("collection/")) {
    // Return an absolute URL, never a page-relative one. On the /game/<id> route
    // a bare "assets/..." would resolve against the page path (-> /game/assets/...,
    // which the SPA catch-all serves as HTML). With an asset base (prod) point at
    // the CDN; without one (localhost) make it root-absolute so it hits the
    // server's remote-asset proxy (which forwards /assets and /collection).
    return assetBaseUrl ? `${assetBaseUrl}/${normalized}` : `/${normalized}`;
  }
  if (!normalized || /^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(normalized) || normalized.startsWith("data:") || normalized.startsWith("blob:")) {
    return normalized;
  }
  return assetBaseUrl ? `${assetBaseUrl}/${normalized}` : normalized;
}

function ensureImageAssetLoaded(src: any): Promise<void> {
  const normalizedSrc = normalizeAssetSrc(src);
  if (!normalizedSrc || typeof window === "undefined") {
    return Promise.resolve();
  }

  const image = loadImageAsset(normalizedSrc);
  if (!image) {
    return Promise.resolve();
  }
  if (imageReady(image)) {
    return Promise.resolve();
  }

  if (image.complete && !imageReady(image)) {
    assetCache.delete(normalizedSrc);
    return Promise.reject(new Error(`Unable to load image asset ${normalizedSrc}`));
  }

  const waitForLoad = new Promise<void>((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      settle(() => reject(new Error(`Timed out loading image asset ${normalizedSrc}`)));
    }, 12000);
    const settle = (finish: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeout);
      image.removeEventListener("load", onLoad);
      image.removeEventListener("error", onError);
      finish();
    };
    const onLoad = () => settle(resolve);
    const onError = () => {
      assetCache.delete(normalizedSrc);
      settle(() => reject(new Error(`Unable to load image asset ${normalizedSrc}`)));
    };
    image.addEventListener("load", onLoad, { once: true });
    image.addEventListener("error", onError, { once: true });

    if (imageReady(image)) {
      settle(resolve);
    } else if (image.complete) {
      onError();
    }
  });

  return waitForLoad.then(() => {
    if (!imageReady(image)) {
      throw new Error(`Image loaded without dimensions: ${normalizedSrc}`);
    }
    if (typeof image.decode !== "function" || /\.svg(?:$|\?)/i.test(normalizedSrc)) {
      return;
    }
    return image.decode().catch((error: any) => {
      if (imageReady(image)) {
        return;
      }
      throw new Error(`Unable to decode image asset ${normalizedSrc}: ${error?.message || error}`);
    });
  });
}

export function collectGameAssetSources(game: any, options: { mobileLite?: boolean } = {}) {
  const mobileLite = options.mobileLite ?? isMobilePerformanceMode();
  const sources = new Map<string, { src: string; critical: boolean }>();
  const add = (src: any, critical = false) => {
    if (isDefaultFieldLogoSrc(src)) {
      return;
    }
    const normalized = normalizeAssetSrc(src);
    if (normalized) {
      sources.set(normalized, {
        src: normalized,
        critical: critical || sources.get(normalized)?.critical || false,
      });
    }
  };

  if (Array.isArray(visualConfig.spriteSheets.players.layers)) {
    visualConfig.spriteSheets.players.layers.forEach((layer: any) => add(layer.src, true));
    PLAYER_SPRITE_PREWARM_ROLES
      .map((role) => spriteLayerVariantsForRole(role))
      .filter(Boolean)
      .forEach((variants: any) => {
        Object.entries(variants).forEach(([layerId, variantIndex]) => {
          add(playerLayerVariantAssetPath(layerId, Number(variantIndex)), false);
        });
      });
    [game?.home, game?.away].forEach((team) => {
      (team?.roster || []).forEach((player: any) => {
        const nftAppearance = renderableNftAppearance(player, mobileLite);
        if (nftAppearance) {
          nftPlayerLayerConfigs(nftAppearance).forEach((layer: any) => add(layer.src, false));
          if (nftAppearance.aura) {
            add(nftAuraAssetPath(nftAppearance.species, nftAppearance.aura), false);
          }
        }
        const variants = spriteLayerVariantsForPlayer(player);
        if (!variants) {
          return;
        }
        Object.entries(variants).forEach(([layerId, variantIndex]) => {
          add(playerLayerVariantAssetPath(layerId, Number(variantIndex)), false);
        });
      });
    });
    add(visualConfig.spriteSheets.players.fallbackSrc, false);
  } else {
    add(visualConfig.spriteSheets.players.src, true);
  }
  add(visualConfig.spriteSheets.football.src, true);
  add(fieldBackgroundAsset(homeFieldVisuals(game?.home).fieldBackground.texture));
  add(GRASS_TEXTURE_ASSET);
  add(GOAL_POST_ASSET);
  add(GOAL_BASE_ASSET);
  [game?.home, game?.away].forEach((team) => {
    if (team) {
      add(homeFieldVisuals(team).logo.src);
    }
  });

  return Array.from(sources.values());
}

export function collectAdditionalGameAssetSources(
  currentGame: any,
  nextGame: any,
  options: { mobileLite?: boolean } = {},
) {
  const loadedSources = new Set(
    collectGameAssetSources(currentGame, options).map((asset) => asset.src),
  );
  return collectGameAssetSources(nextGame, options).filter((asset) => !loadedSources.has(asset.src));
}

export function mergeScheduledGameTeams(currentGame: any, nextGame: any): boolean {
  if (!currentGame || !nextGame) {
    return false;
  }
  let changed = false;
  (["home", "away"] as const).forEach((side) => {
    const nextTeam = nextGame[side];
    if (!nextTeam) {
      return;
    }
    const currentTeam = currentGame[side];
    changed = true;
    currentGame[side] = {
      ...currentTeam,
      ...nextTeam,
      roster: nextTeam.roster || currentTeam?.roster || [],
    };
  });
  return changed;
}

async function preloadAdditionalGameAssets(currentGame: any, nextGame: any) {
  const assets = collectAdditionalGameAssetSources(currentGame, nextGame);
  if (!assets.length) {
    return;
  }
  const queue = [...assets].sort((left, right) => Number(right.critical) - Number(left.critical));
  let nextAssetIndex = 0;
  const worker = async () => {
    while (nextAssetIndex < queue.length) {
      const asset = queue[nextAssetIndex];
      nextAssetIndex += 1;
      try {
        await ensureImageAssetLoaded(asset.src);
      } catch (error) {
        console.warn("Unable to preload halftime player artwork", asset.src, error);
      }
    }
  };
  const preloadConcurrency = isMobilePerformanceMode() ? 4 : 8;
  await Promise.all(Array.from({ length: Math.min(preloadConcurrency, queue.length) }, worker));
  prewarmPlayerSpriteSheets(nextGame);
}

async function preloadGameAssets(game: any, requestId: number) {
  const assets = collectGameAssetSources(game);
  if (!assets.length) {
    return;
  }
  const startedAt = performance.now();
  state.loadingStatus = "Preparing the field and players.";
  renderStageLoading();
  const queue = [...assets].sort((left, right) => Number(right.critical) - Number(left.critical));
  let nextAssetIndex = 0;
  const worker = async () => {
    while (nextAssetIndex < queue.length) {
      if (!isCurrentLoadingRequest(requestId)) {
        throw new Error("Asset loading was cancelled.");
      }
      const asset = queue[nextAssetIndex];
      nextAssetIndex += 1;
      try {
        await ensureImageAssetLoaded(asset.src);
      } catch (error) {
        if (asset.critical) {
          throw error;
        }
        console.warn("Skipping optional simulator artwork", asset.src, error);
      }
    }
  };
  const preloadConcurrency = isMobilePerformanceMode() ? 4 : 8;
  await Promise.all(Array.from({ length: Math.min(preloadConcurrency, queue.length) }, worker));
  prewarmPlayerSpriteSheets(game);
  rememberLoadDuration("assets", performance.now() - startedAt);
  if (!isCurrentLoadingRequest(requestId)) {
    throw new Error("Asset loading was cancelled.");
  }
}

function prewarmPlayerSpriteSheets(game: any) {
  [
    { team: game?.home, side: "home" },
    { team: game?.away, side: "away" },
  ].forEach(({ team, side }) => {
    if (!team) {
      return;
    }
    if (Array.isArray(visualConfig.spriteSheets.players.layers)) {
      composeLayeredPlayerSheet(visualConfig.spriteSheets.players, teamUniformPalette(team, side, "generic"));
    } else {
      recolorMaskSheet(visualConfig.spriteSheets.players, teamUniformPalette(team, side, "generic"));
    }
  });
  prewarmPlayerSpriteFrames(game);
}

function prewarmPlayerSpriteFrames(game: any) {
  const sheetConfig = visualConfig.spriteSheets.players;
  const frameVariants = Object.entries(sheetConfig?.variants || {});
  if (!frameVariants.length) {
    return;
  }

  [
    { team: game?.home, side: "home" },
    { team: game?.away, side: "away" },
  ].forEach(({ team, side }) => {
    if (!team) {
      return;
    }
    PLAYER_SPRITE_PREWARM_ROLES.forEach((role) => {
      const palette = teamUniformPalette(team, side, String(role).toLowerCase());
      const image = sheetConfig?.mode === "layered"
        ? composeLayeredPlayerSheet(sheetConfig, palette) || recolorMaskSheet(sheetConfig, palette)
        : recolorMaskSheet(sheetConfig, palette);
      if (!image) {
        return;
      }
      frameVariants.forEach(([variantName, variant]: [string, any]) => {
        renderCachedSpriteFrame(
          image,
          sheetConfig,
          variant,
          variantName,
          palette,
          PLAYER_SPRITE_DRAW_SIZE,
          PLAYER_SPRITE_DRAW_SIZE,
          { flipX: false },
        );
        renderCachedSpriteFrame(
          image,
          sheetConfig,
          variant,
          variantName,
          palette,
          PLAYER_SPRITE_DRAW_SIZE,
          PLAYER_SPRITE_DRAW_SIZE,
          { flipX: true },
        );
      });
    });
    (team?.roster || []).forEach((player: any) => {
      const nftAppearance = normalizedNftAppearance(player);
      if (nftAppearance) {
        if (nftAppearance.aura) {
          // Kick off the async GIF decode so the aura animates from the snap.
          ensureNftAuraAnimation(nftAuraAssetPath(nftAppearance.species, nftAppearance.aura));
        }
        const nftPalette = {
          ...withSpriteLayerVariants(teamUniformPalette(team, side, String(player?.position || "generic").toLowerCase()), null),
          nftAppearance,
        };
        const nftConfig = nftSheetConfigForPalette(sheetConfig, nftPalette);
        const nftImage = nftConfig ? composeLayeredPlayerSheet(nftConfig, nftPalette) : null;
        if (nftImage) {
          frameVariants.forEach(([variantName, variant]: [string, any]) => {
            renderCachedSpriteFrame(nftImage, nftConfig, variant, variantName, nftPalette, PLAYER_SPRITE_DRAW_SIZE, PLAYER_SPRITE_DRAW_SIZE, { flipX: false });
            renderCachedSpriteFrame(nftImage, nftConfig, variant, variantName, nftPalette, PLAYER_SPRITE_DRAW_SIZE, PLAYER_SPRITE_DRAW_SIZE, { flipX: true });
          });
        }
      }
      const variants = spriteLayerVariantsForPlayer(player);
      if (!variants) {
        return;
      }
      const palette = withSpriteLayerVariants(teamUniformPalette(team, side, String(player?.position || "generic").toLowerCase()), variants);
      const image = sheetConfig?.mode === "layered"
        ? composeLayeredPlayerSheet(sheetConfig, palette) || recolorMaskSheet(sheetConfig, palette)
        : recolorMaskSheet(sheetConfig, palette);
      if (!image) {
        return;
      }
      frameVariants.forEach(([variantName, variant]: [string, any]) => {
        renderCachedSpriteFrame(
          image,
          sheetConfig,
          variant,
          variantName,
          palette,
          PLAYER_SPRITE_DRAW_SIZE,
          PLAYER_SPRITE_DRAW_SIZE,
          { flipX: false },
        );
        renderCachedSpriteFrame(
          image,
          sheetConfig,
          variant,
          variantName,
          palette,
          PLAYER_SPRITE_DRAW_SIZE,
          PLAYER_SPRITE_DRAW_SIZE,
          { flipX: true },
        );
      });
    });
  });
}

  function imageReady(image: any) {
    return imageAssetReady(image);
  }

  function imageAssetLoadFailed(src: any) {
    const image = loadImageAsset(src);
    return Boolean(image && image.complete && !imageReady(image));
  }


function makeCanvas(width: any, height: any) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function makePaletteCacheKey(sheetConfig: any, palette: any) {
  const layerVariants = palette?.spriteLayerVariants || palette?.layerVariants || {};
  const variantKey = Object.keys(layerVariants)
    .sort()
    .map((key) => `${key}:${layerVariants[key]}`)
    .join(",");
  return [
    sheetConfig.src || sheetConfig.fallbackSrc,
    sheetConfig.mode || "mask",
    variantKey ? `variants:${variantKey}` : "variants:base",
    ...Object.keys(palette)
      .filter((key) => key !== "spriteLayerVariants" && key !== "layerVariants")
      .filter((key) => typeof palette[key] === "string" || typeof palette[key] === "number")
      .sort()
      .map((key) => `${key}:${normalizeHex(palette[key])}`),
  ].join("|");
}

function spriteFrameCacheKey(sheetConfig: any, variantName: any, palette: any, dw: any, dh: any, options: any = {}) {
  return [
    makePaletteCacheKey(sheetConfig, palette),
    variantName,
    options.flipX ? "flip" : "normal",
    Math.round(Number(dw) || 0),
    Math.round(Number(dh) || 0),
  ].join("|");
}

function renderCachedSpriteFrame(
  image: any,
  sheetConfig: any,
  variant: any,
  variantName: any,
  palette: any,
  dw: any,
  dh: any,
  options: any = {},
) {
  const width = Math.max(1, Math.round(Number(dw) || sheetConfig.frameWidth || 1));
  const height = Math.max(1, Math.round(Number(dh) || sheetConfig.frameHeight || 1));
  const cacheKey = spriteFrameCacheKey(sheetConfig, variantName, palette, width, height, options);
  if (spriteFrameCache.has(cacheKey)) {
    return spriteFrameCache.get(cacheKey);
  }

  const canvas = makeCanvas(width, height);
  const spriteCtx = canvas.getContext("2d")!;
  spriteCtx.imageSmoothingEnabled = false;
  if (options.flipX) {
    spriteCtx.translate(width, 0);
    spriteCtx.scale(-1, 1);
  }
  spriteCtx.drawImage(
    image,
    variant.x * sheetConfig.frameWidth,
    variant.y * sheetConfig.frameHeight,
    sheetConfig.frameWidth,
    sheetConfig.frameHeight,
    0,
    0,
    width,
    height
  );
  spriteFrameCache.set(cacheKey, canvas);
  return canvas;
}


function recolorMaskSheet(sheetConfig: any, palette: any) {
  const image = loadImageAsset(sheetConfig?.fallbackSrc || sheetConfig?.src);
  if (!imageReady(image) || !sheetConfig?.colorRegions) {
    return null;
  }

  const cacheKey = makePaletteCacheKey(sheetConfig, palette);
  if (recolorCache.has(cacheKey)) {
    return recolorCache.get(cacheKey);
  }

  const canvas = makeCanvas(image.naturalWidth, image.naturalHeight);
  const maskCtx = canvas.getContext("2d", { willReadFrequently: true })!;
  maskCtx.imageSmoothingEnabled = false;
  maskCtx.drawImage(image, 0, 0);

  let imageData;
  try {
    imageData = maskCtx.getImageData(0, 0, canvas.width, canvas.height);
  } catch {
    return null;
  }
  const data = imageData.data;
  const regionLookup = new Map();

  Object.entries(sheetConfig.colorRegions).forEach(([regionName, sourceHex]: [string, any]) => {
    const sourceRgb = hexToRgb(sourceHex);
    const targetRgb = hexToRgb(palette[regionName] || sourceHex);
    regionLookup.set(`${sourceRgb.r},${sourceRgb.g},${sourceRgb.b}`, targetRgb);
  });

  for (let index = 0; index < data.length; index += 4) {
    if (data[index + 3] === 0) {
      continue;
    }
    const replacement = regionLookup.get(`${data[index]},${data[index + 1]},${data[index + 2]}`);
    if (!replacement) {
      continue;
    }
    data[index] = replacement.r;
    data[index + 1] = replacement.g;
    data[index + 2] = replacement.b;
  }

  maskCtx.putImageData(imageData, 0, 0);
  recolorCache.set(cacheKey, canvas);
  return canvas;
}

function clampByte(value: any) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function isPlayerLayerDetailPixel(r: number, g: number, b: number) {
  const hsl = rgbToHsl(r, g, b);
  return Math.max(r, g, b) < 16 || hsl.s < 10;
}

function isPlayerLayerWhitePixel(r: number, g: number, b: number) {
  const minChannel = Math.min(r, g, b);
  const maxChannel = Math.max(r, g, b);
  return minChannel >= 215 || (minChannel >= 180 && maxChannel - minChannel <= 28);
}

function averageTintableLayerHsl(data: Uint8ClampedArray) {
  let red = 0;
  let green = 0;
  let blue = 0;
  let count = 0;

  for (let index = 0; index < data.length; index += 4) {
    if (data[index + 3] === 0 || isPlayerLayerDetailPixel(data[index], data[index + 1], data[index + 2])) {
      continue;
    }
    red += data[index];
    green += data[index + 1];
    blue += data[index + 2];
    count += 1;
  }

  if (!count) {
    return null;
  }

  return rgbToHsl(red / count, green / count, blue / count);
}

function tintLayerSheet(layerConfig: any, targetHex: any) {
  let image = loadImageAsset(layerConfig?.src);
  if (!imageReady(image) && layerConfig?.fallbackSrc) {
    image = loadImageAsset(layerConfig.fallbackSrc);
  }
  if (
    imageReady(image)
    && (layerConfig?.noTint || ((layerConfig?.id === "skin" || layerConfig?.id === "hair") && !layerConfig?.forceTint))
  ) {
    return image;
  }
  if (!imageReady(image) || !targetHex) {
    return null;
  }

  const cacheKey = `${image?.src || layerConfig.src}|${targetHex}`;
  if (recolorCache.has(cacheKey)) {
    return recolorCache.get(cacheKey);
  }

  const canvas = makeCanvas(image.naturalWidth, image.naturalHeight);
  const layerCtx = canvas.getContext("2d", { willReadFrequently: true })!;
  layerCtx.imageSmoothingEnabled = false;
  layerCtx.drawImage(image, 0, 0);

  let imageData;
  try {
    imageData = layerCtx.getImageData(0, 0, canvas.width, canvas.height);
  } catch {
    return null;
  }
  const data = imageData.data;
  const sourceHsl = averageTintableLayerHsl(data);
  if (!sourceHsl) {
    recolorCache.set(cacheKey, canvas);
    return canvas;
  }
  const target = hexToRgb(targetHex);
  const targetHsl = rgbToHsl(target.r, target.g, target.b);
  const hueShift = targetHsl.h - sourceHsl.h;
  const chromaShift = targetHsl.s - sourceHsl.s;
  const lightnessShift = targetHsl.l - sourceHsl.l;

  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3];
    if (alpha === 0) {
      continue;
    }

    if (isPlayerLayerWhitePixel(data[index], data[index + 1], data[index + 2])) {
      data[index] = 255;
      data[index + 1] = 255;
      data[index + 2] = 255;
      data[index + 3] = alpha;
      continue;
    }

    if (isPlayerLayerDetailPixel(data[index], data[index + 1], data[index + 2])) {
      continue;
    }

    const sourcePixelHsl = rgbToHsl(data[index], data[index + 1], data[index + 2]);
    const shifted = hslToRgb(
      sourcePixelHsl.h + hueShift,
      sourcePixelHsl.s + chromaShift,
      sourcePixelHsl.l + lightnessShift,
    );
    data[index] = clampByte(shifted.r);
    data[index + 1] = clampByte(shifted.g);
    data[index + 2] = clampByte(shifted.b);
    data[index + 3] = alpha;
  }

  layerCtx.putImageData(imageData, 0, 0);
  recolorCache.set(cacheKey, canvas);
  return canvas;
}

export function resolveLayerConfigForPalette(layer: any, palette: any) {
  const layerId = String(layer?.id || "");
  if (layerId === "skin" && palette?.skinLayerOverride) {
    return {
      ...layer,
      forceTint: true,
    };
  }
  if ((layerId === "skin" || layerId === "hair") && palette?.[layerId] && !layer?.noTint) {
    return {
      ...layer,
      forceTint: true,
    };
  }
  const layerVariants = palette?.spriteLayerVariants || palette?.layerVariants || {};
  const variantIndex = layerVariants[layerId];
  if (!PLAYER_LAYER_VARIANT_IDS.has(layerId) || typeof variantIndex !== "number") {
    return layer;
  }
  return {
    ...layer,
    src: playerLayerVariantAssetPath(layerId, variantIndex),
    fallbackSrc: layer.src,
  };
}

function composeLayeredPlayerSheet(sheetConfig: any, palette: any) {
  const layers = Array.isArray(sheetConfig?.layers) ? sheetConfig.layers : [];
  if (!layers.length) {
    return null;
  }

  const cacheKey = makePaletteCacheKey(sheetConfig, palette);
  if (recolorCache.has(cacheKey)) {
    return recolorCache.get(cacheKey);
  }

  let width = 0;
  let height = 0;
  const tintedLayers: any[] = [];
  for (const layer of layers) {
    const resolvedLayer = resolveLayerConfigForPalette(layer, palette);
    const tint = tintLayerSheet(
      resolvedLayer,
      layer.id === "skin" && palette.skinLayerOverride
        ? palette.skinLayerOverride
        : palette[layer.maskTintKey || layer.id],
    );
    if (!tint) {
      // An optional layer whose art definitively failed to load (e.g. a
      // species/trait combination with no hosted file) is dropped; anything
      // else missing means the sheet is not composable yet.
      if (layer.optional && imageAssetLoadFailed(layer.src)) {
        continue;
      }
      return null;
    }
    width = Math.max(width, tint.width || 0);
    height = Math.max(height, tint.height || 0);
    tintedLayers.push(tint);
  }

  if (!width || !height) {
    return null;
  }

  const canvas = makeCanvas(width, height);
  const layerCtx = canvas.getContext("2d")!;
  layerCtx.imageSmoothingEnabled = false;
  tintedLayers.forEach((layer) => {
    layerCtx.drawImage(layer, 0, 0);
  });
  recolorCache.set(cacheKey, canvas);
  return canvas;
}

function shiftedImageAsset(src: any, shift: any) {
  const image = loadImageAsset(src);
  if (!imageReady(image)) {
    return null;
  }
  if (isNeutralColorShift(shift)) {
    return image;
  }

  const cacheKey = [src, "field-shift", shift.hueShift || 0, shift.chromaShift || 0, shift.lightnessShift || 0].join("|");
  if (recolorCache.has(cacheKey)) {
    return recolorCache.get(cacheKey);
  }

  const canvas = makeCanvas(image.naturalWidth, image.naturalHeight);
  const imageCtx = canvas.getContext("2d", { willReadFrequently: true })!;
  imageCtx.imageSmoothingEnabled = false;
  imageCtx.drawImage(image, 0, 0);
  let imageData;
  try {
    imageData = imageCtx.getImageData(0, 0, canvas.width, canvas.height);
  } catch {
    return null;
  }
  const data = imageData.data;

  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3];
    if (alpha === 0) {
      continue;
    }

    const shifted = shiftRgbColor(
      { r: data[index], g: data[index + 1], b: data[index + 2] },
      shift,
    );
    data[index] = shifted.r;
    data[index + 1] = shifted.g;
    data[index + 2] = shifted.b;
  }

  imageCtx.putImageData(imageData, 0, 0);
  recolorCache.set(cacheKey, canvas);
  return canvas;
}

function drawMaskSpriteFrame(sheetConfig: any, variantName: any, palette: any, dx: any, dy: any, dw: any, dh: any, options: any = {}) {
  const variant = sheetConfig?.variants?.[variantName];
  if (!sheetConfig || !variant) {
    return false;
  }

  const nftSheetConfig = nftSheetConfigForPalette(sheetConfig, palette);
  const nftImage = nftSheetConfig ? composeLayeredPlayerSheet(nftSheetConfig, palette) : null;
  const image = nftImage
    || (sheetConfig?.mode === "layered"
      ? composeLayeredPlayerSheet(sheetConfig, palette) || recolorMaskSheet(sheetConfig, palette)
      : recolorMaskSheet(sheetConfig, palette));
  if (!image) {
    return false;
  }
  const frameConfig = nftImage ? nftSheetConfig : sheetConfig;

  if (options.cacheFrame) {
    const cachedFrame = renderCachedSpriteFrame(image, frameConfig, variant, variantName, palette, dw, dh, options);
    ctx.save();
    if (options.alpha != null) {
      ctx.globalAlpha = options.alpha;
    }
    ctx.drawImage(cachedFrame, dx, dy);
    ctx.restore();
    return true;
  }

  ctx.save();
  if (options.alpha != null) {
    ctx.globalAlpha = options.alpha;
  }
  if (options.flipX) {
    ctx.translate(dx + dw, dy);
    ctx.scale(-1, 1);
    dx = 0;
    dy = 0;
  }
  ctx.drawImage(
    image,
    variant.x * sheetConfig.frameWidth,
    variant.y * sheetConfig.frameHeight,
    sheetConfig.frameWidth,
    sheetConfig.frameHeight,
    dx,
    dy,
    dw,
    dh
  );
  ctx.restore();
  return true;
}

export function teamUniformPalette(team: any, side: any = "home", playerRole: any = "generic") {
  const colors = team?.colors || {};
  const defaults = visualConfig.defaults.playerUniform;
  const baseSprite = {
    ...defaultPlayerSpritePalette(colors),
    ...(team?.visuals?.playerSprite || {}),
  };
  const visualOverrides = team?.visuals?.uniforms?.[side] || {};
  const roleOverrides = team?.visuals?.playerRoles?.[playerRole] || {};
  const helmet = roleOverrides.helmet || visualOverrides.helmet || baseSprite.helmet || defaults.helmet;
  const jersey = roleOverrides.jersey || visualOverrides.jersey || baseSprite.jersey || colors.primary || defaults.jersey;
  const pants = roleOverrides.pants || visualOverrides.pants || baseSprite.pants || colors.secondary || defaults.pants;
  const accent = roleOverrides.accent || roleOverrides.trim || visualOverrides.accent || visualOverrides.trim || baseSprite.accent || colors.accent || defaults.accent || defaults.trim;
  const gloves = roleOverrides.gloves || visualOverrides.gloves || roleOverrides.accessories || visualOverrides.accessories || baseSprite.gloves || accent || defaults.gloves;
  const shoes = roleOverrides.shoes || visualOverrides.shoes || roleOverrides.accessories || visualOverrides.accessories || baseSprite.shoes || defaults.shoes;
  const spriteLayerVariants = roleOverrides.spriteLayerVariants || roleOverrides.layerVariants || spriteLayerVariantsForRole(playerRole);
  const skinLayerOverride = skinLayerOverrideForVariants(spriteLayerVariants, helmet);
  return {
    ...defaults,
    ...baseSprite,
    outline: roleOverrides.outline || visualOverrides.outline || defaults.outline,
    shadow: roleOverrides.shadow || visualOverrides.shadow || defaults.shadow,
    skin: roleOverrides.skin || visualOverrides.skin || defaults.skin,
    helmet,
    jersey,
    pants,
    accent,
    gloves,
    shoes,
    ...(spriteLayerVariants ? { spriteLayerVariants } : {}),
    ...(skinLayerOverride ? { skinLayerOverride } : {}),
    trim: accent,
    accessories: shoes,
  };
}

function rosterPositionForRole(role: any) {
  if (!role) {
    return null;
  }
  if (role === "QB") return "QB";
  if (role === "RB") return "RB";
  if (role === "FB") return "FB";
  if (role === "TE") return "TE";
  if (role === "C") return "C";
  if (role === "LT" || role === "RT") return "OT";
  if (role === "LG" || role === "RG") return "OG";
  if (role.startsWith("WR") || role === "SLOT") return "WR";
  if (role.startsWith("DE")) return "DE";
  if (role.startsWith("DT")) return "DT";
  if (role.startsWith("LB")) return "LB";
  if (role.startsWith("CB")) return "CB";
  if (role.startsWith("S")) return "S";
  if (role.startsWith("RETURNER")) return "WR";
  if (role === "KICKER") return "K";
  if (role.startsWith("COVER") || role.startsWith("FRONT") || role.startsWith("WALL")) return null;
  return null;
}

function rosterPlayerForFieldRole(team: any, role: any) {
  const position = rosterPositionForRole(role);
  if (!position) {
    return null;
  }
  return rosterTopPlayers(team, position, 1)[0] || null;
}

function findPlayerById(team: any, playerId: any) {
  if (!team || !playerId) {
    return null;
  }
  return (team.roster || []).find((player: any) => player.id === playerId) || null;
}

const REALISTIC_HAIR_COLORS = [
  "#17100c",
  "#2c1a12",
  "#4a2b18",
  "#6a3c1f",
  "#8a5a2f",
  "#b47b43",
  "#d8b16a",
  "#7a4b33",
  "#c8c2b8",
];

function stableIndexFromText(value: any, length: number) {
  const text = String(value || "player");
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash) % Math.max(1, length);
}

function normalizedSpriteLayerVariants(input: any) {
  if (!input || typeof input !== "object") {
    return null;
  }
  const variants: any = {};
  PLAYER_LAYER_VARIANT_IDS.forEach((layerId: any) => {
    const value = Number(input[layerId]);
    if (Number.isFinite(value) && value > 1) {
      variants[layerId] = Math.round(value);
    }
  });
  return Object.keys(variants).length ? variants : null;
}

function explicitSpriteLayerVariantsForPlayer(player: any) {
  const appearance = player?.appearance || {};
  return normalizedSpriteLayerVariants(appearance.spriteLayerVariants || appearance.layerVariants || player?.spriteLayerVariants || player?.layerVariants);
}

function spriteLayerVariantsForPlayer(player: any) {
  void player;
  return null;
}

function withSpriteLayerVariants(palette: any, variants: any) {
  const {
    spriteLayerVariants: _spriteLayerVariants,
    layerVariants: _layerVariants,
    skinLayerOverride: _skinLayerOverride,
    ...basePalette
  } = palette || {};
  const normalizedVariants = normalizedSpriteLayerVariants(variants);
  const skinLayerOverride = skinLayerOverrideForVariants(normalizedVariants, basePalette.helmet);
  return {
    ...basePalette,
    ...(normalizedVariants ? { spriteLayerVariants: normalizedVariants } : {}),
    ...(skinLayerOverride ? { skinLayerOverride } : {}),
  };
}

function defaultHairColorForPlayer(player: any, fallback: string) {
  const key = player?.id || player?.name || player?.number || "player";
  return REALISTIC_HAIR_COLORS[stableIndexFromText(key, REALISTIC_HAIR_COLORS.length)] || fallback;
}

function playerVisualPalette(team: any, side: any, fieldPlayer: any) {
  const rosterPlayer = findPlayerById(team, fieldPlayer?.playerId) || rosterPlayerForFieldRole(team, fieldPlayer?.role);
  const roleKey = String(fieldPlayer?.role || "generic").toLowerCase();
  const basePalette = teamUniformPalette(team, side, roleKey);
  const playerForAppearance = rosterPlayer || fieldPlayer || null;
  const hasPlayerIdentity = !!(rosterPlayer || fieldPlayer?.playerId || fieldPlayer?.name);
  const visualPalette = hasPlayerIdentity
    ? withSpriteLayerVariants(basePalette, spriteLayerVariantsForPlayer(playerForAppearance))
    : basePalette;
  const mobileLite = isMobilePerformanceMode();
  const appearance = fieldPlayer?.appearance || rosterPlayer?.appearance || {};
  const nftAppearance = renderableNftAppearance({ appearance }, mobileLite);
  if (nftAppearance) {
    // NFT species art replaces the whole layered body, so per-player skin/hair
    // colors and v2 layer variants don't apply — strip the variant keys so the
    // jersey layer of the NFT sheet can't be remapped to v2 variant art.
    return {
      ...withSpriteLayerVariants(visualPalette, null),
      nftAppearance,
    };
  }
  return {
    ...visualPalette,
    skin: fieldPlayer?.skinColor || appearance.skinColor || rosterPlayer?.skinColor || visualPalette.skin,
    hair: fieldPlayer?.hairColor || appearance.hairColor || rosterPlayer?.hairColor || defaultHairColorForPlayer(playerForAppearance, visualPalette.hair),
  };
}

function footballVisualPalette(play: any, emphasis: "normal" | "tracked" = "normal") {
  const defaults = visualConfig.defaults.footballPalette;
  const cyberBoost = play?.result === "touchdown" || play?.result === "turnover";
  const trackedBall = emphasis === "tracked";
  return {
    ...defaults,
    outline: trackedBall ? "#07131f" : defaults.outline,
    laces: trackedBall ? "#ffffff" : defaults.laces,
    glow: cyberBoost ? "#8af5ff" : trackedBall ? "#f8fdff" : defaults.glow,
  };
}


  function drawMidfieldLogo(cameraX = 0) {
    const mobileLite = isMobilePerformanceMode();
    const fieldVisuals = homeFieldVisuals();
    const config = {
      ...visualConfig.midfieldLogo,
      src: fieldVisuals.logo.src || visualConfig.midfieldLogo.src,
      glow: rgbaFromHex(shiftHexColor("#6fbeff", fieldVisuals.logo), 0.24),
    };
    if (!config?.enabled) {
      return;
    }

    const metrics = fieldMetrics();
    const centerX = worldToScreenX(yardToX(50), cameraX);
    const centerY = metrics.centerY;
    const hasHomeLogo = !!config.src && !isDefaultFieldLogoSrc(config.src);
    const image = hasHomeLogo ? shiftedImageAsset(config.src, fieldVisuals.logo) : null;

    ctx.save();
    ctx.shadowBlur = mobileLite ? 0 : 22;
    ctx.shadowColor = config.glow;
    if (imageReady(image)) {
      ctx.globalAlpha = mobileLite ? Math.min(config.alpha, 0.18) : config.alpha;
      ctx.drawImage(image, centerX - config.width / 2, centerY - config.height / 2, config.width, config.height);
    } else if (hasHomeLogo) {
      ctx.restore();
      return;
    } else {
      ctx.globalAlpha = 0.14;
      ctx.strokeStyle = shiftHexColor("#59d9ff", fieldVisuals.logo);
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 58, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(centerX, centerY, 28, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(centerX - 72, centerY);
      ctx.lineTo(centerX + 72, centerY);
      ctx.moveTo(centerX, centerY - 72);
      ctx.lineTo(centerX, centerY + 72);
      ctx.stroke();
    }
    ctx.restore();
  }

  const LOAD_TIMING_STORAGE_KEY = "dfl-sim-load-timings-v1";
  const LOAD_TIMING_DEFAULTS: Record<string, number> = {
    bootstrap: 650,
    simulate: 3200,
    "visual-test": 1600,
    assets: 900,
  };

  function readLoadTimingProfile() {
    if (typeof localStorage === "undefined") {
      return {};
    }
    try {
      const raw = localStorage.getItem(LOAD_TIMING_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  function writeLoadTimingProfile(profile: any) {
    if (typeof localStorage === "undefined") {
      return;
    }
    try {
      localStorage.setItem(LOAD_TIMING_STORAGE_KEY, JSON.stringify(profile));
    } catch {
      // Ignore storage failures and fall back to built-in estimates.
    }
  }

  function estimatedLoadDurationMs(key: string) {
    const profile = readLoadTimingProfile();
    const sample = profile?.[key];
    const fallback = LOAD_TIMING_DEFAULTS[key] ?? 2200;
    if (!sample || !Number.isFinite(sample.avgMs)) {
      return fallback;
    }
    return Math.round(clampRange(sample.avgMs, 250, 30000));
  }

  function rememberLoadDuration(key: string, durationMs: number) {
    if (!Number.isFinite(durationMs)) {
      return;
    }
    const safeDuration = Math.round(clampRange(durationMs, 120, 30000));
    const profile = readLoadTimingProfile();
    const previous = profile?.[key];
    const samples = Math.max(0, Math.min(40, Number(previous?.samples) || 0));
    const blendedAverage = samples > 0
      ? Math.round((Number(previous?.avgMs) || safeDuration) * 0.72 + safeDuration * 0.28)
      : safeDuration;
    profile[key] = {
      avgMs: blendedAverage,
      samples: Math.min(40, samples + 1),
    };
    writeLoadTimingProfile(profile);
  }

  export function loadingProgressFromElapsed(elapsedMs: number, estimateMs: number) {
    const safeElapsed = Math.max(0, Number(elapsedMs) || 0);
    const safeEstimate = Math.max(400, Number(estimateMs) || 1);
    const baseProgress = Math.min(1, safeElapsed / safeEstimate);
    const overtimeMs = Math.max(0, safeElapsed - safeEstimate);
    const overtimeProgress = (1 - Math.exp(-overtimeMs / Math.max(260, safeEstimate * 0.45))) * 0.03;
    return Math.min(0.985, 0.04 + baseProgress * 0.915 + overtimeProgress);
  }

  export function loadingEtaLabel(remainingMs: number) {
    if (!Number.isFinite(remainingMs)) {
      return "Estimating load time...";
    }
    if (remainingMs <= 350) {
      return "Finalizing simulation...";
    }
    if (remainingMs < 1000) {
      return `About ${(remainingMs / 1000).toFixed(1)}s left`;
    }
    if (remainingMs < 10000) {
      return `About ${(remainingMs / 1000).toFixed(1)}s left`;
    }
    return `About ${Math.round(remainingMs / 1000)}s left`;
  }

  function renderStageLoading(now = performance.now()) {
    if (!refs.stageLoading || !refs.stageLoadingTitle || !refs.stageLoadingStatus) {
      return;
    }

    if (!state.loadingActive) {
      const syncOverlayActive = Number.isFinite(state.liveSyncOverlayUntil)
        && now < Number(state.liveSyncOverlayUntil);
      if (syncOverlayActive) {
        const remainingMs = Math.max(0, Number(state.liveSyncOverlayUntil) - now);
        const progress = 1 - Math.min(1, remainingMs / LIVE_SYNC_INITIAL_OVERLAY_MS);
        const percent = Math.max(8, Math.min(98, Math.round(progress * 100)));
        refs.stageLoading.classList.add("visible", "syncing");
        refs.stageLoading.classList.remove("error");
        refs.stageLoading.setAttribute("aria-busy", "true");
        refs.stageLoadingTitle.textContent = state.liveSyncOverlayTitle;
        refs.stageLoadingStatus.textContent = state.liveSyncOverlayStatus;
        if (refs.stageLoadingFill) refs.stageLoadingFill.style.width = `${percent}%`;
        if (refs.stageLoadingProgress) refs.stageLoadingProgress.textContent = `${percent}%`;
        if (refs.stageLoadingEta) refs.stageLoadingEta.textContent = "Locking to live clock...";
        refs.stageLoading.querySelector(".stage-loading-bar")?.setAttribute("aria-valuenow", String(percent));
        return;
      }
      state.liveSyncOverlayUntil = null;
      refs.stageLoading.classList.remove("visible", "error", "syncing");
      refs.stageLoading.setAttribute("aria-busy", "false");
      return;
    }

    const elapsed = Math.max(0, now - (state.loadingStartedAt ?? now));
    const progress = state.loadingError ? 1 : loadingProgressFromElapsed(elapsed, state.loadingEstimateMs);
    const percent = Math.round(progress * 100);
    const remainingMs = Math.max(0, state.loadingEstimateMs - elapsed);

    state.loadingProgress = progress;
    refs.stageLoading.classList.add("visible");
    refs.stageLoading.classList.remove("syncing");
    refs.stageLoading.classList.toggle("error", state.loadingError);
    refs.stageLoading.setAttribute("aria-busy", state.loadingError ? "false" : "true");
    refs.stageLoadingTitle.textContent = state.loadingTitle;
    refs.stageLoadingStatus.textContent = state.loadingStatus;
    if (refs.stageLoadingFill) refs.stageLoadingFill.style.width = `${percent}%`;
    if (refs.stageLoadingProgress) refs.stageLoadingProgress.textContent = `${percent}%`;
    if (refs.stageLoadingEta) {
      refs.stageLoadingEta.textContent = state.loadingError
        ? "Use the controls above to try another matchup."
        : loadingEtaLabel(remainingMs);
    }
    refs.stageLoading.querySelector(".stage-loading-bar")?.setAttribute("aria-valuenow", String(percent));
  }

  function kickoffLabelFromBroadcast(broadcast: any) {
    const kickoffAt = broadcast?.kickoffAt || state.liveSyncLastBroadcast?.kickoffAt;
    if (!kickoffAt) {
      return "Kickoff soon";
    }

    const date = new Date(kickoffAt);
    if (Number.isNaN(date.getTime())) {
      return "Kickoff soon";
    }

    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: "America/Chicago",
      timeZoneName: "short",
    });
  }

  function isFutureKickoff(kickoffAt: any, nowMs = Date.now()) {
    const kickoffMs = new Date(kickoffAt || "").getTime();
    return Number.isFinite(kickoffMs) && kickoffMs > nowMs;
  }

  function renderPregameOverlay() {
    if (!refs.pregameOverlay) {
      return;
    }

    const broadcast = state.liveSyncLastBroadcast;
    const matchup = state.game || state.pregamePreview;
    const show = !isDebugMode()
      && !!matchup
      && broadcast?.status === "scheduled"
      && isFutureKickoff(broadcast?.kickoffAt);
    refs.pregameOverlay.classList.toggle("visible", show);
    if (!show) {
      return;
    }

    const away = matchup.away;
    const home = matchup.home;
    refs.pregameOverlay.innerHTML = `
      <div class="pregame-card">
        <p class="pregame-kicker">Upcoming Broadcast</p>
        <div class="pregame-matchup">
          <div class="pregame-team pregame-team-away">
            <img src="${escapeHtml(broadcastTeamLogoSrc(away))}" alt="" />
            <span>${escapeHtml(away.city || "")}</span>
            <strong>${escapeHtml(away.name || away.displayName || away.abbr)}</strong>
          </div>
          <div class="pregame-center">
            <span>Kickoff</span>
            <strong>${escapeHtml(kickoffLabelFromBroadcast(broadcast))}</strong>
            <small>Live feed opens at game time</small>
          </div>
          <div class="pregame-team pregame-team-home">
            <img src="${escapeHtml(broadcastTeamLogoSrc(home))}" alt="" />
            <span>${escapeHtml(home.city || "")}</span>
            <strong>${escapeHtml(home.name || home.displayName || home.abbr)}</strong>
          </div>
        </div>
      </div>
    `;
  }

  function isCurrentLoadingRequest(requestId: number) {
    return requestId === state.loadingRequestId;
  }

  function beginStageLoading(phases: Array<{ key: string; title: string; status: string }>) {
    state.loadingFetchController?.abort();
    state.loadingFetchController = null;
    state.loadingRequestId += 1;
    state.loadingError = false;
    state.loadingActive = true;
    state.loadingStartedAt = performance.now();
    state.loadingPhases = phases.map((phase) => ({
      ...phase,
      estimateMs: estimatedLoadDurationMs(phase.key),
    }));
    state.loadingPhaseIndex = 0;
    state.loadingEstimateMs = Math.max(400, state.loadingPhases.reduce((total, phase) => total + phase.estimateMs, 0));
    state.loadingTitle = state.loadingPhases[0]?.title || "Loading matchup...";
    state.loadingStatus = state.loadingPhases[0]?.status || "Preparing simulator...";
    state.loadingProgress = 0;
    renderStageLoading();
    return state.loadingRequestId;
  }

  function setStageLoadingPhase(requestId: number, phaseIndex: number) {
    if (!isCurrentLoadingRequest(requestId)) {
      return;
    }
    const phase = state.loadingPhases[phaseIndex];
    if (!phase) {
      return;
    }
    state.loadingPhaseIndex = phaseIndex;
    state.loadingTitle = phase.title;
    state.loadingStatus = phase.status;
    renderStageLoading();
  }

  function setStageLoadingPhaseByKey(requestId: number, key: string) {
    const phaseIndex = state.loadingPhases.findIndex((phase) => phase.key === key);
    if (phaseIndex >= 0) {
      setStageLoadingPhase(requestId, phaseIndex);
    }
  }

  function createStageLoadingController(requestId: number) {
    if (!isCurrentLoadingRequest(requestId)) {
      return null;
    }
    state.loadingFetchController?.abort();
    const controller = new AbortController();
    state.loadingFetchController = controller;
    return controller;
  }

  function finishStageLoading(requestId: number) {
    if (!isCurrentLoadingRequest(requestId)) {
      return;
    }
    state.loadingFetchController = null;
    state.loadingError = false;
    state.loadingActive = false;
    state.loadingProgress = 1;
    renderStageLoading();
  }

  function showLiveSyncOverlay(status = "Aligning the broadcast clock and play state.", durationMs = LIVE_SYNC_INITIAL_OVERLAY_MS) {
    state.liveSyncOverlayTitle = "Syncing with live stream...";
    state.liveSyncOverlayStatus = status;
    state.liveSyncOverlayUntil = performance.now() + Math.max(500, durationMs);
    renderStageLoading();
  }

  function clearLiveSyncPresentationState() {
    state.liveSyncOverlayUntil = null;
    state.liveSyncKickoffGraceUntil = null;
  }

  function rememberPregameViewer(gameId: string) {
    if (state.pregameViewerGameId !== gameId) {
      state.pregameViewerGameId = gameId;
      state.pregameKickoffPresented = false;
      state.halftimeKickoffPresented = false;
      state.scheduledPackageGeneratedAt = null;
      state.pregameAuthorityVersion = null;
      state.halftimeAuthorityVersion = null;
    }
  }

  function openingKickoffGraceMs() {
    const openingPlay = state.game?.plays?.[0];
    return kickoffSyncGraceMs(
      openingPlay ? liveAdvanceDurationMs(openingPlay, 0) : 0,
    );
  }

  function beginOpeningKickoffPresentation(now = performance.now()) {
    state.liveSyncKickoffGraceUntil = now + openingKickoffGraceMs();
    if (state.pregameViewerGameId === currentScheduledGameId()) {
      state.pregameKickoffPresented = true;
    }
  }

  function failStageLoading(requestId: number, message: string) {
    if (!isCurrentLoadingRequest(requestId)) {
      return;
    }
    state.loadingFetchController = null;
    state.loadingActive = true;
    state.loadingError = true;
    state.loadingTitle = "Unable to load game";
    state.loadingStatus = message || "Something went wrong while loading the matchup.";
    state.loadingProgress = 1;
    renderStageLoading();
  }

  function isAbortError(error: any) {
    return error?.name === "AbortError";
  }

  async function fetchJson(url: any, options: RequestInit = {}) {
    const response = await fetch(url, options);
    if (!response.ok) {
      const payload = await response.json().catch(() => ({ error: response.statusText }));
      const error: any = new Error(payload.error || `Request failed: ${response.status}`);
      error.status = response.status;
      error.retryable = payload.retryable === true;
      error.retryAfterMs = Number(payload.retryAfterMs || 0);
      error.reference = isErrorReference(payload.reference) ? payload.reference : null;
      throw error;
    }
    return response.json();
  }

  /**
   * Turn a caught error into the message shown to the viewer. 4xx responses
   * already explain themselves and are shown as-is. Anything else is a
   * technical failure: it is posted to the simulator server (which logs it to
   * stdout, streamed to CloudWatch) and the viewer is asked to report the
   * reference in Discord. Idempotent per error object, so the loading overlay
   * and the caption can both call it without double-reporting.
   */
  function reportClientError(context: string, error: any, summary: string): string {
    if (error && typeof error === "object" && typeof error.userMessage === "string") {
      return error.userMessage;
    }
    const status = typeof error?.status === "number" ? error.status : null;
    if (status !== null && status >= 400 && status < 500) {
      return error.message || summary;
    }
    const reference = isErrorReference(error?.reference) ? error.reference : createErrorReference();
    const message = userFacingErrorMessage(summary, reference);
    if (error && typeof error === "object") {
      try {
        error.userMessage = message;
        error.reference = reference;
      } catch {
        // Frozen error objects still get reported; they just cannot cache the message.
      }
    }
    console.error(`[${context}] ${reference}`, error);
    if (typeof fetch === "function") {
      const report = {
        source: "simulator-client",
        reference,
        context,
        error: serializeError(error),
        url: typeof location !== "undefined" ? location.href : undefined,
      };
      try {
        fetch(CLIENT_ERROR_REPORT_PATH, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(report),
          keepalive: true,
        }).catch(() => {});
      } catch {
        // Reporting must never throw into the flow that already failed.
      }
    }
    return message;
  }

  function waitForPregameAuthority(ms: number, signal?: AbortSignal | null) {
    return new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException("The request was aborted.", "AbortError"));
        return;
      }
      const timer = window.setTimeout(() => {
        signal?.removeEventListener("abort", abort);
        resolve();
      }, ms);
      const abort = () => {
        window.clearTimeout(timer);
        reject(new DOMException("The request was aborted.", "AbortError"));
      };
      signal?.addEventListener("abort", abort, { once: true });
    });
  }

  async function fetchScheduledGamePackage(url: string, options: RequestInit = {}) {
    const deadline = Date.now() + 3 * 60_000;
    while (true) {
      try {
        return await fetchJson(url, options);
      } catch (error: any) {
        if (error?.status !== 425 || !error?.retryable || Date.now() >= deadline) {
          throw error;
        }
        state.pregameAuthorityPending = true;
        state.loadingTitle = "Locking in the matchup...";
        state.loadingStatus = "Applying the final roster and schema selections.";
        renderStageLoading();
        await waitForPregameAuthority(
          Math.max(750, Math.min(5_000, Number(error.retryAfterMs || 1_500))),
          options.signal,
        );
      }
    }
  }

  function teamList() {
    return state.bootstrap?.teams || [];
  }

  function teamById(id: string | null | undefined) {
    if (!id) {
      return null;
    }
    const franchiseId = canonicalTeamId(id);
    return teamList().find((team: any) => canonicalTeamId(team.id) === franchiseId) || null;
  }

  function teamDisplayName(team: any) {
    return team?.displayName || [team?.city, team?.name].filter(Boolean).join(" ") || team?.abbr || team?.id || "";
  }

  function refreshTeamFromRegistry(team: any) {
    if (!team?.id) {
      return team;
    }
    const currentTeam = getFranchiseById(team.id);
    if (!currentTeam) {
      return team;
    }
    return {
      ...team,
      ...currentTeam,
      displayName: teamDisplayName(currentTeam),
    };
  }

  function refreshBootstrapTeams() {
    if (!state.bootstrap?.teams) {
      return;
    }
    state.bootstrap = {
      ...state.bootstrap,
      teams: state.bootstrap.teams.map((team: any) => refreshTeamFromRegistry(team)),
    };
  }

  function previewTeamById(id: string) {
    const team = teamById(id) || getFranchiseById(id);
    if (team) {
      return {
        ...team,
        displayName: teamDisplayName(team),
      };
    }

    return {
      id,
      abbr: id,
      city: "",
      name: id,
      displayName: id,
    };
  }

  function refreshGameTeamIdentity(gameTeam: any) {
    if (!gameTeam) {
      return gameTeam;
    }
    const currentTeam = getFranchiseById(gameTeam.id) || teamById(gameTeam.id);
    if (!currentTeam) {
      return gameTeam;
    }
    return {
      ...gameTeam,
      ...currentTeam,
      id: currentTeam.id || gameTeam.id,
      displayName: teamDisplayName(currentTeam),
    };
  }

  function refreshGameBranding(game: any) {
    if (!game) {
      return game;
    }
    return {
      ...game,
      home: refreshGameTeamIdentity(game.home),
      away: refreshGameTeamIdentity(game.away),
    };
  }

  function playTeamMatches(teamRef: any, team: any) {
    const value = String(teamRef || "");
    if (!value || !team) {
      return false;
    }
    return canonicalTeamId(value) === canonicalTeamId(team.id);
  }

  function warmImage(src: string | null | undefined) {
    if (!src || typeof Image === "undefined") {
      return;
    }
    const image = new Image();
    image.decoding = "async";
    image.src = src;
  }

  function warmPregameLogos() {
    if (!state.pregamePreview) {
      return;
    }
    warmImage(broadcastTeamLogoSrc(state.pregamePreview.away));
    warmImage(broadcastTeamLogoSrc(state.pregamePreview.home));
  }

  async function scheduledPregamePackage() {
    const gameId = currentScheduledGameId();
    if (!gameId || isDebugMode()) {
      return null;
    }
    const schedule = await getScheduledGame(gameId);
    return schedule.status === "scheduled" && isFutureKickoff(schedule.broadcastTiming?.kickoffAt)
      ? schedule
      : null;
  }

  async function showPregameLoadingCurtain() {
    const schedule = await scheduledPregamePackage();
    if (!schedule) {
      return false;
    }

    rememberPregameViewer(schedule.gameId);

    state.liveSyncLastBroadcast = {
      status: "scheduled",
      currentPlayIndex: 0,
      currentPlayElapsedMs: 0,
      kickoffAt: schedule.broadcastTiming.kickoffAt,
    };
    updateLiveBroadcastSlot(schedule.broadcastTiming);
    state.pregamePreview = {
      away: previewTeamById(schedule.away),
      home: previewTeamById(schedule.home),
    };
    state.loadingActive = false;
    renderStageLoading();
    warmPregameLogos();
    renderPregameOverlay();
    renderBroadcastHud(0);
    return true;
  }

  function currentPlay() {
    return state.game?.plays?.[state.playIndex] || null;
  }

  function previousPlayAt(playIndex = state.playIndex) {
    return playIndex > 0 ? state.game?.plays?.[playIndex - 1] || null : null;
  }

  // Resolve the running score at/just before a play. The live broadcast strips the
  // top-level game.score (so the final can't leak) and out-of-window plays arrive
  // as empty {} skeletons, so the per-play `score` may be missing on the exact
  // play we land on. Scan back to the most recent play that carries a score rather
  // than falling back to 0-0 (which would visibly reset the scoreboard mid-game).
  function lastKnownScore(uptoIndex = state.playIndex): { home: number; away: number } {
    const plays = state.game?.plays || [];
    const start = Math.max(0, Math.min(uptoIndex, plays.length - 1));
    for (let i = start; i >= 0; i -= 1) {
      const score = plays[i]?.score;
      if (score && (typeof score.home === "number" || typeof score.away === "number")) {
        return { home: Number(score.home) || 0, away: Number(score.away) || 0 };
      }
    }
    return { home: 0, away: 0 };
  }

  function baseTimedPlayDurationMs(play: any, playIndex = state.playIndex) {
    return playDurationMs(play, playIndex, previousPlayAt(playIndex));
  }

  function timedPlayDurationMs(play: any, playIndex = state.playIndex) {
    const baseDurationMs = baseTimedPlayDurationMs(play, playIndex);
    return divingHeroPresentedDurationMs(
      play,
      baseDurationMs,
      livePlayDurationMs(play),
    );
  }

  function liveBroadcastSlotMsFromTiming(timing: any): number | null {
    const playMs = Number(timing?.playMs);
    const betweenPlayMs = Number(timing?.betweenPlayMs);
    if (!Number.isFinite(playMs) || !Number.isFinite(betweenPlayMs)) {
      return null;
    }
    return Math.max(1_000, Math.round(playMs + betweenPlayMs));
  }

  function updateLiveBroadcastSlot(timing: any) {
    const slotMs = liveBroadcastSlotMsFromTiming(timing);
    if (slotMs) {
      state.liveBroadcastSlotMs = slotMs;
    }
  }

  function liveAdvanceDurationMs(play: any, playIndex = state.playIndex) {
    return timedPlayDurationMs(play, playIndex);
  }

  function winnerSideForGame(game = state.game) {
    if (!game) {
      return null;
    }
    // game.score is stripped from live broadcast payloads; fall back to the
    // running score from the plays so a finished live game still names a winner.
    const score = game.score || (game === state.game ? lastKnownScore() : null);
    if (!score) {
      return null;
    }
    if (score.home === score.away) {
      return null;
    }
    return score.home > score.away ? "home" : "away";
  }

  function midfieldCameraX() {
    const metrics = fieldMetrics();
    return snapToPixel(Math.max(0, Math.min(metrics.worldWidth - refs.canvas!.width, yardToX(50) - refs.canvas!.width / 2)));
  }

  function quarterOrdinalLabel(value: any) {
    const number = Number(value);
    if (number === 1) return "1st";
    if (number === 2) return "2nd";
    if (number === 3) return "3rd";
    return `${number}th`;
  }

  function intermissionRunoffMs(kind: string) {
    return kind === "halftime" || kind === "final" ? INTERMISSION_TIMING.runoffMs : 0;
  }

  function intermissionSettleMs(kind: string) {
    if (kind === "quarter-break") {
      return INTERMISSION_TIMING.quarterBreakSettleMs;
    }
    return kind === "halftime" || kind === "final" ? INTERMISSION_TIMING.settleMs : 0;
  }

  function intermissionCameraPanMs(kind: string) {
    return kind === "halftime" || kind === "final" ? INTERMISSION_TIMING.cameraPanMs : 0;
  }

  function intermissionPresentationStartMs(intermission: any) {
    return intermissionSettleMs(intermission.kind)
      + intermissionRunoffMs(intermission.kind)
      + intermissionCameraPanMs(intermission.kind);
  }

  function intermissionDurationMs(kind: string) {
    const holdMs = kind === "quarter-break"
      ? INTERMISSION_TIMING.quarterBreakHoldMs
      : kind === "halftime"
        ? INTERMISSION_TIMING.halftimeHoldMs
        : INTERMISSION_TIMING.finalHoldMs;
    return intermissionPresentationStartMs({ kind }) + holdMs;
  }

  function intermissionStateKey(intermission: any) {
    return [
      state.playIndex,
      intermission.kind,
      intermission.quarter ?? "na",
      intermission.winnerSide ?? "na",
    ].join(":");
  }

  function ensureIntermissionCameraAnchor(intermission: any, fallbackCameraX: any) {
    const key = intermissionStateKey(intermission);
    if (state.intermissionKey !== key || !Number.isFinite(state.intermissionCameraStartX)) {
      const cameraX = Number.isFinite(state.cameraX) ? state.cameraX : fallbackCameraX;
      state.intermissionKey = key;
      state.intermissionCameraStartX = snapToPixel(cameraX);
    }
    return state.intermissionCameraStartX ?? fallbackCameraX;
  }

  function intermissionAfterPlayIndex(playIndex = state.playIndex) {
    if (!state.game?.plays?.length) {
      return null;
    }

    const play = state.game.plays[playIndex];
    if (!play) {
      return null;
    }

    const nextPlay = state.game.plays[playIndex + 1] || null;
    if (nextPlay && (play.quarter === 1 && nextPlay.quarter === 2 || play.quarter === 3 && nextPlay.quarter === 4)) {
      return {
        kind: "quarter-break",
        quarter: play.quarter,
        ribbon: { text: `END Q${play.quarter}`, color: "#dbe8ff", glow: "rgba(219, 232, 255, 0.4)" },
        score: play.score || lastKnownScore(playIndex),
        durationMs: intermissionDurationMs("quarter-break"),
      };
    }

    if (nextPlay && play.quarter === 2 && nextPlay.quarter === 3) {
      return {
        kind: "halftime",
        ribbon: { text: "HALFTIME", color: "#ffe066", glow: "rgba(255, 224, 102, 0.55)" },
        score: play.score || lastKnownScore(playIndex),
        schemaUsage: state.game.schemaUsage,
        durationMs: intermissionDurationMs("halftime"),
      };
    }

    if (playIndex === state.game.plays.length - 1) {
      const winnerSide = winnerSideForGame(state.game);
      const winner = winnerSide ? state.game[winnerSide] : null;
      return {
        kind: "final",
        winnerSide,
        winner,
        ribbon: winner
          ? { text: `${winner.abbr} WINS!`, color: "#8ef6ff", glow: "rgba(142, 246, 255, 0.52)" }
          : { text: "FINAL", color: "#dbe8ff", glow: "rgba(219, 232, 255, 0.42)" },
        score: play.score || lastKnownScore(playIndex),
        durationMs: intermissionDurationMs("final"),
      };
    }

    return null;
  }

  function activePostPlayIntermission(now = performance.now()) {
    if (visualTestPage) {
      return null;
    }
    const play = currentPlay();
    if (!play) {
      return null;
    }

    const intermission = intermissionAfterPlayIndex(state.playIndex);
    if (!intermission) {
      return null;
    }

    const elapsed = state.autoplay
      ? Math.max(0, now - state.lastAdvance)
      : Math.max(0, state.pausedElapsed ?? 0);
    const playMs = timedPlayDurationMs(play, state.playIndex);
    if (elapsed < playMs) {
      return null;
    }

    return {
      ...intermission,
      play,
      elapsedMs: elapsed - playMs,
      playMs,
    };
  }

  function shouldSkipSpecialTeamsExit(previousPlay: any, play: any) {
    return !!previousPlay
      && !!play
      && (
        (
          play.playId === "kickoff"
          && previousPlay.quarter === 2
          && play.quarter === 3
        )
        || (
          play.playId === "pat"
          && (previousPlay.touchdown || previousPlay.returnTouchdown || previousPlay.defensiveScore)
        )
      );
  }

  function populateTeamSelects() {
    const options = teamList()
      .map((team: any) => `<option value="${team.id}">${escapeHtml(teamDisplayName(team))} (${team.abbr})</option>`)
      .join("");
    refs.homeSelect!.innerHTML = options;
    refs.awaySelect!.innerHTML = options;
    refs.homeSelect!.value = teamList()[0]?.id || "";
    refs.awaySelect!.value = teamList()[1]?.id || "";
  }


  function comparePlayers(left: any, right: any) {
    const leftComposite =
      (left?.overall || 0) +
      (
        getPlayerRating(left, 'athleticism', 0)
        + Math.max(getPlayerRating(left, 'awareness', 0), getPlayerRating(left, 'awareness', 0), getPlayerRating(left, 'precision', 0))
      ) / 6;
    const rightComposite =
      (right?.overall || 0) +
      (
        getPlayerRating(right, 'athleticism', 0)
        + Math.max(getPlayerRating(right, 'awareness', 0), getPlayerRating(right, 'awareness', 0), getPlayerRating(right, 'precision', 0))
      ) / 6;
    return rightComposite - leftComposite || (left?.number || 0) - (right?.number || 0);
  }

  function uniquePlayers(players: any) {
    const seen = new Set();
    return players.filter((player: any) => {
      if (!player || seen.has(player.id)) {
        return false;
      }
      seen.add(player.id);
      return true;
    });
  }

  function rosterGroups(team: any): any {
    if (!team) {
      return {};
    }
    if (!team._rosterGroups) {
      const groups: any = {};
      for (const player of team.roster || []) {
        if (!groups[player.position]) {
          groups[player.position] = teamPlayersAtPosition(team, player.position);
        }
      }
      team._rosterGroups = groups;
    }
    return team._rosterGroups;
  }

  function rosterTopPlayers(team: any, position: any, count = 1) {
    return teamPlayersAtPosition(team, position, count);
  }

  function rosterMerged(team: any, positions: any) {
    return uniquePlayers(
      positions.flatMap((position: any) => rosterTopPlayers(team, position, 8))
    ).sort(comparePlayers);
  }

  function teamInGameById(game: any, franchiseId: any) {
    if (!game) {
      return null;
    }
    if (playTeamMatches(franchiseId, game.home)) {
      return game.home;
    }
    if (playTeamMatches(franchiseId, game.away)) {
      return game.away;
    }
    return null;
  }

  function playTeamInitials(game: any, play: any, side: "offense" | "defense") {
    const franchiseId = play?.[side];
    const capturedAbbr = play?.[`${side}Abbr`];
    return teamInGameById(game, franchiseId)?.abbr || capturedAbbr || franchiseId || "";
  }

  function findPlayerByName(team: any, name: any) {
    if (!team || !name) {
      return null;
    }
    const normalized = String(name).trim().toLowerCase();
    return (team.roster || []).find((player: any) => player.name.toLowerCase() === normalized) || null;
  }

  function firstParticipantValue(participants: any, keys: string[] = []) {
    for (const key of keys) {
      const value = participants?.[key];
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
    return null;
  }

  function participantValues(participants: any, keys: string[] = []) {
    const values: any[] = [];
    keys.forEach((key) => {
      const value = participants?.[key];
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (entry !== undefined && entry !== null && entry !== "") {
            values.push(entry);
          }
        });
      } else if (value !== undefined && value !== null && value !== "") {
        values.push(value);
      }
    });
    return values;
  }

  function resolveParticipantPlayer(team: any, play: any, options: { idKeys?: string[]; nameKeys?: string[]; roleKeys?: string[] } = {}) {
    const participants = play?.participants || {};
    const playerId = firstParticipantValue(participants, options.idKeys);
    const playerName = firstParticipantValue(participants, options.nameKeys);
    const playerRole = firstParticipantValue(participants, options.roleKeys);
    return findPlayerById(team, playerId)
      || findPlayerByName(team, playerName)
      || rosterPlayerForFieldRole(team, playerRole);
  }

  function resolveParticipantPlayers(team: any, play: any, options: { idKeys?: string[]; nameKeys?: string[]; roleKeys?: string[] } = {}) {
    const participants = play?.participants || {};
    const resolved: any[] = [];
    const seen = new Set<string>();
    const rememberPlayer = (player: any) => {
      if (!player) {
        return;
      }
      const key = String(player.id || player.name || player.position || "");
      if (!key || seen.has(key)) {
        return;
      }
      seen.add(key);
      resolved.push(player);
    };

    participantValues(participants, options.idKeys).forEach((playerId) => {
      rememberPlayer(findPlayerById(team, playerId));
    });
    participantValues(participants, options.nameKeys).forEach((playerName) => {
      rememberPlayer(findPlayerByName(team, playerName));
    });
    participantValues(participants, options.roleKeys).forEach((playerRole) => {
      rememberPlayer(rosterPlayerForFieldRole(team, playerRole));
    });
    return resolved;
  }

  function defensiveGroups(team: any): any {
    if (!team) {
      return {};
    }
    if (!team._defensiveGroups) {
      const passRush = rosterMerged(team, ["DE", "DT", "LB"]).sort(
        (left: any, right: any) =>
          ((getPlayerRating(right, 'physicality', 0) + getPlayerRating(right, 'strength', 0) + getPlayerRating(right, 'physicality', 0)) -
          (getPlayerRating(left, 'physicality', 0) + getPlayerRating(left, 'strength', 0) + getPlayerRating(left, 'physicality', 0)))
      );
      const box = rosterMerged(team, ["LB", "DT", "DE", "S"]).sort(
        (left: any, right: any) =>
          ((getPlayerRating(right, 'physicality', 0) + getPlayerRating(right, 'athleticism', 0) + getPlayerRating(right, 'physicality', 0)) -
          (getPlayerRating(left, 'physicality', 0) + getPlayerRating(left, 'athleticism', 0) + getPlayerRating(left, 'physicality', 0)))
      );
      const coverage = rosterMerged(team, ["CB", "S", "LB"]).sort(
        (left: any, right: any) =>
          ((getPlayerRating(right, 'awareness', 0) + getPlayerRating(right, 'athleticism', 0) + getPlayerRating(right, 'athleticism', 0)) -
          (getPlayerRating(left, 'awareness', 0) + getPlayerRating(left, 'athleticism', 0) + getPlayerRating(left, 'athleticism', 0)))
      );
      const pursuit = rosterMerged(team, ["LB", "S", "CB"]).sort(
        (left: any, right: any) =>
          ((getPlayerRating(right, 'athleticism', 0) + getPlayerRating(right, 'physicality', 0) + getPlayerRating(right, 'precision', 0)) -
          (getPlayerRating(left, 'athleticism', 0) + getPlayerRating(left, 'physicality', 0) + getPlayerRating(left, 'precision', 0)))
      );
      const special = uniquePlayers([
        ...rosterTopPlayers(team, "CB", 4),
        ...rosterTopPlayers(team, "S", 3),
        ...rosterTopPlayers(team, "LB", 3),
        ...rosterTopPlayers(team, "WR", 2),
        ...rosterTopPlayers(team, "RB", 2),
      ]).sort(
        (left: any, right: any) =>
          ((getPlayerRating(right, 'athleticism', 0) + getPlayerRating(right, 'physicality', 0) + deriveReturnAbility(right)) -
          (getPlayerRating(left, 'athleticism', 0) + getPlayerRating(left, 'physicality', 0) + deriveReturnAbility(left)))
      );
      team._defensiveGroups = { passRush, box, coverage, pursuit, special };
    }
    return team._defensiveGroups;
  }

  function rosterReturner(team: any, kind: 'kick' | 'punt' = 'kick') {
    if (!team) {
      return null;
    }
    if (!team._returners) {
      team._returners = {};
    }
    if (!team._returners[kind]) {
      team._returners[kind] = teamReturnerPlayer(team, kind);
    }
    return team._returners[kind];
  }

  function topQuarterback(team: any) {
    return rosterTopPlayers(team, "QB")[0] || team?.roster?.[0] || null;
  }

  function chooseRunCarrier(team: any, play: any) {
    if (!team) {
      return null;
    }
    if (play.playId === "kneel") {
      return topQuarterback(team);
    }
    if (play.playId === "power-o" && playChance(play, "run-fb", 0.2)) {
      return rosterTopPlayers(team, "FB")[0] || rosterTopPlayers(team, "RB")[0] || topQuarterback(team);
    }
    if (play.playId === "toss-sweep" && playChance(play, "run-wr", 0.14)) {
      return pickDeterministic(rosterTopPlayers(team, "WR", 3), play, "run-sweep-wr", rosterTopPlayers(team, "RB")[0]);
    }
    return rosterTopPlayers(team, "RB")[0] || rosterTopPlayers(team, "FB")[0] || topQuarterback(team);
  }

  function choosePassTarget(team: any, play: any) {
    if (!team) {
      return null;
    }
    switch (play.playId) {
      case "screen-left":
        return pickDeterministic(
          uniquePlayers([rosterTopPlayers(team, "RB")[0], rosterTopPlayers(team, "WR")[0]]).filter(Boolean),
          play,
          "pass-screen-target",
          rosterTopPlayers(team, "RB")[0] || rosterTopPlayers(team, "WR")[0]
        );
      case "quick-slant":
        return pickDeterministic(
          uniquePlayers([rosterTopPlayers(team, "WR")[0], rosterTopPlayers(team, "TE")[0]]).filter(Boolean),
          play,
          "pass-slant-target",
          rosterTopPlayers(team, "WR")[0]
        );
      case "mesh-cross":
        return pickDeterministic(
          uniquePlayers([
            ...rosterTopPlayers(team, "WR", 2),
            rosterTopPlayers(team, "TE")[0],
          ]).filter(Boolean),
          play,
          "pass-mesh-target",
          rosterTopPlayers(team, "WR")[0]
        );
      case "flood-shot":
        return pickDeterministic(
          uniquePlayers([
            ...rosterTopPlayers(team, "WR", 2),
            rosterTopPlayers(team, "TE")[0],
            rosterTopPlayers(team, "RB")[0],
          ]).filter(Boolean),
          play,
          "pass-flood-target",
          rosterTopPlayers(team, "WR")[1] || rosterTopPlayers(team, "WR")[0]
        );
      case "play-action-post":
      case "hail-mary":
        return rosterTopPlayers(team, "WR")[0] || rosterTopPlayers(team, "TE")[0] || rosterTopPlayers(team, "RB")[0];
      default:
        return rosterTopPlayers(team, "WR")[0] || rosterTopPlayers(team, "TE")[0] || rosterTopPlayers(team, "RB")[0];
    }
  }

  function resolveRunCarrier(team: any, play: any) {
    const match = /^(.+?) takes .* for -?\d+ yards\.$/.exec(play.result || "");
    return findPlayerByName(team, play.participants?.runnerName)
      || findPlayerByName(team, play.participants?.ballCarrierName)
      || findPlayerByName(team, match?.[1])
      || chooseRunCarrier(team, play);
  }
  function resolvePasser(team: any, play: any) {
    const result = play.result || "";
    const match =
      /^(.+?) finds /.exec(result) ||
      /^(.+?) fires /.exec(result) ||
      /sack of (.+?)\./.exec(result);
    return findPlayerByName(team, play.participants?.passerName) || findPlayerByName(team, match?.[1]) || topQuarterback(team);
  }
  function resolvePassTarget(team: any, play: any) {
    const match = /^.+? finds (.+?) for -?\d+ yards on /.exec(play.result || "");
    return findPlayerByName(team, play.participants?.targetName) || findPlayerByName(team, match?.[1]) || choosePassTarget(team, play);
  }
  function resolveKicker(team: any, play: any) {
    const match = /^(.+?) (?:drills a \d+-yard field goal|misses from \d+ yards|adds the extra point|pushes the extra point wide)\./.exec(play.result || "");
    return findPlayerByName(team, play.participants?.kickerName) || findPlayerByName(team, match?.[1]) || rosterTopPlayers(team, "K")[0] || topQuarterback(team);
  }
  function resolvePunter(team: any, play: any) {
    const match = /^(.+?) (?:sends the punt into the end zone|flips the field with a \d+-yard punt)/.exec(play.result || "");
    return findPlayerByName(team, play.participants?.punterName) || findPlayerByName(team, match?.[1]) || rosterTopPlayers(team, "P")[0] || rosterTopPlayers(team, "K")[0];
  }
  function resolveKickReturner(team: any, play: any) {
    const match = /^(.+?) brings .* back to the \d+\./.exec(play.result || "");
    return findPlayerByName(team, play.participants?.returnerName) || findPlayerByName(team, match?.[1]) || rosterReturner(team, "kick");
  }
  function resolvePuntReturner(team: any, play: any) {
    return findPlayerByName(team, play.participants?.returnerName) || rosterReturner(team, "punt");
  }
  function chooseRunDefenders(team: any, play: any) {
    const groups = defensiveGroups(team);
    const yards = visualYardsForPlay(play);
    const lane = play.visual?.lane ?? 0;
    const outside = Math.abs(lane) >= 2;
    const explosive = yards >= 14;
    const loss = yards < 0;
    const primaryPool = loss
      ? groups.passRush
      : explosive
        ? outside
          ? uniquePlayers([...(groups.pursuit || []).slice(0, 5), ...rosterTopPlayers(team, "CB", 2)])
          : groups.pursuit
        : outside
          ? uniquePlayers([...(groups.box || []).slice(0, 4), ...rosterTopPlayers(team, "CB", 2), ...rosterTopPlayers(team, "S", 2)])
          : groups.box;
    const assistPool = loss
      ? uniquePlayers([...(groups.box || []).slice(0, 4), ...rosterTopPlayers(team, "LB", 3)])
      : explosive
        ? uniquePlayers([...(groups.coverage || []).slice(0, 4), ...rosterTopPlayers(team, "LB", 2)])
        : uniquePlayers([...(groups.pursuit || []).slice(0, 5), ...rosterTopPlayers(team, "LB", 2)]);
    const [primary] = pickDeterministicGroup(primaryPool, play, "run-primary", 1) as any[];
    const [assist] = pickDeterministicGroup(
      assistPool.filter((player: any) => player?.id !== primary?.id),
      play,
      "run-assist",
      loss ? 1 : playChance(play, "run-assist", 0.64) ? 1 : 0
    ) as any[];
    return { primary, assist };
  }

  function choosePassDefenders(team: any, play: any) {
    const groups = defensiveGroups(team);
    if (isInterceptionPlay(play)) {
      const interceptor = resolveParticipantPlayer(team, play, {
        idKeys: ["interceptorId"],
        nameKeys: ["interceptorName"],
        roleKeys: ["interceptorRole"],
      });
      if (interceptor) {
        return { primary: interceptor, assist: null };
      }
      const [primary] = pickDeterministicGroup(groups.coverage, play, "pass-int-primary", 1) as any[];
      const [assist] = pickDeterministicGroup(
        (groups.coverage || []).filter((player: any) => player?.id !== primary?.id),
        play,
        "pass-int-assist",
        playChance(play, "pass-int-assist", 0.38) ? 1 : 0
      ) as any[];
      return { primary, assist };
    }
    if (isSackPlay(play)) {
      const [primaryFromParticipants, assistFromParticipants] = resolveParticipantPlayers(team, play, {
        idKeys: ["forcedById", "tacklerId", "tacklerIds"],
        nameKeys: ["forcedByName", "tacklerName"],
        roleKeys: ["forcedByRole", "tacklerRole", "tacklerRoles"],
      });
      if (primaryFromParticipants) {
        const [assistFallback] = pickDeterministicGroup(
          (groups.passRush || []).filter(
            (player: any) => player?.id !== primaryFromParticipants?.id && player?.id !== assistFromParticipants?.id
          ),
          play,
          "pass-sack-assist",
          assistFromParticipants ? 0 : (playChance(play, "pass-sack-assist", 0.33) ? 1 : 0)
        ) as any[];
        return {
          primary: primaryFromParticipants,
          assist: assistFromParticipants || assistFallback,
        };
      }
      const [primary] = pickDeterministicGroup(groups.passRush, play, "pass-sack-primary", 1) as any[];
      const [assist] = pickDeterministicGroup(
        (groups.passRush || []).filter((player: any) => player?.id !== primary?.id),
        play,
        "pass-sack-assist",
        playChance(play, "pass-sack-assist", 0.33) ? 1 : 0
      ) as any[];
      return { primary, assist };
    }
    const deep = (play.airYards || 0) >= 14 || visualYardsForPlay(play) >= 18 || ["play-action-post", "hail-mary", "flood-shot"].includes(play.playId);
    const [primary] = pickDeterministicGroup(deep ? groups.coverage : groups.pursuit, play, "pass-tackle-primary", 1) as any[];
    const [assist] = pickDeterministicGroup(
      uniquePlayers([...(deep ? groups.pursuit || [] : groups.coverage || []), ...rosterTopPlayers(team, "LB", 2)]).filter((player: any) => player?.id !== primary?.id),
      play,
      "pass-tackle-assist",
      playChance(play, "pass-tackle-assist", deep ? 0.58 : 0.42) ? 1 : 0
    ) as any[];
    return { primary, assist };
  }

  function chooseReturnCoverageDefenders(team: any, play: any, salt = "st") {
    const pool = defensiveGroups(team).special || [];
    const [primary] = pickDeterministicGroup(pool, play, `${salt}-primary`, 1) as any[];
    const [assist] = pickDeterministicGroup(
      pool.filter((player: any) => player?.id !== primary?.id),
      play,
      `${salt}-assist`,
      playChance(play, `${salt}-assist`, 0.52) ? 1 : 0
    ) as any[];
    return { primary, assist };
  }

  function createLiveStatsEntry(team: any, player: any) {
    return {
      teamId: team.id,
      playerId: player.id,
      name: player.name,
      number: player.number,
      position: player.position,
      passing: { attempts: 0, completions: 0, yards: 0, tds: 0, interceptions: 0, sacks: 0 },
      rushing: { attempts: 0, yards: 0, tds: 0, long: 0 },
      receiving: { targets: 0, catches: 0, yards: 0, tds: 0, long: 0 },
      defense: { tackles: 0, assists: 0, sacks: 0, interceptions: 0, forcedFumbles: 0, fumbleRecoveries: 0 },
      special: {
        fgm: 0,
        fga: 0,
        patm: 0,
        pata: 0,
        punts: 0,
        puntYards: 0,
        puntLong: 0,
        puntInside20: 0,
        puntTouchbacks: 0,
        kickReturns: 0,
        kickReturnYards: 0,
        kickReturnLong: 0,
        kickReturnTds: 0,
        puntReturns: 0,
        puntReturnYards: 0,
        puntReturnLong: 0,
        puntReturnTds: 0,
        fieldGoalLong: 0,
        returnTds: 0,
        touchbacks: 0,
      },
    };
  }

  function emptyLiveStatsSnapshot() {
    return {};
  }

  function cloneLiveStatsSnapshot(snapshot: any) {
    const next: any = {};
    for (const [key, entry] of Object.entries(snapshot) as [string, any][]) {
      next[key] = {
        ...entry,
        passing: { ...entry.passing },
        rushing: { ...entry.rushing },
        receiving: { ...entry.receiving },
        defense: { ...entry.defense },
        special: { ...entry.special },
      };
    }
    return next;
  }

  function ensureLiveStatsEntry(snapshot: any, team: any, player: any) {
    if (!team || !player) {
      return null;
    }
    const key = `${team.id}:${player.id}`;
    if (!snapshot[key]) {
      snapshot[key] = createLiveStatsEntry(team, player);
    }
    return snapshot[key];
  }

  function addDefenderStop(snapshot: any, team: any, primary: any, assist: any, options: any = {}) {
    if (primary) {
      const primaryEntry = ensureLiveStatsEntry(snapshot, team, primary);
      primaryEntry.defense.tackles += 1;
      if (options.sack) {
        primaryEntry.defense.sacks += 1;
      }
      if (options.interception) {
        primaryEntry.defense.interceptions += 1;
      }
      if (options.forcedFumble) {
        primaryEntry.defense.forcedFumbles += 1;
      }
    }
    if (assist && assist.id !== primary?.id) {
      const assistEntry = ensureLiveStatsEntry(snapshot, team, assist);
      assistEntry.defense.assists += 1;
    }
    if (options.recovery && options.recovery.id !== primary?.id && options.recovery.id !== assist?.id) {
      const recoveryEntry = ensureLiveStatsEntry(snapshot, team, options.recovery);
      recoveryEntry.defense.fumbleRecoveries += 1;
    } else if (options.recovery && primary) {
      const primaryEntry = ensureLiveStatsEntry(snapshot, team, primary);
      primaryEntry.defense.fumbleRecoveries += 1;
    }
  }

  function playYards(play: any) {
    return typeof play.yards === "number" ? play.yards : 0;
  }

  function isOffensiveTouchdown(play: any) {
    return (play.touchdown || play.stopReason === "touchdown")
      && !play.turnover
      && !play.defensiveScore
      && !play.returnTouchdown;
  }

  function applyPlayToLiveStats(snapshot: any, game: any, play: any) {
    const offenseTeam = teamInGameById(game, play.offense);
    const defenseTeam = teamInGameById(game, play.defense);
    if (!offenseTeam || !defenseTeam) {
      return;
    }
    if (play.twoPointAttempt) {
      return;
    }

    if (play.family === "run") {
      const runner = resolveRunCarrier(offenseTeam, play);
      const runnerEntry = ensureLiveStatsEntry(snapshot, offenseTeam, runner);
      const yards = playYards(play);
      if (runnerEntry) {
        runnerEntry.rushing.attempts += 1;
        runnerEntry.rushing.yards += yards;
        runnerEntry.rushing.long = Math.max(runnerEntry.rushing.long, yards);
        if (play.touchdown) {
          runnerEntry.rushing.tds += 1;
        }
      }

      if (play.playId === "kneel") {
        return;
      }

      if (play.turnover && /fumble/i.test(play.result || "")) {
        const stop = chooseRunDefenders(defenseTeam, play);
        const recovery = pickDeterministic(
          defensiveGroups(defenseTeam).box.filter((player: any) => player?.id !== stop.primary?.id && player?.id !== stop.assist?.id),
          play,
          "run-fumble-recovery",
          stop.primary || stop.assist
        );
        addDefenderStop(snapshot, defenseTeam, stop.primary, stop.assist, {
          forcedFumble: true,
          recovery,
        });
        return;
      }

      if (["tackle", "turnover-on-downs", "safety"].includes(play.stopReason)) {
        const stop = chooseRunDefenders(defenseTeam, play);
        addDefenderStop(snapshot, defenseTeam, stop.primary, stop.assist);
      }
      return;
    }

    if (play.family === "pass") {
      const qb = resolvePasser(offenseTeam, play);
      const target = resolvePassTarget(offenseTeam, play);
      const result = play.result || "";
      const scramble = !!play.scramble || /scrambles for|tucks it and is corralled/i.test(result);
      const sack = isSackPlay(play);
      const interception = isInterceptionPlay(play);
      const complete = isCompletedPassPlay(play);
      const offensiveTouchdown = isOffensiveTouchdown(play);
      const yards = playYards(play);
      const qbEntry = ensureLiveStatsEntry(snapshot, offenseTeam, qb);
      const targetEntry = target ? ensureLiveStatsEntry(snapshot, offenseTeam, target) : null;

      if (sack) {
        if (qbEntry) {
          qbEntry.passing.sacks += 1;
        }
        const stop = choosePassDefenders(defenseTeam, play);
        addDefenderStop(snapshot, defenseTeam, stop.primary, stop.assist, { sack: true });
        return;
      }

      if (scramble) {
        if (qbEntry) {
          qbEntry.rushing.attempts += 1;
          qbEntry.rushing.yards += yards;
          qbEntry.rushing.long = Math.max(qbEntry.rushing.long, yards);
          if (offensiveTouchdown) {
            qbEntry.rushing.tds += 1;
          }
        }

        if (["tackle", "turnover-on-downs", "safety"].includes(play.stopReason)) {
          const stop = choosePassDefenders(defenseTeam, play);
          addDefenderStop(snapshot, defenseTeam, stop.primary, stop.assist);
        }
        return;
      }

      if (qbEntry) {
        qbEntry.passing.attempts += 1;
      }
      if (targetEntry) {
        targetEntry.receiving.targets += 1;
      }

      if (interception) {
        if (qbEntry) {
          qbEntry.passing.interceptions += 1;
        }
        const interceptor = resolveParticipantPlayer(defenseTeam, play, {
          idKeys: ["interceptorId"],
          nameKeys: ["interceptorName"],
          roleKeys: ["interceptorRole"],
        }) || choosePassDefenders(defenseTeam, play).primary;
        if (interceptor) {
          const interceptorEntry = ensureLiveStatsEntry(snapshot, defenseTeam, interceptor);
          interceptorEntry.defense.interceptions += 1;
        }
        return;
      }

      if (!complete) {
        return;
      }

      if (qbEntry) {
        qbEntry.passing.completions += 1;
        qbEntry.passing.yards += yards;
        if (offensiveTouchdown) {
          qbEntry.passing.tds += 1;
        }
      }

      if (targetEntry) {
        targetEntry.receiving.catches += 1;
        targetEntry.receiving.yards += yards;
        targetEntry.receiving.long = Math.max(targetEntry.receiving.long, yards);
        if (offensiveTouchdown) {
          targetEntry.receiving.tds += 1;
        }
      }

      if (["tackle", "turnover-on-downs", "safety"].includes(play.stopReason)) {
        const stop = choosePassDefenders(defenseTeam, play);
        addDefenderStop(snapshot, defenseTeam, stop.primary, stop.assist);
      }
      return;
    }

    if (play.playId === "field-goal" || play.playId === "pat") {
      const kicker = resolveKicker(offenseTeam, play);
      const kickerEntry = ensureLiveStatsEntry(snapshot, offenseTeam, kicker);
      if (!kickerEntry) {
        return;
      }
      if (play.playId === "field-goal") {
        kickerEntry.special.fga += 1;
        kickerEntry.special.fieldGoalLong = Math.max(kickerEntry.special.fieldGoalLong, play.fieldGoal?.distance || 0);
        if (play.fieldGoal?.good) {
          kickerEntry.special.fgm += 1;
        }
      } else {
        kickerEntry.special.pata += 1;
        if (/adds the extra point/i.test(play.result || "")) {
          kickerEntry.special.patm += 1;
        }
      }
      return;
    }

    if (play.playId === "punt") {
      const punter = resolvePunter(offenseTeam, play);
      const punterEntry = ensureLiveStatsEntry(snapshot, offenseTeam, punter);
      if (punterEntry) {
        punterEntry.special.punts += 1;
        punterEntry.special.puntYards += play.grossYards || 0;
        punterEntry.special.puntLong = Math.max(punterEntry.special.puntLong, play.grossYards || 0);
        if (play.stopReason === "touchback") {
          punterEntry.special.puntTouchbacks += 1;
        } else if ((play.endYardLine || 0) >= 80) {
          punterEntry.special.puntInside20 += 1;
        }
      }

      if (play.stopReason !== "touchback") {
        const returner = resolvePuntReturner(defenseTeam, play);
        const returnerEntry = ensureLiveStatsEntry(snapshot, defenseTeam, returner);
        if (returnerEntry) {
          returnerEntry.special.puntReturns += 1;
          returnerEntry.special.puntReturnYards += play.returnYards || 0;
          returnerEntry.special.puntReturnLong = Math.max(returnerEntry.special.puntReturnLong, play.returnYards || 0);
          if (isReturnTouchdown(play)) {
            returnerEntry.special.puntReturnTds += 1;
            returnerEntry.special.returnTds += 1;
          }
        }
        if (["tackle", "turnover-on-downs"].includes(play.stopReason)) {
          const stop = chooseReturnCoverageDefenders(offenseTeam, play, "punt-cover");
          addDefenderStop(snapshot, offenseTeam, stop.primary, stop.assist);
        }
      }
      return;
    }

    if (play.playId === "kickoff") {
      const kicker = rosterTopPlayers(offenseTeam, "K")[0] || resolveKicker(offenseTeam, play);
      const kickerEntry = ensureLiveStatsEntry(snapshot, offenseTeam, kicker);
      if (play.stopReason === "touchback" && kickerEntry) {
        kickerEntry.special.touchbacks += 1;
      }

      const returner = resolveKickReturner(defenseTeam, play);
      const returnerEntry = ensureLiveStatsEntry(snapshot, defenseTeam, returner);
      if (!["touchback", "dead-short"].includes(play.stopReason) && returnerEntry) {
        returnerEntry.special.kickReturns += 1;
        returnerEntry.special.kickReturnYards += play.returnYards || 0;
        returnerEntry.special.kickReturnLong = Math.max(returnerEntry.special.kickReturnLong, play.returnYards || 0);
        if (isReturnTouchdown(play)) {
          returnerEntry.special.kickReturnTds += 1;
          returnerEntry.special.returnTds += 1;
        }
      }

      if (play.stopReason === "tackle") {
        const stop = chooseReturnCoverageDefenders(offenseTeam, play, "kick-cover");
        addDefenderStop(snapshot, offenseTeam, stop.primary, stop.assist);
      }
    }
  }

  export function liveStatSnapshotsForGame(game: any) {
    if (!game) {
      return [];
    }
    if (game._liveStatSnapshots) {
      return game._liveStatSnapshots;
    }
    const snapshots = [];
    let running = emptyLiveStatsSnapshot();
    for (const play of game.plays || []) {
      running = cloneLiveStatsSnapshot(running);
      applyPlayToLiveStats(running, game, play);
      snapshots.push(running);
    }
    game._liveStatSnapshots = snapshots;
    return snapshots;
  }

  function liveLeadersForGameSide(game: any, side: "away" | "home", completedCount: number) {
    const team = game?.[side];
    const snapshots = liveStatSnapshotsForGame(game);
    const snapshotIndex = Math.max(0, Math.min(completedCount, snapshots.length) - 1);
    const snapshot = snapshots[snapshotIndex] || emptyLiveStatsSnapshot();
    const entries = activeEntriesForTeam(snapshot, team) as any[];
    const passing = entries
      .filter((entry: any) => entry.passing.attempts > 0)
      .sort((left: any, right: any) => right.passing.yards - left.passing.yards)[0];
    const rushing = entries
      .filter((entry: any) => entry.rushing.attempts > 0)
      .sort((left: any, right: any) => right.rushing.yards - left.rushing.yards)[0];
    const receiving = entries
      .filter((entry: any) => entry.receiving.catches > 0)
      .sort((left: any, right: any) => right.receiving.yards - left.receiving.yards)[0];
    const kicking = entries
      .filter((entry: any) => entry.special.fga > 0 || entry.special.pata > 0)
      .sort(
        (left: any, right: any) =>
          (right.special.fgm * 3 + right.special.patm) - (left.special.fgm * 3 + left.special.patm)
      )[0];

    return {
      passing: passing
        ? `${passing.name} ${passing.passing.completions}/${passing.passing.attempts}, ${passing.passing.yards} yds, ${passing.passing.tds} TD, ${passing.passing.interceptions} INT`
        : "No passing stats",
      rushing: rushing
        ? `${rushing.name} ${rushing.rushing.attempts} car, ${rushing.rushing.yards} yds, ${rushing.rushing.tds} TD`
        : "No rushing stats",
      receiving: receiving
        ? `${receiving.name} ${receiving.receiving.catches} rec, ${receiving.receiving.yards} yds, ${receiving.receiving.tds} TD`
        : "No receiving stats",
      kicking: kicking
        ? `${kicking.name} FG ${kicking.special.fgm}/${kicking.special.fga}, PAT ${kicking.special.patm}/${kicking.special.pata}`
        : "No kicking stats",
    };
  }

  export function finalLeaderRowsForGame(
    game: any,
    side: "away" | "home",
    completedCount = game?.plays?.length || 0,
  ) {
    const official = game?.leaders?.[side];
    const hasOfficialLeaders = official
      && [official.passing, official.rushing, official.receiving, official.kicking]
        .every((value) => typeof value === "string" && value.length > 0);
    const leaders = hasOfficialLeaders
      ? official
      : liveLeadersForGameSide(game, side, completedCount);

    return [
      ["Passing", leaders.passing],
      ["Rushing", leaders.rushing],
      ["Receiving", leaders.receiving],
      ["Kicking", leaders.kicking],
    ];
  }

  function completedPlayCount(now = performance.now()) {
    if (!state.game?.plays?.length) {
      return 0;
    }
    let completed = state.playIndex;
    const play = currentPlay();
    const _elapsed = state.autoplay ? now - state.lastAdvance : (state.pausedElapsed ?? 0);
    if (play && _elapsed > liveAdvanceDurationMs(play, state.playIndex)) {
      completed = Math.min(state.playIndex + 1, state.game.plays.length);
    }
    return Math.max(0, Math.min(completed, state.game.plays.length));
  }

  function applyLiveBroadcastSync(broadcast: LiveBroadcastSync | null, options: { force?: boolean; suppressOverlay?: boolean } = {}) {
    if (!broadcast || !state.game?.plays?.length || isDebugMode()) {
      return;
    }

    const now = performance.now();
    const maxPlayIndex = state.game.plays.length - 1;
    let nextPlayIndex = Math.max(0, Math.min(maxPlayIndex, Number(broadcast.currentPlayIndex || 0)));
    let nextElapsed = Math.max(0, Number(broadcast.currentPlayElapsedMs || 0));
    const status = broadcast.status || "live";
    const isFinal = status === "final";
    const localElapsed = state.autoplay ? Math.max(0, now - state.lastAdvance) : Math.max(0, state.pausedElapsed ?? 0);
    const previousStatus = state.liveSyncLastBroadcast?.status;
    const transitioningFromPregame = previousStatus === "scheduled" && status === "live";
    const currentQuarter = Number(state.game.plays[state.playIndex]?.quarter || 0);
    const serverQuarter = Number(state.game.plays[nextPlayIndex]?.quarter || 0);
    const enteringSecondHalf = !state.halftimeKickoffPresented
      && currentQuarter === 2
      && serverQuarter >= 3;
    if (transitioningFromPregame) {
      beginOpeningKickoffPresentation(now);
      nextPlayIndex = 0;
      nextElapsed = 0;
    } else if (enteringSecondHalf) {
      const kickoffIndex = halftimeKickoffIndex(state.game.plays);
      if (kickoffIndex >= 0 && kickoffIndex <= nextPlayIndex) {
        const kickoff = state.game.plays[kickoffIndex];
        state.halftimeKickoffPresented = true;
        state.liveSyncKickoffGraceUntil = now + kickoffSyncGraceMs(
          liveAdvanceDurationMs(kickoff, kickoffIndex),
        );
        nextPlayIndex = kickoffIndex;
        nextElapsed = 0;
      }
    } else if (
      status === "live"
      && Number.isFinite(state.liveSyncKickoffGraceUntil)
      && now < Number(state.liveSyncKickoffGraceUntil)
      && (
        nextPlayIndex > state.playIndex
        || (state.playIndex === 0 && nextElapsed > localElapsed)
      )
    ) {
      state.liveSyncLastBroadcast = broadcast;
      renderPregameOverlay();
      renderHalftimeCountdown();
      return;
    }
    const samePlay = state.playIndex === nextPlayIndex;
    const serverIsBehind = nextPlayIndex < state.playIndex || (samePlay && nextElapsed < localElapsed);
    const forwardDrift = Math.max(0, nextElapsed - localElapsed);
    const recoveringPlaybackHalt = Boolean(state.playbackHaltKey);
    const shouldResync = options.force
      || isFinal
      || recoveringPlaybackHalt
      || !state.autoplay
      || nextPlayIndex > state.playIndex;

    state.liveSyncLastBroadcast = broadcast;
    renderPregameOverlay();
    renderHalftimeCountdown();
    if (isFinal && showScheduledStatsPage(currentScheduledGameId())) {
      return;
    }
    if (!options.force && !isFinal && serverIsBehind && !recoveringPlaybackHalt) {
      return;
    }
    if (!shouldResync) {
      return;
    }

    if (!options.suppressOverlay && !transitioningFromPregame && status === "live" && (options.force || nextPlayIndex !== state.playIndex || forwardDrift > 2_500)) {
      const statusLine = nextPlayIndex > state.playIndex
        ? "Catching up to the current live play."
        : "Tightening the live broadcast clock.";
      showLiveSyncOverlay(statusLine);
    }

    state.playIndex = nextPlayIndex;
    state.lastAdvance = now - nextElapsed;
    state.pausedElapsed = status === "scheduled" ? 0 : null;
    state.autoplay = status === "live";

    if (isFinal) {
      const finalPlay = state.game.plays[maxPlayIndex];
      state.playIndex = maxPlayIndex;
      state.lastAdvance = now - timedPlayDurationMs(finalPlay, maxPlayIndex) - 1;
    }

    state.intermissionCameraStartX = null;
    state.intermissionKey = null;
    clearPlaybackHalt();
    clearVisualPlayerCache();
    resetFieldZoomState();
    renderPlayLog();
    renderAll();
    updatePlayPauseButton();
  }

  function renderHalftimeCountdown(nowMs = Date.now()) {
    if (!refs.halftimeCountdown) {
      return;
    }
    const presentation = halftimeCountdownPresentation(state.liveSyncLastBroadcast?.halftime, nowMs, {
      secondHalfStarted: Number(currentPlay()?.quarter || 0) >= 3,
    });
    refs.halftimeCountdown.classList.toggle("visible", presentation.visible);
    refs.halftimeCountdown.setAttribute("aria-hidden", presentation.visible ? "false" : "true");
    if (!presentation.visible) {
      return;
    }
    refs.halftimeCountdownRing?.style.setProperty("--halftime-progress", presentation.progressPercent);
    if (refs.halftimeCountdownTime && refs.halftimeCountdownTime.textContent !== presentation.timeLabel) {
      refs.halftimeCountdownTime.textContent = presentation.timeLabel;
    }
    if (refs.halftimeCountdownPhase && refs.halftimeCountdownPhase.textContent !== presentation.phaseLabel) {
      refs.halftimeCountdownPhase.textContent = presentation.phaseLabel;
    }
    refs.halftimeCountdown.setAttribute(
      "aria-label",
      `Halftime ${presentation.timeLabel}. ${presentation.phaseLabel.toLowerCase()}.`,
    );
  }

  async function pollLiveBroadcastSync(gameId: string) {
    if (state.liveSyncInFlight || isDebugMode()) {
      return;
    }

    state.liveSyncInFlight = true;
    try {
      const payload = await fetchJson(liveBroadcastPollUrl(
        gameId,
        state.liveSyncLastBroadcast?.status,
      ));
      const nextPregameAuthorityVersion = String(payload.package?.pregameAuthorityVersion || '');
      const nextHalftimeAuthorityVersion = String(payload.package?.halftimeAuthorityVersion || '');
      if (
        nextPregameAuthorityVersion
        && nextPregameAuthorityVersion !== state.pregameAuthorityVersion
      ) {
        state.pregameAuthorityPending = false;
        const authoritativeGeneratedAt = String(payload.package?.generatedAt || '') || null;
        if (shouldReloadPregamePackage({
          hasLoadedGame: Boolean(state.game),
          loadedGeneratedAt: state.scheduledPackageGeneratedAt,
          authoritativeGeneratedAt,
        })) {
          await simulate();
          return;
        }
        // Promoting an unchanged warm replay only adds the authority marker.
        // Keep the already-loaded kickoff window and assets instead of pulling
        // the same heavy payload again at the one-minute boundary.
        state.pregameAuthorityVersion = nextPregameAuthorityVersion;
        state.scheduledPackageGeneratedAt = authoritativeGeneratedAt;
      }
      updateLiveBroadcastSlot(payload.schedule?.broadcastTiming);
      await preloadAdditionalGameAssets(state.game, payload.game);
      mergeScheduledGameWindow(payload.game);
      if (nextHalftimeAuthorityVersion) {
        state.halftimeAuthorityVersion = nextHalftimeAuthorityVersion;
      }
      applyLiveBroadcastSync(payload.broadcast);
      renderPregameOverlay();
    } catch (error: any) {
      if (error?.status === 425 && error?.retryable) {
        state.pregameAuthorityPending = true;
        state.autoplay = false;
        state.pausedElapsed = 0;
        state.liveSyncKickoffGraceUntil = null;
        showLiveSyncOverlay(
          "Applying the final roster and schema selections.",
          Math.max(6_000, Number(error.retryAfterMs || 1_500) + 1_000),
        );
        updatePlayPauseButton();
        return;
      }
      console.warn("Live broadcast sync failed", error);
    } finally {
      state.liveSyncInFlight = false;
    }
  }

  function mergeScheduledGameWindow(nextGame: any) {
    if (!state.game || !nextGame?.plays || !Array.isArray(nextGame.plays)) {
      return;
    }

    const teamsChanged = mergeScheduledGameTeams(state.game, nextGame);
    const currentPlays = state.game.plays || [];
    // A halftime owner-schema refresh can produce a different number of
    // second-half snaps. Keep the locked first-half indices, but let the server
    // resize the future window so stale precomputed plays cannot survive.
    currentPlays.length = nextGame.plays.length;
    let hydratedCurrentPlay = false;
    let mergedAnyPlay = false;
    nextGame.plays.forEach((nextPlay: any, index: number) => {
      if (!currentPlays[index]) {
        currentPlays[index] = {};
      }
      // Future plays beyond the lookahead arrive as empty {} skeletons — skip them
      // so we never clobber an already-hydrated play. Any other play carries full
      // data and must be merged: in-window run/pass plays include arcadeReplay.frames,
      // special-teams plays (kickoff/PAT/punt — which start new quarters) have no
      // frames yet still carry quarter/score/visual the HUD and renderer need, and
      // past plays carry metadata used for cumulative live stats.
      // Only merging frame-bearing plays left those as skeletons → "QUNDEFINED"
      // quarter and a 0-0 scoreboard when the playhead reached them.
      if (!nextPlay || typeof nextPlay !== "object" || Object.keys(nextPlay).length === 0) {
        return;
      }
      // Every nonempty play in the live window is a complete render payload.
      // Replace it atomically instead of spreading it over an older simulated
      // play: retained `_liveSequence`, replay, or result fields can otherwise
      // flash the kickoff package's future play during a halftime refresh.
      currentPlays[index] = { ...nextPlay };
      mergedAnyPlay = true;
      if (index === state.playIndex && nextPlay?.arcadeReplay?.frames) {
        hydratedCurrentPlay = true;
      }
    });
    // liveStatSnapshotsForGame memoizes its cumulative snapshots on the game object
    // and we mutate state.game.plays in place above, so the cache must be dropped
    // whenever new play metadata arrives — otherwise live stats freeze at the values
    // computed on the first render and never reflect later plays.
    if (mergedAnyPlay || teamsChanged) {
      delete state.game._liveStatSnapshots;
    }
    if (nextGame.schemaUsage) {
      state.game.schemaUsage = nextGame.schemaUsage;
    }
    if (hydratedCurrentPlay && currentPlay() && hasRawArcadeReplay(currentPlay())) {
      clearPlaybackHalt();
    }
  }

  function startLiveBroadcastSync(
    gameId: string,
    initialBroadcast?: LiveBroadcastSync | null,
    options: { preserveKickoffGrace?: boolean } = {},
  ) {
    const preservedKickoffGraceUntil = options.preserveKickoffGrace ? state.liveSyncKickoffGraceUntil : null;
    const preservedLastBroadcast = options.preserveKickoffGrace ? state.liveSyncLastBroadcast : null;
    stopLiveBroadcastSync();
    if (!gameId || isDebugMode()) {
      return;
    }

    if (options.preserveKickoffGrace) {
      state.liveSyncKickoffGraceUntil = preservedKickoffGraceUntil;
      state.liveSyncLastBroadcast = preservedLastBroadcast;
    }
    applyLiveBroadcastSync(initialBroadcast || null, {
      force: true,
      suppressOverlay: options.preserveKickoffGrace,
    });
    state.liveSyncTimer = setInterval(() => {
      void pollLiveBroadcastSync(gameId);
    }, LIVE_SYNC_POLL_MS);
  }

  function pollVisibleScheduledBroadcast() {
    const gameId = currentScheduledGameId();
    if (
      !gameId
      || !state.game
      || !state.liveSyncTimer
      || (typeof document !== "undefined" && document.visibilityState === "hidden")
    ) {
      return;
    }
    void pollLiveBroadcastSync(gameId);
  }

  function activeEntriesForTeam(snapshot: any, team: any) {
    return Object.values(snapshot || {}).filter((entry: any) => entry.teamId === team.id);
  }

  function formatAverage(total: any, count: any) {
    return count > 0 ? (total / count).toFixed(1) : "0.0";
  }

  function passerRating(passing: any) {
    const attempts = passing.attempts || 0;
    if (!attempts) {
      return "0.0";
    }
    const a = Math.max(0, Math.min(2.375, ((passing.completions / attempts) - 0.3) * 5));
    const b = Math.max(0, Math.min(2.375, ((passing.yards / attempts) - 3) * 0.25));
    const c = Math.max(0, Math.min(2.375, (passing.tds / attempts) * 20));
    const d = Math.max(0, Math.min(2.375, 2.375 - (passing.interceptions / attempts) * 25));
    return ((((a + b + c + d) / 6) * 100) || 0).toFixed(1);
  }

  function statsPlayerLabel(entry: any) {
    return `#${String(entry.number).padStart(2, "0")} ${entry.name}`;
  }

  function renderStatsTable(headers: any, rows: any, emptyMessage = "No stats yet.") {
    if (!rows.length) {
      return `<div class="live-stats-empty">${escapeHtml(emptyMessage)}</div>`;
    }
    return `
      <table class="live-stats-table">
        <thead>
          <tr>${headers.map((header: any) => `<th>${escapeHtml(header)}</th>`).join("")}</tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (row: any) => `
                <tr>
                  <td class="live-stats-player">${escapeHtml(row.player)}</td>
                  ${row.values.map((value: any) => `<td>${escapeHtml(value)}</td>`).join("")}
                </tr>
              `
            )
            .join("")}
        </tbody>
      </table>
    `;
  }

  function buildPassingRows(entries: any) {
    return entries
      .filter((entry: any) => entry.passing.attempts > 0 || entry.passing.sacks > 0)
      .sort(
        (left: any, right: any) =>
          right.passing.yards - left.passing.yards ||
          right.passing.completions - left.passing.completions
      )
      .map((entry: any) => ({
        player: statsPlayerLabel(entry),
        values: [
          `${entry.passing.completions}/${entry.passing.attempts}`,
          String(entry.passing.yards),
          String(entry.passing.tds),
          String(entry.passing.interceptions),
          String(entry.passing.sacks),
          passerRating(entry.passing),
        ],
      }));
  }

  function buildRushingRows(entries: any) {
    return entries
      .filter((entry: any) => entry.rushing.attempts > 0)
      .sort(
        (left: any, right: any) =>
          right.rushing.yards - left.rushing.yards ||
          right.rushing.attempts - left.rushing.attempts
      )
      .map((entry: any) => ({
        player: statsPlayerLabel(entry),
        values: [
          String(entry.rushing.attempts),
          String(entry.rushing.yards),
          formatAverage(entry.rushing.yards, entry.rushing.attempts),
          String(entry.rushing.tds),
          String(entry.rushing.long),
        ],
      }));
  }

  function buildReceivingRows(entries: any) {
    return entries
      .filter((entry: any) => entry.receiving.targets > 0 || entry.receiving.catches > 0)
      .sort(
        (left: any, right: any) =>
          right.receiving.yards - left.receiving.yards ||
          right.receiving.catches - left.receiving.catches
      )
      .map((entry: any) => ({
        player: statsPlayerLabel(entry),
        values: [
          String(entry.receiving.catches),
          String(entry.receiving.yards),
          formatAverage(entry.receiving.yards, Math.max(1, entry.receiving.catches)),
          String(entry.receiving.tds),
          String(entry.receiving.long),
          String(entry.receiving.targets),
        ],
      }));
  }

  function buildDefenseRows(entries: any) {
    return entries
      .filter(
        (entry: any) =>
          entry.defense.tackles > 0 ||
          entry.defense.assists > 0 ||
          entry.defense.sacks > 0 ||
          entry.defense.interceptions > 0 ||
          entry.defense.forcedFumbles > 0 ||
          entry.defense.fumbleRecoveries > 0
      )
      .sort(
        (left: any, right: any) =>
          (right.defense.tackles + right.defense.assists + right.defense.sacks * 2 + right.defense.interceptions * 3) -
          (left.defense.tackles + left.defense.assists + left.defense.sacks * 2 + left.defense.interceptions * 3)
      )
      .map((entry: any) => ({
        player: statsPlayerLabel(entry),
        values: [
          `${entry.defense.tackles}/${entry.defense.assists}`,
          entry.defense.sacks.toFixed(1),
          String(entry.defense.interceptions),
          String(entry.defense.forcedFumbles),
          String(entry.defense.fumbleRecoveries),
        ],
      }));
  }

  function formatPercent(made: any, attempts: any) {
    return attempts > 0 ? ((made / attempts) * 100).toFixed(1) : "-";
  }

  function formatStatLong(value: any) {
    return value > 0 ? String(value) : "-";
  }

  function buildKickReturnRows(entries: any) {
    return entries
      .filter((entry: any) => entry.special.kickReturns > 0 || entry.special.kickReturnYards > 0 || entry.special.kickReturnTds > 0)
      .sort((left: any, right: any) => right.special.kickReturnYards - left.special.kickReturnYards || right.special.kickReturns - left.special.kickReturns)
      .map((entry: any) => ({
        player: statsPlayerLabel(entry),
        values: [
          String(entry.special.kickReturns),
          String(entry.special.kickReturnYards),
          formatAverage(entry.special.kickReturnYards, entry.special.kickReturns),
          String(entry.special.kickReturnTds),
          formatStatLong(entry.special.kickReturnLong),
        ],
      }));
  }

  function buildPuntReturnRows(entries: any) {
    return entries
      .filter((entry: any) => entry.special.puntReturns > 0 || entry.special.puntReturnYards > 0 || entry.special.puntReturnTds > 0)
      .sort((left: any, right: any) => right.special.puntReturnYards - left.special.puntReturnYards || right.special.puntReturns - left.special.puntReturns)
      .map((entry: any) => ({
        player: statsPlayerLabel(entry),
        values: [
          String(entry.special.puntReturns),
          String(entry.special.puntReturnYards),
          formatAverage(entry.special.puntReturnYards, entry.special.puntReturns),
          String(entry.special.puntReturnTds),
          formatStatLong(entry.special.puntReturnLong),
        ],
      }));
  }

  function buildKickingRows(entries: any) {
    return entries
      .filter((entry: any) => entry.special.fga > 0 || entry.special.pata > 0)
      .sort((left: any, right: any) => (right.special.fgm * 3 + right.special.patm) - (left.special.fgm * 3 + left.special.patm) || right.special.fieldGoalLong - left.special.fieldGoalLong)
      .map((entry: any) => ({
        player: statsPlayerLabel(entry),
        values: [
          entry.special.fga > 0 ? `${entry.special.fgm}/${entry.special.fga}` : "-",
          formatPercent(entry.special.fgm, entry.special.fga),
          formatStatLong(entry.special.fieldGoalLong),
          entry.special.pata > 0 ? `${entry.special.patm}/${entry.special.pata}` : "-",
          String(entry.special.fgm * 3 + entry.special.patm),
        ],
      }));
  }

  function buildPuntingRows(entries: any) {
    return entries
      .filter((entry: any) => entry.special.punts > 0)
      .sort((left: any, right: any) => right.special.puntYards - left.special.puntYards || right.special.punts - left.special.punts)
      .map((entry: any) => ({
        player: statsPlayerLabel(entry),
        values: [
          String(entry.special.punts),
          String(entry.special.puntYards),
          formatAverage(entry.special.puntYards, entry.special.punts),
          String(entry.special.puntInside20),
          String(entry.special.puntTouchbacks),
          formatStatLong(entry.special.puntLong),
        ],
      }));
  }
  function renderLiveStatsTableCard(team: any, rows: any, headers: any, emptyMessage: any, side: "away" | "home") {
    return `
      <article class="live-stats-team ${state.selectedLiveStatsTeam === side ? "active" : ""}" data-live-stats-card="${side}">
        <div class="live-stats-team-head">
          <strong>${escapeHtml(team.displayName || team.name || team.abbr)}</strong>
          <span>${escapeHtml(team.abbr)}</span>
        </div>
        ${renderStatsTable(headers, rows, emptyMessage)}
      </article>
    `;
  }

  function renderLiveStatsColumnsHeader(game: any) {
    const selected = state.selectedLiveStatsTeam;
    return `
      <div class="live-stats-team-banner">
        <button class="live-stats-team-label ${selected === "away" ? "active" : ""}" type="button" data-live-stats-team="away" aria-pressed="${selected === "away"}">
          <img class="live-stats-team-label-logo" src="${escapeHtml(broadcastTeamLogoSrc(game.away))}" alt="" />
          <strong>${escapeHtml(game.away.displayName)}</strong><span>${escapeHtml(game.away.abbr)}</span>
        </button>
        <button class="live-stats-team-label ${selected === "home" ? "active" : ""}" type="button" data-live-stats-team="home" aria-pressed="${selected === "home"}">
          <img class="live-stats-team-label-logo" src="${escapeHtml(broadcastTeamLogoSrc(game.home))}" alt="" />
          <strong>${escapeHtml(game.home.displayName)}</strong><span>${escapeHtml(game.home.abbr)}</span>
        </button>
      </div>
    `;
  }

  function renderLiveStatsSection(title: any, game: any, snapshot: any, headers: any, rowBuilder: any, emptyMessage: any) {
    const awayRows = rowBuilder(activeEntriesForTeam(snapshot, game.away));
    const homeRows = rowBuilder(activeEntriesForTeam(snapshot, game.home));
    return `
      <section class="live-stats-section">
        <h3>${escapeHtml(title)}</h3>
        <div class="live-stats-team-grid">
          ${renderLiveStatsTableCard(game.away, awayRows, headers, emptyMessage, "away")}
          ${renderLiveStatsTableCard(game.home, homeRows, headers, emptyMessage, "home")}
        </div>
      </section>
    `;
  }
  function renderLiveStats(completedCount = completedPlayCount()) {
    if (!state.game) {
      refs.liveStats!.innerHTML = "<p>Live player stats will appear here.</p>";
      return;
    }

    if (completedCount <= 0) {
      refs.liveStats!.innerHTML = `
        ${renderLiveStatsColumnsHeader(state.game)}
        <div class="live-stats-empty">Player stats begin updating after the first completed play.</div>
      `;
      return;
    }

    const snapshots = liveStatSnapshotsForGame(state.game);
    const snapshot = snapshots[Math.max(0, completedCount - 1)] || emptyLiveStatsSnapshot();
    refs.liveStats!.innerHTML = `
      ${renderLiveStatsColumnsHeader(state.game)}
      ${renderLiveStatsSection("Passing", state.game, snapshot, ["Player", "C/ATT", "YDS", "TD", "INT", "SCK", "RTG"], buildPassingRows, "No passing stats yet.")}
      ${renderLiveStatsSection("Rushing", state.game, snapshot, ["Player", "CAR", "YDS", "AVG", "TD", "LONG"], buildRushingRows, "No rushing stats yet.")}
      ${renderLiveStatsSection("Receiving", state.game, snapshot, ["Player", "REC", "YDS", "AVG", "TD", "LONG", "TGT"], buildReceivingRows, "No receiving stats yet.")}
      ${renderLiveStatsSection("Defense", state.game, snapshot, ["Player", "TCK/AST", "SACK", "INT", "FF", "FR"], buildDefenseRows, "No defensive stats yet.")}
      ${renderLiveStatsSection("Kick Returns", state.game, snapshot, ["Player", "RET", "YDS", "AVG", "TD", "LONG"], buildKickReturnRows, "No kick return stats yet.")}
      ${renderLiveStatsSection("Punt Returns", state.game, snapshot, ["Player", "RET", "YDS", "AVG", "TD", "LONG"], buildPuntReturnRows, "No punt return stats yet.")}
      ${renderLiveStatsSection("Kicking", state.game, snapshot, ["Player", "FG", "PCT", "LONG", "XP", "PTS"], buildKickingRows, "No kicking stats yet.")}
      ${renderLiveStatsSection("Punting", state.game, snapshot, ["Player", "NUM", "YDS", "AVG", "IN20", "TB", "LONG"], buildPuntingRows, "No punting stats yet.")}
    `;
  }
  function renderBoxScore() {
    if (!state.game) {
      refs.boxScore!.innerHTML = "<p>Box score will appear here.</p>";
      return;
    }

    // Live/scheduled broadcasts ship without final aggregates (teamStats/leaders/
    // highlights are stripped server-side so the outcome can't leak). For those
    // games the box score is served separately once the broadcast is final; until
    // the aggregates are present, render a placeholder instead of dereferencing them.
    if (!state.game.teamStats || !state.game.leaders || !state.game.highlights) {
      refs.boxScore!.innerHTML = "<p>Box score will appear when the game is final.</p>";
      return;
    }

    const renderStats = (label: any, stats: any) => `
      <section class="box-card">
        <h3>${escapeHtml(label)}</h3>
        <div class="stat-list">
          ${stats.map((item: any) => `<div class="stat-row"><span>${escapeHtml(item.label)}</span><strong>${escapeHtml(item.value)}</strong></div>`).join("")}
        </div>
      </section>
    `;

    const renderLeaders = (label: any, leaders: any) => `
      <section class="box-card">
        <h3>${escapeHtml(label)} Leaders</h3>
        <div class="leader-list">
          <div class="leader-item"><span>Passing</span><strong>${escapeHtml(leaders.passing)}</strong></div>
          <div class="leader-item"><span>Rushing</span><strong>${escapeHtml(leaders.rushing)}</strong></div>
          <div class="leader-item"><span>Receiving</span><strong>${escapeHtml(leaders.receiving)}</strong></div>
          <div class="leader-item"><span>Kicking</span><strong>${escapeHtml(leaders.kicking)}</strong></div>
        </div>
      </section>
    `;

    refs.boxScore!.innerHTML = `
      <div class="box-score-layout">
        <div class="box-columns">
          ${renderStats(state.game.home.abbr, state.game.teamStats.home)}
          ${renderStats(state.game.away.abbr, state.game.teamStats.away)}
        </div>
        <div class="box-columns">
          ${renderLeaders(state.game.home.abbr, state.game.leaders.home)}
          ${renderLeaders(state.game.away.abbr, state.game.leaders.away)}
        </div>
        <section class="box-card">
          <h3>Highlights</h3>
          <div class="highlight-list">
            ${state.game.highlights.map((item: any) => `<div class="highlight-item">${escapeHtml(item)}</div>`).join("")}
          </div>
        </section>
      </div>
    `;
  }

  function renderPlayLog() {
    if (!state.game) {
      refs.playLog!.innerHTML = "<p>Play log will appear here.</p>";
      return;
    }

    refs.playLog!.innerHTML = state.game.plays
      .map(
        (play: any, index: any) => {
          // Only show plays the broadcast has reached. Future plays are either
          // empty skeletons (no playLabel) or in-window plays that arrive
          // pre-hydrated with their result — rendering them would spoil the game.
          if (index > state.playIndex || !play?.playLabel) {
            return "";
          }
          return `
          <article class="play-row ${index === state.playIndex ? "active" : ""}">
            <div class="play-head">
              <strong>${escapeHtml(`Q${play.quarter} ${play.clock}`)}</strong>
              <span>${escapeHtml(`${playTeamInitials(state.game, play, "offense")} ${play.down > 0 ? formatCompactDownDistance(play) : "ST"} ${play.spot}`)}</span>
            </div>
            <div><strong>${escapeHtml(play.playLabel)}</strong></div>
            <div>${escapeHtml(play.result || "")}</div>
          </article>
        `;
        }
      )
      .join("");

    const active = refs.playLog!.querySelector(".play-row.active") as HTMLElement | null;
    if (active) {
      const padding = 24;
      const containerTop = refs.playLog!.scrollTop;
      const containerBottom = containerTop + refs.playLog!.clientHeight;
      const activeTop = active.offsetTop;
      const activeBottom = activeTop + active.offsetHeight;

      if (activeBottom > containerBottom - padding) {
        refs.playLog!.scrollTop = activeBottom - refs.playLog!.clientHeight + padding;
      } else if (activeTop < containerTop + padding) {
        refs.playLog!.scrollTop = Math.max(0, activeTop - padding);
      }
    }

    renderRecentPlaysStrip();
  }

  // Bottom-of-screen strip showing the last 5 *completed* plays. The play
  // currently in progress (index === state.playIndex) is excluded so the
  // result text never spoils the live animation.
  function renderRecentPlaysStrip() {
    if (!refs.recentPlaysStrip) {
      return;
    }
    if (!state.game) {
      refs.recentPlaysStrip.innerHTML = "";
      return;
    }

    const recent: any[] = [];
    for (let i = state.playIndex - 1; i >= 0 && recent.length < 5; i -= 1) {
      const play = state.game.plays[i];
      if (play?.playLabel) {
        recent.push(play);
      }
    }

    refs.recentPlaysStrip.innerHTML = recent
      .map((play: any) => {
        const situation = play.down > 0 ? formatCompactDownDistance(play) : "ST";
        const status = recentPlayStatus(play);
        return `
          <div class="recent-play">
            <span class="recent-play-status" style="color:${status.color};border-color:${status.color}">${escapeHtml(status.text)}</span>
            <span class="recent-play-meta">${escapeHtml(`${playTeamInitials(state.game, play, "offense")} ${situation}`)}</span>
            <span class="recent-play-label">${escapeHtml(play.playLabel)}</span>
            <span class="recent-play-result">${escapeHtml(play.result || "")}</span>
          </div>
        `;
      })
      .join("");
  }

  // Short, color-coded status for a completed play. Prefers the same ribbon
  // headline shown over the field (TOUCHDOWN, INTERCEPTION, SACK, …); falls
  // back to a yardage label so every chip carries a clear outcome.
  function recentPlayStatus(play: any): { text: string; color: string } {
    const ribbon = ribbonHeadlineForPlay(play);
    // Keep the meaningful headlines (TOUCHDOWN, INTERCEPTION, SACK, …) but not the
    // generic "COMPLETE" for a routine catch — this chip is a box score, so a
    // completed pass should read as the yardage gained, not just "complete".
    if (ribbon && ribbon.text !== "COMPLETE") {
      return { text: ribbon.text, color: ribbon.color };
    }
    if (play.turnover) {
      return { text: "TURNOVER", color: "#ff7aa8" };
    }
    // Punts and kickoffs gain no offensive yards, so the yardage fallback below
    // would read "NO GAIN" — label them by play type instead.
    if (play.playId === "punt") {
      return { text: "PUNT", color: "#dbe8ff" };
    }
    if (play.playId === "kickoff") {
      return { text: "KICKOFF", color: "#dbe8ff" };
    }
    const yards = Math.round(visualYardsForPlay(play));
    if (yards > 0) {
      return { text: `+${yards} YDS`, color: "#9cff8a" };
    }
    if (yards < 0) {
      return { text: `${yards} YDS`, color: "#ff8f6b" };
    }
    return { text: "NO GAIN", color: "#dbe8ff" };
  }

  function formatHudOrdinal(value: any) {
    const number = Number(value);
    if (number === 1) return "1ST";
    if (number === 2) return "2ND";
    if (number === 3) return "3RD";
    return `${number}TH`;
  }

  export function isGoalToGoPlay(play: any): boolean {
    const startYardLine = Number(play?.startYardLine);
    const distance = Number(play?.distance);
    return Number.isFinite(startYardLine)
      && Number.isFinite(distance)
      && distance > 0
      && startYardLine + distance >= 100;
  }

  export function formatHudDownDistance(play: any): string {
    const distance = isGoalToGoPlay(play) ? "GOAL" : play?.distance;
    return `${formatHudOrdinal(play?.down)} & ${distance}`;
  }

  function formatCompactDownDistance(play: any): string {
    const distance = isGoalToGoPlay(play) ? "GOAL" : play?.distance;
    return `${play?.down}&${distance}`;
  }

  function completedScoreForHud(completedCount = completedPlayCount()) {
    if (!state.game || completedCount <= 0) {
      return { home: 0, away: 0 };
    }
    const idx = Math.max(0, completedCount - 1);
    // Fall back to the last play that carries a score (not 0-0) so the scoreboard
    // never visibly resets when the play at `idx` lacks a score — e.g. an
    // un-hydrated live-window skeleton at a quarter break.
    return state.game.plays[idx]?.score || lastKnownScore(idx);
  }

  function broadcastTeamLogoSrc(team: any) {
    const src = homeFieldVisuals(team).logo.src || visualConfig.midfieldLogo.src;
    const normalized = normalizeAssetSrc(src);
    // normalizeAssetSrc already returns an absolute URL (a scheme URL in prod, or
    // a root-absolute "/assets/..." on localhost), so only prepend "/" for a bare
    // relative path — otherwise "/assets/..." would become "//assets/..." (a
    // protocol-relative URL pointing at host "assets") and the logo won't load.
    if (!normalized || /^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(normalized) || normalized.startsWith("/")) {
      return normalized;
    }
    return `/${normalized}`;
  }

  export function activeHudSchemas(game: any, team: any, quarter: number) {
    const teamId = canonicalTeamId(team?.id);
    const homeId = canonicalTeamId(game?.home?.id);
    const side = teamId && teamId === homeId ? "home" : "away";
    const usage = game?.schemaUsage?.[side];
    const halfUsage = Number(quarter) >= 3 ? usage?.secondHalf : usage?.firstHalf;
    return {
      offense: halfUsage?.offense || team?.identity?.offense || "balanced",
      defense: halfUsage?.defense || team?.identity?.defense || "front",
    };
  }

  function renderBroadcastHudTeam(
    team: any,
    hasBall: any,
    side: "away" | "home",
    schemas: { offense: string; defense: string } | null = null,
  ) {
    const colors = team?.colors || {};
    const primary = colors.primary || "#27406d";
    const secondary = colors.secondary || "#172339";
    const accent = colors.accent || "#f5c542";
    const city = team.city || String(team.displayName || "").split(" ").slice(0, -1).join(" ") || team.displayName || "";
    const name = team.name || String(team.displayName || "").split(" ").slice(-1).join(" ") || "";
    const cityLength = String(city).length;
    const nameLength = String(name).length;
    const citySize = cityLength >= 15 ? 0.54 : cityLength >= 12 ? 0.62 : cityLength >= 9 ? 0.7 : 0.8;
    const nameSize = nameLength >= 13 ? 0.86 : nameLength >= 10 ? 0.98 : nameLength >= 8 ? 1.08 : 1.24;
    const cityTracking = cityLength >= 12 ? 0.08 : cityLength >= 9 ? 0.12 : 0.18;
    const logo = `
      <span class="broadcast-team-logo-wrap">
        <img class="broadcast-team-logo" src="${escapeHtml(broadcastTeamLogoSrc(team))}" alt="" />
      </span>
    `;
    const copy = `
      <span class="broadcast-team-copy">
        <span class="broadcast-team-city">${escapeHtml(city)}</span>
        <span class="broadcast-team-name">${escapeHtml(name)}</span>
        ${schemas ? `<span class="broadcast-team-schemas">O ${escapeHtml(schemaDisplayName(schemas.offense))} · D ${escapeHtml(schemaDisplayName(schemas.defense))}</span>` : ""}
        ${schemas ? `<span class="broadcast-team-schemas broadcast-team-schemas-compact">O ${escapeHtml(schemaHudAbbreviation(schemas.offense))} &middot; D ${escapeHtml(schemaHudAbbreviation(schemas.defense))}</span>` : ""}
        ${hasBall ? '<span class="broadcast-possession-dot"></span>' : ""}
      </span>
    `;
    return `
      <div class="broadcast-team broadcast-team-${side}" style="--team-primary:${escapeHtml(primary)}; --team-secondary:${escapeHtml(secondary)}; --team-accent:${escapeHtml(accent)}; --team-city-size:${citySize}rem; --team-name-size:${nameSize}rem; --team-city-tracking:${cityTracking}em;">
        ${side === "home" ? copy + logo : logo + copy}
      </div>
    `;
  }

  function renderBroadcastHud(completedCount = completedPlayCount()) {
    if (!refs.broadcastHud) {
      return;
    }
    if (!state.game) {
      if (state.pregamePreview) {
        const kickoffLine = kickoffLabelFromBroadcast(state.liveSyncLastBroadcast);
        refs.broadcastHud.innerHTML = `
          <div class="broadcast-scorebar">
            <div class="broadcast-main-row">
              ${renderBroadcastHudTeam(state.pregamePreview.away, false, "away")}
              <div class="broadcast-score-center">
                <span class="broadcast-team-score broadcast-score-away">0</span>
                <span class="broadcast-team-score broadcast-score-home">0</span>
              </div>
              ${renderBroadcastHudTeam(state.pregamePreview.home, false, "home")}
            </div>
            <div class="broadcast-state">
              <span class="broadcast-state-kicker">PREGAME</span>
              <strong class="broadcast-game-clock">${escapeHtml(kickoffLine)}</strong>
              <strong class="broadcast-down-distance">KICKOFF</strong>
              <span class="broadcast-spot">LIVE SOON</span>
            </div>
          </div>
        `;
        return;
      }

      refs.broadcastHud.innerHTML = `
        <div class="broadcast-scorebar broadcast-scorebar-empty">
          <div class="broadcast-network">RGS</div>
          <div class="broadcast-state broadcast-state-wide">
            <span class="broadcast-state-kicker">PREVIEW</span>
            <strong class="broadcast-game-clock">15:00</strong>
            <span class="broadcast-down-distance">Awaiting Simulation</span>
          </div>
        </div>
      `;
      return;
    }

    const play = currentPlay() || state.game.plays[state.game.plays.length - 1] || null;
    const intermission = activePostPlayIntermission();
    const isFinal = completedCount >= state.game.plays.length;
    const score = completedScoreForHud(completedCount);
    const endedQuarter = Number(intermission?.quarter ?? play?.quarter ?? 1);
    const intermissionQuarterLine = endedQuarter >= 5 ? "OT" : `Q${endedQuarter}`;
    const intermissionStateLine = intermission?.kind === "quarter-break"
      ? `END Q${endedQuarter}`
      : intermission?.kind === "halftime"
        ? "HALFTIME"
        : intermission?.kind === "final"
          ? "FINAL"
          : null;
    const intermissionSpotLine = intermission?.kind === "quarter-break"
      ? "END OF QUARTER"
      : intermission?.kind === "halftime"
        ? "HALFTIME"
        : intermission?.kind === "final"
          ? "END OF GAME"
          : null;
    const awayHasBall = !isFinal && !intermission && !!play && playTeamMatches(play.offense, state.game.away);
    const homeHasBall = !isFinal && !intermission && !!play && playTeamMatches(play.offense, state.game.home);
    const stateLine = intermissionStateLine || (!play
      ? "Pregame"
      : play.down > 0
        ? formatHudDownDistance(play)
        : (play.playLabel || "").toUpperCase());
    const spotLine = intermissionSpotLine || play?.spot || "KICKOFF";
    const quarterLine = intermission ? intermissionQuarterLine : (!play ? "Q1" : play.quarter >= 5 ? "OT" : `Q${play.quarter}`);
    const clockLine = intermission ? "0:00" : (play?.clock || "15:00");
    const schemaQuarter = intermission?.kind === "halftime" ? 2 : Number(play?.quarter || 1);
    const awaySchemas = activeHudSchemas(state.game, state.game.away, schemaQuarter);
    const homeSchemas = activeHudSchemas(state.game, state.game.home, schemaQuarter);

    refs.broadcastHud.innerHTML = `
      <div class="broadcast-scorebar">
        <div class="broadcast-main-row">
          ${renderBroadcastHudTeam(state.game.away, awayHasBall, "away", awaySchemas)}
          <div class="broadcast-score-center">
            <span class="broadcast-team-score broadcast-score-away">${escapeHtml(score.away ?? 0)}</span>
            <span class="broadcast-team-score broadcast-score-home">${escapeHtml(score.home ?? 0)}</span>
          </div>
          ${renderBroadcastHudTeam(state.game.home, homeHasBall, "home", homeSchemas)}
        </div>
        ${isFinal && !intermission
          ? `<div class="broadcast-state broadcast-state-final"><strong class="broadcast-final-label">FINAL</strong></div>`
          : `<div class="broadcast-state">
              <span class="broadcast-state-kicker">${escapeHtml(quarterLine)}</span>
              <strong class="broadcast-game-clock">${escapeHtml(clockLine)}</strong>
              <strong class="broadcast-down-distance">${escapeHtml(stateLine)}</strong>
              <span class="broadcast-spot">${escapeHtml(spotLine)}</span>
            </div>`}
      </div>
    `;
  }

  // Milliseconds into the current play at which the live play phase begins.
  function livePhaseOffsetMs(play: any, playIndex: any) {
    const deadHoldMs = playIndex > 0 ? PLAY_TIMING.deadHoldMs : 0;
    const previousPlay = previousPlayAt(playIndex);
    if (isScrimmageSnap(play)) {
      const changeoverMs = scrimmageChangeoverMs(play, previousPlay);
      return deadHoldMs + changeoverMs + PLAY_TIMING.huddleRunMs + PLAY_TIMING.breakHuddleMs + PLAY_TIMING.preSnapHoldMs;
    }
    if (isSpecialTeamsPlay(play)) {
      const hasExit = previousPlay && !shouldSkipSpecialTeamsExit(previousPlay, play);
      return deadHoldMs + (hasExit ? PLAY_TIMING.specialTeamsExitMs : 0) + PLAY_TIMING.specialTeamsEnterMs + PLAY_TIMING.specialTeamsSetMs;
    }
    return deadHoldMs;
  }

  // Returns {base, result, resultOpacity} so base (location/down) never fades
  // while result fades independently.
  function playCaptionParts(now = performance.now()) {
    const play = currentPlay();
    if (!state.game || !play) {
      return { base: "Awaiting simulation data.", result: null, resultOpacity: 0 };
    }

    if (!isDebugMode() && state.liveSyncLastBroadcast?.status === "scheduled") {
      return { base: "Pregame | Broadcast begins at kickoff.", result: null, resultOpacity: 0 };
    }

    const intermission = activePostPlayIntermission(now);
    if (intermission?.kind === "quarter-break") {
      const score = intermission.score || lastKnownScore();
      return {
        base: `END OF ${String(quarterOrdinalLabel(intermission.quarter)).toUpperCase()} QUARTER | ${state.game.away.abbr} ${score.away ?? 0} - ${score.home ?? 0} ${state.game.home.abbr}`,
        result: null,
        resultOpacity: 0,
      };
    }

    if (intermission?.kind === "halftime") {
      const score = intermission.score || lastKnownScore();
      return { base: `HALFTIME | ${state.game.away.abbr} ${score.away ?? 0} - ${score.home ?? 0} ${state.game.home.abbr}`, result: null, resultOpacity: 0 };
    }

    if (intermission?.kind === "final") {
      const score = intermission.score || lastKnownScore();
      const base = intermission.winner
        ? `${intermission.winner.abbr} WINS! | FINAL ${state.game.away.abbr} ${score.away ?? 0} - ${score.home ?? 0} ${state.game.home.abbr}`
        : `FINAL | ${state.game.away.abbr} ${score.away ?? 0} - ${score.home ?? 0} ${state.game.home.abbr}`;
      return { base, result: null, resultOpacity: 0 };
    }

    const downdist = play.down > 0 ? formatCompactDownDistance(play) : "special teams";
    const base = `${playTeamInitials(state.game, play, "offense")} ${downdist} at ${play.spot}`;

    if (state.playbackHaltMessage) {
      return { base, result: state.playbackHaltMessage, resultOpacity: 1 };
    }

    // Paused: freeze whatever was visible at the moment of pause.
    if (!state.autoplay) {
      const paused = state.pausedElapsed ?? 0;
      const playDur = timedPlayDurationMs(play, state.playIndex);
      // Past play end → show current play's result; otherwise keep prevResult visible.
      const result = paused >= playDur ? (play.result || null) : (state.prevResult || null);
      return { base, result, resultOpacity: 1 };
    }

    // Hold/replay mode: show current play's result once the play has finished.
    if (state.playMode === "hold" || state.playMode === "replay") {
      const playDur = timedPlayDurationMs(play, state.playIndex);
      if (now - state.lastAdvance >= playDur) {
        return { base, result: play.result || null, resultOpacity: 1 };
      }
    }

    // Autoplay: result comes from the previous play (stored when playIndex advanced).
    // It stays at full opacity until the current play's live phase starts, then fades.
    if (!state.prevResult) {
      return { base, result: null, resultOpacity: 0 };
    }

    const livePhaseStartTime = state.lastAdvance + livePhaseOffsetMs(play, state.playIndex);
    const fadeElapsed = Math.max(0, now - livePhaseStartTime);
    const resultOpacity = fadeElapsed === 0 ? 1 : Math.max(0, 1 - fadeElapsed / PLAY_TIMING.resultFadeMs);

    if (resultOpacity <= 0) {
      return { base, result: null, resultOpacity: 0 };
    }
    return { base, result: state.prevResult, resultOpacity };
  }

  function updatePlayCaption(now = performance.now()) {
    const { base, result, resultOpacity } = playCaptionParts(now);

    if (result && resultOpacity > 0) {
      // During the fade, use innerHTML so only the result span has reduced opacity.
      refs.playCaption!.innerHTML = `${escapeHtml(base)}<span style="opacity:${resultOpacity.toFixed(3)}"> | ${escapeHtml(result)}</span>`;
      state.renderedCaption = null; // force textContent refresh once fade ends
    } else {
      if (refs.playCaption!.textContent !== base) {
        refs.playCaption!.textContent = base;
        state.renderedCaption = base;
      }
    }
  }

  function renderAll() {
    renderLiveStats();
    renderBoxScore();
    renderPlayLog();
    renderBroadcastHud(completedPlayCount());
    updatePlayCaption();
    renderStageLoading();
    renderPregameOverlay();
    renderHalftimeCountdown();
  }

  function teamTitleLabel(team: any, fallback: any) {
    return String(team?.abbr || team?.id || fallback || "").trim().toUpperCase();
  }

  function updateDocumentTitle(game: any = state.game) {
    const away = teamTitleLabel(game?.away, refs.awaySelect?.value || "Away");
    const home = teamTitleLabel(game?.home, refs.homeSelect?.value || "Home");
    document.title = away && home ? `DFL | ${away} @ ${home}` : "DFL | Simulator";
  }

  function pickRandomMatchup() {
    const teams = teamList();
    const home = teams[Math.floor(Math.random() * teams.length)];
    let away = teams[Math.floor(Math.random() * teams.length)];
    while (away.id === home.id) {
      away = teams[Math.floor(Math.random() * teams.length)];
    }
    refs.homeSelect!.value = home.id;
    refs.awaySelect!.value = away.id;
    refs.seedInput!.value = `SHOW-${Math.floor(Math.random() * 900 + 100)}`;
  }

  async function simulate(options: { loadingRequestId?: number } = {}) {
    const home = refs.homeSelect!.value;
    const away = refs.awaySelect!.value;
    const scheduledGameId = currentScheduledGameId();
    if (home === away) {
      refs.playCaption!.textContent = "Choose two different teams.";
      return;
    }

    const visualTest = visualTestPage ? currentVisualTestConfig() : null;
    const debugMode = isDebugMode();
    const requestId = options.loadingRequestId ?? beginStageLoading([
      {
        key: visualTestPage ? "visual-test" : "simulate",
        title: "Loading game...",
        status: visualTestPage
          ? `Building ${visualTest?.playId || "test"} for ${away} at ${home}.`
          : debugMode
            ? `Simulating ${away} at ${home}.`
            : `Preparing ${away} at ${home}.`,
      },
      {
        key: "assets",
        title: "Loading game...",
        status: "Preparing the field and players.",
      },
    ]);
    const pregameCurtainActive = await showPregameLoadingCurtain();
    if (!options.loadingRequestId) {
      setStageLoadingPhase(requestId, 0);
    }

    let payload;
    if (visualTestPage) {
      refs.playCaption!.textContent = "Rendering visual test...";
      const fetchStartedAt = performance.now();
      try {
        const controller = createStageLoadingController(requestId);
        payload = await fetchJson(
          `/api/visual-test?home=${encodeURIComponent(home)}`
          + `&away=${encodeURIComponent(away)}`
          + `&playId=${encodeURIComponent(visualTest!.playId)}`
          + `&yards=${encodeURIComponent(String(visualTest!.yards))}`
          + `&engine=${encodeURIComponent(visualTest!.engine)}`
          + `&arcadeSeed=${encodeURIComponent(visualTest!.arcadeSeed || "")}`
          + `&arcadeView=${encodeURIComponent(visualTest!.arcadeView)}`
          + `&passOutcome=${encodeURIComponent(visualTest!.passOutcome)}`
          + `&turnoverOutcome=${encodeURIComponent(visualTest!.turnoverOutcome)}`
          + `&onsideOutcome=${encodeURIComponent(visualTest!.onsideOutcome)}`
          + `&celebrationStyle=${encodeURIComponent(visualTest!.celebrationStyle || "")}`,
          controller ? { signal: controller.signal } : {},
        );
        rememberLoadDuration("visual-test", performance.now() - fetchStartedAt);
      } catch (error: any) {
        if (isAbortError(error)) {
          return;
        }
        failStageLoading(requestId, reportClientError("visual-test", error, "Could not render the visual test"));
        throw error;
      }
    } else {
      refs.playCaption!.textContent = pregameCurtainActive
        ? "Pregame | Broadcast begins at kickoff."
        : debugMode ? "Simulating full game..." : "Preparing broadcast...";
      const fetchStartedAt = performance.now();
      try {
        const controller = createStageLoadingController(requestId);
        const debugParam = debugMode ? "&debug=true" : "";
        const localParams = currentUrlParams();
        const localCoachingKeys = ["homePersonnel", "homeFocus", "homeDefense", "homeRbStyle", "homeStyleSet", "awayPersonnel", "awayFocus", "awayDefense", "awayRbStyle", "awayStyleSet"];
        const localCoachingParam = localCoachingKeys
          .filter((key) => localParams.get(key))
          .map((key) => `&${key}=${encodeURIComponent(localParams.get(key) || "")}`)
          .join("");
        const localPreviewParam = !scheduledGameId && localParams.get("preview") === "true"
          ? `&preview=true&previewPlays=${encodeURIComponent(localParams.get("previewPlays") || "12")}${localCoachingParam}`
          : "";
        const simulationUrl = scheduledGameId
          ? `/api/games/${encodeURIComponent(scheduledGameId)}${debugMode ? "?debug=true" : ""}`
          : `/api/simulate?home=${encodeURIComponent(home)}&away=${encodeURIComponent(away)}&seed=${encodeURIComponent(refs.seedInput!.value || Date.now())}${debugParam}${localPreviewParam}`;
        const requestOptions = controller ? { signal: controller.signal } : {};
        payload = scheduledGameId && !debugMode
          ? await fetchScheduledGamePackage(simulationUrl, requestOptions)
          : await fetchJson(simulationUrl, requestOptions);
        if (payload.package?.pregameAuthorityVersion) {
          state.pregameAuthorityPending = false;
          state.pregameAuthorityVersion = String(payload.package.pregameAuthorityVersion);
        } else if (scheduledGameId && !debugMode) {
          state.pregameAuthorityVersion = null;
        }
        if (scheduledGameId && !debugMode) {
          state.scheduledPackageGeneratedAt = String(payload.package?.generatedAt || '') || null;
        }
        rememberLoadDuration("simulate", performance.now() - fetchStartedAt);
      } catch (error: any) {
        if (isAbortError(error)) {
          return;
        }
        failStageLoading(requestId, reportClientError("simulate", error, "Could not load the game"));
        throw error;
      }
    }
    if (scheduledGameId && !debugMode && payload.broadcast?.status === "final") {
      console.log(payload);
      showScheduledStatsPage(scheduledGameId);
      return;
    }
    if (!isCurrentLoadingRequest(requestId)) {
      return;
    }
    if (payload.schedule) {
      updateLiveBroadcastSlot(payload.schedule.broadcastTiming);
      refs.homeSelect!.value = payload.schedule.home;
      refs.awaySelect!.value = payload.schedule.away;
      refs.seedInput!.value = payload.schedule.seed;
      state.pregamePreview = {
        away: previewTeamById(payload.schedule.away),
        home: previewTeamById(payload.schedule.home),
      };
      warmPregameLogos();
      renderPregameOverlay();
    }
    setStageLoadingPhaseByKey(requestId, "assets");
    await preloadGameAssets(payload.game, requestId);
    if (!isCurrentLoadingRequest(requestId)) {
      return;
    }
    state.game = refreshGameBranding(payload.game);
    updateDocumentTitle(state.game);
    const broadcast = payload.broadcast;
    const publicScheduledStatus = scheduledGameId && !debugMode ? broadcast?.status : null;
    const preloadedKickoffStart = !debugMode && shouldPreservePregameKickoff({
      viewerGameId: state.pregameViewerGameId,
      currentGameId: scheduledGameId,
      kickoffPresented: state.pregameKickoffPresented,
      broadcastStatus: publicScheduledStatus,
    });
    const now = performance.now();
    const livePlayIndex = scheduledGameId && !debugMode && broadcast
      ? preloadedKickoffStart
        ? 0
        : Math.max(0, Math.min((payload.game?.plays?.length ?? 1) - 1, Number(broadcast.currentPlayIndex || 0)))
      : 0;
    state.playIndex = livePlayIndex;
    state.lastAdvance = scheduledGameId && !debugMode && broadcast
      ? preloadedKickoffStart
        ? now
        : now - Math.max(0, Number(broadcast.currentPlayElapsedMs || 0))
      : now;
    state.pausedElapsed = publicScheduledStatus === "scheduled" ? 0 : null;
    state.autoplay = publicScheduledStatus ? publicScheduledStatus === "live" : true;
    if (preloadedKickoffStart) {
      state.liveSyncKickoffGraceUntil = now + openingKickoffGraceMs();
      state.liveSyncOverlayUntil = null;
    }
    state.cameraX = null;
    state.cameraVelocityX = 0;
    state.cameraUpdatedAt = null;
    resetFieldZoomState();
    state.renderedStatsCount = -1;
    state.renderedPlayIndex = -1;
    state.renderedCaption = "";
    state.prevResult = null;
    state.playedSoundEffects.clear();
    clearVisualPlayerCache();
    renderAll();
    if (scheduledGameId && !debugMode) {
      startLiveBroadcastSync(scheduledGameId, broadcast || null, {
        preserveKickoffGrace: preloadedKickoffStart,
      });
      if (preloadedKickoffStart) {
        state.pregameKickoffPresented = true;
      }
    } else {
      stopLiveBroadcastSync();
    }
    playAmbientCrowdAudio();
    playMatchMusicAudio();
    updateUrl();
    if (!options.loadingRequestId) {
      finishStageLoading(requestId);
    }
  }

  function hasActiveDivingCatch(frame: any) {
    return [...(frame?.offensePlayers || []), ...(frame?.defensePlayers || [])].some((player: any) => {
      const assignment = String(player?.arcadeAssignment || "");
      return assignment === "catch-dive-launch"
        || assignment === "catch-dive-extension"
        || assignment === "catch-dive-contact"
        || assignment === "catch-dive-landing";
    });
  }

  function rawArcadePassTracksAirborneBall(frame: any) {
    const rawArcadePass = frame?.play?.arcadeReplay?.source === "arcade-sim-v1"
      && frame?.play?.arcadePlaybackMode === "raw"
      && frame?.play?.family === "pass";
    return rawArcadePass && (
      frame?.ball?.arcadeState === "pass-flight"
      || hasActiveDivingCatch(frame)
    );
  }

  function isSpecialTeamsReturnPossession(frame: any) {
    const specialTeamsPlay = frame?.play?.playId === "kickoff" || frame?.play?.playId === "punt";
    return specialTeamsPlay
      && (
        String(frame?.ball?.arcadeOwnerRole || "").startsWith("RETURNER")
        || frame?.ball?.arcadeState === "carried"
      );
  }

  function isSpecialTeamsCameraChangeover(frame: any) {
    const specialTeamsPlay = frame?.play?.playId === "kickoff" || frame?.play?.playId === "punt";
    const scenePhase = String(frame?.scenePhase || "");
    return specialTeamsPlay
      && (
        scenePhase === "special-exit"
        || scenePhase === "special-entry"
        || scenePhase === "special-set"
      );
  }

  function specialTeamsCameraLookAhead(frame: any) {
    const direction = Number(frame?.direction) || 1;
    if (isSpecialTeamsReturnPossession(frame)) {
      return -direction * 62;
    }
    const ballState = String(frame?.ball?.arcadeState || "");
    if (ballState === "kick-flight" || ballState === "onside-chip" || ballState === "kick-bounce") {
      return direction * 48;
    }
    return direction * 68;
  }

  function cameraTargetX(frame: any) {
    const metrics = fieldMetrics();
    if (frame.play.playId === "field-goal" || frame.play.playId === "pat") {
      const losX = Number.isFinite(frame.losX) ? frame.losX : frame.ball.ballX;
      return kickAttemptCameraTargetX(losX, frame.direction, refs.canvas!.width);
    }
    if (frame.play.playId === "punt") {
      return puntBallCameraTargetX(frame.ball.ballX, refs.canvas!.width, metrics.worldWidth);
    }
    const rawArcadeReplay = frame.play?.arcadeReplay?.source === "arcade-sim-v1"
      && frame.play?.arcadePlaybackMode === "raw";
    const rawArcadeRun = rawArcadeReplay && frame.play?.family === "run";
    const rawArcadePass = rawArcadeReplay && frame.play?.family === "pass";
    const rawArcadePassFlight = rawArcadePassTracksAirborneBall(frame);
    const specialTeamsPlay = frame.play.playId === "kickoff" || frame.play.playId === "punt";
    const lookAhead =
      specialTeamsPlay
        ? specialTeamsCameraLookAhead(frame)
        : rawArcadePass
          ? frame.direction * (rawArcadePassFlight ? 76 : 46)
        : frame.play.family === "pass"
          ? frame.direction * 112
          : frame.direction * (rawArcadeRun ? 58 : 72);
    const target = frame.ball.ballX + lookAhead - refs.canvas!.width * 0.5;
    return Math.max(0, Math.min(metrics.worldWidth - refs.canvas!.width, target));
  }

  function targetActionZoomScale(frame: any, cameraX: number) {
    if (frame?.play?.playId === "field-goal" || frame?.play?.playId === "pat") {
      const losX = Number.isFinite(frame.losX) ? frame.losX : frame.ball.ballX;
      return kickAttemptZoomScale(
        frame.play?.fieldGoal?.distance,
        frame.play?.playId,
        losX,
        frame.direction,
        cameraX,
        refs.canvas!.width,
      );
    }
    if (!frame?.play || !isScrimmageSnap(frame.play)) {
      return 1;
    }

    if (String(frame.scenePhase || "") !== "live") {
      return 1;
    }
    const snapRamp = easeInOut(clamp01((frame.liveProgress ?? 0) / 0.2));
    if (frame.heroMoment) {
      const heroIntensity = clamp01(Number(frame.heroMoment.intensity || 0));
      return lerp(1, Number(frame.heroMoment.zoomScale || 1.34), 0.72 + heroIntensity * 0.28);
    }
    if (frame.play.family === "run") {
      return lerp(1, 1.18, snapRamp);
    }
    if (frame.play.family === "pass") {
      return lerp(1, rawArcadePassTracksAirborneBall(frame) ? 1.13 : 1.17, snapRamp);
    }
    return lerp(1, 1.12, snapRamp);
  }

  function targetActionZoomFocus(frame: any, cameraX: number, ballX: number, ballY: number, metrics: any) {
    if (frame?.play?.playId === "field-goal" || frame?.play?.playId === "pat") {
      const losX = Number.isFinite(frame.losX) ? frame.losX : frame.ball.ballX;
      return {
        x: kickAttemptZoomFocusX(losX, frame.direction, cameraX, refs.canvas!.width),
        y: snapToPixel(clampRange(lerp(metrics.centerY, ballY + 4, 0.22), metrics.fieldTop + 64, metrics.fieldBottom - 40)),
      };
    }
    if (frame?.heroMoment?.role) {
      const heroPlayers = frame.heroMoment.teamType === "defense"
        ? frame.defensePlayers
        : frame.offensePlayers;
      const heroPlayer = heroPlayers?.find?.((player: any) => player.role === frame.heroMoment.role);
      if (heroPlayer) {
        return {
          x: snapToPixel(clampRange(
            regularPlayerVisualCenterX(
              worldToScreenX(heroPlayer.x, cameraX),
              heroPlayer.spriteFacing || (frame.heroMoment.teamType === "defense" ? -frame.direction : frame.direction),
              frame.heroMoment.teamType,
              heroPlayer.role || null,
              !!heroPlayer.moving,
            ),
            refs.canvas!.width * 0.3,
            refs.canvas!.width * 0.7,
          )),
          y: snapToPixel(clampRange(heroPlayer.y + 7, metrics.fieldTop + 62, metrics.fieldBottom - 34)),
        };
      }
    }
    const unownedBall = !frame?.ball?.arcadeOwnerRole;
    const followY = unownedBall ? 0.2 : 0.38;
    return {
      x: snapToPixel(clampRange(ballX + 4, refs.canvas!.width * 0.26, refs.canvas!.width * 0.74)),
      y: snapToPixel(clampRange(lerp(metrics.centerY, ballY + 3, followY), metrics.fieldTop + 56, metrics.fieldBottom - 30)),
    };
  }

  function resolveActionZoom(frame: any, cameraX: number, ballX: number, ballY: number, metrics: any) {
    const targetScale = targetActionZoomScale(frame, cameraX);
    const targetFocus = targetActionZoomFocus(frame, cameraX, ballX, ballY, metrics);
    const actionActive = targetScale > 1.001;
    const releasingZoom = !actionActive && state.fieldZoom > 1.001;
    const heroMomentActive = !!frame?.heroMoment;
    const focusLerp = actionActive ? (heroMomentActive ? 0.22 : 0.15) : 0.06;
    const zoomLerp = actionActive
      ? (targetScale > state.fieldZoom ? (heroMomentActive ? 0.18 : 0.12) : 0.08)
      : releasingZoom ? 0.045 : 0.12;

    if (!Number.isFinite(state.zoomFocusX) || !Number.isFinite(state.zoomFocusY)) {
      state.zoomFocusX = targetFocus.x;
      state.zoomFocusY = targetFocus.y;
    } else if (actionActive) {
      state.zoomFocusX = snapToPixel(lerp(state.zoomFocusX!, targetFocus.x, focusLerp));
      state.zoomFocusY = snapToPixel(lerp(state.zoomFocusY!, targetFocus.y, focusLerp));
    } else if (!releasingZoom) {
      state.zoomFocusX = targetFocus.x;
      state.zoomFocusY = targetFocus.y;
    }

    state.fieldZoom = Math.abs(targetScale - state.fieldZoom) < 0.001
      ? targetScale
      : lerp(state.fieldZoom, targetScale, zoomLerp);
    if (targetScale === 1 && Math.abs(state.fieldZoom - 1) < 0.001) {
      state.fieldZoom = 1;
    }

    return {
      scale: state.fieldZoom,
      focusX: state.zoomFocusX ?? targetFocus.x,
      focusY: state.zoomFocusY ?? targetFocus.y,
    };
  }

  function applyActionZoom(zoom: { scale: number; focusX: number; focusY: number }) {
    if (!zoom || Math.abs((zoom.scale || 1) - 1) < 0.001) {
      return;
    }
    ctx.translate(zoom.focusX, zoom.focusY);
    ctx.scale(zoom.scale, zoom.scale);
    ctx.translate(-zoom.focusX, -zoom.focusY);
  }

  function specialTeamsFlightWindow(play: any) {
    if (!play) {
      return null;
    }
    if (play.playId === "kickoff") {
      return kickoffFlightWindow(play);
    }
    if (play.playId === "punt") {
      return puntFlightWindow(play);
    }
    return null;
  }

  function shouldSmoothSpecialTeamsFlight(frame: any) {
    const flightWindow = specialTeamsFlightWindow(frame?.play);
    return !!frame
      && !!flightWindow
      && frame.scenePhase === "live"
      && (frame.liveProgress ?? 0) >= flightWindow.start
      && (frame.liveProgress ?? 0) < flightWindow.end;
  }

export function animateRegularPlayerState(variantName: any, now = performance.now()) {
  const normalized = String(variantName || "");
  if (!normalized) {
    return normalized;
  }

  const directionSuffix = normalized.endsWith("Left") ? "Left" : (normalized.endsWith("Right") ? "Right" : "");
  const base = directionSuffix ? normalized.slice(0, -directionSuffix.length) : normalized;
  if (base === "idle") {
    const tick2 = Math.floor(now / 240) % 2;
    return `idle${tick2 + 1}${directionSuffix}`;
  }
  if (base === "run") {
    const tick3 = Math.floor(now / 120) % 3;
    return `run${tick3 + 1}${directionSuffix}`;
  }
  return normalized;
}

export function resolveRegularPlayerSpriteVariant(variantName: any, facing: any) {
  const normalized = String(variantName || "");
  if (normalized.endsWith("Left")) {
    return {
      variantName: normalized.replace(/Left$/, "Right"),
      flipX: true,
    };
  }
  if (normalized.endsWith("Right")) {
    return {
      variantName: normalized,
      flipX: false,
    };
  }
  return {
    variantName: facing > 0 ? "idleRight" : "idleRight",
    flipX: facing < 0,
  };
}

function drawPixelSprite(x: any, y: any, palette: any, facing = 1, moving = false, stateName = null, player: any = null, frame: any = null, teamType = "offense") {
  const rawVariantName = stateName || (moving ? (facing > 0 ? "runRight" : "runLeft") : (facing > 0 ? "idleRight" : "idleLeft"));
  const animatedVariantName = animateRegularPlayerState(rawVariantName);
  const regularSprite = resolveRegularPlayerSpriteVariant(animatedVariantName, facing);
  const spriteOriginX = regularPlayerSpriteOriginX(x, facing, teamType, player?.role || null, !!moving);
  drawNftAuraUnderlay(
    palette,
    regularSprite.variantName,
    spriteOriginX,
    y - 18,
    PLAYER_SPRITE_DRAW_SIZE,
    PLAYER_SPRITE_DRAW_SIZE,
    regularSprite.flipX,
  );
  const usedSheet = drawMaskSpriteFrame(
    visualConfig.spriteSheets.players,
    regularSprite.variantName,
    palette,
    spriteOriginX,
    y - 18,
    PLAYER_SPRITE_DRAW_SIZE,
    PLAYER_SPRITE_DRAW_SIZE,
    { cacheFrame: true, flipX: regularSprite.flipX }
  );

  if (usedSheet) {
    return;
  }

  const width = 14;
  const height = 18;
  const legOffset = moving ? 1 : 0;

  ctx.fillStyle = palette.shadow || "#111";
  ctx.fillRect(x, y, width, height);
  ctx.fillStyle = palette.helmet || palette.secondary;
  ctx.fillRect(x + 2, y + 1, 10, 4);
  ctx.fillStyle = palette.jersey || palette.primary;
  ctx.fillRect(x + 1, y + 5, 12, 7);
  ctx.fillStyle = palette.pants || palette.accent;
  ctx.fillRect(x + 2, y + 12, 10, 4);
  ctx.fillStyle = palette.skin || "#d0a17a";
  ctx.fillRect(x + (facing > 0 ? 11 : 1), y + 6, 1, 1);
  ctx.fillStyle = palette.accessories || palette.outline || "#111";
  ctx.fillRect(x + 3, y + 16, 2, 2);
  ctx.fillRect(x + 9, y + 16 - legOffset, 2, 2 + legOffset);
}

  export function resolveBallCarrierIndicator(frame: any) {
    if (!frame?.ball || frame.scenePhase !== "live") {
      return null;
    }

    const ownerRole = frame.ball.arcadeOwnerRole;
    if (!ownerRole) {
      return null;
    }

    const ownershipEntries = [
      { players: frame.offensePlayers || [], team: frame.offense, teamType: "offense" },
      { players: frame.defensePlayers || [], team: frame.defense, teamType: "defense" },
    ];
    const ownerEntry = ownershipEntries.find(({ players }) => players.some((player: any) => player.role === ownerRole));
    if (!ownerEntry) {
      return null;
    }

    const player = ownerEntry.players.find((candidate: any) => candidate.role === ownerRole);
    if (!player) {
      return null;
    }

    return {
      player,
      team: ownerEntry.team,
      teamType: ownerEntry.teamType,
      distance: 0,
      facing: player?.spriteFacing || (ownerEntry.teamType === "offense" ? frame.direction : -frame.direction),
    };
  }

  function ballCarrierIndicatorBaseline(player: any, teamType = "offense") {
    if (teamType === "offense" && player?.role === "QB") {
      // QB art sits higher inside the custom frame, so anchor the ring a touch closer to the body.
      return 18;
    }
    return 24;
  }

  function drawBallCarrierIndicator(indicator: any, cameraX = 0) {
    if (!indicator?.player) {
      return;
    }

    const accent = indicator.team?.colors?.accent || "#ffde59";
    const screenX = regularPlayerVisualCenterX(
      worldToScreenX(indicator.player.x, cameraX),
      indicator.facing || 1,
      indicator.teamType,
      indicator.player?.role || null,
      !!indicator.player?.moving,
    );
    const screenY = snapToPixel(
      indicator.player.y
      + (indicator.player.arcadeVerticalLift || 0)
      + ballCarrierIndicatorBaseline(indicator.player, indicator.teamType),
    );
    const pulse = (Math.sin(performance.now() / 160) + 1) * 0.5;
    const radiusX = 14 + pulse * 4;
    const radiusY = 6 + pulse * 1.5;

    ctx.save();
    ctx.beginPath();
    ctx.ellipse(screenX, screenY + 4, radiusX + 2, radiusY + 2, 0, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(2, 6, 12, 0.44)";
    ctx.fill();

    ctx.beginPath();
    ctx.ellipse(screenX, screenY + 4, radiusX, radiusY, 0, 0, Math.PI * 2);
    ctx.fillStyle = rgbaFromHex(accent, 0.18);
    ctx.fill();

    ctx.shadowColor = rgbaFromHex(accent, 0.58);
    ctx.shadowBlur = 10;
    ctx.lineWidth = 2;
    ctx.strokeStyle = rgbaFromHex(accent, 0.98);
    ctx.beginPath();
    ctx.ellipse(screenX, screenY + 4, radiusX, radiusY, 0, 0, Math.PI * 2);
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.strokeStyle = "rgba(255,255,255,0.92)";
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(screenX - 4, screenY + 4);
    ctx.lineTo(screenX + 4, screenY + 4);
    ctx.moveTo(screenX, screenY + 1);
    ctx.lineTo(screenX, screenY + 7);
    ctx.stroke();

    ctx.fillStyle = rgbaFromHex(accent, 0.92);
    ctx.fillRect(screenX - 1, screenY + 3, 2, 2);
    ctx.restore();
  }

  function drawTakeawayFlash(flash: any, frame: any, cameraX = 0) {
    if (!flash?.role || !frame) {
      return;
    }

    const teamType = flash.teamType === "offense" ? "offense" : "defense";
    const team = teamType === "offense" ? frame.offense : frame.defense;
    const players = teamType === "offense" ? frame.offensePlayers : frame.defensePlayers;
    const player = (players || []).find((candidate: any) => candidate.role === flash.role);
    if (!player) {
      return;
    }

    const intensity = clamp01(flash.intensity ?? 1);
    if (intensity <= 0) {
      return;
    }

    const facing = player?.spriteFacing || (teamType === "offense" ? frame.direction : -frame.direction);
    const accent = flash.color || team?.colors?.accent || "#ff7aa8";
    const screenX = regularPlayerVisualCenterX(
      worldToScreenX(player.x, cameraX),
      facing,
      teamType,
      player?.role || null,
      !!player?.moving,
    );
    const screenY = snapToPixel(player.y + 6);
    const glowRadius = 12 + intensity * 10;
    const ringRadius = 10 + intensity * 7;

    ctx.save();
    ctx.globalAlpha = 0.18 + intensity * 0.22;
    ctx.shadowColor = rgbaFromHex(accent, 0.72);
    ctx.shadowBlur = 12 + intensity * 14;
    ctx.fillStyle = rgbaFromHex(accent, 0.24 + intensity * 0.14);
    ctx.beginPath();
    ctx.arc(screenX, screenY, glowRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 0.36 + intensity * 0.24;
    ctx.shadowBlur = 0;
    ctx.lineWidth = 2;
    ctx.strokeStyle = rgbaFromHex(accent, 0.8 + intensity * 0.12);
    ctx.beginPath();
    ctx.arc(screenX, screenY, ringRadius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  function drawFumbleImpactBurst(frame: any, cameraX = 0) {
    const cue = resolveFumbleImpactCue(frame);
    if (!cue) {
      return;
    }

    const entries = [
      { players: frame?.offensePlayers || [], teamType: "offense" },
      { players: frame?.defensePlayers || [], teamType: "defense" },
    ];
    const entry = entries.find(({ players }) => players.some((player: any) => player.role === cue.fumblerRole));
    const player = entry?.players.find((candidate: any) => candidate.role === cue.fumblerRole);
    if (!entry || !player) {
      return;
    }

    const tuning = cpuVisualPolishTuning.fumbleBurst;
    const facing = player?.spriteFacing || (entry.teamType === "offense" ? frame.direction : -frame.direction);
    const screenX = regularPlayerVisualCenterX(
      worldToScreenX(player.x, cameraX),
      facing,
      entry.teamType,
      player?.role || null,
      !!player?.moving,
    );
    const screenY = snapToPixel(player.y + 7);
    const radius = tuning.minimumRadiusPixels
      + (tuning.maximumRadiusPixels - tuning.minimumRadiusPixels) * cue.progress;
    const rayStart = radius + 2;
    const rayEnd = rayStart + tuning.rayLengthPixels * cue.intensity;

    ctx.save();
    ctx.globalAlpha = cue.intensity * tuning.maximumAlpha;
    ctx.strokeStyle = "rgba(255, 220, 112, 0.96)";
    ctx.lineWidth = 2;
    ctx.shadowColor = "rgba(255, 182, 72, 0.8)";
    ctx.shadowBlur = 7;
    ctx.beginPath();
    ctx.arc(screenX, screenY, radius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.strokeStyle = "rgba(255, 246, 202, 0.98)";
    for (let index = 0; index < 8; index += 1) {
      const angle = index * Math.PI / 4;
      ctx.beginPath();
      ctx.moveTo(
        snapToPixel(screenX + Math.cos(angle) * rayStart),
        snapToPixel(screenY + Math.sin(angle) * rayStart),
      );
      ctx.lineTo(
        snapToPixel(screenX + Math.cos(angle) * rayEnd),
        snapToPixel(screenY + Math.sin(angle) * rayEnd),
      );
      ctx.stroke();
    }
    ctx.restore();
  }

  function drawCatchFlair(flair: any, frame: any, cameraX = 0) {
    if (!flair?.role || !frame) {
      return;
    }

    const teamType = flair.teamType === "defense" ? "defense" : "offense";
    const players = teamType === "offense" ? frame.offensePlayers : frame.defensePlayers;
    const player = (players || []).find((candidate: any) => candidate.role === flair.role);
    if (!player) {
      return;
    }

    const intensity = clamp01(flair.intensity ?? 1);
    const facing = player?.spriteFacing || (teamType === "offense" ? frame.direction : -frame.direction);
    const screenX = regularPlayerVisualCenterX(
      worldToScreenX(player.x, cameraX),
      facing,
      teamType,
      player?.role || null,
      !!player?.moving,
    );
    const screenY = snapToPixel(player.y + 7);

    ctx.save();
    const puffAlpha = flair.phase === "landing" ? 0.34 : 0.14;
    ctx.globalAlpha = puffAlpha * Math.max(0.3, intensity);
    ctx.fillStyle = "rgba(202, 225, 174, 0.9)";
    ctx.shadowColor = "rgba(218, 240, 190, 0.5)";
    ctx.shadowBlur = 5;
    ctx.beginPath();
    ctx.ellipse(screenX - facing * 6, screenY + 10, 7, 2.7, 0, 0, Math.PI * 2);
    ctx.ellipse(screenX - facing * 13, screenY + 9, 3.8, 1.8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  function teamForPlayId(franchiseId: any) {
    if (!state.game) {
      return null;
    }
    return teamInGameById(state.game, franchiseId);
  }

  function runPathProfile(play: any) {
    const offense = teamForPlayId(play.offense);
    const carrierRole = play.playId === "kneel" ? "QB" : "RB";
    if (!offense) {
      return { carrierRole, carrierSpeed: 75, distance: 264 };
    }

    const centerY = fieldMetrics().centerY;
    const direction = directionForOffense(offense);
    const losX = screenXForYard(play.startYardLine, direction);
    const endX = visualEndXForPlay(play, direction);
    const laneY = resolveLaneY(play, centerY);
    const carrierSpeed = speedRatingForRole(offense, carrierRole);
    const gainedYards = (play.endYardLine ?? play.startYardLine ?? 0) - (play.startYardLine ?? 0);
    const negativeRun = play.playId !== "kneel" && gainedYards < 0;

    if (play.playId === "kneel") {
      const startX = orientX(losX, -54, direction);
      const startY = centerY - 2;
      return {
        carrierRole,
        carrierSpeed,
        distance: pathDistance([
          { x: startX, y: startY },
          { x: orientX(losX, -58, direction), y: centerY + 2 },
          { x: endX, y: laneY },
        ]),
      };
    }

    const startX = orientX(losX, -88, direction);
    const startY = centerY + 26;
    const handoffX = orientX(losX, -62, direction);
    const handoffY = centerY + 10;
    const pressX = orientX(losX, 6, direction);
    const carrierLaneY = negativeRun ? lossRunLaneY(play, handoffY, laneY) : laneY;
    const pressY = snapToPixel(lerp(handoffY, carrierLaneY, 0.44));
    const waypoints = negativeRun
      ? buildLossRunWaypoints(play, startX, startY, handoffX, handoffY, endX, carrierLaneY, direction)
      : buildRunWaypoints(play, startX, startY, handoffX, handoffY, pressX, pressY, endX, laneY, centerY, direction);

    return {
      carrierRole,
      carrierSpeed,
      distance: pathDistance([{ x: startX, y: startY }, ...waypoints]),
    };
  }

  function passPathProfile(play: any) {
    const offense = teamForPlayId(play.offense);
    if (!offense) {
      return {
        targetRole: "WR1",
        targetSpeed: 82,
        routeDistance: 220,
        yacDistance: 132,
        routeFrames: 12,
        yacFrames: 8,
      };
    }

    const centerY = fieldMetrics().centerY;
    const direction = directionForOffense(offense);
    const losX = screenXForYard(play.startYardLine, direction);
    const endX = visualEndXForPlay(play, direction);
    const laneY = resolveLaneY(play, centerY);
    const turnover = isInterceptionReturn(play);
    const defense = turnover ? teamForPlayId(play.defense) : null;
    const formation = buildFormation(play, losX, centerY, direction);
    const plan = routePlan(play, losX, centerY, endX, direction);
    const targetRole = plan.targetRole || "WR1";
    const catchX = screenXForYard(play.catchYardLine || play.endYardLine || play.startYardLine, direction);
    const routeTargets = Object.fromEntries(
      Object.entries(plan.routes).map(([role, [targetX, targetY]]) => [role, role === targetRole ? [catchX, targetY] : [targetX, targetY]])
    );
    const catchTarget = routeTargets[targetRole] || [catchX, laneY];
    const catchPoint = { x: catchTarget[0], y: catchTarget[1] };
    const pickPoint = turnover ? { x: snapToPixel(catchPoint.x + direction * 10), y: snapToPixel(catchPoint.y) } : catchPoint;
    const carrierEndPoint = turnover
      ? interceptionReturnEndPoint(play, pickPoint, endX, laneY, centerY)
      : { x: endX, y: laneY };
    const targetBase = findPlayer(formation.offense, targetRole) || findPlayer(formation.offense, "WR1") || formation.offense[0];
    const routePoints = buildRouteShapePoints(play, targetRole, { x: targetBase.x, y: targetBase.y }, catchPoint, losX, centerY, direction);
    const yacPoints = turnover
      ? buildInterceptionReturnPoints(play, pickPoint, carrierEndPoint, direction, centerY)
      : buildPostCatchPoints(play, catchPoint, carrierEndPoint);
    const routeDistance = pathDistance(routePoints);
    const yacDistance = pathDistance(yacPoints);
    const routeSpeed = speedRatingForRole(offense, targetRole);
    const carrierSpeed = turnover
      ? (defense ? speedRatingForRole(defense, turnoverDefenderRole(targetRole)) : 80)
      : routeSpeed;
    const routeFrames = Math.max(8, Math.round(routeDistance / playerPixelsPerFrame(routeSpeed || 78, "pass")));
    const yacFrames = Math.max(6, Math.round(yacDistance / playerPixelsPerFrame(carrierSpeed || 78, "pass", turnover || play.playId === "screen-left" ? 0.94 : 0.9)));

    return {
      targetRole,
      targetSpeed: carrierSpeed,
      routeDistance,
      yacDistance,
      routeFrames,
      yacFrames,
    };
  }

  function drawRibbonMessage(headline: any) {
    if (!headline) {
      return;
    }

    const letters = headline.text.split("");
    const fontSize = headline.text.length >= 11 ? 18 : headline.text.length >= 9 ? 20 : 22;
    const tracking = headline.text.length >= 11 ? 15 : headline.text.length >= 9 ? 17 : 19;
    const totalWidth = tracking * Math.max(0, letters.length - 1);
    const startX = refs.canvas!.width / 2 - totalWidth / 2;
    const baselineY = 21;
    const panelTop = 5;
    const panelHeight = 28;

    ctx.save();
    ctx.fillStyle = "rgba(5, 12, 24, 0.26)";
    ctx.fillRect(startX - 28, panelTop, totalWidth + 56, panelHeight);
    ctx.strokeStyle = "rgba(132, 188, 255, 0.16)";
    ctx.lineWidth = 1;
    ctx.strokeRect(startX - 28, panelTop, totalWidth + 56, panelHeight);

    ctx.font = `bold ${fontSize}px "Lucida Console", monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    letters.forEach((letter: any, index: any) => {
      const x = startX + tracking * index;

      ctx.shadowColor = headline.glow;
      ctx.shadowBlur = 16;
      ctx.lineWidth = 6;
      ctx.strokeStyle = "rgba(6, 14, 24, 0.9)";
      ctx.strokeText(letter, x, baselineY);

      ctx.fillStyle = headline.color;
      ctx.fillText(letter, x, baselineY);

      ctx.shadowBlur = 0;
      ctx.fillStyle = "rgba(255,255,255,0.32)";
      ctx.fillText(letter, x, baselineY - 1);
    });
    ctx.restore();
  }

  function drawRibbonHeadline(play: any) {
    drawRibbonMessage(ribbonHeadlineForPlay(play));
  }

  function wrapPixelText(text: any, maxWidth: any, font: any) {
    const value = String(text || "").trim();
    if (!value) {
      return [];
    }

    ctx.save();
    ctx.font = font;

    const lines = [];
    let current = "";
    value.split(/\s+/).forEach((word) => {
      const attempt = current ? `${current} ${word}` : word;
      if (current && ctx.measureText(attempt).width > maxWidth) {
        lines.push(current);
        current = word;
      } else {
        current = attempt;
      }
    });
    if (current) {
      lines.push(current);
    }

    ctx.restore();
    return lines;
  }

  function drawPixelLines(lines: any, x: any, y: any, lineHeight: any, options: any = {}) {
    if (!lines?.length) {
      return;
    }

    ctx.save();
    ctx.font = options.font || "bold 16px 'Lucida Console', monospace";
    ctx.textAlign = options.align || "left";
    ctx.textBaseline = "top";
    ctx.lineWidth = options.strokeWidth ?? 4;
    ctx.strokeStyle = options.stroke || "rgba(7, 14, 26, 0.94)";
    ctx.fillStyle = options.fill || "#eef5ff";
    ctx.shadowColor = options.glow || "transparent";
    ctx.shadowBlur = options.shadowBlur || 0;

    lines.forEach((line: any, index: any) => {
      const lineY = y + index * lineHeight;
      if (options.stroke !== false) {
        ctx.strokeText(line, x, lineY);
      }
      ctx.fillText(line, x, lineY);
    });

    ctx.restore();
  }

  function leaderRowsForSide(side: any) {
    if (!state.game || (side !== "away" && side !== "home")) {
      return [];
    }
    // Public live packages deliberately omit final aggregates so the result
    // cannot leak. The final presentation begins as soon as the last local play
    // completes, just before the server marks the broadcast final, so derive the
    // same four leaders from the already-completed live-stat ledger in that gap.
    return finalLeaderRowsForGame(state.game, side, completedPlayCount());
  }

  function drawLeaderCard(team: any, side: any, x: any, y: any, width: any, height: any, now: any, alpha = 1) {
    if (!team) {
      return;
    }

    const winnerSide = winnerSideForGame(state.game);
    const active = winnerSide === side;
    const pulse = active ? (Math.sin(now / 240) + 1) * 0.5 : 0;
    const borderColor = active ? rgbaFromHex(team.colors.primary || "#8ef6ff", 0.92) : "rgba(132, 156, 210, 0.28)";
    const glowColor = active ? rgbaFromHex(team.colors.primary || "#8ef6ff", 0.72) : "transparent";
    const rows = leaderRowsForSide(side);

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.shadowColor = glowColor;
    ctx.shadowBlur = active ? 18 + pulse * 16 : 0;
    ctx.fillStyle = "rgba(10, 18, 32, 0.9)";
    ctx.fillRect(x, y, width, height);
    ctx.shadowBlur = 0;
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = active ? 2 : 1;
    ctx.strokeRect(x, y, width, height);

    ctx.fillStyle = rgbaFromHex(team.colors.primary || "#8ef6ff", active ? 0.88 : 0.56);
    ctx.fillRect(x + 1, y + 1, width - 2, 6);

    drawPixelLines([`${team.abbr} LEADERS`], x + 18, y + 18, 18, {
      font: "bold 20px 'Lucida Console', monospace",
      fill: active ? "#f9fbff" : "#d8e6ff",
      glow: active ? glowColor : "transparent",
      shadowBlur: active ? 10 + pulse * 8 : 0,
    });

    rows.forEach(([label, value], index) => {
      const rowY = y + 52 + index * 68;
      ctx.fillStyle = "rgba(18, 28, 48, 0.88)";
      ctx.fillRect(x + 14, rowY, width - 28, 62);
      ctx.strokeStyle = "rgba(112, 140, 196, 0.18)";
      ctx.lineWidth = 1;
      ctx.strokeRect(x + 14, rowY, width - 28, 48);

      drawPixelLines([label], x + 28, rowY + 10, 16, {
        font: "16px 'Lucida Console', monospace",
        fill: "rgba(210, 224, 255, 0.88)",
        strokeWidth: 3,
      });

      const valueLines = wrapPixelText(value, width - 162, "bold 16px 'Lucida Console', monospace").slice(0, 3);
      drawPixelLines(valueLines, x + 128, rowY + 8, 16, {
        font: "bold 16px 'Lucida Console', monospace",
        fill: active ? "#f4fbff" : "#d6e4ff",
        glow: active ? glowColor : "transparent",
        shadowBlur: active ? 4 + pulse * 4 : 0,
      });
    });

    ctx.restore();
  }

  function drawFinalLeadersOverlay(intermission: any, now: any) {
    if (!state.game) {
      return;
    }

    const alpha = clamp01((intermission.elapsedMs - intermissionPresentationStartMs(intermission)) / INTERMISSION_TIMING.finalOverlayFadeMs);
    if (alpha <= 0) {
      return;
    }

    const overlayTop = 120;
    const cardWidth = 336;
    const cardHeight = 330;
    const gap = 34;
    const leftX = Math.round((refs.canvas!.width - (cardWidth * 2 + gap)) / 2);
    const rightX = leftX + cardWidth + gap;
    const titleY = 96;
    const score = intermission.score || state.game.score;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = "rgba(4, 10, 20, 0.68)";
    ctx.fillRect(0, 0, refs.canvas!.width, refs.canvas!.height);
    ctx.restore();

    ctx.save();
    ctx.globalAlpha = alpha;
    drawPixelLines(
      [`FINAL ${state.game.away.abbr} ${score.away ?? 0}  ${score.home ?? 0} ${state.game.home.abbr}`],
      refs.canvas!.width / 2,
      titleY,
      20,
      {
        font: "bold 22px 'Lucida Console', monospace",
        align: "center",
        fill: "#eef6ff",
        glow: "rgba(122, 206, 255, 0.34)",
        shadowBlur: 10,
      }
    );
    ctx.restore();

    drawLeaderCard(state.game.away, "away", leftX, overlayTop, cardWidth, cardHeight, now, alpha);
    drawLeaderCard(state.game.home, "home", rightX, overlayTop, cardWidth, cardHeight, now, alpha);
  }

  function resolveLaneY(play: any, centerY: any) {
    if (!play?.visual) {
      return centerY;
    }
    if (stopReasonForPlay(play) === "out-of-bounds") {
      const side = play.visual.lane < 0 ? -1 : 1;
      return centerY + side * 176;
    }
    return centerY + play.visual.lane * 18;
  }

  function movePlayer(players: any, role: any, targetX: any, targetY: any, amount: any, moving = true, useLiveTempo = true, movementStyle = "orthogonal") {
    const player = findPlayer(players, role);
    if (!player) {
      return null;
    }

    const adjustedAmount = clamp01(amount * (useLiveTempo ? player.liveTempo || 1 : 1));
    const next = stepPosition(movementStyle, player.x, player.y, targetX, targetY, adjustedAmount);
    player.x = next.x;
    player.y = next.y;
    player.moving = moving;
    return player;
  }

  function movePlayerAtSpeed(players: any, role: any, targetX: any, targetY: any, elapsedMs: any, moving = true, movementStyle = "orthogonal", speedScale = 1, speedFn = passPixelsPerSecondForSpeed) {
    const player = findPlayer(players, role);
    if (!player) {
      return null;
    }

    const distance = movementDistanceForStyle(movementStyle, player.x, player.y, targetX, targetY);
    if (distance <= 0) {
      player.x = snapToPixel(targetX);
      player.y = snapToPixel(targetY);
      player.moving = moving;
      return player;
    }

    const pixelsPerSecond = speedFn(player.speedRating || 75) * speedScale;
    const amount = clamp01((Math.max(0, elapsedMs) / 1000) * pixelsPerSecond / distance);
    const next = stepPosition(movementStyle, player.x, player.y, targetX, targetY, amount);
    player.x = next.x;
    player.y = next.y;
    player.moving = moving;
    return player;
  }

  function offsetPlayer(players: any, role: any, deltaX: any, deltaY: any, amount: any, moving = true, useLiveTempo = true, movementStyle = "orthogonal") {
    const player = findPlayer(players, role);
    if (!player) {
      return null;
    }

    const adjustedAmount = clamp01(amount * (useLiveTempo ? player.liveTempo || 1 : 1));
    const next = stepPosition(movementStyle, player.x, player.y, player.x + deltaX, player.y + deltaY, adjustedAmount);
    player.x = next.x;
    player.y = next.y;
    player.moving = moving;
    return player;
  }

  function movePlayerAlongSpline(players: any, role: any, waypoints: any, progress: any, moving = true, useLiveTempo = true, timing = "constant") {
    const player = findPlayer(players, role);
    if (!player) {
      return null;
    }

    const tempo = useLiveTempo ? 1 + ((player.liveTempo || 1) - 1) * 0.18 : 1;
    const path = [{ x: player.x, y: player.y }, ...waypoints];
    const next = catmullRomPoint(path, clamp01(progress * tempo), timing);
    player.x = next.x;
    player.y = next.y;
    player.moving = moving;
    return player;
  }

  function movePlayerAlongPolyline(players: any, role: any, points: any, progress: any, moving = true, useLiveTempo = true) {
    const player = findPlayer(players, role);
    if (!player) {
      return null;
    }

    const tempo = useLiveTempo ? 1 + ((player.liveTempo || 1) - 1) * 0.14 : 1;
    const path = compactPathPoints([{ x: player.x, y: player.y }, ...(points || [])]);
    const next = polylinePointAt(path, clamp01(progress * tempo));
    player.x = next.x;
    player.y = next.y;
    player.moving = moving;
    return player;
  }

  function playerAnimState(player: any, facing: any) {
    const resolvedFacing = player?.spriteFacing || facing;
    if (player.state) {
      return player.state;
    }
    if (player.down || player.tackled) {
      return resolvedFacing > 0 ? "fallRight" : "fallLeft";
    }
    switch (player?.moveStyle) {
      case "engaged":
        return resolvedFacing > 0 ? "tackleRight" : "tackleLeft";
      case "run":
        return resolvedFacing > 0 ? "runRight" : "runLeft";
      case "backpedal":
      case "shuffle":
      case "idle":
      default:
        return resolvedFacing > 0 ? "idleRight" : "idleLeft";
    }
  }

  function drawPlayers(players: any, palette: any, facing: any, cameraX = 0, frame: any = null, teamType = "offense") {
    let team = teamType === "offense" ? frame?.offense : frame?.defense;
    if (!team && frame?.play && state.game) {
      const offenseTeam = teamInGameById(state.game, frame.play.offense);
      const defenseTeam = teamInGameById(state.game, frame.play.defense);
      team = teamType === "offense" ? offenseTeam : defenseTeam;
    }
    const side = teamType === "offense" ? "home" : "away";
    players.forEach((player: any) => {
      const resolvedPalette = team ? playerVisualPalette(team, side, player) : palette;
      const playerFacing = player?.spriteFacing || facing;
      const presentation = resolvePlayerPresentationTransform(player, frame, teamType, playerFacing);
      const spriteX = worldToScreenX(player.x, cameraX) + presentation.offsetX;
      const spriteY = player.y + presentation.offsetY;
      const diveLift = Math.max(0, Number(player?.arcadeVerticalLift || 0));
      if (diveLift > 0.75) {
        const heroDive = frame?.heroMoment?.role === player.role
          && frame?.heroMoment?.teamType === teamType;
        const shadowX = regularPlayerVisualCenterX(
          worldToScreenX(player.x, cameraX),
          playerFacing,
          teamType,
          player?.role || null,
          !!player?.moving,
        );
        const groundY = snapToPixel(player.y + diveLift + 11);
        const liftRatio = clamp01(diveLift / 10);
        ctx.save();
        ctx.globalAlpha = (heroDive ? 0.34 : 0.27) - liftRatio * 0.07;
        ctx.fillStyle = "rgba(2, 7, 13, 0.82)";
        ctx.beginPath();
        ctx.ellipse(
          shadowX,
          groundY,
          (heroDive ? 12 : 10) + liftRatio * 2,
          (heroDive ? 3.6 : 3.2) - liftRatio * 0.5,
          0,
          0,
          Math.PI * 2,
        );
        ctx.fill();
        ctx.restore();
      }
      const pivotX = regularPlayerVisualCenterX(
        spriteX,
        playerFacing,
        teamType,
        player?.role || null,
        !!player?.moving,
      );
      const pivotY = spriteY + 2;
      ctx.save();
      ctx.translate(pivotX, pivotY);
      ctx.rotate(presentation.rotation);
      ctx.scale(presentation.scaleX, presentation.scaleY);
      ctx.translate(-pivotX, -pivotY);
      drawPixelSprite(
        spriteX,
        spriteY,
        resolvedPalette,
        playerFacing,
        player.moving,
        playerAnimState(player, playerFacing),
        player,
        frame,
        teamType
      );
      ctx.restore();
    });
  }

  function collisionBias(roleA: any, roleB: any) {
    const text = roleA + ":" + roleB;
    let hash = 0;
    for (let index = 0; index < text.length; index += 1) {
      hash = (hash * 31 + text.charCodeAt(index)) % 360;
    }
    return (hash / 180) * Math.PI;
  }

  export function prepareVisualFrameForRender(
    frame: any,
    cache: Map<string, VisualPlayerCacheEntry> = new Map(),
    metrics = fieldMetrics()
  ) {
    const offenseCachePrefix = `${frame.offense?.id || frame.offense?.abbr || "offense"}:offense`;
    const defenseCachePrefix = `${frame.defense?.id || frame.defense?.abbr || "defense"}:defense`;
    const offensePreviousEntries = snapshotVisualPlayerEntries(frame.offensePlayers, cache, offenseCachePrefix);
    const defensePreviousEntries = snapshotVisualPlayerEntries(frame.defensePlayers, cache, defenseCachePrefix);
    const offensePlayers = stabilizeVisualPlayers(
      clonePlayers(frame.offensePlayers),
      frame.formation?.offense || [],
      cache,
      offenseCachePrefix,
      metrics
    );
    const defensePlayers = stabilizeVisualPlayers(
      clonePlayers(frame.defensePlayers),
      frame.formation?.defense || [],
      cache,
      defenseCachePrefix,
      metrics
    );
    applyDefensiveReactionStagger(defensePlayers, frame.formation?.defense || [], frame);
    defensePlayers.forEach((player: any) => {
      Object.assign(player, clampVisualPlayerPosition(player, metrics));
    });
    const ballPoint = { x: frame.ball.ballX - 6, y: frame.ball.ballY - 8 };
    const nearestOffense = nearestPlayerToPoint(offensePlayers, ballPoint);
    const nearestDefense = nearestPlayerToPoint(defensePlayers, ballPoint);
    const offenseDistance = nearestOffense ? pointDistance(ballPoint, nearestOffense) : Infinity;
    const defenseDistance = nearestDefense ? pointDistance(ballPoint, nearestDefense) : Infinity;
    const kickoffFrame = frame.play?.playId === "kickoff";
    const anchorPlayer = offenseDistance <= defenseDistance ? nearestOffense : nearestDefense;
    resolveTightPlayerClusters(offensePlayers, anchorPlayer, metrics, 12);
    resolveTightPlayerClusters(defensePlayers, anchorPlayer, metrics, 12);
    const players = [
      ...offensePlayers.map((player, index) => ({ player, team: "offense", index })),
      ...defensePlayers.map((player, index) => ({ player, team: "defense", index })),
    ];

    for (let pass = 0; pass < 4; pass += 1) {
      for (let leftIndex = 0; leftIndex < players.length; leftIndex += 1) {
        for (let rightIndex = leftIndex + 1; rightIndex < players.length; rightIndex += 1) {
          const leftEntry = players[leftIndex];
          const rightEntry = players[rightIndex];
          const left = leftEntry.player;
          const right = rightEntry.player;
          if (!Number.isFinite(left?.x) || !Number.isFinite(left?.y) || !Number.isFinite(right?.x) || !Number.isFinite(right?.y)) {
            continue;
          }
          const sameTeam = leftEntry.team === rightEntry.team;
          const returnUnitPair = /^(FRONT|WALL)\d+$/.test(left.role) || /^(FRONT|WALL)\d+$/.test(right.role);
          if (kickoffFrame && sameTeam && returnUnitPair && (/^RETURNER/.test(left.role) || /^RETURNER/.test(right.role))) {
            continue;
          }
          spreadPlayersApart(left, right, sameTeam, anchorPlayer, metrics, sameTeam ? 12 : 8);
        }
      }
    }

    const ownerRole = frame.ball?.arcadeOwnerRole;
    const offenseOwner = ownerRole ? offensePlayers.find((player: any) => player.role === ownerRole) : null;
    const defenseOwner = !offenseOwner && ownerRole ? defensePlayers.find((player: any) => player.role === ownerRole) : null;
    const ballOwner = offenseOwner || defenseOwner || null;
    const ballOwnerTeamType = offenseOwner ? "offense" : defenseOwner ? "defense" : null;
    const pursuitPlayers = offenseOwner ? defensePlayers : defenseOwner ? offensePlayers : [];
    applyPursuitLaneSpacing(pursuitPlayers, ballOwner);
    pursuitPlayers.forEach((player: any) => {
      Object.assign(player, clampVisualPlayerPosition(player, metrics));
    });
    applyVisualSpriteFacing(
      offensePlayers,
      defensePlayers,
      frame.direction || 1,
      offensePreviousEntries,
      defensePreviousEntries,
      ballOwner,
      ballOwnerTeamType,
      frame,
    );
    applyVisualMoveStyles(
      offensePlayers,
      defensePlayers,
      offensePreviousEntries,
      defensePreviousEntries,
      ballOwner,
      ballOwnerTeamType,
      frame,
      frame.direction || 1,
    );

    rememberVisualPlayers(offensePlayers, cache, offenseCachePrefix);
    rememberVisualPlayers(defensePlayers, cache, defenseCachePrefix);
    const ball = resolveOwnedBallPoint(frame, offensePlayers, defensePlayers) || frame.ball;

    return {
      ...frame,
      offensePlayers,
      defensePlayers,
      ball,
    };
  }

  function visualizedFrame(frame: any) {
    return prepareVisualFrameForRender(frame, state.visualPlayerCache, fieldMetrics());
  }


  function buildLiveSequence(context: any) {
    if (context.play.playId === "kickoff") {
      return buildKickoffLiveSequenceV4(context);
    }
    if (context.play.playId === "punt") {
      return buildPuntLiveSequence(context);
    }
    if (context.play.playId === "field-goal" || context.play.playId === "pat") {
      return buildFieldGoalLiveSequence(context);
    }
    if (context.play.family === "run") {
      return buildRunLiveSequence(context);
    }
    if (context.play.family === "pass") {
      return buildPassLiveSequence(context);
    }
    return null;
  }

  function liveSequenceForContext(context: any) {
    const key = liveSequenceCacheKey(context);
    const cache = context.play._liveSequence;
    if (cache && cache.key === key) {
      return cache.frames;
    }
    const frames = buildLiveSequence(context);
    context.play._liveSequence = { key, frames };

    return frames;
  }

  function clearPlaybackHalt() {
    state.playbackHaltKey = null;
    state.playbackHaltMessage = null;
  }

  function arcadePlaybackHaltKey(context: any) {
    return `${context.play?.sequence ?? state.playIndex}:${context.play?.playId ?? "unknown"}:${liveSequenceCacheKey(context)}`;
  }

  function frozenLiveFailureFrame(context: any, progress: any) {
    const offensePlayers = clonePlayers(context.formation.offense);
    const defensePlayers = clonePlayers(context.formation.defense);
    setPlayersStill(offensePlayers, defensePlayers);
    const snapper = findPlayer(offensePlayers, "C");
    const ball = snapper
      ? centerSnapBallPoint(snapper as any, context.direction, context.losX, context.centerY - 6)
      : scrimmageBallPoint(offensePlayers, context.direction, context.losX, context.centerY - 6);
    return {
      ...context,
      scenePhase: "live-sequence-halted",
      liveProgress: progress,
      offensePlayers,
      defensePlayers,
      ball,
    };
  }

  function haltArcadePlayback(context: any, progress: any, reason: string, error?: unknown) {
    const haltKey = arcadePlaybackHaltKey(context);
    if (state.playbackHaltKey !== haltKey) {
      console.error("Raw arcade playback halted", {
        reason,
        playId: context.play?.playId,
        sequence: context.play?.sequence,
        family: context.play?.family,
        arcadePlaybackMode: context.play?.arcadePlaybackMode,
        arcadeSource: context.play?.arcadeReplay?.source,
        seed: state.game?.seed,
      }, error);
      const scheduledGameId = currentScheduledGameId();
      if (scheduledGameId && !isDebugMode()) {
        showLiveSyncOverlay("Recovering live replay frames.", 2_000);
        void pollLiveBroadcastSync(scheduledGameId);
      }
    }
    state.playbackHaltKey = haltKey;
    state.playbackHaltMessage = `Playback paused: ${context.play?.playId || "arcade play"} needs resync.`;
    if (context.play === currentPlay()) {
      const duration = timedPlayDurationMs(context.play, state.playIndex);
      state.pausedElapsed = Math.max(0, Math.min(duration, Math.round(duration * clamp01(Number(progress) || 0))));
      state.autoplay = false;
      updatePlayPauseButton();
    }
    return frozenLiveFailureFrame(context, progress);
  }

  function buildAnimatedFrame(context: any, progress: any) {
    if (shouldHaltInsteadOfLegacyFallback(context.play) && state.playbackHaltKey === arcadePlaybackHaltKey(context)) {
      return frozenLiveFailureFrame(context, progress);
    }
    if (isScrimmageRunPass(context.play) && !hasRawArcadeReplay(context.play)) {
      return haltArcadePlayback(context, progress, "missing-raw-arcade-replay");
    }

    try {
      const liveFrames = liveSequenceForContext(context);
      if (liveFrames && liveFrames.length) {
        const frameWindow = liveFrameWindow(progress, liveFrames.length);
        const liveFrame = liveFrames[frameWindow.fromIndex];
        const nextFrame = liveFrames[frameWindow.toIndex];
        const smoothSpecialTeamsFlight = shouldSmoothSpecialTeamsFlight({
          play: context.play,
          scenePhase: "live",
          liveProgress: progress,
        });
        const rawArcadeReplay = context.play?.arcadeReplay?.source === "arcade-sim-v1"
          && context.play?.arcadePlaybackMode === "raw";
        return {
          ...context,
          ...liveFrame,
          scenePhase: "live",
          liveProgress: progress,
          offensePlayers: rawArcadeReplay && nextFrame
            ? interpolateLivePlayers(liveFrame.offensePlayers, nextFrame.offensePlayers, frameWindow.amount)
            : clonePlayers(liveFrame.offensePlayers),
          defensePlayers: rawArcadeReplay && nextFrame
            ? interpolateLivePlayers(liveFrame.defensePlayers, nextFrame.defensePlayers, frameWindow.amount)
            : clonePlayers(liveFrame.defensePlayers),
          ball: (smoothSpecialTeamsFlight || rawArcadeReplay) && nextFrame
            ? { ...liveFrame.ball, ...interpolateBallPoint(liveFrame.ball, nextFrame.ball, frameWindow.amount) }
            : { ...liveFrame.ball },
        };
      }
      if (shouldHaltInsteadOfLegacyFallback(context.play)) {
        return haltArcadePlayback(context, progress, "empty-live-sequence");
      }
    } catch (error) {
      if (shouldHaltInsteadOfLegacyFallback(context.play)) {
        return haltArcadePlayback(context, progress, "live-sequence-error", error);
      }
      console.error("Live sequence fallback", {
        playId: context.play?.playId,
        sequence: context.play?.sequence,
        family: context.play?.family,
        arcadePlaybackMode: context.play?.arcadePlaybackMode,
        arcadeSource: context.play?.arcadeReplay?.source,
        seed: state.game?.seed,
      }, error);
    }

    const offensePlayers = clonePlayers(context.formation.offense);
    const defensePlayers = clonePlayers(context.formation.defense);
    const ball = animateFormation(
      context.play,
      offensePlayers,
      defensePlayers,
      progress,
      easeInOut(progress),
      context.losX,
      context.endX,
      context.laneY,
      context.centerY,
      context.direction
    );

    return {
      ...context,
      scenePhase: "live",
      liveProgress: progress,
      offensePlayers,
      defensePlayers,
      ball,
    };
  }
  function drawPlayFrame(frame: any) {
    const visualFrame = visualizedFrame(frame);
    const ballCarrierIndicator = resolveBallCarrierIndicator(visualFrame);
    const metrics = fieldMetrics();
    const smoothSpecialTeamsFlight = shouldSmoothSpecialTeamsFlight(visualFrame);
    const cameraNow = performance.now();
    const cameraElapsedMs = Number.isFinite(state.cameraUpdatedAt)
      ? clampRange(cameraNow - state.cameraUpdatedAt!, 4, 40)
      : 1000 / 60;
    state.cameraUpdatedAt = cameraNow;
    const forcedCameraX = Number.isFinite(frame.cameraXOverride) ? snapToPixel(frame.cameraXOverride) : null;
    if (forcedCameraX !== null) {
      state.cameraX = forcedCameraX;
      state.cameraVelocityX = 0;
    } else {
      const targetCameraX = cameraTargetX(visualFrame);
      const rawArcadeReplay = visualFrame.play?.arcadeReplay?.source === "arcade-sim-v1"
        && visualFrame.play?.arcadePlaybackMode === "raw";
      const rawArcadeRun = rawArcadeReplay && visualFrame.play?.family === "run";
      const rawArcadePass = rawArcadeReplay && visualFrame.play?.family === "pass";
      const specialTeamsPlay = visualFrame.play?.playId === "kickoff" || visualFrame.play?.playId === "punt";
      const puntPlay = visualFrame.play?.playId === "punt";
      const specialTeamsChangeover = isSpecialTeamsCameraChangeover(visualFrame);
      if (!Number.isFinite(state.cameraX)) {
        state.cameraX = targetCameraX;
        state.cameraVelocityX = 0;
      } else if (puntPlay) {
        const puntCameraPhase = specialTeamsChangeover
          ? "changeover"
          : isSpecialTeamsReturnPossession(visualFrame)
            ? "return"
            : "flight";
        const cameraMotion = advanceComfortCamera(
          state.cameraX!,
          targetCameraX,
          state.cameraVelocityX,
          cameraElapsedMs,
          puntCameraMotionOptions(puntCameraPhase),
        );
        state.cameraX = cameraMotion.position;
        state.cameraVelocityX = cameraMotion.velocity;
      } else {
        state.cameraVelocityX = 0;
        const cameraFollow = specialTeamsPlay
          ? specialTeamsChangeover
            ? 0.16
            : (isSpecialTeamsReturnPossession(visualFrame) ? 0.055 : 0.07)
          : rawArcadePassTracksAirborneBall(visualFrame)
          ? 0.1
          : rawArcadePass
            ? 0.06
            : rawArcadeRun
              ? 0.08
              : visualFrame.play.family === "run"
                ? 0.12
                : 0.18;
        const nextCameraX = lerp(state.cameraX!, targetCameraX, cameraFollow);
        const specialTeamsMaxSpeed = specialTeamsChangeover ? 1100 : 380;
        const specialTeamsMaxStep = specialTeamsMaxSpeed * (cameraElapsedMs / 1000);
        const easedCameraX = specialTeamsPlay
          ? state.cameraX! + clampRange(nextCameraX - state.cameraX!, -specialTeamsMaxStep, specialTeamsMaxStep)
          : nextCameraX;
        state.cameraX = specialTeamsPlay || smoothSpecialTeamsFlight || rawArcadeRun || rawArcadePass
          ? easedCameraX
          : snapToPixel(easedCameraX);
      }
    }
    const cameraX = state.cameraX;
    const firstDownX = worldToScreenX(visualFrame.firstDownX, cameraX);
    const losX = worldToScreenX(visualFrame.losX, cameraX);
    const baseBallX = smoothSpecialTeamsFlight
      ? Math.round(visualFrame.ball.ballX - cameraX)
      : worldToScreenX(visualFrame.ball.ballX, cameraX);
    const baseBallY = smoothSpecialTeamsFlight ? Math.round(visualFrame.ball.ballY) : visualFrame.ball.ballY;
    const celebrationBallOffset = resolveCelebrationBallOffset(visualFrame);
    const ballX = baseBallX + celebrationBallOffset.offsetX;
    const ballBaseY = baseBallY + celebrationBallOffset.offsetY;
    const actionZoom = resolveActionZoom(visualFrame, cameraX, ballX, ballBaseY, metrics);

    clearCanvasFrame();
    drawStadiumBackdrop(metrics, cameraX);

    ctx.save();
    applyActionZoom(actionZoom);
    if (isMobilePerformanceMode()) {
      drawMobileFieldSurface(cameraX, metrics);
    } else {
      drawFieldSurface(cameraX, metrics);
    }

    if (!visualFrame.hideMarkers && visualFrame.play.playId !== "kickoff" && visualFrame.play.playId !== "pat" && firstDownX > -32 && firstDownX < refs.canvas!.width + 32) {
      ctx.strokeStyle = "#ffef8c";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(firstDownX, metrics.fieldTop);
      ctx.lineTo(firstDownX, metrics.fieldBottom);
      ctx.stroke();
      ctx.fillStyle = "rgba(255, 239, 140, 0.18)";
      ctx.fillRect(firstDownX - 7, metrics.fieldTop, 14, metrics.fieldHeight);
      ctx.fillStyle = "#ffef8c";
      ctx.fillRect(firstDownX - 6, metrics.crowdTop + 8, 12, 24);
    }

    if (!visualFrame.hideMarkers && visualFrame.play.playId !== "kickoff" && losX > -32 && losX < refs.canvas!.width + 32) {
      ctx.strokeStyle = "#8ce9ff";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(losX, metrics.fieldTop);
      ctx.lineTo(losX, metrics.fieldBottom);
      ctx.stroke();
      ctx.fillStyle = "rgba(140, 233, 255, 0.16)";
      ctx.fillRect(losX - 7, metrics.fieldTop, 14, metrics.fieldHeight);
      ctx.fillStyle = "#8ce9ff";
      ctx.fillRect(losX - 6, metrics.crowdTop + 8, 12, 24);
    }

    if (visualFrame.play.playId === "field-goal" || visualFrame.play.playId === "pat") {
      drawUprights(visualFrame.centerY, visualFrame.direction, cameraX);
    }

    drawPlayers(visualFrame.defensePlayers, visualFrame.defensePalette, -visualFrame.direction, cameraX, visualFrame, "defense");
    drawPlayers(visualFrame.offensePlayers, visualFrame.offensePalette, visualFrame.direction, cameraX, visualFrame, "offense");
    drawFumbleImpactBurst(visualFrame, cameraX);
    drawCatchFlair(visualFrame.catchFlair, visualFrame, cameraX);
    drawTakeawayFlash(visualFrame.takeawayFlash, visualFrame, cameraX);
    maybePlayVisualFrameSoundEffects(visualFrame);
    maybePlayCatchSoundForReceiverCue(visualFrame, ballCarrierIndicator);
    drawBallCarrierIndicator(ballCarrierIndicator, cameraX);

    if (!visualFrame.hideBall) {
      const unownedBall = !visualFrame.ball?.arcadeOwnerRole;
      const airborneShadow = unownedBall ? resolveAirborneBallShadow(visualFrame.ball) : null;
      if (airborneShadow) {
        const shadowX = worldToScreenX(airborneShadow.centerX, cameraX);
        ctx.save();
        ctx.globalAlpha = airborneShadow.alpha;
        ctx.fillStyle = "#02070d";
        ctx.fillRect(
          shadowX - airborneShadow.halfWidth,
          airborneShadow.groundY,
          airborneShadow.halfWidth * 2,
          airborneShadow.height,
        );
        ctx.restore();
      }
      const ballSize = unownedBall ? (usesAirborneBallPresentation(visualFrame.ball) ? 12 : 10) : 8;
      const ballOffset = Math.floor((ballSize - 8) / 2);
      const ballDrawX = ballX - ballOffset;
      const ballDrawY = ballBaseY - 1 - ballOffset;
      const usedFootballSprite = drawMaskSpriteFrame(
        visualConfig.spriteSheets.football,
        Math.abs(visualFrame.direction) > 0 ? "spiral" : "default",
        footballVisualPalette(visualFrame.play, unownedBall ? "tracked" : "normal"),
        ballDrawX,
        ballDrawY,
        ballSize,
        ballSize
      );
      if (!usedFootballSprite) {
        const ballHeight = Math.max(5, Math.round(ballSize * 0.58));
        ctx.fillStyle = "#5b3410";
        ctx.fillRect(ballDrawX, ballDrawY + Math.max(0, Math.floor(ballSize * 0.2)), ballSize, ballHeight);
        ctx.fillStyle = "rgba(255,255,255,0.8)";
        ctx.fillRect(ballDrawX + Math.floor(ballSize * 0.38), ballDrawY + Math.max(1, Math.floor(ballSize * 0.26)), Math.max(2, Math.round(ballSize * 0.24)), Math.max(3, Math.round(ballSize * 0.34)));
      }
    }
    ctx.restore();
    drawStadiumBoardOverlay();

    if (visualFrame.ribbonMessage) {
      drawRibbonMessage(visualFrame.ribbonMessage);
    } else if (ribbonHeadlineCueReached(visualFrame)) {
      drawRibbonHeadline(visualFrame.play);
    }

  }

  function playersFromSource(templatePlayers: any, sourcePlayers: any) {
    return templatePlayers.map((player: any) => {
      const source = findPlayer(sourcePlayers, player.role);
      return source ? { ...player, x: source.x, y: source.y, moving: source.moving } : { ...player };
    });
  }

  function transitionPlayers(templatePlayers: any, sourcePlayers: any, targetPlayers: any, amount: any) {
    const players = playersFromSource(templatePlayers, sourcePlayers);
    targetPlayers.forEach((target: any) => {
      movePlayer(players, target.role, target.x, target.y, amount, amount > 0, false);
    });
    return players;
  }

  function schemaDisplayName(value: any) {
    return String(value || "BALANCED").replace(/-/g, " ").toUpperCase();
  }

  function schemaHudAbbreviation(value: any) {
    const normalized = schemaDisplayName(value);
    const abbreviations: Record<string, string> = {
      "BALANCED": "BAL",
      "WEST COAST": "WC",
      "SPREAD": "SPR",
      "OPTION": "OPT",
      "POWER": "POW",
      "VERTICAL": "VERT",
      "FRONT": "FRONT",
      "SHELL": "SHELL",
      "PRESSURE": "PRESS",
      "MATCH": "MATCH",
    };
    return abbreviations[normalized] || normalized.slice(0, 5);
  }

  function drawHalftimeOverlay(intermission: any) {
    const score = intermission.score || state.game?.score || { home: 0, away: 0 };
    drawPixelLines(
      [`${state.game.away.abbr} ${score.away ?? 0}   ${score.home ?? 0} ${state.game.home.abbr}`],
      refs.canvas!.width / 2,
      155,
      22,
      {
        font: "bold 24px 'Lucida Console', monospace",
        align: "center",
        fill: "#f7fbff",
        glow: "rgba(255, 224, 102, 0.28)",
        shadowBlur: 10,
      }
    );

    drawPixelLines(
      ["COACHES REVIEWING SECOND-HALF PLAN"],
      refs.canvas!.width / 2,
      202,
      18,
      {
        font: "bold 17px 'Lucida Console', monospace",
        align: "center",
        fill: "#ffe066",
        glow: "rgba(255, 224, 102, 0.3)",
        shadowBlur: 8,
      }
    );
  }

  function drawQuarterBreakOverlay(intermission: any) {
    const score = intermission.score || state.game?.score || { home: 0, away: 0 };
    ctx.save();
    ctx.fillStyle = "rgba(4, 10, 20, 0.42)";
    ctx.fillRect(0, 0, refs.canvas!.width, refs.canvas!.height);
    ctx.restore();

    drawPixelLines(
      [`END OF ${String(quarterOrdinalLabel(intermission.quarter)).toUpperCase()} QUARTER`],
      refs.canvas!.width / 2,
      182,
      26,
      {
        font: "bold 26px 'Lucida Console', monospace",
        align: "center",
        fill: "#eef6ff",
        glow: "rgba(219, 232, 255, 0.32)",
        shadowBlur: 10,
      }
    );
    drawPixelLines(
      [`${state.game.away.abbr} ${score.away ?? 0}   ${score.home ?? 0} ${state.game.home.abbr}`],
      refs.canvas!.width / 2,
      220,
      18,
      {
        font: "bold 18px 'Lucida Console', monospace",
        align: "center",
        fill: "rgba(238, 246, 255, 0.94)",
        strokeWidth: 3,
      }
    );
  }

  function drawPostPlayIntermission(context: any, intermission: any, now: any) {
    const finalFrame = buildAnimatedFrame(context, 1);
    const anchoredCameraX = ensureIntermissionCameraAnchor(intermission, cameraTargetX(finalFrame));
    const settleMs = intermissionSettleMs(intermission.kind);
    const runoffMs = intermissionRunoffMs(intermission.kind);
    const panEndMs = intermissionPresentationStartMs(intermission);
    const cameraX = intermission.kind === "quarter-break"
      ? anchoredCameraX
      : snapToPixel(lerp(
        anchoredCameraX,
        midfieldCameraX(),
        phaseAmount(
          intermission.elapsedMs,
          settleMs + runoffMs,
          panEndMs,
        ),
      ));

    if (intermission.elapsedMs < settleMs) {
      drawPlayFrame({
        ...finalFrame,
        cameraXOverride: anchoredCameraX,
      });
      return;
    }

    if (intermission.elapsedMs < settleMs + runoffMs) {
      const runoffAmount = phaseAmount(intermission.elapsedMs, settleMs, settleMs + runoffMs);
      drawPlayFrame({
        ...finalFrame,
        offensePlayers: transitionPlayers(finalFrame.offensePlayers, finalFrame.offensePlayers, buildSidelineTargets(finalFrame.offensePlayers, "bottom"), runoffAmount),
        defensePlayers: transitionPlayers(finalFrame.defensePlayers, finalFrame.defensePlayers, buildSidelineTargets(finalFrame.defensePlayers, "top"), runoffAmount),
        scenePhase: intermission.kind === "halftime" ? "halftime-exit" : "final-exit",
        cameraXOverride: anchoredCameraX,
        hideMarkers: true,
        hideBall: true,
        ribbonMessage: intermission.ribbon,
      });
      return;
    }

    if (intermission.kind === "quarter-break") {
      drawPlayFrame({
        ...finalFrame,
        cameraXOverride: cameraX,
        ribbonMessage: intermission.ribbon,
      });
      drawQuarterBreakOverlay(intermission);
      return;
    }

    state.cameraX = cameraX;
    drawFieldBase(cameraX);
    drawRibbonMessage(intermission.ribbon);

    if (intermission.kind === "halftime") {
      if (intermission.elapsedMs < panEndMs) {
        return;
      }
      drawHalftimeOverlay(intermission);
      return;
    }

    drawFinalLeadersOverlay(intermission, now);
  }

  function buildScrimmageHuddle(context: any) {
    const offenseCenterX = orientX(context.losX, -104, context.direction);
    const defenseCenterX = orientX(context.losX, 88, context.direction);
    const offenseOffsets = [
      [-18, -24], [-6, -30], [8, -26], [20, -18],
      [-22, -4], [-10, 0], [4, 2], [18, 8],
      [-16, 18], [-2, 24], [14, 18],
    ];
    const defenseOffsets = [
      [-18, -28], [-4, -32], [10, -24], [22, -16],
      [-22, -2], [-8, 4], [6, 8], [20, 14],
      [-14, 24], [0, 30], [16, 24],
    ];

    return {
      offense: context.formation.offense.map((player: any, index: any) =>
        ({
          ...player,
          x: orientX(offenseCenterX, offenseOffsets[index][0], context.direction),
          y: context.centerY + offenseOffsets[index][1],
          moving: false,
        })
      ),
      defense: context.formation.defense.map((player: any, index: any) =>
        ({
          ...player,
          x: orientX(defenseCenterX, defenseOffsets[index][0], context.direction),
          y: context.centerY + defenseOffsets[index][1],
          moving: false,
        })
      ),
    };
  }

  function buildSidelineTargets(players: any, side: any) {
    const metrics = fieldMetrics();
    const baseY = side === "top" ? metrics.fieldTop - 34 : metrics.fieldBottom + 12;
    const yDirection = side === "top" ? -1 : 1;

    return players.map((player: any, index: any) => {
      const xOffset = ((index % 5) - 2) * 16;
      const rowOffset = Math.floor(index / 5) * 10 * yDirection;
      const targetX = Math.max(metrics.endZoneLeft + 36, Math.min(metrics.endZoneRight - 36, player.x + xOffset));

      return {
        ...player,
        x: snapToPixel(targetX),
        y: snapToPixel(baseY + rowOffset),
        moving: false,
      };
    });
  }

  function clearCanvasFrame() {
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, refs.canvas!.width, refs.canvas!.height);
    ctx.restore();
  }

  function drawStadiumBackdrop(metrics = fieldMetrics(), cameraX = 0) {
    const mobileLite = isMobilePerformanceMode();
    const fieldBackground = homeFieldVisuals().fieldBackground;
    const backgroundOption = fieldBackgroundOption(fieldBackground.texture);
    const backgroundTexture = shiftedImageAsset(
      fieldBackgroundAsset(backgroundOption.id),
      fieldBackground,
    );
    if (mobileLite) {
      const mobileBackdrop = mobileStadiumBackdropCanvas(metrics, backgroundTexture, backgroundOption);
      if (mobileBackdrop) {
        ctx.drawImage(mobileBackdrop, 0, 0);
        return;
      }
    }

    const skyGradient = ctx.createLinearGradient(0, 0, 0, metrics.fieldBottom + 40);
    skyGradient.addColorStop(0, "#050b18");
    skyGradient.addColorStop(0.48, "#091427");
    skyGradient.addColorStop(1, "#040914");
    ctx.fillStyle = skyGradient;
    ctx.fillRect(0, 0, refs.canvas!.width, refs.canvas!.height);
    if (imageReady(backgroundTexture)) {
      const backgroundPattern = ctx.createPattern(backgroundTexture, "repeat");
      if (backgroundPattern) {
        const textureWidth = backgroundTexture.naturalWidth || backgroundTexture.width;
        const textureHeight = backgroundTexture.naturalHeight || backgroundTexture.height;
        const offsetX = -((cameraX * 0.18) % textureWidth);
        const offsetY = -((cameraX * 0.045) % textureHeight);
        ctx.save();
        ctx.globalAlpha = backgroundOption.opacity;
        ctx.fillStyle = backgroundPattern;
        ctx.translate(offsetX, offsetY);
        ctx.fillRect(-textureWidth, -textureHeight, refs.canvas!.width + textureWidth * 2, refs.canvas!.height + textureHeight * 2);
        ctx.restore();
        ctx.save();
        ctx.globalAlpha = backgroundOption.shadeAlpha;
        ctx.fillStyle = "#020713";
        ctx.fillRect(0, 0, refs.canvas!.width, refs.canvas!.height);
        ctx.restore();
      }
    }

    drawStadiumBoardOverlay();
  }

  function mobileStadiumBackdropCanvas(metrics: any, backgroundTexture: any, backgroundOption: any) {
    const width = refs.canvas!.width;
    const height = refs.canvas!.height;
    const fieldVisuals = homeFieldVisuals();
    const visualKey = JSON.stringify({
      board: fieldVisuals.fieldBoardColor || {},
      background: fieldVisuals.fieldBackground || {},
    });
    const textureWidth = imageReady(backgroundTexture) ? backgroundTexture.naturalWidth || backgroundTexture.width || 0 : 0;
    const textureHeight = imageReady(backgroundTexture) ? backgroundTexture.naturalHeight || backgroundTexture.height || 0 : 0;
    if (
      mobileStadiumBackdropCache
      && mobileStadiumBackdropCache.width === width
      && mobileStadiumBackdropCache.height === height
      && mobileStadiumBackdropCache.fieldBottom === metrics.fieldBottom
      && mobileStadiumBackdropCache.visualKey === visualKey
      && mobileStadiumBackdropCache.textureWidth === textureWidth
      && mobileStadiumBackdropCache.textureHeight === textureHeight
    ) {
      return mobileStadiumBackdropCache.canvas;
    }

    const canvas = makeCanvas(width, height);
    const backdropCtx = canvas.getContext("2d");
    if (!backdropCtx) {
      return null;
    }

    const skyGradient = backdropCtx.createLinearGradient(0, 0, 0, metrics.fieldBottom + 40);
    skyGradient.addColorStop(0, "#050b18");
    skyGradient.addColorStop(0.48, "#091427");
    skyGradient.addColorStop(1, "#040914");
    backdropCtx.fillStyle = skyGradient;
    backdropCtx.fillRect(0, 0, width, height);
    drawStaticMobileBackgroundTexture(backdropCtx, backgroundTexture, backgroundOption, width, height);
    drawStadiumBoardOverlay(backdropCtx, width, true);
    mobileStadiumBackdropCache = {
      canvas,
      width,
      height,
      fieldBottom: metrics.fieldBottom,
      visualKey,
      textureWidth,
      textureHeight,
    };
    return canvas;
  }

  function drawStaticMobileBackgroundTexture(
    targetCtx: CanvasRenderingContext2D,
    backgroundTexture: any,
    backgroundOption: any,
    width: number,
    height: number,
  ) {
    if (!imageReady(backgroundTexture)) {
      return;
    }
    const pattern = targetCtx.createPattern(backgroundTexture, "repeat");
    if (!pattern) {
      return;
    }
    targetCtx.save();
    targetCtx.globalAlpha = clamp01(Number(backgroundOption.opacity) || 0);
    targetCtx.imageSmoothingEnabled = false;
    targetCtx.fillStyle = pattern;
    targetCtx.fillRect(0, 0, width, height);
    targetCtx.restore();
    targetCtx.save();
    targetCtx.globalAlpha = clamp01(Number(backgroundOption.shadeAlpha) || 0);
    targetCtx.fillStyle = "#020713";
    targetCtx.fillRect(0, 0, width, height);
    targetCtx.restore();
  }

  function drawStadiumBoardOverlay(targetCtx = ctx, canvasWidth = refs.canvas!.width, mobileLite = isMobilePerformanceMode()) {
    const boardVisual = homeFieldVisuals().fieldBoardColor;
    const boardTopColor = shiftHexColor("#18243c", boardVisual);
    const boardBottomColor = shiftHexColor("#0f1628", boardVisual);
    const ribbonTopColor = shiftHexColor("#9fe7ff", boardVisual);
    const ribbonBottomColor = shiftHexColor("#417cff", boardVisual);
    const crowdColors = ["#74d8ff", "#f5f7ff", "#4488ff", "#98f6ff", "#89a7ff"]
      .map((color) => shiftHexColor(color, boardVisual));
    const boardBottom = 46;
    const boardLineY = boardBottom - 10;

    const bowlGradient = targetCtx.createLinearGradient(0, 0, 0, boardBottom);
    bowlGradient.addColorStop(0, boardTopColor);
    bowlGradient.addColorStop(1, rgbaFromHex(boardBottomColor, 0.76));
    targetCtx.fillStyle = bowlGradient;
    targetCtx.fillRect(0, 0, canvasWidth, boardLineY);

    const crowdStep = mobileLite ? 16 : 8;
    const crowdSize = mobileLite ? 8 : 5;
    for (let y = 4; y < boardLineY - 4; y += crowdStep) {
      for (let x = 0; x < canvasWidth; x += crowdStep) {
        targetCtx.fillStyle = crowdColors[(Math.floor(x / crowdStep) + Math.floor(y / crowdStep) * 5) % crowdColors.length];
        targetCtx.globalAlpha = mobileLite ? 0.18 : 0.18 + (((x + y) / 8) % 3) * 0.08;
        targetCtx.fillRect(x, y, crowdSize, crowdSize);
      }
    }
    targetCtx.globalAlpha = 1;

    const ribbonGradient = targetCtx.createLinearGradient(0, boardLineY, 0, boardLineY + 4);
    ribbonGradient.addColorStop(0, ribbonTopColor);
    ribbonGradient.addColorStop(1, ribbonBottomColor);
    targetCtx.fillStyle = ribbonGradient;
    targetCtx.fillRect(0, boardLineY, canvasWidth, 4);
  }

  function mobileFieldSurfaceKey(metrics: any) {
    const game = state.game || {};
    const fieldVisuals = homeFieldVisuals();
    const grassTexture = loadImageAsset(GRASS_TEXTURE_ASSET);
    const logoConfig = {
      ...visualConfig.midfieldLogo,
      src: fieldVisuals.logo.src || visualConfig.midfieldLogo.src,
    };
    const hasHomeLogo = !!logoConfig.src && !isDefaultFieldLogoSrc(logoConfig.src);
    const logoImage = hasHomeLogo ? shiftedImageAsset(logoConfig.src, fieldVisuals.logo) : null;
    return JSON.stringify({
      width: metrics.worldWidth,
      height: refs.canvas!.height,
      fieldTop: metrics.fieldTop,
      fieldBottom: metrics.fieldBottom,
      grassReady: imageReady(grassTexture),
      logo: fieldLogoCacheSignature(logoConfig.src, logoImage),
      home: {
        id: game.home?.id,
        primary: game.home?.colors?.primary,
        secondary: game.home?.colors?.secondary,
      },
      away: {
        id: game.away?.id,
        primary: game.away?.colors?.primary,
        secondary: game.away?.colors?.secondary,
      },
      visuals: fieldVisuals,
    });
  }

  function drawGoalPostSpriteOnContext(targetCtx: CanvasRenderingContext2D, side: any, cameraX = 0) {
    const fieldVisuals = homeFieldVisuals();
    const poleImage = shiftedImageAsset(GOAL_POST_ASSET, fieldVisuals.fieldGoalPostColor);
    const baseImage = shiftedImageAsset(GOAL_BASE_ASSET, fieldVisuals.fieldGoalBaseColor);
    if (!imageReady(poleImage) || !imageReady(baseImage)) {
      return false;
    }

    const metrics = fieldMetrics();
    const isLeft = side === "left";
    const baseWidth = poleImage.naturalWidth || 17;
    const baseHeight = poleImage.naturalHeight || 82;
    const scale = 2;
    const spriteWidth = Math.round(baseWidth * scale);
    const spriteHeight = Math.round(baseHeight * scale);
    const anchorWorldX = isLeft ? metrics.endZoneLeft - 21 : metrics.endZoneRight - 3;
    const drawX = worldToScreenX(anchorWorldX, cameraX);
    const drawY = snapToPixel(metrics.fieldTop + 76);

    targetCtx.save();
    if (isLeft) {
      targetCtx.drawImage(baseImage, drawX, drawY, spriteWidth, spriteHeight);
      targetCtx.drawImage(poleImage, drawX, drawY, spriteWidth, spriteHeight);
    } else {
      targetCtx.translate(drawX + spriteWidth, drawY);
      targetCtx.scale(-1, 1);
      targetCtx.drawImage(baseImage, 0, 0, spriteWidth, spriteHeight);
      targetCtx.drawImage(poleImage, 0, 0, spriteWidth, spriteHeight);
    }
    targetCtx.restore();
    return true;
  }

  function drawMobileCachedMidfieldLogo(targetCtx: CanvasRenderingContext2D, metrics: any) {
    const fieldVisuals = homeFieldVisuals();
    const config = {
      ...visualConfig.midfieldLogo,
      src: fieldVisuals.logo.src || visualConfig.midfieldLogo.src,
    };
    if (!config?.enabled) {
      return;
    }
    const hasHomeLogo = !!config.src && !isDefaultFieldLogoSrc(config.src);
    const image = hasHomeLogo ? shiftedImageAsset(config.src, fieldVisuals.logo) : null;
    if (!imageReady(image)) {
      return;
    }
    const centerX = yardToX(50);
    const centerY = metrics.centerY;
    const width = Number(config.width || 168);
    const height = Number(config.height || 168);
    targetCtx.save();
    targetCtx.globalAlpha = 0.18;
    targetCtx.imageSmoothingEnabled = true;
    targetCtx.drawImage(image, centerX - width / 2, centerY - height / 2, width, height);
    targetCtx.restore();
  }

  function mobileFieldSurfaceCanvas(metrics: any) {
    const width = metrics.worldWidth;
    const height = refs.canvas!.height;
    const key = mobileFieldSurfaceKey(metrics);
    if (
      mobileFieldSurfaceCache
      && mobileFieldSurfaceCache.width === width
      && mobileFieldSurfaceCache.height === height
      && mobileFieldSurfaceCache.key === key
    ) {
      return mobileFieldSurfaceCache.canvas;
    }

    const canvas = makeCanvas(width, height);
    const fieldCtx = canvas.getContext("2d");
    if (!fieldCtx) {
      return null;
    }
    fieldCtx.imageSmoothingEnabled = false;

    const fieldVisuals = homeFieldVisuals();
    const grassTexture = loadImageAsset(GRASS_TEXTURE_ASSET);
    const playableWidth = metrics.goalRight - metrics.goalLeft;
    const runoffTop = metrics.fieldTop - 12;
    const runoffBottom = metrics.fieldBottom + 14;
    const postPadDepth = 44;
    const postPadOuterColor = shiftHexColor("#05101c", fieldVisuals.homeFieldColor);
    const postPadInnerColor = shiftHexColor("#0a243c", fieldVisuals.homeFieldColor);
    const markingMajorColor = shiftHexColor("#b6f4ff", fieldVisuals.fieldMarkingColor);
    const markingMinorColor = shiftHexColor("#6cccff", fieldVisuals.fieldMarkingColor);
    const markingHashColor = shiftHexColor("#d6faff", fieldVisuals.fieldMarkingColor);
    const markingBoundaryColor = shiftHexColor("#dbfaff", fieldVisuals.fieldMarkingColor);
    const markingRunoffColor = shiftHexColor("#8fe6ff", fieldVisuals.fieldMarkingColor);
    const markingEndZoneColor = shiftHexColor("#aaeaff", fieldVisuals.fieldMarkingColor);

    const fieldGradient = fieldCtx.createLinearGradient(0, metrics.fieldTop, 0, metrics.fieldBottom);
    fieldGradient.addColorStop(0, shiftHexColor("#102d4c", fieldVisuals.homeFieldColor));
    fieldGradient.addColorStop(0.45, shiftHexColor("#0d4068", fieldVisuals.homeFieldColor));
    fieldGradient.addColorStop(1, shiftHexColor("#0b2743", fieldVisuals.homeFieldColor));
    fieldCtx.fillStyle = fieldGradient;
    fieldCtx.fillRect(metrics.goalLeft, metrics.fieldTop, playableWidth, metrics.fieldHeight);
    if (imageReady(grassTexture)) {
      const grassPattern = fieldCtx.createPattern(grassTexture, "repeat");
      if (grassPattern) {
        fieldCtx.save();
        fieldCtx.globalAlpha = 0.14;
        fieldCtx.fillStyle = grassPattern;
        fieldCtx.translate(metrics.goalLeft % grassTexture.naturalWidth, metrics.fieldTop % grassTexture.naturalHeight);
        fieldCtx.fillRect(
          metrics.goalLeft - (metrics.goalLeft % grassTexture.naturalWidth),
          metrics.fieldTop - (metrics.fieldTop % grassTexture.naturalHeight),
          playableWidth + grassTexture.naturalWidth,
          metrics.fieldHeight + grassTexture.naturalHeight,
        );
        fieldCtx.restore();
      }
    }

    const leftEndGradient = fieldCtx.createLinearGradient(metrics.endZoneLeft, 0, metrics.endZoneLeft + metrics.endZoneWidth, 0);
    leftEndGradient.addColorStop(0, "#112845");
    leftEndGradient.addColorStop(1, state.game?.home?.colors?.primary || "#385dff");
    fieldCtx.fillStyle = leftEndGradient;
    fieldCtx.fillRect(metrics.endZoneLeft, metrics.fieldTop, metrics.endZoneWidth, metrics.fieldHeight);

    const rightEndGradient = fieldCtx.createLinearGradient(metrics.goalRight, 0, metrics.goalRight + metrics.endZoneWidth, 0);
    rightEndGradient.addColorStop(0, state.game?.away?.colors?.primary || "#385dff");
    rightEndGradient.addColorStop(1, "#112845");
    fieldCtx.fillStyle = rightEndGradient;
    fieldCtx.fillRect(metrics.goalRight, metrics.fieldTop, metrics.endZoneWidth, metrics.fieldHeight);

    const topRunoffGradient = fieldCtx.createLinearGradient(0, runoffTop, 0, metrics.fieldTop);
    topRunoffGradient.addColorStop(0, rgbaFromHex(postPadOuterColor, 0.74));
    topRunoffGradient.addColorStop(1, rgbaFromHex(postPadInnerColor, 0.94));
    fieldCtx.fillStyle = topRunoffGradient;
    fieldCtx.fillRect(metrics.endZoneLeft, runoffTop, metrics.endZoneRight - metrics.endZoneLeft, metrics.fieldTop - runoffTop);

    const bottomRunoffGradient = fieldCtx.createLinearGradient(0, metrics.fieldBottom, 0, runoffBottom);
    bottomRunoffGradient.addColorStop(0, rgbaFromHex(postPadInnerColor, 0.94));
    bottomRunoffGradient.addColorStop(1, rgbaFromHex(postPadOuterColor, 0.74));
    fieldCtx.fillStyle = bottomRunoffGradient;
    fieldCtx.fillRect(metrics.endZoneLeft, metrics.fieldBottom, metrics.endZoneRight - metrics.endZoneLeft, runoffBottom - metrics.fieldBottom);

    fieldCtx.fillStyle = rgbaFromHex(postPadOuterColor, 0.78);
    fieldCtx.fillRect(metrics.endZoneLeft - postPadDepth, runoffTop, postPadDepth, runoffBottom - runoffTop);
    fieldCtx.fillRect(metrics.endZoneRight, runoffTop, postPadDepth, runoffBottom - runoffTop);

    for (let stripe = 0; stripe < 20; stripe += 1) {
      const stripeStartX = yardToX(stripe * 5);
      const stripeEndX = yardToX(stripe * 5 + 5);
      fieldCtx.fillStyle = stripe % 2 === 0 ? "rgba(90, 212, 255, 0.05)" : "rgba(18, 135, 255, 0.12)";
      fieldCtx.fillRect(stripeStartX, metrics.fieldTop, stripeEndX - stripeStartX, metrics.fieldHeight);
    }

    fieldCtx.strokeStyle = rgbaFromHex(markingMinorColor, 0.22);
    fieldCtx.lineWidth = 1;
    for (let yard = 0; yard <= 100; yard += 5) {
      const x = yardToX(yard);
      fieldCtx.beginPath();
      fieldCtx.moveTo(x, metrics.fieldTop);
      fieldCtx.lineTo(x, metrics.fieldBottom);
      fieldCtx.stroke();
    }

    for (let yard = 0; yard <= 100; yard += 5) {
      const x = yardToX(yard);
      fieldCtx.strokeStyle = yard % 10 === 0 ? rgbaFromHex(markingMajorColor, 0.95) : rgbaFromHex(markingMinorColor, 0.34);
      fieldCtx.lineWidth = yard % 10 === 0 ? 3 : 1;
      fieldCtx.beginPath();
      fieldCtx.moveTo(x, metrics.fieldTop);
      fieldCtx.lineTo(x, metrics.fieldBottom);
      fieldCtx.stroke();
    }

    const hashRows = [metrics.centerY - 62, metrics.centerY + 58];
    fieldCtx.strokeStyle = rgbaFromHex(markingHashColor, 0.82);
    fieldCtx.lineWidth = 2;
    hashRows.forEach((hashY) => {
      for (let yard = 0; yard <= 100; yard += 1) {
        const x = yardToX(yard);
        fieldCtx.beginPath();
        fieldCtx.moveTo(x, hashY - 4);
        fieldCtx.lineTo(x, hashY + 4);
        fieldCtx.stroke();
      }
    });

    fieldCtx.strokeStyle = rgbaFromHex(markingBoundaryColor, 0.98);
    fieldCtx.lineWidth = 4;
    fieldCtx.beginPath();
    fieldCtx.moveTo(metrics.endZoneLeft, metrics.fieldTop);
    fieldCtx.lineTo(metrics.endZoneRight, metrics.fieldTop);
    fieldCtx.moveTo(metrics.endZoneLeft, metrics.fieldBottom);
    fieldCtx.lineTo(metrics.endZoneRight, metrics.fieldBottom);
    fieldCtx.stroke();

    fieldCtx.strokeStyle = rgbaFromHex(markingRunoffColor, 0.8);
    fieldCtx.lineWidth = 3;
    fieldCtx.strokeRect(metrics.endZoneLeft, runoffTop, metrics.endZoneRight - metrics.endZoneLeft, runoffBottom - runoffTop);
    fieldCtx.strokeStyle = rgbaFromHex(markingEndZoneColor, 0.95);
    fieldCtx.strokeRect(metrics.endZoneLeft, metrics.fieldTop, metrics.endZoneWidth, metrics.fieldHeight);
    fieldCtx.strokeRect(metrics.goalRight, metrics.fieldTop, metrics.endZoneWidth, metrics.fieldHeight);

    drawMobileCachedMidfieldLogo(fieldCtx, metrics);
    drawGoalPostSpriteOnContext(fieldCtx, "left", 0);
    drawGoalPostSpriteOnContext(fieldCtx, "right", 0);

    fieldCtx.font = "30px 'Lucida Console', monospace";
    fieldCtx.textAlign = "center";
    fieldCtx.lineWidth = 8;
    fieldCtx.strokeStyle = "rgba(10, 24, 39, 0.9)";
    fieldCtx.fillStyle = markingHashColor;
    for (let yard = 10; yard < 100; yard += 10) {
      const x = yardToX(yard);
      const display = yard > 50 ? 100 - yard : yard;
      fieldCtx.strokeText(String(display), x, metrics.fieldTop + 36);
      fieldCtx.fillText(String(display), x, metrics.fieldTop + 36);
      fieldCtx.strokeText(String(display), x, metrics.fieldBottom - 16);
      fieldCtx.fillText(String(display), x, metrics.fieldBottom - 16);
    }

    mobileFieldSurfaceCache = { canvas, width, height, key };
    return canvas;
  }

  function drawMobileFieldSurface(cameraX = 0, metrics = fieldMetrics()) {
    const surface = mobileFieldSurfaceCanvas(metrics);
    if (!surface) {
      drawFieldSurface(cameraX, metrics);
      return;
    }
    // Keep world pixels available outside the current viewport. Kick attempts
    // zoom below 1 after this draw, and a pre-clipped source slice otherwise
    // exposes empty backdrop along the mobile edges.
    ctx.drawImage(surface, snapToPixel(-cameraX), 0);
  }

  function drawFieldSurface(cameraX = 0, metrics = fieldMetrics()) {
    const mobileLite = isMobilePerformanceMode();
    const leftEndZoneX = worldToScreenX(metrics.endZoneLeft, cameraX);
    const rightEndZoneX = worldToScreenX(metrics.goalRight, cameraX);
    const fieldLeftX = worldToScreenX(metrics.goalLeft, cameraX);
    const playableWidth = metrics.goalRight - metrics.goalLeft;
    const fullFieldLeft = worldToScreenX(metrics.endZoneLeft, cameraX);
    const fullFieldRight = worldToScreenX(metrics.endZoneRight, cameraX);

    const runoffTop = metrics.fieldTop - 12;
    const runoffBottom = metrics.fieldBottom + 14;
    const grassTexture = loadImageAsset(GRASS_TEXTURE_ASSET);

    const fieldVisuals = homeFieldVisuals();
    const postPadOuterColor = shiftHexColor("#05101c", fieldVisuals.homeFieldColor);
    const postPadInnerColor = shiftHexColor("#0a243c", fieldVisuals.homeFieldColor);
    const markingSubtleColor = shiftHexColor("#73e1ff", fieldVisuals.fieldMarkingColor);
    const markingAccentColor = shiftHexColor("#54dcff", fieldVisuals.fieldMarkingColor);
    const markingMajorColor = shiftHexColor("#b6f4ff", fieldVisuals.fieldMarkingColor);
    const markingMinorColor = shiftHexColor("#6cccff", fieldVisuals.fieldMarkingColor);
    const markingHashColor = shiftHexColor("#d6faff", fieldVisuals.fieldMarkingColor);
    const markingBoundaryColor = shiftHexColor("#dbfaff", fieldVisuals.fieldMarkingColor);
    const markingRunoffColor = shiftHexColor("#8fe6ff", fieldVisuals.fieldMarkingColor);
    const markingEndZoneColor = shiftHexColor("#aaeaff", fieldVisuals.fieldMarkingColor);
    const markingShadowColor = shiftHexColor("#61e7ff", fieldVisuals.fieldMarkingColor);
    const fieldGradient = ctx.createLinearGradient(0, metrics.fieldTop, 0, metrics.fieldBottom);
    fieldGradient.addColorStop(0, shiftHexColor("#102d4c", fieldVisuals.homeFieldColor));
    fieldGradient.addColorStop(0.45, shiftHexColor("#0d4068", fieldVisuals.homeFieldColor));
    fieldGradient.addColorStop(1, shiftHexColor("#0b2743", fieldVisuals.homeFieldColor));
    ctx.fillStyle = fieldGradient;
    ctx.fillRect(fieldLeftX, metrics.fieldTop, playableWidth, metrics.fieldHeight);
    if (imageReady(grassTexture)) {
      const grassPattern = ctx.createPattern(grassTexture, "repeat");
      if (grassPattern) {
        ctx.save();
        ctx.globalAlpha = 0.18;
        ctx.fillStyle = grassPattern;
        ctx.translate(Math.round(-cameraX), 0);
        // Paint in world space so field-goal/PAT zoom-out cannot reveal a seam
        // between the base gradient and its texture overlay.
        ctx.fillRect(metrics.goalLeft, metrics.fieldTop, playableWidth, metrics.fieldHeight);
        ctx.restore();
      }
    }

    const leftEndGradient = ctx.createLinearGradient(leftEndZoneX, 0, leftEndZoneX + metrics.endZoneWidth, 0);
    leftEndGradient.addColorStop(0, "#112845");
    leftEndGradient.addColorStop(1, state.game?.home?.colors?.primary || "#385dff");
    ctx.fillStyle = leftEndGradient;
    ctx.fillRect(leftEndZoneX, metrics.fieldTop, metrics.endZoneWidth, metrics.fieldHeight);

    const rightEndGradient = ctx.createLinearGradient(rightEndZoneX, 0, rightEndZoneX + metrics.endZoneWidth, 0);
    rightEndGradient.addColorStop(0, state.game?.away?.colors?.primary || "#385dff");
    rightEndGradient.addColorStop(1, "#112845");
    ctx.fillStyle = rightEndGradient;
    ctx.fillRect(rightEndZoneX, metrics.fieldTop, metrics.endZoneWidth, metrics.fieldHeight);

    const topRunoffGradient = ctx.createLinearGradient(0, runoffTop, 0, metrics.fieldTop);
    topRunoffGradient.addColorStop(0, rgbaFromHex(postPadOuterColor, 0.74));
    topRunoffGradient.addColorStop(1, rgbaFromHex(postPadInnerColor, 0.94));
    ctx.fillStyle = topRunoffGradient;
    ctx.fillRect(fullFieldLeft, runoffTop, metrics.endZoneRight - metrics.endZoneLeft, metrics.fieldTop - runoffTop);

    const bottomRunoffGradient = ctx.createLinearGradient(0, metrics.fieldBottom, 0, runoffBottom);
    bottomRunoffGradient.addColorStop(0, rgbaFromHex(postPadInnerColor, 0.94));
    bottomRunoffGradient.addColorStop(1, rgbaFromHex(postPadOuterColor, 0.74));
    ctx.fillStyle = bottomRunoffGradient;
    ctx.fillRect(fullFieldLeft, metrics.fieldBottom, metrics.endZoneRight - metrics.endZoneLeft, runoffBottom - metrics.fieldBottom);

    const postPadDepth = 44;
    const leftPostPadGradient = ctx.createLinearGradient(fullFieldLeft - postPadDepth, 0, fullFieldLeft, 0);
    leftPostPadGradient.addColorStop(0, rgbaFromHex(postPadOuterColor, 0.72));
    leftPostPadGradient.addColorStop(1, rgbaFromHex(postPadInnerColor, 0.92));
    ctx.fillStyle = leftPostPadGradient;
    ctx.fillRect(fullFieldLeft - postPadDepth, runoffTop, postPadDepth, runoffBottom - runoffTop);

    const rightPostPadGradient = ctx.createLinearGradient(fullFieldRight, 0, fullFieldRight + postPadDepth, 0);
    rightPostPadGradient.addColorStop(0, rgbaFromHex(postPadInnerColor, 0.92));
    rightPostPadGradient.addColorStop(1, rgbaFromHex(postPadOuterColor, 0.72));
    ctx.fillStyle = rightPostPadGradient;
    ctx.fillRect(fullFieldRight, runoffTop, postPadDepth, runoffBottom - runoffTop);

    ctx.strokeStyle = rgbaFromHex(markingRunoffColor, 0.64);
    ctx.lineWidth = 2;
    ctx.strokeRect(fullFieldLeft, runoffTop, metrics.endZoneRight - metrics.endZoneLeft, metrics.fieldTop - runoffTop);
    ctx.strokeRect(fullFieldLeft, metrics.fieldBottom, metrics.endZoneRight - metrics.endZoneLeft, runoffBottom - metrics.fieldBottom);
    ctx.strokeRect(fullFieldLeft - postPadDepth, runoffTop, postPadDepth, runoffBottom - runoffTop);
    ctx.strokeRect(fullFieldRight, runoffTop, postPadDepth, runoffBottom - runoffTop);

    for (let stripe = 0; stripe < 20; stripe += 1) {
      const stripeStartX = worldToScreenX(yardToX(stripe * 5), cameraX);
      const stripeEndX = worldToScreenX(yardToX(stripe * 5 + 5), cameraX);
      ctx.fillStyle = stripe % 2 === 0 ? "rgba(90, 212, 255, 0.05)" : "rgba(18, 135, 255, 0.12)";
      ctx.fillRect(stripeStartX, metrics.fieldTop, stripeEndX - stripeStartX, metrics.fieldHeight);
    }

    ctx.save();
    ctx.strokeStyle = rgbaFromHex(markingSubtleColor, 0.11);
    ctx.lineWidth = 1;
    for (let y = metrics.fieldTop + 14; y < metrics.fieldBottom - 12; y += 18) {
      ctx.beginPath();
      ctx.moveTo(fieldLeftX, y);
      ctx.lineTo(fieldLeftX + playableWidth, y);
      ctx.stroke();
    }
    for (let yard = 0; yard <= 100; yard += 5) {
      const x = worldToScreenX(yardToX(yard), cameraX);
      ctx.beginPath();
      ctx.moveTo(x, metrics.fieldTop);
      ctx.lineTo(x, metrics.fieldBottom);
      ctx.stroke();
    }
    ctx.restore();

    if (!mobileLite) {
      ctx.save();
      ctx.strokeStyle = rgbaFromHex(markingAccentColor, 0.14);
      ctx.lineWidth = 1;
      for (let yard = 5; yard < 100; yard += 10) {
        const x = worldToScreenX(yardToX(yard), cameraX);
        ctx.beginPath();
        ctx.moveTo(x - 14, metrics.centerY - 66);
        ctx.lineTo(x + 14, metrics.centerY - 48);
        ctx.moveTo(x - 14, metrics.centerY + 66);
        ctx.lineTo(x + 14, metrics.centerY + 48);
        ctx.stroke();
      }
      ctx.restore();
    }

    drawMidfieldLogo(cameraX);

    for (let yard = 0; yard <= 100; yard += 5) {
      const x = worldToScreenX(yardToX(yard), cameraX);
      if (x < -8 || x > refs.canvas!.width + 8) {
        continue;
      }
      ctx.strokeStyle = yard % 10 === 0 ? rgbaFromHex(markingMajorColor, 0.95) : rgbaFromHex(markingMinorColor, 0.34);
      ctx.lineWidth = yard % 10 === 0 ? 3 : 1;
      ctx.beginPath();
      ctx.moveTo(x, metrics.fieldTop);
      ctx.lineTo(x, metrics.fieldBottom);
      ctx.stroke();
    }

    const hashRows = [metrics.centerY - 62, metrics.centerY + 58];
    ctx.strokeStyle = rgbaFromHex(markingHashColor, 0.88);
    ctx.lineWidth = 2;
    hashRows.forEach((hashY) => {
      for (let yard = 0; yard <= 100; yard += 1) {
        const x = worldToScreenX(yardToX(yard), cameraX);
        if (x < -6 || x > refs.canvas!.width + 6) {
          continue;
        }
        ctx.beginPath();
        ctx.moveTo(x, hashY - 4);
        ctx.lineTo(x, hashY + 4);
        ctx.stroke();
      }
    });

    ctx.strokeStyle = rgbaFromHex(markingBoundaryColor, 0.98);
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(worldToScreenX(metrics.endZoneLeft, cameraX), metrics.fieldTop);
    ctx.lineTo(worldToScreenX(metrics.endZoneRight, cameraX), metrics.fieldTop);
    ctx.moveTo(worldToScreenX(metrics.endZoneLeft, cameraX), metrics.fieldBottom);
    ctx.lineTo(worldToScreenX(metrics.endZoneRight, cameraX), metrics.fieldBottom);
    ctx.stroke();

    ctx.strokeStyle = rgbaFromHex(markingRunoffColor, 0.8);
    ctx.lineWidth = 3;
    ctx.strokeRect(fullFieldLeft, runoffTop, metrics.endZoneRight - metrics.endZoneLeft, runoffBottom - runoffTop);
    ctx.strokeStyle = rgbaFromHex(markingEndZoneColor, 0.95);
    ctx.strokeRect(leftEndZoneX, metrics.fieldTop, metrics.endZoneWidth, metrics.fieldHeight);
    ctx.strokeRect(rightEndZoneX, metrics.fieldTop, metrics.endZoneWidth, metrics.fieldHeight);

    drawGoalPostSprite("left", cameraX);
    drawGoalPostSprite("right", cameraX);

    ctx.font = "30px 'Lucida Console', monospace";
    ctx.textAlign = "center";
    ctx.lineWidth = 8;
    ctx.strokeStyle = "rgba(10, 24, 39, 0.9)";
    ctx.fillStyle = markingHashColor;
    ctx.shadowBlur = mobileLite ? 0 : 14;
    ctx.shadowColor = rgbaFromHex(markingShadowColor, 0.52);
    for (let yard = 10; yard < 100; yard += 10) {
      const x = worldToScreenX(yardToX(yard), cameraX);
      if (x < -48 || x > refs.canvas!.width + 48) {
        continue;
      }
      const display = yard > 50 ? 100 - yard : yard;
      ctx.strokeText(String(display), x, metrics.fieldTop + 36);
      ctx.fillText(String(display), x, metrics.fieldTop + 36);
      ctx.strokeText(String(display), x, metrics.fieldBottom - 16);
      ctx.fillText(String(display), x, metrics.fieldBottom - 16);
    }
    ctx.shadowBlur = 0;
  }

  function drawFieldBase(cameraX = 0) {
    const metrics = fieldMetrics();
    clearCanvasFrame();
    drawStadiumBackdrop(metrics, cameraX);
    if (isMobilePerformanceMode()) {
      drawMobileFieldSurface(cameraX, metrics);
    } else {
      drawFieldSurface(cameraX, metrics);
    }
    if (!isMobilePerformanceMode()) {
      drawStadiumBoardOverlay();
    }
  }

  function drawGoalPostSprite(side: any, cameraX = 0) {
    const fieldVisuals = homeFieldVisuals();
    const poleImage = shiftedImageAsset(GOAL_POST_ASSET, fieldVisuals.fieldGoalPostColor);
    const baseImage = shiftedImageAsset(GOAL_BASE_ASSET, fieldVisuals.fieldGoalBaseColor);
    if (!imageReady(poleImage) || !imageReady(baseImage)) {
      return false;
    }

    const metrics = fieldMetrics();
    const isLeft = side === "left";
    const baseWidth = poleImage.naturalWidth || 17;
    const baseHeight = poleImage.naturalHeight || 82;
    const scale = 2;
    const spriteWidth = Math.round(baseWidth * scale);
    const spriteHeight = Math.round(baseHeight * scale);
    const anchorWorldX = isLeft ? metrics.endZoneLeft - 21 : metrics.endZoneRight - 3;
    const drawX = worldToScreenX(anchorWorldX, cameraX);
    const drawY = snapToPixel(metrics.fieldTop + 76);
    const visiblePadding = 56;

    if (drawX < -spriteWidth - visiblePadding || drawX > refs.canvas!.width + visiblePadding) {
      return false;
    }

    ctx.save();
    if (isLeft) {
      ctx.drawImage(baseImage, drawX, drawY, spriteWidth, spriteHeight);
      ctx.drawImage(poleImage, drawX, drawY, spriteWidth, spriteHeight);
    } else {
      ctx.translate(drawX + spriteWidth, drawY);
      ctx.scale(-1, 1);
      ctx.drawImage(baseImage, 0, 0, spriteWidth, spriteHeight);
      ctx.drawImage(poleImage, 0, 0, spriteWidth, spriteHeight);
    }
    ctx.restore();
    return true;
  }

  function drawUprights(centerY: any, direction: any, cameraX = 0) {
    const drawn = drawGoalPostSprite(direction < 0 ? "left" : "right", cameraX);
    if (drawn) {
      return;
    }

    const postWorldX = goalPostWorldX(direction);
    const postX = worldToScreenX(postWorldX, cameraX);
    if (postX < -48 || postX > refs.canvas!.width + 48) {
      return;
    }

    ctx.strokeStyle = shiftHexColor("#ffd84d", homeFieldVisuals().fieldGoalPostColor);
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(postX, centerY + 18);
    ctx.lineTo(postX, centerY - 104);
    ctx.moveTo(postX, centerY - 104);
    ctx.lineTo(postX - 36, centerY - 104);
    ctx.moveTo(postX, centerY - 104);
    ctx.lineTo(postX + 36, centerY - 104);
    ctx.stroke();
  }

  function animateFormation(play: any, offensePlayers: any, defensePlayers: any, progress: any, eased: any, losX: any, endX: any, laneY: any, centerY: any, direction: any) {
    const resultText = (play.result || "").toLowerCase();
    const xDelta = (value: any) => value * direction;
    const orientMax = (base: any, target: any) => (direction === 1 ? Math.max(base, target) : Math.min(base, target));
    let { ballX, ballY } = scrimmageBallPoint(offensePlayers, direction, losX, centerY - 8);

    if (play.playId === "kickoff") {
      const stopReason = stopReasonForPlay(play);
      const kickoffOutcome = play.kickoffOutcome || "";
      const touchback = stopReason === "touchback";
      const deadShort = stopReason === "dead-short";
      const noReturn = touchback || deadShort;
      const lane = clampRange(Math.round(play.returnLane ?? 0), -2, 2);
      const activeReturnerRole = lane > 0 ? "RETURNER2" : "RETURNER1";
      const decoyReturnerRole = activeReturnerRole === "RETURNER1" ? "RETURNER2" : "RETURNER1";
      const landingYard = Math.max(74, Math.min(100, play.landingYardLine ?? 95));
      const fieldedYard = Math.max(80, Math.min(100, play.fieldedYardLine ?? landingYard));
      const fieldedX = screenXForYard(fieldedYard, direction);
      const laneTargetY = kickoffReturnLaneY(play, centerY);
      const finishY = kickoffFinishY(play, centerY);
      const catchProgress = Math.min(1, progress / 0.58);
      const secureProgress = Math.max(0, Math.min(1, (progress - 0.58) / 0.14));
      const returnProgress = Math.max(0, (progress - 0.72) / 0.28);
      const coverageRoles = Array.from({ length: 10 }, (_, index) => "COVER" + (index + 1));
      const frontRoles = Array.from({ length: 5 }, (_, index) => "FRONT" + (index + 1));
      const wallRoles = Array.from({ length: 4 }, (_, index) => "WALL" + (index + 1));
      const forwardReturnX = (baseX: any, targetX: any) => (direction > 0 ? Math.min(baseX, targetX) : Math.max(baseX, targetX));
      const coverageAdvanceX = (baseX: any, targetX: any) => (direction > 0 ? Math.max(baseX, targetX) : Math.min(baseX, targetX));

      movePlayer(defensePlayers, activeReturnerRole, fieldedX, laneTargetY, catchProgress, true, true, "natural");
      movePlayer(
        defensePlayers,
        decoyReturnerRole,
        findPlayer(defensePlayers, decoyReturnerRole)?.x ?? fieldedX,
        laneTargetY + (activeReturnerRole === "RETURNER1" ? 84 : -84),
        Math.min(1, secureProgress * 0.35),
        false,
        true,
        "natural"
      );

      if (!noReturn && progress >= 0.72) {
        movePlayer(defensePlayers, activeReturnerRole, endX, finishY, returnProgress, true, true, "natural");
      }

      frontRoles.forEach((role, index) => {
        const basePlayer = findPlayer(defensePlayers, role);
        if (!basePlayer) {
          return;
        }
        const blockY = clampRange(laneTargetY + [-110, -56, 0, 56, 110][index], fieldMetrics().fieldTop + 20, fieldMetrics().fieldBottom - 20);
        const blockX = forwardReturnX(basePlayer.x, basePlayer.x - direction * 16);
        if (!noReturn && progress >= 0.72) {
          movePlayer(defensePlayers, role, blockX, blockY, returnProgress, true, true, "natural");
        }
      });

      wallRoles.forEach((role, index) => {
        const basePlayer = findPlayer(defensePlayers, role);
        if (!basePlayer) {
          return;
        }
        const blockY = clampRange(laneTargetY + [-84, -28, 28, 84][index], fieldMetrics().fieldTop + 20, fieldMetrics().fieldBottom - 20);
        const blockX = forwardReturnX(basePlayer.x, basePlayer.x - direction * 10);
        if (!noReturn && progress >= 0.72) {
          movePlayer(defensePlayers, role, blockX, blockY, returnProgress, true, true, "natural");
        }
      });

      coverageRoles.forEach((role, index) => {
        const basePlayer = findPlayer(offensePlayers, role);
        if (!basePlayer) {
          return;
        }
        const shellY = clampRange(laneTargetY + (index - 4.5) * 22, fieldMetrics().fieldTop + 18, fieldMetrics().fieldBottom - 18);
        const shellX = coverageAdvanceX(basePlayer.x, basePlayer.x + direction * (14 + Math.abs(index - 4.5) * 3));
        if (progress >= 0.72) {
          movePlayer(offensePlayers, role, shellX, shellY, returnProgress * 0.76, true, true, "natural");
        }
      });

      const activeReturner = findPlayer(defensePlayers, activeReturnerRole);
      if (touchback) {
        ballX = snapToPixel(lerp(losX, screenXForYard(100, direction) + 6, progress));
        ballY = snapToPixel(lerp(centerY - 10, laneTargetY + 8, progress) - Math.sin(progress * Math.PI) * 110);
      } else if (deadShort) {
        ballX = screenXForYard(play.deadBallYardLine ?? landingYard, direction) + 6;
        ballY = centerY + 8;
      } else if (kickoffOutcome === "landing-zone-roll-touchback") {
        const rollProgress = Math.max(0, Math.min(1, (progress - 0.58) / 0.42));
        ballX = snapToPixel(lerp(fieldedX + 6, screenXForYard(100, direction) + 6, rollProgress));
        ballY = snapToPixel(lerp(laneTargetY + 8, laneTargetY + 8, rollProgress));
      } else if (activeReturner) {
        if (progress < 0.58) {
          ballX = snapToPixel(lerp(losX, fieldedX + 6, catchProgress));
          ballY = snapToPixel(lerp(centerY - 6, laneTargetY + 8, catchProgress) - Math.sin(catchProgress * Math.PI) * 118);
        } else {
          const carryBall = carriedBallPoint(activeReturner, fieldedX + 6, laneTargetY + 8, -direction, "defense");
          ballX = carryBall.ballX;
          ballY = carryBall.ballY;
        }
      }

      return { ballX, ballY };
    }

    if (play.playId === "punt") {
      const landingYard = play.landingYardLine || play.endYardLine || play.startYardLine;
      const landingX = screenXForYard(landingYard, direction);
      const shieldCatchX = orientX(landingX, -12, direction);
      const containCatchX = orientX(landingX, -24, direction);
      const coverageCatchX = orientX(landingX, -18, direction);
      const catchProgress = Math.min(1, progress / 0.62);
      const returnProgress = Math.max(0, (progress - 0.62) / 0.38);
      const tacklePhase = Math.max(0, (progress - 0.76) / 0.24);
      const touchback = resultText.includes("touchback");
      const returnDirection = -direction;

      offensePlayers.forEach((player: any) => {
        if (player.role.startsWith("PROTECT")) {
          offsetPlayer(offensePlayers, player.role, xDelta(10), 0, Math.min(1, progress * 0.34), true);
        }
      });
      movePlayer(offensePlayers, "UPBACK", orientX(losX, -36, direction), centerY + 20, Math.min(1, progress * 0.4), true);
      movePlayer(offensePlayers, "PUNTER", orientX(losX, -72, direction), centerY - 4, Math.min(1, progress * 0.34), true);
      if (touchback) {
        movePlayer(offensePlayers, "GUNNER1", coverageCatchX, centerY - 152, Math.min(1, Math.max(0, progress - 0.18) / 0.82), true);
        movePlayer(offensePlayers, "GUNNER2", coverageCatchX, centerY + 148, Math.min(1, Math.max(0, progress - 0.18) / 0.82), true);
      } else {
        // Two-phase spline eliminates the double-lerp speed spike.
        // Spline 0→0.5 maps to animation 0.18→0.62 (punt flight, coverage sprint).
        // Spline 0.5→1.0 maps to animation 0.62→1.0 (return pursuit to tackle).
        // The catmull-rom curve through the intermediate produces smooth, continuous velocity.
        const gunnerSpline = progress < 0.62
          ? Math.max(0, (progress - 0.18) / 0.44) * 0.5
          : 0.5 + (progress - 0.62) / 0.38 * 0.5;
        // Intermediate: 44 px past landing in the KICK direction so the GUNNER
        // is behind the returner at catch time.  The gap (44 px) exceeds the
        // extra X the GUNNER covers vs the returner (41 px), guaranteeing the
        // GUNNER never crosses the returner during the return phase.
        const gunnerPivotX = orientX(landingX, 44, direction);
        movePlayerAlongSpline(offensePlayers, "GUNNER1", [
          { x: gunnerPivotX, y: centerY - 86 },
          { x: endX + xDelta(3), y: laneY - 14 },
        ], gunnerSpline, true);
        movePlayerAlongSpline(offensePlayers, "GUNNER2", [
          { x: gunnerPivotX, y: centerY + 86 },
          { x: endX + xDelta(6), y: laneY + 14 },
        ], gunnerSpline, true);
      }

      defensePlayers.forEach((player: any) => {
        if (player.role.startsWith("RUSH")) {
          offsetPlayer(defensePlayers, player.role, xDelta(-12), 0, Math.min(1, progress * 0.28), true);
        }
      });
      movePlayer(defensePlayers, "CONTAIN1", containCatchX, centerY - 104, catchProgress, true);
      movePlayer(defensePlayers, "CONTAIN2", containCatchX, centerY + 100, catchProgress, true);
      movePlayer(defensePlayers, "SHIELD", shieldCatchX, laneY, catchProgress, true);
      movePlayer(defensePlayers, "RETURNER", landingX, laneY, catchProgress, true);

      if (!touchback && progress >= 0.62) {
        movePlayer(defensePlayers, "RETURNER", endX, laneY, returnProgress, true);
        movePlayer(defensePlayers, "SHIELD", snapToPixel(lerp(orientX(endX, -26, direction), endX + returnDirection * 22, tacklePhase * 0.82)), snapToPixel(lerp(laneY - 20, laneY - 26, tacklePhase * 0.82)), returnProgress, true);
        movePlayer(defensePlayers, "CONTAIN1", snapToPixel(lerp(orientX(endX, -42, direction), endX + returnDirection * 44, tacklePhase * 0.72)), snapToPixel(lerp(laneY - 58, laneY - 50, tacklePhase * 0.72)), returnProgress, true);
        movePlayer(defensePlayers, "CONTAIN2", snapToPixel(lerp(orientX(endX, -42, direction), endX + returnDirection * 44, tacklePhase * 0.72)), snapToPixel(lerp(laneY + 54, laneY + 46, tacklePhase * 0.72)), returnProgress, true);
      }

      const punter = findPlayer(offensePlayers, "PUNTER");
      const activeReturner = findPlayer(defensePlayers, "RETURNER");
      const puntOrigin = punter
        ? carriedBallPoint(punter, orientX(losX, -30, direction), centerY - 10, direction, "offense")
        : { ballX: orientX(losX, -30, direction), ballY: centerY - 10 };
      const puntOriginX = puntOrigin.ballX;
      const puntOriginY = puntOrigin.ballY;

      if (touchback) {
        ballX = snapToPixel(lerp(puntOriginX, screenXForYard(play.landingYardLine || 100, direction), progress));
        ballY = snapToPixel(lerp(puntOriginY, laneY + 8, progress) - Math.sin(progress * Math.PI) * 132);
      } else if (activeReturner) {
        if (progress < 0.62) {
          ballX = snapToPixel(lerp(puntOriginX, landingX + 6, catchProgress));
          ballY = snapToPixel(lerp(puntOriginY, laneY + 8, catchProgress) - Math.sin(catchProgress * Math.PI) * 132);
        } else {
          const carryBall = carriedBallPoint(activeReturner, landingX + 6, laneY + 8, -direction, "defense");
          ballX = carryBall.ballX;
          ballY = carryBall.ballY;
        }
      }

      return { ballX, ballY };
    }

    if (play.playId === "field-goal" || play.playId === "pat") {
      const snapper = findPlayer(offensePlayers, "BLOCK4");
      for (let index = 1; index <= 7; index += 1) {
        offsetPlayer(offensePlayers, "BLOCK" + index, xDelta(8), 0, eased * 0.42, true);
        offsetPlayer(defensePlayers, "RUSH" + index, xDelta(-16), 0, eased * 0.72, true);
      }
      offsetPlayer(offensePlayers, "WING1", xDelta(12), -10, eased * 0.44, true);
      offsetPlayer(offensePlayers, "WING2", xDelta(12), 10, eased * 0.44, true);
      offsetPlayer(defensePlayers, "EDGE1", xDelta(-24), -12, eased * 0.82, true);
      offsetPlayer(defensePlayers, "EDGE2", xDelta(-24), 12, eased * 0.82, true);
      offsetPlayer(defensePlayers, "LEAPER1", xDelta(-10), -8, eased * 0.58, true);
      offsetPlayer(defensePlayers, "LEAPER2", xDelta(-10), 8, eased * 0.58, true);
      movePlayer(offensePlayers, "KICKER", orientX(losX, -76, direction), centerY + 10, Math.min(1, progress * 1.4), true);

      const holder = findPlayer(offensePlayers, "HOLDER");
      const snapEnd = 0.16;
      const holdEnd = 0.28;
      const kickProgress = Math.max(0, (progress - 0.28) / 0.72);
      const postX = goalPostWorldX(direction);
      const kickMeta = play.fieldGoal || {};
      const missSide = kickMeta.missSide || (kickMeta.good === false ? (playHash(play, "fg-miss-side") % 2 === 0 ? "left" : "right") : "center");
      const targetX = snapToPixel(postX + direction * 34);
      const targetY = kickMeta.good
        ? centerY - 104
        : missSide === "left"
          ? centerY + 56
          : centerY - 164;
      const holderAnchor = holder
        ? (ballAnchorPointsForPlayer(holder as any, { direction }, "offense").slice(-1)[0] || { x: holder.x + 10, y: holder.y + 8 })
        : null;
      const snapBall = snapper
        ? fieldGoalSnapBallPoint(snapper as any, direction, losX, centerY - 6)
        : { ballX: snapToPixel(losX), ballY: snapToPixel(centerY - 6) };
      if (holderAnchor && progress < snapEnd) {
        const snapBlend = progress / Math.max(snapEnd, 0.001);
        const blendedSnap = blendBallPoint(snapBall, { ballX: holderAnchor.x, ballY: holderAnchor.y }, snapBlend);
        ballX = blendedSnap.ballX;
        ballY = blendedSnap.ballY;
      } else if (holderAnchor && progress < holdEnd) {
        ballX = holderAnchor.x;
        ballY = holderAnchor.y;
      } else if (holder) {
        ballX = snapToPixel(lerp(holderAnchor?.x ?? (holder.x + 10), targetX, kickProgress));
        ballY = snapToPixel(lerp(holderAnchor?.y ?? (holder.y + 8), targetY, kickProgress) - Math.sin(kickProgress * Math.PI) * 72);
      }

      return { ballX, ballY };
    }

    const offensiveLine = ["LT", "LG", "C", "RG", "RT"];
    const defensiveFront = ["DE1", "DT1", "DT2", "DE2"];
    offensiveLine.forEach((role) => offsetPlayer(offensePlayers, role, xDelta(8), 0, eased * 0.65, true));
    defensiveFront.forEach((role) => offsetPlayer(defensePlayers, role, xDelta(-14), 0, eased * 0.72, true));
    offsetPlayer(defensePlayers, "LB1", xDelta(-12), 8, eased * 0.46, true);
    offsetPlayer(defensePlayers, "LB2", xDelta(-14), 0, eased * 0.54, true);
    offsetPlayer(defensePlayers, "LB3", xDelta(-12), -8, eased * 0.46, true);

    if (play.family === "run") {
      const carrierRole = play.playId === "kneel" ? "QB" : "RB";
      const runFlow = phaseAmount(progress, 0, stopPointForPlay(play));
      const supportMotion = phaseAmount(runFlow, 0.2, 1);
      const pursuitMotion = phaseAmount(runFlow, 0.34, 1);
      const handoffX = orientX(losX, -62, direction);
      const handoffY = centerY + 10;
      const pressX = orientX(losX, 6, direction);
      const pressY = snapToPixel(lerp(handoffY, laneY, 0.44));

      if (play.playId === "kneel") {
        movePlayerAlongSpline(offensePlayers, "QB", [
          { x: orientX(losX, -58, direction), y: centerY + 2 },
          { x: endX, y: laneY },
        ], runFlow, true, false);
      } else {
        const runner = findPlayer(offensePlayers, "RB");
        if (runner) {
          movePlayerAlongSpline(
            offensePlayers,
            "RB",
            buildRunWaypoints(play, runner.x, runner.y, handoffX, handoffY, pressX, pressY, endX, laneY, centerY, direction),
            runFlow,
            true,
            false
          );
        }
      }

      if (play.playId !== "kneel") {
        movePlayer(offensePlayers, "QB", orientX(losX, -56, direction), centerY - 2, Math.min(1, runFlow * 0.36), true, true, "natural");
      }
      movePlayer(offensePlayers, "TE", orientX(losX, 12, direction), centerY - 94, supportMotion * 0.44, true, true, "natural");
      movePlayer(offensePlayers, "WR1", orientX(losX, 22, direction), centerY - 148, supportMotion * 0.32, true, true, "natural");
      movePlayer(offensePlayers, "WR2", orientX(losX, 22, direction), centerY + 146, supportMotion * 0.32, true, true, "natural");
      movePlayer(offensePlayers, "SLOT", orientX(losX, 18, direction), centerY + 92, supportMotion * 0.34, true, true, "natural");

      const carrier = findPlayer(offensePlayers, carrierRole);
      if (carrier) {
        [
          ["LB2", 18, -28, 0.56],
          ["LB3", 12, 18, 0.6],
          ["CB2", 26, 54, 0.46],
          ["S2", 38, 86, 0.38],
        ].forEach(([role, xOffset, yOffset, speed]: any[]) => {
          movePlayer(defensePlayers, role, carrier.x + xDelta(xOffset as number), carrier.y + (yOffset as number), Math.min(1, pursuitMotion * (speed as number)), true, true, "natural");
        });
        movePlayer(defensePlayers, "CB1", carrier.x + xDelta(34), carrier.y - 98, pursuitMotion * 0.2, true, true, "natural");
        movePlayer(defensePlayers, "S1", carrier.x + xDelta(24), carrier.y - 34, pursuitMotion * 0.28, true, true, "natural");
        const carryBall = carriedBallPoint(carrier, endX, laneY, direction, "offense");
        ballX = carryBall.ballX;
        ballY = carryBall.ballY;
      }

      if (runFlow >= 1) {
        setPlayersStill(offensePlayers, defensePlayers);
      }
      return { ballX, ballY };
    }

    const sack = resultText.includes("sack");
    const incomplete = resultText.includes("incomplete") || stopReasonForPlay(play) === "incomplete";
    const turnover = resultText.includes("interception") || !!play.turnover;
    const completedPass = !turnover && !incomplete && !sack;
    const stopPoint = stopPointForPlay(play);
    const passFlow = phaseAmount(progress, 0, stopPoint);
    const airFactor = clamp01(Math.max(0, (play.airYards || 0) - 5) / 24);
    const dropEnd = lerp(0.24, 0.3, airFactor);
    const plan = routePlan(play, losX, centerY, endX, direction);
    const actualCatchYard = play.catchYardLine || play.passTargetYardLine || play.endYardLine || play.startYardLine;
    const routeTargetYard = completedPass || turnover ? actualCatchYard : (play.passTargetYardLine || actualCatchYard);
    const catchX = screenXForYard(routeTargetYard, direction);
    const routeTargets = Object.fromEntries(
      Object.entries(plan.routes).map(([role, [targetX, targetY]]) => [role, role === plan.targetRole ? [catchX, targetY] : [targetX, targetY]])
    );
    const catchTarget = routeTargets[plan.targetRole] || [catchX, laneY];
    const catchY = catchTarget[1];
    const catchPoint = { x: catchX, y: catchY };
    const endPoint = { x: endX, y: laneY };
    const interceptorRole = turnover ? turnoverDefenderRole(plan.targetRole) : null;
    const pickPoint = turnover ? { x: snapToPixel(catchX + xDelta(10)), y: snapToPixel(catchY) } : null;
    const turnoverEndPoint = turnover ? interceptionReturnEndPoint(play, pickPoint!, endX, laneY, centerY) : null;
    const turnoverPath = turnover ? buildInterceptionReturnPoints(play, pickPoint!, turnoverEndPoint!, direction, centerY) : null;
    const liveMs = livePlayDurationMs(play);
    const defense = turnover ? teamForPlayId(play.defense) : null;
    const interceptorSpeed = turnover
      ? passPixelsPerSecondForSpeed(defense ? speedRatingForRole(defense, interceptorRole!) : 80)
      : 0;
    const targetReceiver = findPlayer(offensePlayers, plan.targetRole) || findPlayer(offensePlayers, "WR1");
    const targetRouteDistance = targetReceiver
      ? movementDistanceForStyle("orthogonal", targetReceiver.x, targetReceiver.y, catchX, catchY)
      : Math.abs(catchX - losX);
    const targetRouteMs = targetRouteDistance / passPixelsPerSecondForSpeed(targetReceiver ? targetReceiver.speedRating ?? 75 : 75) * 1000;
    const routeCatchPhase = targetRouteMs / Math.max(1, liveMs * stopPoint);
    const flightWindow = lerp(0.14, 0.2, airFactor);
    const catchFloor = dropEnd + lerp(0.08, 0.12, airFactor);
    const catchPhase = Math.max(catchFloor, Math.min(0.82, routeCatchPhase));
    const throwStart = Math.max(dropEnd + 0.02, catchPhase - flightWindow);
    const turnoverReturnMs = turnoverPath?.length
      ? (pathDistance(turnoverPath) / Math.max(1, interceptorSpeed)) * 1000
      : 0;
    const postCatchPhase = turnover
      ? Math.min(1, catchPhase + Math.max(0.14, turnoverReturnMs / Math.max(1, liveMs * stopPoint)))
      : Math.min(1, catchPhase + 0.18);
    const routeElapsedMs = liveMs * stopPoint * Math.min(passFlow, catchPhase);
    const dropAmount = phaseAmount(passFlow, 0, dropEnd);
    const flightAmount = phaseAmount(passFlow, throwStart, catchPhase);
    const finishAmount = phaseAmount(passFlow, catchPhase, 1);

    movePlayer(offensePlayers, "QB", orientX(losX, -74, direction), centerY - 8, dropAmount, true);
    Object.entries(routeTargets).forEach(([role, [targetX, targetY]]) => {
      movePlayerAtSpeed(offensePlayers, role, targetX, targetY, routeElapsedMs, true, "orthogonal", 1);
    });

    if (routeTargets.WR1) {
      movePlayerAtSpeed(defensePlayers, "CB1", routeTargets.WR1[0] + xDelta(16), routeTargets.WR1[1], routeElapsedMs, true, "orthogonal", 0.96);
    }
    if (routeTargets.WR2) {
      movePlayerAtSpeed(defensePlayers, "CB2", routeTargets.WR2[0] + xDelta(16), routeTargets.WR2[1], routeElapsedMs, true, "orthogonal", 0.96);
    }
    if (routeTargets.TE) {
      movePlayerAtSpeed(defensePlayers, "S1", orientMax(orientX(losX, 90, direction), routeTargets.TE[0] + xDelta(18)), routeTargets.TE[1] + 6, routeElapsedMs, true, "orthogonal", 0.9);
    }
    if (routeTargets.SLOT) {
      movePlayerAtSpeed(defensePlayers, "S2", orientMax(orientX(losX, 90, direction), routeTargets.SLOT[0] + xDelta(18)), routeTargets.SLOT[1] - 6, routeElapsedMs, true, "orthogonal", 0.9);
    }

    const qb = findPlayer(offensePlayers, "QB");
    let target = findPlayer(offensePlayers, plan.targetRole) || findPlayer(offensePlayers, "WR1") || qb;
    if (completedPass && plan.targetRole && passFlow > catchPhase) {
      const yacPath = buildPostCatchPoints(play, catchPoint, endPoint);
      const yacProgress = clamp01(1 - Math.pow(1 - finishAmount, 1.18));
      target = movePlayerAlongPolyline(
        offensePlayers,
        plan.targetRole,
        yacPath.slice(1),
        yacProgress,
        true,
        true
      ) || target;
    }

    if (sack && qb) {
      [
        ["DE1", 18, -28, 0.84],
        ["DT1", 12, 0, 0.9],
        ["LB2", 20, 26, 0.78],
      ].forEach(([role, xOffset, yOffset, speed]: any[]) => {
        movePlayer(defensePlayers, role, qb.x + xDelta(xOffset as number), qb.y + (yOffset as number), Math.min(1, passFlow * (speed as number)), true);
      });
      if (passFlow >= 1) {
        setPlayersStill(offensePlayers, defensePlayers);
      }
      const qbCarryBall = carriedBallPoint(qb, catchX + 6, catchY + 8, direction, "offense");
      ballX = qbCarryBall.ballX;
      ballY = qbCarryBall.ballY;
      return { ballX, ballY };
    }

    if (qb && target) {
      const movingTarget = findPlayer(offensePlayers, plan.targetRole) || target;
      const qbCarryBall = carriedBallPoint(qb, catchX + 6, catchY + 8, direction, "offense");
      const targetCarryBall = completedPass && movingTarget
        ? carriedBallPoint(movingTarget, catchX + 6, catchY + 8, direction, "offense")
        : { ballX: catchX + 6, ballY: catchY + 8 };
      const flightTargetX = targetCarryBall.ballX;
      const flightTargetY = targetCarryBall.ballY;

      if (passFlow < throwStart) {
        ballX = qbCarryBall.ballX;
        ballY = qbCarryBall.ballY;
      } else if (passFlow < catchPhase) {
        ballX = snapToPixel(lerp(qbCarryBall.ballX, flightTargetX, flightAmount));
        ballY = snapToPixel(lerp(qbCarryBall.ballY, flightTargetY, flightAmount) - Math.sin(flightAmount * Math.PI) * 54);
      } else if (turnover) {
        if (turnoverPath?.length) {
          movePlayerAlongSpline(
            defensePlayers,
            interceptorRole,
            turnoverPath,
            phaseAmount(passFlow, catchPhase, postCatchPhase),
            true,
            true,
            "constant"
          );
        }
        const defender = findPlayer(defensePlayers, interceptorRole!);
        const carryBall = defender
          ? carriedBallPoint(defender, catchX + 6, catchY + 8, -direction, "defense")
          : { ballX: catchX + 6, ballY: catchY + 8 };
        ballX = carryBall.ballX;
        ballY = carryBall.ballY;
      } else if (incomplete) {
        ballX = catchX + 6;
        ballY = catchY + 22;
      } else {

        const activeTarget = findPlayer(offensePlayers, plan.targetRole) || target;
        const rallyRoles = plan.targetRole === "WR2"
          ? [["CB2", 18, 4, 1], ["S2", 34, -18, 0.72], ["LB3", 28, 22, 0.6]]
          : plan.targetRole === "RB"
            ? [["LB2", 14, -12, 0.96], ["LB3", 22, 16, 0.82], ["S2", 34, -4, 0.58]]
            : plan.targetRole === "TE"
              ? [["S1", 18, -8, 0.96], ["LB1", 28, -24, 0.66], ["LB2", 24, 18, 0.62]]
              : plan.targetRole === "SLOT"
                ? [["S2", 18, 8, 0.94], ["LB3", 28, 24, 0.68], ["CB2", 34, 46, 0.52]]
                : [["CB1", 18, -4, 1], ["S1", 34, 18, 0.7], ["LB1", 28, -26, 0.56]];
        const rallyStart = Math.min(0.94, catchPhase + 0.06);
        const rallyAmount = phaseAmount(passFlow, rallyStart, 1);
        rallyRoles.forEach(([role, xOffset, yOffset, speed]: any[], index: number) => {
          const closeTackle = index === 0;
          const assistTackle = index === 1 && finishAmount > 0.58;
          const containSpread = closeTackle ? 1 : assistTackle ? 1.28 : 2.15;
          const rallySpeed = closeTackle ? speed : assistTackle ? speed * 0.82 : speed * 0.46;
          const targetX = activeTarget.x + xDelta((xOffset as number) * containSpread);
          const targetY = activeTarget.y + (yOffset as number) * containSpread;
          movePlayer(defensePlayers, role, targetX, targetY, Math.min(1, rallyAmount * (rallySpeed as number)), true, true, "natural");
        });
        const carryBall = carriedBallPoint(activeTarget, catchX + 6, catchY + 8, direction, "offense");
        ballX = carryBall.ballX;
        ballY = carryBall.ballY;
      }
    }

    if (passFlow >= 1) {
      setPlayersStill(offensePlayers, defensePlayers);
    }
    return { ballX, ballY };
  }

  function drawPlayScene(now: any) {
    const play = currentPlay();
    if (!play || !state.game) {
      drawFieldBase(0);
      return;
    }

    // A play without `visual` is an un-hydrated live-window skeleton ({}). It has
    // no positions/lanes to render, so draw the field instead of dereferencing
    // missing data — an uncaught throw here freezes the whole animation loop.
    if (!play.visual) {
      drawFieldBase(0);
      return;
    }

    const centerY = fieldMetrics().centerY;
    const previousPlay = previousPlayAt(state.playIndex);
    const currentContext = buildPlayContext(play, centerY);
    const totalDuration = baseTimedPlayDurationMs(play, state.playIndex);
    const rawElapsed = state.autoplay
      ? Math.max(0, now - state.lastAdvance)
      : Math.max(0, state.pausedElapsed ?? 0);
    const elapsed = Math.min(
      totalDuration,
      divingHeroBaseElapsedMs(
        play,
        rawElapsed,
        totalDuration,
        livePlayDurationMs(play),
      ),
    );
    const deadHoldMs = state.playIndex > 0 ? PLAY_TIMING.deadHoldMs : 0;
    const intermission = activePostPlayIntermission(now);

    if (intermission) {
      drawPostPlayIntermission(currentContext, intermission, now);
      return;
    }

    state.intermissionCameraStartX = null;
    state.intermissionKey = null;

    if (previousPlay && previousPlay.visual && elapsed < deadHoldMs) {
      drawPlayFrame(buildAnimatedFrame(buildPlayContext(previousPlay, centerY), 1));
      return;
    }

    if (isSpecialTeamsPlay(play)) {
      const preSnapElapsed = Math.max(0, elapsed - deadHoldMs);
      const previousContext = previousPlay ? buildPlayContext(previousPlay, centerY) : null;
      const suppressExit = shouldSkipSpecialTeamsExit(previousPlay, play);
      const previousFrame = previousContext && !suppressExit ? buildAnimatedFrame(previousContext, 1) : null;
      const exitMs = previousFrame ? PLAY_TIMING.specialTeamsExitMs : 0;
      const puntSnapper = play.playId === 'punt'
        ? findPlayer(currentContext.formation.offense, 'PROTECT4')
          || findPlayer(currentContext.formation.offense, 'PROTECT3')
          || currentContext.formation.offense.find((player: any) => String(player?.role || '').startsWith('PROTECT'))
        : null;
      const fieldGoalSnapper = play.playId === 'field-goal' || play.playId === 'pat'
        ? findPlayer(currentContext.formation.offense, 'BLOCK4')
          || findPlayer(currentContext.formation.offense, 'BLOCK3')
          || currentContext.formation.offense.find((player: any) => String(player?.role || '').startsWith('BLOCK'))
        : null;
      const staticBall = play.playId === 'punt'
        ? puntSnapBallPoint(
            puntSnapper as any,
            currentContext.direction,
            currentContext.losX,
            currentContext.centerY - 6,
          )
        : (play.playId === 'field-goal' || play.playId === 'pat')
          ? fieldGoalSnapBallPoint(
              fieldGoalSnapper as any,
              currentContext.direction,
              currentContext.losX,
              currentContext.centerY - 6,
            )
        : {
            ballX: snapToPixel(currentContext.losX),
            ballY: currentContext.centerY - 6,
          };

      if (previousFrame && preSnapElapsed < exitMs) {
        drawPlayFrame({
          ...currentContext,
          offensePlayers: transitionPlayers(
            previousFrame.offensePlayers,
            previousFrame.offensePlayers,
            buildSidelineTargets(previousFrame.offensePlayers, "bottom"),
            phaseAmount(preSnapElapsed, 0, exitMs)
          ),
          defensePlayers: transitionPlayers(
            previousFrame.defensePlayers,
            previousFrame.defensePlayers,
            buildSidelineTargets(previousFrame.defensePlayers, "top"),
            phaseAmount(preSnapElapsed, 0, exitMs)
          ),
          offensePalette: previousContext!.offensePalette,
          defensePalette: previousContext!.defensePalette,
          scenePhase: "special-exit",
          ball: staticBall,
        });
        return;
      }

      const entryStart = exitMs;
      const entryEnd = entryStart + PLAY_TIMING.specialTeamsEnterMs;
      const setEnd = entryEnd + PLAY_TIMING.specialTeamsSetMs;
      const offenseBench = buildSidelineTargets(currentContext.formation.offense, "bottom");
      const defenseBench = buildSidelineTargets(currentContext.formation.defense, "top");

      if (preSnapElapsed < entryEnd) {
        const entryProgress = phaseAmount(preSnapElapsed, entryStart, entryEnd);
        drawPlayFrame({
          ...currentContext,
          offensePlayers: transitionPlayers(currentContext.formation.offense, offenseBench, currentContext.formation.offense, entryProgress),
          defensePlayers: transitionPlayers(currentContext.formation.defense, defenseBench, currentContext.formation.defense, entryProgress),
          scenePhase: "special-entry",
          ball: staticBall,
        });
        return;
      }

      if (preSnapElapsed < setEnd) {
        const offensePlayers = clonePlayers(currentContext.formation.offense);
        const defensePlayers = clonePlayers(currentContext.formation.defense);
        setPlayersStill(offensePlayers, defensePlayers);
        drawPlayFrame({
          ...currentContext,
          offensePlayers,
          defensePlayers,
          scenePhase: "special-set",
          ball: staticBall,
        });
        return;
      }

      drawPlayFrame(
        buildAnimatedFrame(
          currentContext,
          phaseAmount(preSnapElapsed, setEnd, totalDuration - deadHoldMs)
        )
      );
      return;
    }

    if (!isScrimmageSnap(play)) {
      drawPlayFrame(buildAnimatedFrame(currentContext, phaseAmount(elapsed, deadHoldMs, totalDuration)));
      return;
    }

    const preSnapElapsed = Math.max(0, elapsed - deadHoldMs);
    const huddle = buildScrimmageHuddle(currentContext);
    const previousWasSpecialTeams = !!previousPlay && isSpecialTeamsPlay(previousPlay);
    const previousSameUnit =
      previousPlay &&
      isScrimmageSnap(previousPlay) &&
      previousPlay.offense === play.offense &&
      previousPlay.defense === play.defense;
    const changeoverMs = scrimmageChangeoverMs(play, previousPlay);
    const previousNeedsRunoff = !!previousPlay && changeoverMs > 0;
    const huddleStart = changeoverMs;
    const huddleEnd = huddleStart + PLAY_TIMING.huddleRunMs;
    const breakEnd = huddleEnd + PLAY_TIMING.breakHuddleMs;
    const setEnd = breakEnd + PLAY_TIMING.preSnapHoldMs;
    const scrimmageSnapper = findPlayer(currentContext.formation.offense, 'C');
    const staticBall = scrimmageSnapper
      ? centerSnapBallPoint(
          scrimmageSnapper as any,
          currentContext.direction,
          currentContext.losX,
          currentContext.centerY - 6,
        )
      : scrimmageBallPoint(
          currentContext.formation.offense,
          currentContext.direction,
          currentContext.losX,
          currentContext.centerY - 6
        );
    const offenseBench = buildSidelineTargets(currentContext.formation.offense, "bottom");
    const defenseBench = buildSidelineTargets(currentContext.formation.defense, "top");

    if (previousNeedsRunoff && preSnapElapsed < changeoverMs) {
      const previousContext = buildPlayContext(previousPlay, centerY);
      const previousFrame = buildAnimatedFrame(previousContext, 1);
      const runoffProgress = phaseAmount(preSnapElapsed, 0, changeoverMs);
      drawPlayFrame({
        ...currentContext,
        offensePlayers: transitionPlayers(
          previousFrame.offensePlayers,
          previousFrame.offensePlayers,
          buildSidelineTargets(previousFrame.offensePlayers, "bottom"),
          runoffProgress
        ),
        defensePlayers: transitionPlayers(
          previousFrame.defensePlayers,
          previousFrame.defensePlayers,
          buildSidelineTargets(previousFrame.defensePlayers, "top"),
          runoffProgress
        ),
        offensePalette: previousContext.offensePalette,
        defensePalette: previousContext.defensePalette,
        scenePhase: previousWasSpecialTeams ? "special-exit" : "scrimmage-changeover",
        ball: staticBall,
      });
      return;
    }

    if (preSnapElapsed < huddleEnd) {
      if (previousNeedsRunoff) {
        const huddleProgress = phaseAmount(preSnapElapsed, huddleStart, huddleEnd);
        drawPlayFrame({
          ...currentContext,
          offensePlayers: transitionPlayers(currentContext.formation.offense, offenseBench, huddle.offense, huddleProgress),
          defensePlayers: transitionPlayers(currentContext.formation.defense, defenseBench, huddle.defense, huddleProgress),
          scenePhase: "huddle",
          ball: staticBall,
        });
      } else if (previousSameUnit) {
        const previousFrame = buildAnimatedFrame(buildPlayContext(previousPlay, centerY), 1);
        const huddleProgress = phaseAmount(preSnapElapsed, 0, huddleEnd);
        drawPlayFrame({
          ...currentContext,
          offensePlayers: transitionPersonnelPlayers(
            previousFrame.offensePlayers,
            huddle.offense,
            buildSidelineTargets(previousFrame.offensePlayers, "bottom"),
            buildSidelineTargets(huddle.offense, "bottom"),
            huddleProgress
          ),
          defensePlayers: transitionPersonnelPlayers(
            previousFrame.defensePlayers,
            huddle.defense,
            buildSidelineTargets(previousFrame.defensePlayers, "top"),
            buildSidelineTargets(huddle.defense, "top"),
            huddleProgress
          ),
          scenePhase: "huddle",
          ball: staticBall,
        });
      } else {
        const offensePlayers = clonePlayers(huddle.offense);
        const defensePlayers = clonePlayers(huddle.defense);
        setPlayersStill(offensePlayers, defensePlayers);
        drawPlayFrame({
          ...currentContext,
          offensePlayers,
          defensePlayers,
          scenePhase: "huddle",
          ball: staticBall,
        });
      }
      return;
    }

    if (preSnapElapsed < breakEnd) {
      const formationProgress = phaseAmount(preSnapElapsed, huddleEnd, breakEnd);
      drawPlayFrame({
        ...currentContext,
        offensePlayers: transitionPlayers(currentContext.formation.offense, huddle.offense, currentContext.formation.offense, formationProgress),
        defensePlayers: transitionPlayers(currentContext.formation.defense, huddle.defense, currentContext.formation.defense, formationProgress),
        scenePhase: "huddleBreak",
        ball: staticBall,
      });
      return;
    }

    if (preSnapElapsed < setEnd) {
      const offensePlayers = clonePlayers(currentContext.formation.offense);
      const defensePlayers = clonePlayers(currentContext.formation.defense);
      setPlayersStill(offensePlayers, defensePlayers);
      drawPlayFrame({
        ...currentContext,
        offensePlayers,
        defensePlayers,
        scenePhase: "presnap",
        ball: staticBall,
      });
      return;
    }

    drawPlayFrame(
      buildAnimatedFrame(
        currentContext,
        phaseAmount(
          preSnapElapsed,
          setEnd,
          totalDuration - deadHoldMs
        )
      )
    );
  }

  function animationFrame(now: any) {
    drawPlayScene(now);
    const completedCount = completedPlayCount(now);
    if (completedCount !== state.renderedStatsCount || state.playIndex !== state.renderedPlayIndex) {
      state.renderedStatsCount = completedCount;
      state.renderedPlayIndex = state.playIndex;
      renderLiveStats(completedCount);
      renderBroadcastHud(completedCount);
    }
    updatePlayCaption(now);
    renderHalftimeCountdown();

    const play = currentPlay();
    const intermission = activePostPlayIntermission(now);
    const holdAfterPlayMs = intermission && intermission.kind !== "final" ? intermission.durationMs : 0;
    const halftimePresentation = halftimeCountdownPresentation(state.liveSyncLastBroadcast?.halftime, Date.now(), {
      secondHalfStarted: Number(play?.quarter || 0) >= 3,
    });
    if (state.autoplay && state.game && play) {
      const elapsed = now - state.lastAdvance;
      const duration = timedPlayDurationMs(play, state.playIndex);
      const advanceDuration = liveAdvanceDurationMs(play, state.playIndex);
      if (state.playMode === "replay" && elapsed > duration + replayHoldMs()) {
        resetReplayLoopState(state, play, now, { visualTest: visualTestPage });
        state.renderedProgressPercent = null;
        renderPlayLog();
        updatePlayCaption();
        updateUrl();
      } else if (
        state.playMode === "continue"
        && state.playIndex < state.game.plays.length - 1
        && elapsed > advanceDuration + holdAfterPlayMs
        && !shouldHoldForHalftimeAuthority(halftimePresentation)
      ) {
        const upcomingPlay = state.game.plays[state.playIndex + 1];
        if (upcomingPlay?.visual) {
          advanceContinuePlayback(state, play, now, { skipDeadHold: holdAfterPlayMs > 0 });
          state.renderedProgressPercent = null;
          clearVisualPlayerCache();
          renderPlayLog();
          updatePlayCaption();
          updateUrl();
        } else if (currentScheduledGameId() && !isDebugMode()) {
          // The local playhead reached the edge of the hydrated live window before
          // the next scheduled poll: out-of-window plays arrive as empty skeletons
          // ({} with no `visual`), which the renderer can't draw. Hold on the
          // current play and pull the next window now instead of advancing onto an
          // un-renderable play (which would throw in the rAF loop and freeze
          // playback). pollLiveBroadcastSync's in-flight guard throttles this.
          void pollLiveBroadcastSync(currentScheduledGameId() as string);
        }
      }
      // "hold" mode: do nothing — play freezes at end until user acts
    }

    // Update progress bar
    if (state.game && play) {
      const elapsed = state.autoplay ? now - state.lastAdvance : (state.pausedElapsed ?? 0);
      const duration = liveAdvanceDurationMs(play, state.playIndex);
      const progressPercent = Math.min(100, Math.round((elapsed / duration) * 100));
      if (progressPercent !== state.renderedProgressPercent) {
        state.renderedProgressPercent = progressPercent;
        refs.playProgressFill!.style.width = `${progressPercent}%`;
      }
    }

    renderStageLoading(now);

    requestAnimationFrame(animationFrame);
  }

  async function load() {
    // Safety net: getFranchiseById throws if the shared snapshot was never
    // hydrated. Seed it empty so any stray early read is safe; the real franchise
    // registry from /api/bootstrap replaces it below, before the team-specific
    // pregame curtain renders. Guarded so a reload doesn't wipe live data.
    if (!isSharedDataHydrated()) {
      hydrateSharedData({ franchises: [], players: [] });
    }
    const requestId = beginStageLoading([
      {
        key: "bootstrap",
        title: "Loading game...",
        status: "Connecting to the broadcast.",
      },
      {
        key: visualTestPage ? "visual-test" : "simulate",
        title: "Loading game...",
        status: visualTestPage
          ? "Building the requested visual test scene."
          : "Generating the live broadcast feed.",
      },
      {
        key: "assets",
        title: "Loading game...",
        status: "Preparing the field and players.",
      },
    ]);
    try {
      const bootstrapStartedAt = performance.now();
      const bootstrapController = createStageLoadingController(requestId);
      state.bootstrap = await fetchJson("/api/bootstrap", bootstrapController ? { signal: bootstrapController.signal } : {});
      // Hydrate the shared franchise snapshot from the bootstrap payload BEFORE
      // showing the pregame curtain (which resolves team logos/colors via
      // getFranchiseById). The player pool isn't used client-side, so an empty
      // array keeps the payload lean.
      hydrateSharedData({ franchises: state.bootstrap?.franchises || [], players: [] });
      refreshBootstrapTeams();
      rememberLoadDuration("bootstrap", performance.now() - bootstrapStartedAt);
      if (!isCurrentLoadingRequest(requestId)) {
        return;
      }
      const pregameCurtainActive = await showPregameLoadingCurtain();
      populateTeamSelects();

      const params = currentUrlParams();
      const urlHome = params.get("home");
      const urlAway = params.get("away");
      const urlSeed = params.get("seed");
      const urlPlay = params.get("play");
      const urlMode = params.get("mode");
      const scheduledRoute = currentScheduledGameId()
        ? await getScheduledGame(currentScheduledGameId()!)
        : null;

      if (scheduledRoute) {
        refs.homeSelect!.value = scheduledRoute.home;
        refs.awaySelect!.value = scheduledRoute.away;
        refs.seedInput!.value = `s${scheduledRoute.seasonId}WK${scheduledRoute.week}-${scheduledRoute.index}`;
        if (pregameCurtainActive) {
          showPregameLoadingCurtain();
        }
      } else if (urlHome || urlAway || urlSeed || visualTestPage) {
        if (urlHome) refs.homeSelect!.value = urlHome;
        if (urlAway) refs.awaySelect!.value = urlAway;
        if (urlSeed) refs.seedInput!.value = urlSeed;
        if (visualTestPage && !urlSeed) {
          refs.seedInput!.value = "VISUAL-TEST";
        }
      } else {
        pickRandomMatchup();
      }

      if (urlMode && ["hold", "replay", "continue"].includes(urlMode)) {
        setPlayMode(urlMode);
      } else if (visualTestPage) {
        setPlayMode("replay");
      }

      setStageLoadingPhase(requestId, 1);
      await simulate({ loadingRequestId: requestId });
      if (!isCurrentLoadingRequest(requestId)) {
        return;
      }

      if (!visualTestPage && urlPlay !== null) {
        const targetIndex = Math.max(0, parseInt(urlPlay, 10) - 1);
        state.playIndex = Math.min(targetIndex, (state.game?.plays?.length ?? 1) - 1);
        // Skip the inter-play dead-hold so the target play's pre-snap starts immediately.
        state.lastAdvance = performance.now() - PLAY_TIMING.deadHoldMs;
        resetFieldZoomState();
        updateUrl();
      }
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
      const message = reportClientError("load", error, "Could not load the game");
      failStageLoading(requestId, message);
      refs.playCaption!.textContent = message;
      renderBroadcastHud(0);
      return;
    }
    finishStageLoading(requestId);
  }



  // Node.js animation test hook
  if (typeof globalThis !== 'undefined' && (globalThis as any).__dflTestMode) {
    (globalThis as any).__dflTestHarness = { state, buildPlayContext, buildRunLiveSequence, buildPassLiveSequence, buildPuntLiveSequence, liveFrameCount };
  }

  function updatePlayPauseButton() {
    refs.playPauseButton!.textContent = state.autoplay ? "\u23F8" : "\u25B6";
  }

  function setPlayMode(mode: any) {
    state.playMode = mode;
    refs.modeButtons.forEach((btn: any) => {
      btn.classList.toggle("active", btn.dataset.mode === mode);
    });
    updateUrl();
  }

  function updateUrl() {
    if (!state.game || typeof history === "undefined" || typeof history.replaceState !== "function") return;
    const params = new URLSearchParams();
    const scheduledGameId = currentScheduledGameId();
    if (scheduledGameId) {
      params.set("game", canonicalGameId(scheduledGameId));
      if (isDebugMode()) {
        params.set("play", String(state.playIndex + 1));
        if (state.playMode !== "continue") params.set("mode", state.playMode);
      }
    } else {
      params.set("home", refs.homeSelect!.value);
      params.set("away", refs.awaySelect!.value);
    }
    if (visualTestPage) {
      const visualTest = currentVisualTestConfig();
      params.set("playId", visualTest.playId);
      params.set("yards", String(visualTest.yards));
      if (visualTest.engine === "arcade") params.set("engine", "arcade");
      if (visualTest.arcadeSeed) params.set("arcadeSeed", visualTest.arcadeSeed);
      if (visualTest.engine === "arcade" && visualTest.arcadeView === "raw") params.set("arcadeView", "raw");
      if (visualTest.passOutcome !== "auto") params.set("passOutcome", visualTest.passOutcome);
      if (visualTest.turnoverOutcome !== "auto") params.set("turnoverOutcome", visualTest.turnoverOutcome);
      if (visualTest.onsideOutcome !== "auto") params.set("onsideOutcome", visualTest.onsideOutcome);
      if (visualTest.celebrationStyle) params.set("celebrationStyle", visualTest.celebrationStyle);
      if (visualTest.embed) params.set("embed", "1");
      params.set("mode", state.playMode);
    } else if (!scheduledGameId) {
      params.set("seed", refs.seedInput!.value);
      params.set("play", String(state.playIndex + 1));
      if (state.playMode !== "continue") params.set("mode", state.playMode);
    }
    if (isDebugMode()) {
      params.set("debug", "true");
    }
    history.replaceState(null, "", "?" + params.toString());
  }

  // Event listeners -- only wire up in browser environment
  if (typeof window !== 'undefined' && typeof (window as any).addEventListener === 'function') {

  refs.simulateButton!.addEventListener("click", () => {
    clearPlaybackHalt();
    simulate().catch((error: any) => {
      refs.playCaption!.textContent = reportClientError("simulate", error, "Could not load the game");
    });
  });

  refs.randomButton!.addEventListener("click", () => {
    clearPlaybackHalt();
    pickRandomMatchup();
    simulate().catch((error: any) => {
      refs.playCaption!.textContent = reportClientError("simulate", error, "Could not load the game");
    });
  });

  refs.audioToggleButton?.addEventListener("click", () => {
    setAudioMuted(!state.audioMuted);
  });

  window.addEventListener("pointerdown", () => {
    playAmbientCrowdAudio();
    playMatchMusicAudio();
  }, { passive: true });

  window.addEventListener("keydown", () => {
    playAmbientCrowdAudio();
  });

  document.addEventListener("visibilitychange", pollVisibleScheduledBroadcast);
  window.addEventListener("focus", pollVisibleScheduledBroadcast);

  refs.playPauseButton!.addEventListener("click", () => {
    if (state.autoplay) {
      state.pausedElapsed = Math.max(0, performance.now() - state.lastAdvance);
      state.autoplay = false;
    } else {
      clearPlaybackHalt();
      state.lastAdvance = performance.now() - (state.pausedElapsed ?? 0);
      state.pausedElapsed = null;
      state.autoplay = true;
    }
    updatePlayPauseButton();
  });

  refs.prevPlayButton!.addEventListener("click", () => {
    if (!state.game?.plays?.length || state.playIndex <= 0) return;
    state.playIndex -= 1;
    state.lastAdvance = performance.now() - PLAY_TIMING.deadHoldMs;
    state.pausedElapsed = null;
    state.autoplay = true;
    clearPlaybackHalt();
    resetFieldZoomState();
    clearVisualPlayerCache();
    updatePlayPauseButton();
    renderPlayLog();
    updatePlayCaption();
    updateUrl();
  });

  refs.nextPlayButton!.addEventListener("click", () => {
    if (!state.game?.plays?.length || state.playIndex >= state.game.plays.length - 1) return;
    const play = currentPlay();
    state.prevResult = play?.result || null;
    state.playIndex += 1;
    state.lastAdvance = performance.now() - PLAY_TIMING.deadHoldMs;
    state.pausedElapsed = null;
    state.autoplay = true;
    clearPlaybackHalt();
    resetFieldZoomState();
    clearVisualPlayerCache();
    updatePlayPauseButton();
    renderPlayLog();
    updatePlayCaption();
    updateUrl();
  });

  refs.modeButtons.forEach((btn: any) => {
    btn.addEventListener("click", () => setPlayMode(btn.dataset.mode));
  });

  refs.liveStats?.addEventListener("click", (event: any) => {
    const button = event.target?.closest?.("[data-live-stats-team]") as HTMLElement | null;
    if (!button) {
      return;
    }
    const nextTeam = button.dataset.liveStatsTeam === "home" ? "home" : "away";
    if (state.selectedLiveStatsTeam === nextTeam) {
      return;
    }
    state.selectedLiveStatsTeam = nextTeam;
    renderLiveStats();
  });

  refs.playProgressBar!.addEventListener("click", (e: any) => {
    if (!state.game) return;
    const play = currentPlay();
    if (!play) return;
    const rect = refs.playProgressBar!.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const targetElapsed = fraction * timedPlayDurationMs(play, state.playIndex);
    clearPlaybackHalt();
    if (state.autoplay) {
      state.lastAdvance = performance.now() - targetElapsed;
    } else {
      state.pausedElapsed = targetElapsed;
    }
  });

  refs.ownerCoachingButton?.addEventListener("click", () => {
    if (refs.ownerCoachingPanel) refs.ownerCoachingPanel.hidden = !refs.ownerCoachingPanel.hidden;
  });
  refs.ownerCoachingClose?.addEventListener("click", () => {
    if (refs.ownerCoachingPanel) refs.ownerCoachingPanel.hidden = true;
  });
  refs.ownerWalletConnect?.addEventListener("click", () => {
    connectOwnerCoachingWallet().catch((error) => setOwnerCoachingStatus(error?.message || "Wallet verification failed.", "error"));
  });
  refs.ownerCoachingTeam?.addEventListener("change", () => {
    loadOwnerCoachingIdentity().catch((error) => setOwnerCoachingStatus(error?.message || "Could not load team schemas.", "error"));
  });
  refs.ownerCoachingPreset?.addEventListener("change", previewOwnerCoachingPreset);
  refs.ownerCoachingSave?.addEventListener("click", () => {
    saveOwnerCoachingSchemas().catch((error) => setOwnerCoachingStatus(error?.message || "Could not save schemas.", "error"));
  });

  requestAnimationFrame(animationFrame);
  load();
  }
